import { statementForExpression } from '../parser/expression-statement';
import { parseExpression as expressionParserFn } from '../parser/lezer-expression';
const expressionParser = { parse: (input: string) => { const v = expressionParserFn(input); return { status: v !== null, value: v }; } };
import { contextAwareFunctions, stdlib } from '../stdlib';
import { STATEMENT_BUILTINS } from './constructor-registry';
import { RESERVED_UNIT_NAMES, reservedNameBindingError, reservedNameReferenceError } from './reserved-names';
import { BUILTIN_NAMES, builtinNameError, builtinNamespaceType } from '../builtin-names';
import { splitSubpaths } from './subpaths';
import { dispatchSubscriptions } from './subscriptions';
import { CALLBACK_METHODS } from '../callback-methods';
import { arrayMutationError, isArrayLocked, lockArray, unlockArray } from './iteration-lock';
import {
  contextToObject,
  createPathContext,
  createTransformState,
  restoreContext,
  setLastTangent,
  snapshotContext,
  transformStateToSvg,
  updateContextForCommand,
} from './context';
import { formatNum, resetNumberFormat, setNumberFormat } from './format';
import { CHAR_CLASS_PREDICATES, isWhitespaceChar } from './char-class';
import { BUILTIN_ENUMS } from './builtin-enums';
import { assignGradientProperty, assignMarkerProperty, assignMeshPointProperty, assignPatternProperty } from './member-assign';
import { getStructDescriptor } from './struct-properties';

export { BUILTIN_ENUMS };
import { angle, angleMethod, callStdlibPreservingAngles, formatAngleForDisplay, isAngleValue, radiansToDegreesSnapped } from './angle';
import { isBooleanValue, isTruthy, toNumber, valuesEqual } from './value-semantics';
import { selectSwitchArm, selectSwitchClause, type MatchHost } from './switch-match';
import { planRange, RANGE_MESSAGES, rangeValues } from './range-loop';
import type { RangeKind, RangePlan } from './range-loop';
import {
  describeMissingAngleUnit,
  describeMissingAngleUnitForProp,
  describeMissingAngleUnits,
  receiverTypeName,
} from './angle-unit-check';
import { checkAngleUnitMismatch, convertUnitSuffix, convertUnitSuffixToDegrees } from './units';
import { validateCSSIdent, validateCSSValue } from './sanitize';
import { sanitizeSVGFragment } from './svg-sanitize';

/** Maximum iterations allowed per for-loop to prevent runaway programs. */
const MAX_ITERATIONS = 32000;
import {
  cssSourceExpr,
  darken,
  darkenCSS,
  desaturate,
  desaturateCSS,
  flattenColor,
  hueShift,
  hueShiftCSS,
  lighten,
  lightenCSS,
  mixColors,
  mixCSS,
  oklchToCSS,
  parseColor,
  saturate,
  saturateCSS,
  setAlpha,
  setAlphaCSS,
  setLightnessCSS,
} from '../color';
import {
  chamferCommands,
  computeBoundingBox,
  computeBoundingBoxCenter,
  concatenateCommands,
  ellipticalFilletCommands,
  filletCommands,
  mirrorCommands,
  offsetCommands,
  type OffsetJoinOptions,
  reverseCommands,
  rotateAtVertexCommands,
  rotateAboutPointCommands,
  scaleCommands,
  subPathCommands,
} from './path-transforms';
import {
  describeMissingPathArgs,
  describeNonFiniteContextResult,
  describeNonFiniteEmit,
  describeNonFinitePathArgs,
  describeNonFiniteRawArg,
  describeNullPathArg,
  isPathEmittingStdlib,
} from './path-emit-guard';
import { describeFunctionArgument, describeFunctionAsNumber, formatStdlibFunction, isStdlibFunctionValue } from './function-values';
import { describeIgnoredMove } from './ignored-move';
import { pathCut, pathDifference, pathIntersection, pathUnion, pathXor } from './boolean-ops';
import {
  dashCommands,
  outlineCommands,
  parseDashStyles,
  parseOutlineStyles,
  rotateStartCommands,
} from './stroke-geometry';
import { calculatePathLength, partitionPath, samplePathAtFraction, wrapToPi } from './sampling';
import {
  buildSimpleVariableOffset,
  buildCompoundVariableOffset,
  continuityFromValue,
  type SimpleStop,
  type CompoundStop,
  type CapSpec,
} from './variable-offset-geometry';
import { estimateTextBoundingBox, bboxOverlaps, bboxPathIntersects, bboxPathIntersectionPoints, resolveFontFamily, resolveFontWeight, resolveEffectiveFontSize } from './font-metrics';
import { getFont, getGlyphName, lookupGlyph, recordMissingGlyph, buildMissingGlyphReports, splitContours } from './font-provider';
import { buildFontCoverageRequests, fontCharacters, fontGlyphs, fontInfo } from './font-namespace';
import type { FontNamespaceDeps } from './font-namespace';
import { blockOf, categoryOf, scriptOf } from './unicode-props';
import { normalizeCodeText, tokenizeLine, getTokenColor } from './code-snippet';

import type { PathContext, TransformState } from './context';
import type { OKLCH } from '../color';
import type {
  AngleValue,
  ArrayValue,
  BooleanValue,
  CalledFn,
  CallValue,
  CapNamespace,
  CapValue,
  ClipPathOutput,
  ClipPathValue,
  ColorNamespace,
  ColorValue,
  CommandTraceEntry,
  CommandValue,
  CompileResult,
  CompileWarning,
  CSSPropertyDeclaration,
  CSSVarValue,
  CyclerValue,
  ElevationShadowFilterValue,
  EmbossFilterValue,
  EndpointValue,
  EvaluationState,
  FilterOutput,
  FilterValue,
  FontRegistry,
  FragmentLayerState,
  GlowFilterValue,
  GlowModeName,
  GradientOutput,
  GradientStop,
  GradientValue,
  GridInterpolationMode,
  GridOutOfBoundsMode,
  GridValue,
  GroupLayerState,
  InnerShadowFilterValue,
  LayerOutput,
  LayerReference,
  LayerState,
  LayerStyle,
  LogEntry,
  LogPart,
  MarkerNamespace,
  MarkerOutput,
  MarkerValue,
  MaskOutput,
  MaskValue,
  MeshPointValue,
  MotionBlurFilterValue,
  MotionBlurTypeName,
  NoiseFilterStyleName,
  NoiseFilterValue,
  ObjectNamespace,
  ObjectValue,
  PathBlockCommand,
  PathBlockNamespace,
  FontNamespace,
  PathBlockValue,
  PathLayerState,
  PathRecordOutput,
  PathSegment,
  PathStore,
  PatternOutput,
  PatternValue,
  PixelateFilterValue,
  PointValue,
  PolarVectorValue,
  ProjectedPathValue,
  ProjectedTextValue,
  QuerySource,
  Scope,
  SegmentValue,
  StrictOption,
  StyleBlockValue,
  SubpathValue,
  SubscriptionValue,
  SVGFragmentValue,
  TextBlockElement,
  TextBlockValue,
  TextChild,
  TextElement,
  TextLayerState,
  UserFunction,
  Value,
  VariableOffsetBuilderValue,
  WarningCode,
} from './types';
import type {
  ArrayDestructuringPattern,
  CasePattern,
  SourceLocation,
  SwitchStatement,
  Expression,
  FunctionCall,
  IndexExpression,
  LayerConstructorExpression,
  MemberExpression,
  MethodCallExpression,
  ObjectDestructuringPattern,
  PathArg,
  PathBlockExpression,
  PathCommand,
  Program,
  Statement,
  StyleBlockLiteral,
  TemplateLiteral,
  TextBlockExpression,
  TextBodyItem,
} from '../parser/ast';

// Re-export all types from the dedicated types module
export type {
  AngleValue,
  ArrayValue,
  BooleanValue,
  CallValue,
  CapNamespace,
  CapValue,
  ClipPathOutput,
  ClipPathValue,
  ColorNamespace,
  ColorValue,
  CommandTraceEntry,
  CommandValue,
  CompileResult,
  CompileWarning,
  ContextObject,
  CSSPropertyDeclaration,
  CSSVarValue,
  CyclerValue,
  EndpointValue,
  EvaluationState,
  FilterOutput,
  FilterValue,
  FontData,
  FontRegistry,
  FragmentLayerState,
  FreeformPoint,
  GradientOutput,
  GradientStop,
  GradientValue,
  GridInterpolationMode,
  GridOutOfBoundsMode,
  GridValue,
  GroupLayerState,
  LayerOutput,
  LayerReference,
  LayerState,
  LayerStyle,
  LogEntry,
  LogPart,
  MarkerNamespace,
  MarkerOutput,
  MarkerValue,
  MaskOutput,
  MaskPathEntry,
  MaskValue,
  MeshPointValue,
  NoiseFilterStyleName,
  NoiseFilterValue,
  ObjectNamespace,
  ObjectValue,
  PathBlockCommand,
  PathBlockNamespace,
  FontNamespace,
  PathBlockValue,
  PathCommandMeta,
  PathLayerState,
  PathRecord,
  PathRecordOutput,
  PathSegment,
  PathStore,
  PathWithResult,
  PatternOutput,
  PatternValue,
  PointValue,
  PolarVectorValue,
  ProjectedPathValue,
  ProjectedTextValue,
  QuerySource,
  RecordedCornerOp,
  Scope,
  SegmentValue,
  StrictOption,
  StyleBlockValue,
  SubpathValue,
  SubscriptionValue,
  SVGFragmentValue,
  TextBlockElement,
  TextBlockValue,
  TextChild,
  TextElement,
  TextLayerState,
  TopoContour,
  TransformPropertyReference,
  TransformReference,
  UserFunction,
  Value,
  VariableOffsetBuilderValue,
  WarningCode,
} from './types';
import {
  applyAnnotationsToStore,
  applyRecordedCornerOps,
  collectEndpointLabels,
  collectSegmentLabels,
  commandsToPathData,
  createPathStore,
  ensureLeadingMove,
  findEndpointCommands,
  labelNameError,
  firstInkedPointOf,
  peekRecordSeq,
  pseudoRangeError,
  queryLabeledRuns,
  rejectPseudoOnNonSegmentQuery,
  recordPath,
  recordsFromCommands,
  parsePathStringAt,
  parsePathStringToCommands,
  storeToPathData,
  derivedMeta,
} from './segments';
import {
  commandsToAbsoluteD as commandsToAbsoluteDFormatted,
  commandsToRelativeD,
  hasCommandLetter,
  normalizeToRelativeArgs,
  serializeRelativeAndTrack,
} from './path-data';
import { commandValues, endpointValue, parsePathQuery, runPathQuery, wrapCommands } from './path-query';
import { tryResolveCSSFunctionArgs as sharedTryResolveCSSFunctionArgs } from './css-function-resolve';
import { LEXICAL_RULE, isLexicalScopeMiss } from './lexical-scope';
import { constOverExistingError, constReassignError, constRedeclareError } from './const-bindings';
import { spliceTemplateFragments } from '../css-value-utils';

/** CSS properties that reference defs elements via url(#id) */
const URL_REF_PROPERTIES = new Set(['mask', 'clip-path', 'filter', 'marker', 'marker-start', 'marker-mid', 'marker-end']);

/**
 * Store one evaluated style declaration. The `marker` shorthand is expanded
 * into `marker-start` / `marker-mid` / `marker-end` here, at declaration time,
 * so a later `marker-end:` still overrides it (CSS cascade order) — and so the
 * SVG never carries a `marker` attribute: browsers honour `marker` only as a
 * CSS property, never as a presentation attribute, so the shorthand rendered
 * nothing in Chrome, Firefox and Safari (ISSUE-031).
 */
function storeStyleProperty(properties: Record<string, string>, name: string, value: string): void {
  if (name === 'marker') {
    properties['marker-start'] = value;
    properties['marker-mid'] = value;
    properties['marker-end'] = value;
    return;
  }
  properties[name] = value;
}

export function isArrayValue(value: Value): value is ArrayValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'ArrayValue';
}

export function isPointValue(value: Value): value is PointValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'PointValue';
}

export function isPolarVectorValue(value: Value): value is PolarVectorValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'PolarVectorValue';
}

export function isCapValue(value: Value): value is CapValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'CapValue';
}

export function isCyclerValue(value: Value): value is CyclerValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'CyclerValue';
}

export function isSVGFragmentValue(value: Value): value is SVGFragmentValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'SVGFragmentValue';
}

export function isMaskValue(value: Value): value is MaskValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'MaskValue';
}

export function isClipPathValue(value: Value): value is ClipPathValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'ClipPathValue';
}

export function isPatternValue(value: Value): value is PatternValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'PatternValue';
}

export function isMarkerValue(value: Value): value is MarkerValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'MarkerValue';
}

export function isGridValue(value: Value): value is GridValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'GridValue';
}

// Resolve an integer cell index (r or c) under the grid's outOfBounds mode.
// Returns null when the mode is 'null' and the index is outside the range.
function gridResolveIndex(idx: number, size: number, mode: GridOutOfBoundsMode): number | null {
  if (idx >= 0 && idx < size) return idx;
  if (mode === 'clamp') return Math.max(0, Math.min(size - 1, idx));
  if (mode === 'wrap') {
    const m = ((idx % size) + size) % size;
    return m;
  }
  return null; // mode === 'null'
}

function gridSampleNearest(grid: GridValue, x: number, y: number): Value {
  const fc = (x - grid.origin.x) / grid.xDim - 0.5;
  const fr = (y - grid.origin.y) / grid.yDim - 0.5;
  const r = gridResolveIndex(Math.round(fr), grid.rows, grid.outOfBounds);
  const c = gridResolveIndex(Math.round(fc), grid.cols, grid.outOfBounds);
  if (r === null || c === null) return null;
  return grid.cells[r][c];
}

function gridSampleBilinear(grid: GridValue, x: number, y: number, mError: (msg: string) => Error): Value {
  const fc = (x - grid.origin.x) / grid.xDim - 0.5;
  const fr = (y - grid.origin.y) / grid.yDim - 0.5;
  const c0base = Math.floor(fc);
  const r0base = Math.floor(fr);
  const fx = fc - c0base;
  const fy = fr - r0base;
  const r0 = gridResolveIndex(r0base, grid.rows, grid.outOfBounds);
  const r1 = gridResolveIndex(r0base + 1, grid.rows, grid.outOfBounds);
  const c0 = gridResolveIndex(c0base, grid.cols, grid.outOfBounds);
  const c1 = gridResolveIndex(c0base + 1, grid.cols, grid.outOfBounds);
  if (r0 === null || r1 === null || c0 === null || c1 === null) return null;
  const v00 = grid.cells[r0][c0];
  const v01 = grid.cells[r0][c1];
  const v10 = grid.cells[r1][c0];
  const v11 = grid.cells[r1][c1];
  // Numeric scalars: standard bilinear
  if (typeof v00 === 'number' && typeof v01 === 'number' && typeof v10 === 'number' && typeof v11 === 'number') {
    const top = v00 * (1 - fx) + v01 * fx;
    const bottom = v10 * (1 - fx) + v11 * fx;
    return top * (1 - fy) + bottom * fy;
  }
  // PointValues: interpolate x and y separately (the standard fix for direction sampling)
  if (
    typeof v00 === 'object' && v00 !== null && 'type' in v00 && v00.type === 'PointValue' &&
    typeof v01 === 'object' && v01 !== null && 'type' in v01 && v01.type === 'PointValue' &&
    typeof v10 === 'object' && v10 !== null && 'type' in v10 && v10.type === 'PointValue' &&
    typeof v11 === 'object' && v11 !== null && 'type' in v11 && v11.type === 'PointValue'
  ) {
    const xTop = v00.x * (1 - fx) + v01.x * fx;
    const xBot = v10.x * (1 - fx) + v11.x * fx;
    const yTop = v00.y * (1 - fx) + v01.y * fx;
    const yBot = v10.y * (1 - fx) + v11.y * fx;
    return {
      type: 'PointValue' as const,
      x: xTop * (1 - fy) + xBot * fy,
      y: yTop * (1 - fy) + yBot * fy,
    };
  }
  throw mError('Grid.sampleBilinear() requires cells to be numbers or Points');
}

export function isFilterValue(value: Value): value is FilterValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'FilterValue';
}

export function isNoiseFilterValue(value: Value): value is NoiseFilterValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    value.type === 'FilterValue' &&
    'kind' in value &&
    value.kind === 'noise'
  );
}

export function isGlowFilterValue(value: Value): value is GlowFilterValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    value.type === 'FilterValue' &&
    'kind' in value &&
    value.kind === 'glow'
  );
}

export function isEmbossFilterValue(value: Value): value is EmbossFilterValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    value.type === 'FilterValue' &&
    'kind' in value &&
    value.kind === 'emboss'
  );
}

export function isElevationShadowFilterValue(value: Value): value is ElevationShadowFilterValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    value.type === 'FilterValue' &&
    'kind' in value &&
    value.kind === 'elevation-shadow'
  );
}

export function isInnerShadowFilterValue(value: Value): value is InnerShadowFilterValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    value.type === 'FilterValue' &&
    'kind' in value &&
    value.kind === 'inner-shadow'
  );
}

export function isPixelateFilterValue(value: Value): value is PixelateFilterValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    value.type === 'FilterValue' &&
    'kind' in value &&
    value.kind === 'pixelate'
  );
}

export function isMotionBlurFilterValue(value: Value): value is MotionBlurFilterValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    value.type === 'FilterValue' &&
    'kind' in value &&
    value.kind === 'motion-blur'
  );
}

export function isMeshPointValue(value: Value): value is MeshPointValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'MeshPointValue';
}

export function isGradientValue(value: Value): value is GradientValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'GradientValue';
}

export function isColorValue(value: Value): value is ColorValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'ColorValue';
}

export function isCSSVarValue(value: Value): value is CSSVarValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'CSSVarValue';
}

export function isObjectValue(value: Value): value is ObjectValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'ObjectValue';
}

export function isPathBlockValue(value: Value): value is PathBlockValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'PathBlockValue';
}

export function isProjectedPathValue(value: Value): value is ProjectedPathValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'ProjectedPathValue';
}

export function isEndpointValue(value: Value): value is EndpointValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'EndpointValue';
}

export function isCommandValue(value: Value): value is CommandValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'CommandValue';
}

export function isCallValue(value: Value): value is CallValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'CallValue';
}

export function isSegmentValue(value: Value): value is SegmentValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'SegmentValue';
}

export function isSubpathValue(value: Value): value is SubpathValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'SubpathValue';
}

export function isSubscriptionValue(value: Value): value is SubscriptionValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'SubscriptionValue';
}

/** Error text for a failed label query, listing what the path actually has. */
function queryLabelError(kind: 'segment' | 'endpoint', name: string, commands: PathBlockCommand[]): string {
  const available = kind === 'segment' ? collectSegmentLabels(commands) : collectEndpointLabels(commands);
  const list = available.length > 0 ? available.map((l) => `'${l}'`).join(', ') : '(none)';
  return `No ${kind} named '${name}' — available: ${list}`;
}

/** queryLabeledRuns with its parse errors (unknown/chained pseudo)
 *  rethrown through the call site's line-aware error wrapper. */
function runSegmentQuery(
  commands: PathBlockCommand[],
  raw: string,
  mError: (msg: string) => Error,
): import('./segments').SegmentQueryResult {
  try {
    return queryLabeledRuns(commands, raw);
  } catch (e) {
    throw mError(e instanceof Error ? e.message : String(e));
  }
}

/** query()/queryAll() with parse and no-match errors rethrown through the call site's line-aware wrapper. */
function runQueryMethod(
  source: QuerySource,
  method: 'query' | 'queryAll',
  selector: string,
  mError: (msg: string) => Error,
): Value {
  try {
    return runPathQuery(source, method, selector);
  } catch (e) {
    throw mError(e instanceof Error ? e.message : String(e));
  }
}

/** The fn/method name a statement's callee contributes to `call(fn)` queries. */
function calleeName(expr: { type: string; name?: unknown; method?: unknown } | undefined): string | undefined {
  if (!expr) return undefined;
  if (expr.type === 'FunctionCall' && typeof expr.name === 'string') return expr.name;
  if (expr.type === 'MethodCallExpression' && typeof expr.method === 'string') return expr.method;
  return undefined;
}

export function isTextBlockValue(value: Value): value is TextBlockValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'TextBlockValue';
}

export function isProjectedTextValue(value: Value): value is ProjectedTextValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'ProjectedTextValue';
}

export { isBooleanValue };

/** Create a BooleanValue from a JS boolean or truthy/falsy expression */
function boolVal(v: boolean | number): BooleanValue {
  return { type: 'BooleanValue', value: v ? 1 : 0 };
}

/**
 * Read a color-method angle argument (hueShift/analogous/splitComplementary
 * and Color(L, C, H)'s hue): an Angle value converts to degrees exactly; a
 * bare number is already degrees. Returns undefined for non-numeric values.
 */
function colorAngleDegrees(v: Value): number | undefined {
  if (isAngleValue(v)) return radiansToDegreesSnapped(v.radians);
  if (typeof v === 'number') return v;
  if (isBooleanValue(v)) return v.value;
  return undefined;
}


/**
 * Convert an Expression AST node to its source text representation
 */
function casePatternToSource(pattern: CasePattern): string {
  switch (pattern.type) {
    case 'ValuePattern':
      return expressionToSource(pattern.value);
    case 'RangePattern':
      return `${pattern.start ? expressionToSource(pattern.start) : ''}${pattern.inclusive ? '..' : '..<'}${pattern.end ? expressionToSource(pattern.end) : ''}`;
    case 'ArrayDestructuringPattern':
      return `[${[...pattern.elements, ...(pattern.rest ? [`...${pattern.rest}`] : [])].join(', ')}]`;
    case 'ObjectDestructuringPattern':
      return `{${[...pattern.properties.map((p) => (p.alias ? `${p.key}: ${p.alias}` : p.key)), ...(pattern.rest ? [`...${pattern.rest}`] : [])].join(', ')}}`;
  }
}

function expressionToSource(expr: Expression): string {
  switch (expr.type) {
    case 'NumberLiteral':
      return String(expr.value) + (expr.unit || '');
    case 'ColorLiteral':
      return expr.raw;
    case 'StringLiteral':
      return `"${expr.value}"`;
    case 'Identifier':
      return expr.name;
    case 'MemberExpression':
      return `${expressionToSource(expr.object)}.${expr.property}`;
    case 'FunctionCall':
      return `${expr.name}(${expr.args.map(expressionToSource).join(', ')})`;
    case 'CalcExpression':
      return `calc(${expressionToSource(expr.expression)})`;
    case 'RangeExpression':
      return `(${expressionToSource(expr.start)}${expr.inclusive ? '..' : '..<'}${expressionToSource(expr.end)})`;
    case 'TernaryExpression':
      return `(${expressionToSource(expr.condition)} ? ${expressionToSource(expr.consequent)} : ${expressionToSource(expr.alternate)})`;
    case 'SwitchExpression': {
      const arms = expr.arms.map((arm) => {
        const patterns = arm.patterns.map(casePatternToSource).join(', ');
        const guard = arm.guard ? ` where ${expressionToSource(arm.guard)}` : '';
        return `case ${patterns}${guard} { ${expressionToSource(arm.value)} }`;
      });
      arms.push(`default { ${expressionToSource(expr.defaultValue)} }`);
      return `switch (${expressionToSource(expr.discriminant)}) { ${arms.join(' ')} }`;
    }
    case 'BinaryExpression':
      return `(${expressionToSource(expr.left)} ${expr.operator} ${expressionToSource(expr.right)})`;
    case 'UnaryExpression':
      return `${expr.operator}${expressionToSource(expr.argument)}`;
    case 'TemplateLiteral':
      return `\`${expr.parts.map((p) => (typeof p === 'string' ? p : `\${${expressionToSource(p)}}`)).join('')}\``;
    case 'StyleBlockLiteral':
      return `\${ ${expr.properties.map((p) => `${p.name}: ${p.value};`).join(' ')} }`;
    case 'NullLiteral':
      return 'null';
    case 'BooleanLiteral':
      return expr.value ? 'true' : 'false';
    case 'ArrayLiteral':
      return `[${expr.elements.map((el) => el.type === 'SpreadElement' ? `...${expressionToSource(el.argument)}` : expressionToSource(el)).join(', ')}]`;
    case 'IndexExpression':
      return `${expressionToSource(expr.object)}[${expressionToSource(expr.index)}]`;
    case 'MethodCallExpression':
      return `${expressionToSource(expr.object)}.${expr.method}(${expr.args.map(expressionToSource).join(', ')})${expr.block ? ` {|${expr.block.params.join(', ')}| ...}` : ''}`;
    case 'ObjectLiteral':
      return `{${expr.properties.map((p) => {
        if (p.type === 'SpreadElement') return `...${expressionToSource(p.argument)}`;
        if (p.shorthand && p.value.type === 'Identifier' && p.value.name === p.key) return p.key;
        return `${p.key}: ${expressionToSource(p.value)}`;
      }).join(', ')}}`;
    case 'PathBlockExpression':
      return '@{ ... }';
    case 'LambdaExpression':
      return `{|${expr.params.join(', ')}| ...}`;
    default:
      return '?';
  }
}

/**
 * Deterministic 32-bit hash for filter id → seed derivation. djb2 variant —
 * cheap and stable across compiles, so the same source produces the same
 * noise unless the user assigns f.seed explicitly.
 */
function hashFilterId(id: string): number {
  let h = 5381;
  for (let i = 0; i < id.length; i++) h = ((h << 5) + h + id.charCodeAt(i)) | 0;
  return Math.abs(h) % 65536;
}

/** Allocate a fresh, conflict-free auto-id for an inline filter constructor. */
function nextAutoFilterId(state: EvaluationState, kind: string): string {
  let n = 1;
  // Bump until we land on a free id across all defs maps (auto-ids only ever collide with each other in practice).
  // Termination: the id space is unbounded — we walk until we find a gap.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const id = `pathogen-${kind}-${n}`;
    if (
      !state.masks.has(id) &&
      !state.clipPaths.has(id) &&
      !state.gradients.has(id) &&
      !state.patterns.has(id) &&
      !state.markers.has(id) &&
      !state.filters.has(id)
    ) {
      return id;
    }
    n++;
  }
}

interface NoiseFilterStyleDefaults {
  scale: number;
  octaves: number;
  amount: number;
  monochrome: boolean;
  blend: string;
  contrast: number;
  stitch: boolean;
}

/** Per-style baselines. Keep these in sync with docs/filters.md and the renderer. */
function noiseFilterDefaults(style: NoiseFilterStyleName): NoiseFilterStyleDefaults {
  switch (style) {
    case 'grain':
      return { scale: 5.0, octaves: 6, amount: 0.4, monochrome: true, blend: 'color-burn', contrast: 1.0, stitch: false };
    case 'paper':
      return { scale: 1.0, octaves: 3, amount: 0.5, monochrome: true, blend: 'multiply', contrast: 1.0, stitch: false };
    case 'speckle':
      return { scale: 0.3, octaves: 2, amount: 0.6, monochrome: false, blend: 'multiply', contrast: 1.0, stitch: false };
    case 'static':
      return { scale: 5.0, octaves: 8, amount: 0.7, monochrome: true, blend: 'hard-light', contrast: 1.0, stitch: false };
    case 'gradient':
      return { scale: 1.0, octaves: 3, amount: 0.6, monochrome: false, blend: 'overlay', contrast: 1.7, stitch: true };
  }
}

function makeDefaultNoiseFilter(id: string, style: NoiseFilterStyleName): NoiseFilterValue {
  const d = noiseFilterDefaults(style);
  return {
    type: 'FilterValue',
    kind: 'noise',
    id,
    style,
    scale: d.scale,
    octaves: d.octaves,
    amount: d.amount,
    monochrome: d.monochrome,
    seed: hashFilterId(id),
    blend: d.blend,
    contrast: d.contrast,
    stitch: d.stitch,
  };
}

function makeDefaultGlowFilter(id: string): GlowFilterValue {
  return {
    type: 'FilterValue',
    kind: 'glow',
    id,
    mode: 'outer',
    color: 'rgb(255, 255, 255)',
    radius: 4,
    spread: 0,
    opacity: 0.8,
  };
}

function makeDefaultEmbossFilter(id: string): EmbossFilterValue {
  return {
    type: 'FilterValue',
    kind: 'emboss',
    id,
    angle: (135 * Math.PI) / 180,
    elevation: (45 * Math.PI) / 180,
    depth: 2,
    strength: 0.8,
    shininess: 20,
    lightColor: 'rgb(255, 255, 255)',
    smooth: 1,
  };
}

function makeDefaultElevationShadowFilter(id: string): ElevationShadowFilterValue {
  return {
    type: 'FilterValue',
    kind: 'elevation-shadow',
    id,
    elevation: 4,
    color: 'rgb(0, 0, 0)',
    direction: Math.PI / 2,
    tightness: 1.0,
  };
}

function makeDefaultInnerShadowFilter(id: string): InnerShadowFilterValue {
  return {
    type: 'FilterValue',
    kind: 'inner-shadow',
    id,
    offsetX: 0,
    offsetY: 2,
    blur: 4,
    color: 'rgb(0, 0, 0)',
    opacity: 0.5,
  };
}

function makeDefaultPixelateFilter(id: string): PixelateFilterValue {
  return {
    type: 'FilterValue',
    kind: 'pixelate',
    id,
    width: 10,
    height: 10,
    radius: 5,
  };
}

function makeDefaultMotionBlurFilter(id: string): MotionBlurFilterValue {
  return {
    type: 'FilterValue',
    kind: 'motion-blur',
    id,
    motionType: 'linear',
    distance: 10,
    angle: 0,
    samples: 12,
  };
}

/**
 * Signal class used to propagate return values up the call stack.
 * Thrown by return statements and caught by function call evaluation.
 */
class ReturnSignal {
  constructor(
    public value: Value,
    /** The `return` statement's line, for the error when nothing catches it. */
    public line?: number,
  ) {}
}

/**
 * A `return` that no fn, lambda or block caught — at the top level, in a
 * layer's apply block, in a path block — reaches compile() as a ReturnSignal.
 * Never let that bare object out: it would reach the user as "[object Object]".
 */
function strayReturnError(err: unknown): unknown {
  if (!(err instanceof ReturnSignal)) return err;
  return new Error(formatError(`'return' is only valid inside a fn, a lambda or a {|…| } block`, err.line));
}

/**
 * A layer's effective SVG transform: its own, with every ancestor group's applied
 * outside it, outermost first — the same composition nested `<g transform>`
 * elements produce at render (src/render/build-layers.ts).
 *
 * Returns null when the whole chain is identity, so callers can compare two
 * layers with `!==` and have "neither is transformed" come out equal.
 */
function effectiveLayerTransform(evalState: EvaluationState, layerName: string): string | null {
  const chain: string[] = [];
  const seen = new Set<string>();
  let current: string | undefined = layerName;
  while (current && !seen.has(current)) {
    seen.add(current);
    const layer = evalState.layers.get(current);
    if (!layer) break;
    const own = 'transformState' in layer && layer.transformState ? transformStateToSvg(layer.transformState) : null;
    // An explicit `transform:` style string outranks the state, as it does at emit.
    const explicit = typeof layer.styles?.transform === 'string' ? layer.styles.transform : null;
    const resolved = explicit ?? own;
    if (resolved) chain.push(resolved);

    // Climb to the group that holds this layer, if any.
    const child = current;
    current = undefined;
    for (const [name, candidate] of evalState.layers) {
      if (candidate.layerType === 'GroupLayer' && candidate.children.includes(child)) {
        current = name;
        break;
      }
    }
  }
  // Innermost was pushed first; the outermost group's transform applies first.
  return chain.length > 0 ? chain.reverse().join(' ') : null;
}

/**
 * Program end: deliver every subscription's matches (src/evaluator/subscriptions.ts).
 * Callbacks run as top-level code — apply blocks are legal, bare commands go to
 * the default layer — and errors name the subscription and the triggering statement.
 */
function runSubscriptionDispatch(scope: Scope, evalState: EvaluationState): void {
  dispatchSubscriptions(evalState, {
    finalizedCommands: (layer) => applyRecordedCornerOps(layer.accum.records.flatMap((r) => r.commands)).commands,
    layerRef: (layer) => ({ type: 'LayerReference' as const, layer }),
    fail: (message, loc) => new Error(formatError(message, loc?.line, loc?.column)),
    effectiveTransform: (layerName) => effectiveLayerTransform(evalState, layerName),
    warn: (message, loc) => warn(evalState, 'layer-transform', message, loc),
    invoke: (sub, match, ordinal, trigger) => {
      const blockScope = createScope(sub.callback.closure ?? scope);
      if (sub.callback.calledFn && sub.callback.subscribedIn) {
        blockScope.dynamicParent = sub.callback.subscribedIn;
        blockScope.calledFn = sub.callback.calledFn;
      }
      const params = sub.callback.params;
      if (params.length > 0) setVariable(blockScope, params[0], match);
      if (params.length > 1) setVariable(blockScope, params[1], ordinal);
      if (params.length > 2) setVariable(blockScope, params[2], sub);
      try {
        for (const stmt of sub.callback.body) {
          const flow = evaluateStatementToAccum(stmt, blockScope, evalState.rootAccum ?? createPathStore());
          if (flow) throw loopFlowBoundaryError(flow);
        }
      } catch (e) {
        if (e instanceof ReturnSignal) return;
        // Keep the failing statement's own line as the location; name the
        // triggering statement and the subscribe line in the message.
        const raw = e instanceof Error ? e.message : String(e);
        const inner = /^Line (\d+)(?:, col (\d+))?: ([\s\S]*)$/.exec(raw);
        const line = inner ? Number(inner[1]) : sub.loc?.line;
        const col = inner?.[2] !== undefined ? Number(inner[2]) : inner ? undefined : sub.loc?.column;
        const msg = inner ? inner[3] : raw;
        const where = `statement at line ${trigger?.line ?? '?'}, subscribed at line ${sub.loc?.line ?? '?'}`;
        throw new Error(
          formatError(
            `Error in subscription on '${sub.layerName}', match ${ordinal + 1} (${where}): ${msg}`,
            line,
            col,
          ),
        );
      }
    },
  });
}

function createScope(parent: Scope | null = null): Scope {
  return {
    variables: new Map(),
    parent,
    evalState: parent?.evalState, // Inherit evaluation state from parent
    // Declared on every scope so a named fn's call scope (callScope sets
    // them) has the same shape as the rest: lookups walk mixed chains.
    dynamicParent: undefined,
    calledFn: undefined,
    consts: undefined,
  };
}

/**
 * The scope a function, lambda or callback body runs in (phase 0a steps 2–3;
 * docs/syntax.md "Scoping" and "Lambdas"). Names resolve from `lexical` — the
 * scope the fn or lambda was written in — or, for a literal trailing block,
 * from the caller's scope, where the block is written. The DRAWING state (the
 * pen, the active layer, the path-block rules) is always the caller's: a fn
 * called inside a path block draws into the block, one called inside
 * layer().apply draws on that layer. `calledFn` marks a named fn's call, so a
 * lookup miss can say what the old caller-scope rule would have found.
 */
function callScope(lexical: Scope | undefined, caller: Scope, calledFn?: CalledFn): Scope {
  const scope = createScope(lexical ?? caller);
  scope.evalState = caller.evalState;
  if (calledFn) {
    scope.dynamicParent = caller;
    scope.calledFn = calledFn;
  }
  return scope;
}

/** A named fn's call record for callScope; lambdas changed nothing in step 3. */
function calledFnFor(fn: UserFunction, viaWorker: boolean): CalledFn | undefined {
  return fn.isLambda || !fn.name ? undefined : { name: fn.name, params: fn.params, viaWorker };
}


/**
 * Where the pre-step-3 rule would have bound `name`, looked up from `origin`:
 * the old chain swaps each named fn's call scope's parent for its caller.
 * `crossed` lists the named fns whose callers the walk went through,
 * innermost first; `fn` is the first of them.
 */
function oldRuleBinding(
  origin: Scope,
  name: string,
): { scope: Scope | null; fn: CalledFn | undefined; crossed: CalledFn[] } {
  const crossed: CalledFn[] = [];
  for (let s: Scope | null = origin; s; ) {
    if (s.variables.has(name)) return { scope: s, fn: crossed[0], crossed };
    if (s.dynamicParent) {
      if (s.calledFn) crossed.push(s.calledFn);
      s = s.dynamicParent;
    } else {
      s = s.parent;
    }
  }
  return { scope: null, fn: crossed[0], crossed };
}

/**
 * The step 3 audit hook (scripts/audit-semantics.ts --step 3). Told whenever
 * a name used inside a named fn resolves differently from the old rule:
 * - `bound-elsewhere`: both rules find a binding, but not the same one (or
 *   the new rule finds a built-in where the old found a caller's variable) —
 *   the silent case;
 * - `only-where-written`: only the new rule finds it (an error before);
 * - `only-where-called`: only the old rule finds it (the hinted error now).
 * Off by default; when off, a lookup pays one null check.
 */
export interface ScopeAuditEvent {
  kind: 'bound-elsewhere' | 'only-where-written' | 'only-where-called';
  name: string;
  fn: string;
}
let scopeAudit: ((event: ScopeAuditEvent) => void) | null = null;
export function __setScopeAudit(recorder: ((event: ScopeAuditEvent) => void) | null): void {
  scopeAudit = recorder;
}

/** Report to the audit hook when `found` (null: a built-in or a miss) differs from the old rule's binding. */
function auditBinding(origin: Scope, name: string, found: Scope | null, missed: boolean): void {
  const old = oldRuleBinding(origin, name);
  if (old.scope === found || !old.fn) return;
  const kind = missed ? 'only-where-called' : old.scope === null ? 'only-where-written' : 'bound-elsewhere';
  scopeAudit?.({ kind, name, fn: old.fn.name });
}

/**
 * Text appended to a lookup or assignment miss when the pre-step-3 rule — a
 * named fn's free names resolve where it is CALLED — would have found the
 * name. The hint names the innermost such fn and shows the fix (docs/syntax.md
 * "Scoping"). Runs only on a miss, so lookups pay nothing.
 */
function lexicalMissHint(origin: Scope, name: string, use: 'read' | 'assign'): string {
  const { scope: found, fn, crossed } = oldRuleBinding(origin, name);
  if (!found || !fn) return '';
  if (fn.viaWorker) {
    const verb = use === 'read' ? `take ${name} as a parameter` : `assign to ${name}`;
    // Not "an inline lambda": a lambda literal after << is its own error.
    return ` — ${LEXICAL_RULE}. ${fn.name} is applied with <<, so it can't ${verb}: write it where ${name} is visible, as a trailing block or a let-bound lambda`;
  }
  if (use === 'read') {
    const params = [...fn.params, name].join(', ');
    // The name is more than one call away: each fn on the way has to pass it along.
    const between = [...new Set(crossed.slice(1).map((c) => c.name))].filter((n) => n !== fn.name);
    const along = between.length
      ? `. ${between.join(', ')} ${between.length === 1 ? 'needs' : 'need'} it as a parameter too, to pass it along`
      : '';
    return ` — ${LEXICAL_RULE}. Pass it in: add ${name} to ${fn.name}'s parameters, fn ${fn.name}(${params}), and pass it where ${fn.name} is called${along}`;
  }
  return ` — ${LEXICAL_RULE}. Return the new value instead: ${name} = ${fn.name}(${fn.params.join(', ')});`;
}

type DrawingState = EvaluationState & { _insidePathBlock?: boolean; _ctxSource?: EvaluationState };

/** Whether `scope` is building a path block — its own statements or code called from them. */
function isInsidePathBlock(scope: Scope): boolean {
  return !!(scope.evalState as DrawingState | undefined)?._insidePathBlock;
}

/**
 * `ctx`: the pen drawing in `state` right now (docs/debug.md "ctx Object").
 * - In a path block, the block's own cursor, with no transform (ctx.transform
 *   stays an error there).
 * - In a text block, the state that created it: the same pen, and its
 *   transform.
 * - Otherwise the active PathLayer's pen and transform, else the program's.
 * Computed when read, not rebound after every command: contextToObject builds
 * a new object only after the pen has moved, so `let saved = ctx;` keeps that
 * moment's values.
 */
function ctxValueFor(state: EvaluationState | undefined): Value | undefined {
  let drawing = state as DrawingState | undefined;
  while (drawing?._ctxSource) drawing = drawing._ctxSource as DrawingState;
  if (!drawing) return undefined;
  if (drawing._insidePathBlock) {
    return { type: 'ContextObject' as const, value: contextToObject(drawing.pathContext) } as Value;
  }
  let { transformState } = drawing;
  if (drawing.activeLayerName) {
    const layer = drawing.layers.get(drawing.activeLayerName);
    if (layer?.layerType === 'PathLayer') transformState = layer.transformState;
  }
  return { type: 'ContextObject' as const, value: contextToObject(drawing.pathContext, transformState) } as Value;
}

function formatError(message: string, line?: number, column?: number): string {
  if (line !== undefined && line > 0) {
    if (column !== undefined && column > 0) {
      return `Line ${line}, col ${column}: ${message}`;
    }
    return `Line ${line}: ${message}`;
  }
  return message;
}

/** `strict` option → the form warn() reads: true, a non-empty set, or undefined (off). */
function resolveStrict(strict: StrictOption | undefined): true | ReadonlySet<WarningCode> | undefined {
  if (strict === true) return true;
  if (Array.isArray(strict) && strict.length > 0) return new Set(strict as readonly WarningCode[]);
  return undefined;
}

function getLine(node: unknown): number | undefined {
  return (node as { loc?: { line: number } })?.loc?.line;
}

/**
 * Record a compiler warning: a non-fatal problem the evaluator worked around.
 * Pushes the structured entry (CLI stderr, editor diagnostics, --json) AND a
 * `[warn] …` log mirror carrying the same line, so the console and
 * --log-file keep everything in one stream.
 */
function warn(
  evalState: EvaluationState | undefined,
  code: WarningCode,
  message: string,
  loc?: { line?: number; column?: number } | null,
): void {
  if (!evalState) return;
  const line = loc?.line;
  const column = loc?.column;
  // Strict mode: the warning stops the compile as an error, positioned when the
  // warning has a loc (formatError returns the bare message without one). Every
  // warning passes through here, so this is the only place strictness lives.
  const strict = evalState.strictWarnings;
  if (strict === true || strict?.has(code)) {
    throw new Error(formatError(`${message} (strict: ${code})`, line, column));
  }
  evalState.warnings.push({
    code,
    message,
    ...(line != null ? { line } : {}),
    ...(column != null ? { column } : {}),
  });
  evalState.logs.push({
    line: line ?? null,
    severity: 'warn',
    parts: [{ type: 'string', value: `[warn] ${message}` }],
  });
}

function getCol(node: unknown): number | undefined {
  return (node as { loc?: { column: number } })?.loc?.column;
}

/** Walk left children of a BinaryExpression to find the nearest node with loc */
function getLineDeep(node: unknown): number | undefined {
  const line = getLine(node);
  if (line) return line;
  const left = (node as { left?: unknown })?.left;
  if (left) return getLineDeep(left);
  return undefined;
}

function lookupVariable(scope: Scope, name: string, line?: number, column?: number, origin: Scope = scope): Value {
  if (scope.variables.has(name)) {
    if (scopeAudit !== null && scope !== origin) auditBinding(origin, name, scope, false);
    return scope.variables.get(name)!;
  }
  if (scope.parent) {
    return lookupVariable(scope.parent, name, line, column, origin);
  }
  const builtin = builtinValue(scope, name, line, column, origin);
  if (scopeAudit !== null) auditBinding(origin, name, null, builtin === undefined);
  if (builtin !== undefined) return builtin;
  throw new Error(formatError(`Undefined variable: ${name}${lexicalMissHint(origin, name, 'read')}`, line, column));
}

/**
 * A name no scope binds: `ctx`, a namespace, `viewbox`, a built-in enum or a
 * stdlib function, or undefined. `scope` is the root; `origin` is where the
 * lookup started.
 */
function builtinValue(scope: Scope, name: string, line?: number, column?: number, origin: Scope = scope): Value | undefined {
  // `ctx` is computed, never bound: the pen drawing where the lookup STARTED
  // (`origin` — by now `scope` is the root, whose state is the program's).
  // A user binding named ctx was found above and still wins (until step 5).
  if (name === 'ctx') {
    const ctx = ctxValueFor(origin.evalState);
    if (ctx) return ctx;
  }
  // Namespaces — Object, Color, PathBlock, Font (docs/fonts.md), Cap (end caps
  // for compoundVariableOffset), Marker (Marker.fromPathBlock; Marker(...)
  // itself is a constructor call). One table, shared with the name rule
  // (builtin-names.ts).
  const namespaceType = builtinNamespaceType(name);
  if (namespaceType) {
    return { type: namespaceType } as ObjectNamespace | ColorNamespace | PathBlockNamespace | FontNamespace | CapNamespace | MarkerNamespace;
  }
  // Ambient viewbox global — fresh copy per read so the struct is read-only
  // and evalState's `loc` never leaks into user code.
  // NOTE: fallbacks run only at the ROOT scope (recursion above returns
  // through scope.parent first), whose evalState is the real one. Path/text
  // blocks put a synthetic evalState on the BLOCK scope, so reads inside
  // @{ } / &{ } correctly see the program's viewBox. Do not add `viewBox`
  // to the synthetic block-state field lists or short-circuit this lookup
  // at a non-root scope — either would silently break in-block reads.
  if (name === 'viewbox') {
    const vb = scope.evalState?.viewBox;
    if (!vb) {
      throw new Error(
        formatError('viewbox is not available until define ViewBox(...) has run', line, column),
      );
    }
    return {
      type: 'ViewBoxStructValue',
      originX: vb.originX,
      originY: vb.originY,
      width: vb.width,
      height: vb.height,
    };
  }
  // Built-in enums
  if (name in BUILTIN_ENUMS) {
    const props = new Map<string, Value>();
    for (const [k, v] of Object.entries(BUILTIN_ENUMS[name])) props.set(k, v);
    return { type: 'ObjectValue', properties: props } as ObjectValue;
  }
  // Check stdlib
  if (name in stdlib) {
    return stdlib[name as keyof typeof stdlib] as unknown as Value;
  }
  return undefined;
}

/**
 * Whether a call to `name` reaches a plain standard-library function: not a
 * user fn or variable that shadows it, and not a context-aware function
 * (polarLine, tangentArc, …), which draws from the pen.
 */
function isPlainStdlibFunction(name: string, scope: Scope): boolean {
  if (!(name in stdlib) || contextAwareFunctions.has(name)) return false;
  return typeof lookupVariable(scope, name) === 'function';
}

function setVariable(scope: Scope, name: string, value: Value): void {
  // The single funnel for every binding form (let, for, fn names,
  // fn/lambda params, destructuring, builtin callback params) — the
  // one-line reservation check covers them all. It has no source location
  // and only fires when a binding executes, so it is the BACKSTOP: parse()
  // reports the same rule statically, at the name (parser/reserved-bindings.ts).
  if (RESERVED_UNIT_NAMES.has(name)) {
    throw new Error(reservedNameBindingError(name));
  }
  // A built-in name can't be declared (phase 0a step 5, builtin-names.ts).
  if (BUILTIN_NAMES.has(name)) {
    throw new Error(builtinNameError(name));
  }
  scope.variables.set(name, value);
}

/**
 * Declare a name in `scope` with `let`, `const` or `fn` — every declaration
 * statement and destructured name goes through here (phase 0a step 4). Two
 * `let`s (or a fn and a let) of one name in one scope stay legal: the second
 * replaces the first, as before. A const makes a second declaration an error
 * either way round. parse() reports the same rule statically when the source
 * decides it (parser/const-check.ts); this is the run-time backstop.
 */
function declareBinding(scope: Scope, name: string, value: Value, kind: 'let' | 'const' | 'fn', line?: number): void {
  if (scope.consts?.has(name)) throw new Error(formatError(constRedeclareError(name), line));
  if (kind === 'const' && scope.variables.has(name)) throw new Error(formatError(constOverExistingError(name), line));
  setVariable(scope, name, value);
  if (kind === 'const') (scope.consts ??= new Set()).add(name);
}

function updateVariable(scope: Scope, name: string, value: Value, line?: number): void {
  let current: Scope | null = scope;
  while (current) {
    if (current.variables.has(name)) {
      if (current.consts?.has(name)) throw new Error(formatError(constReassignError(name), line));
      if (scopeAudit !== null && current !== scope) auditBinding(scope, name, current, false);
      current.variables.set(name, value);
      return;
    }
    current = current.parent;
  }
  if (scopeAudit !== null) auditBinding(scope, name, null, true);
  const hint = lexicalMissHint(scope, name, 'assign');
  throw new Error(
    formatError(hint ? `Cannot assign to ${name} here${hint}` : `Cannot assign to undeclared variable: ${name}`, line),
  );
}

function isStyleBlock(value: Value): value is StyleBlockValue {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'StyleBlockValue';
}

function isLayerReference(value: Value): value is LayerReference {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'LayerReference';
}

function camelToKebab(name: string): string {
  return name.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}

// --- Path length calculation utilities ---

/**
 * Extract vertices (start/end points of each segment) from commands
 */
function extractVertices(commands: PathBlockCommand[]): PointValue[] {
  if (commands.length === 0) return [];

  const vertices: PointValue[] = [];
  const seen = new Set<string>();

  for (const cmd of commands) {
    const startKey = `${cmd.start.x},${cmd.start.y}`;
    if (!seen.has(startKey)) {
      seen.add(startKey);
      vertices.push({ type: 'PointValue', x: cmd.start.x, y: cmd.start.y });
    }
    const endKey = `${cmd.end.x},${cmd.end.y}`;
    if (!seen.has(endKey)) {
      seen.add(endKey);
      vertices.push({ type: 'PointValue', x: cmd.end.x, y: cmd.end.y });
    }
  }

  return vertices;
}

/**
 * Count subpaths (separated by m commands after the first command)
 */
function countSubPaths(commands: PathBlockCommand[]): number {
  // SVG rule, shared with .contours and the `subpath` query noun.
  return splitSubpaths(commands).length;
}


/** Resolve cut()'s cutter argument: one PathBlock/ProjectedPath, or an
 *  array of them — array knives cut exactly as if their strokes lived in
 *  one block (chains split on coordinate discontinuity downstream). */
function resolveCutterCommands(cutterVal: Value, mkErr: (message: string) => Error): PathBlockCommand[] {
  if (isPathBlockValue(cutterVal) || isProjectedPathValue(cutterVal)) return cutterVal.commands;
  if (isArrayValue(cutterVal)) {
    if (cutterVal.elements.length === 0) throw mkErr('cut() cutter array must hold at least one cutter');
    const all: PathBlockCommand[] = [];
    for (const el of cutterVal.elements) {
      if (isPathBlockValue(el) || isProjectedPathValue(el)) all.push(...el.commands);
      else throw mkErr('cut() cutter array elements must each be a PathBlock or ProjectedPath');
    }
    return all;
  }
  throw mkErr('cut() argument must be a PathBlock or ProjectedPath (or an array of them)');
}

/** Validate offset()'s optional second argument: { join: 'miter' | 'bevel' | 'round' }. */
function parseOffsetJoinOptions(val: Value, mkErr: (message: string) => Error): OffsetJoinOptions {
  if (typeof val !== 'object' || val === null || (val as { type?: string }).type !== 'ObjectValue') {
    throw mkErr("offset() options must be an object, e.g. { join: 'round' }");
  }
  const props = (val as { properties: Map<string, Value> }).properties;
  const options: OffsetJoinOptions = {};
  for (const key of props.keys()) {
    if (key !== 'join') throw mkErr(`offset() options: unknown key '${key}' (supported: join)`);
  }
  const joinVal = props.get('join');
  if (joinVal !== undefined) {
    if (joinVal !== 'miter' && joinVal !== 'bevel' && joinVal !== 'round') {
      throw mkErr("offset() join must be 'miter', 'bevel', or 'round'");
    }
    options.join = joinVal;
  }
  return options;
}

/**
 * Validate mapSlice()'s optional second argument: { partial: boolean }.
 * Unknown keys throw, as offset() does — `{ strict: false }` must not pass
 * silently. A Pathogen boolean is a wrapped BooleanValue (always truthy in JS),
 * so it is unwrapped here the way every other boolean slot does it.
 */
function parseMapSliceOptions(val: Value, mkErr: (message: string) => Error): { partial: boolean } {
  if (!isObjectValue(val)) throw mkErr('mapSlice() options must be an object, e.g. { partial: true }');
  for (const key of val.properties.keys()) {
    if (key !== 'partial') throw mkErr(`mapSlice() options: unknown key '${key}' (supported: partial)`);
  }
  const partialVal = val.properties.get('partial');
  if (partialVal === undefined) return { partial: false };
  if (isBooleanValue(partialVal)) return { partial: partialVal.value === 1 };
  if (typeof partialVal === 'number') return { partial: partialVal !== 0 };
  throw mkErr('mapSlice() partial must be a boolean');
}

/**
 * The two ways a derived PathBlock is built, named so the choice is stated at
 * every call site (placement audit V8). Both normalize `start`/`end` and carry
 * labels through `derivedMeta`; they differ in one thing:
 *
 * - `fromCommandsKeepingFrame` subtracts nothing. The result stays in the
 *   receiver's frame, so a positioned block or a cut piece keeps its place.
 *   This is what `dash`, `cut`, `outline`, the fillets, the boolean ops,
 *   `.contours`, `fromGlyph` and `TextBlock.toPathBlock` do.
 * - `fromCommandsRebased` subtracts the first command's start. The result is
 *   free-floating, re-based to its own first point, and the removed
 *   translation is what `buildRebasedWithAnchor` exposes as `anchor`. This is
 *   what `reverse`, `subPath`, `offset`, `mirror`, `rotateAtVertexIndex` and
 *   `ProjectedPath.toPathBlock` do. For a block whose first command starts at
 *   (0, 0) — every block literal, and a `segment()` result — the subtraction
 *   is a no-op, which is why `offset` and `mirror` appear to keep the frame in
 *   the probe matrix; on a receiver with a real frame (a `cut` or `dash` piece)
 *   they do re-base.
 *
 * The previous spelling, `buildPathBlockFromCommands(cmds, origin?)`, encoded
 * this choice as the presence of a `{ x: 0, y: 0 }` argument: read as "put it
 * at the origin" it meant the opposite, and each method picked by hand
 * (project-docs/placement-audit/03-principles.md, "The mechanism").
 */
/** Expando properties a glyph PathBlock carries (fromGlyph / Font.glyphs). */
type GlyphBlock = PathBlockValue & { advanceWidth: number; char: string; glyphName: string | null };

/**
 * One glyph PathBlock per character, exactly as PathBlock.fromGlyph has
 * always built them: outline normalized to the origin (empty for spaces),
 * with `advanceWidth`, the source `char` and the font's `glyphName` attached.
 * Characters no loaded variant covers render as .notdef and are recorded for
 * the missing-glyph report. Shared by PathBlock.fromGlyph and Font.glyphs.
 */
function makeGlyphBlocks(
  chars: string[],
  registry: FontRegistry,
  family: string,
  weight: number,
  fontSize: number,
  evalState: EvaluationState | undefined,
): Value[] {
  const glyphs: Value[] = [];
  for (const char of chars) {
    const lookup = lookupGlyph(registry, family, weight, 'normal', char, fontSize)!;
    if (lookup.missing && evalState) recordMissingGlyph(evalState, family, weight, char);
    const block: PathBlockValue =
      lookup.commands.length === 0
        ? { type: 'PathBlockValue' as const, commands: [], records: [], startPoint: { x: 0, y: 0 }, endPoint: { x: 0, y: 0 } }
        : fromCommandsKeepingFrame(lookup.commands);
    const glyph = block as GlyphBlock;
    glyph.advanceWidth = lookup.advanceWidth;
    glyph.char = char;
    glyph.glyphName = lookup.missing ? null : getGlyphName(lookup.fontData, char);
    glyphs.push(glyph);
  }
  return glyphs;
}

function fromCommandsKeepingFrame(cmds: PathBlockCommand[]): PathBlockValue {
  return buildDerivedPathBlock(cmds, 0, 0);
}

function fromCommandsRebased(cmds: PathBlockCommand[]): PathBlockValue {
  if (cmds.length === 0) return buildDerivedPathBlock(cmds, 0, 0);
  return buildDerivedPathBlock(cmds, cmds[0].start.x, cmds[0].start.y);
}

/** The shared body: subtract `(originX, originY)` from every start/end and rebuild the value. */
function buildDerivedPathBlock(cmds: PathBlockCommand[], originX: number, originY: number): PathBlockValue {
  if (cmds.length === 0) {
    return {
      type: 'PathBlockValue' as const,
      commands: [],
      records: [],
      startPoint: { x: 0, y: 0 },
      endPoint: { x: 0, y: 0 },
    };
  }
  const normalized = cmds.map((cmd) => {
    const meta = derivedMeta(cmd.meta);
    return {
      command: cmd.command,
      args: [...cmd.args],
      start: { x: cmd.start.x - originX, y: cmd.start.y - originY },
      end: { x: cmd.end.x - originX, y: cmd.end.y - originY },
      ...(meta !== undefined ? { meta } : {}),
    };
  });
  const last = normalized[normalized.length - 1];
  return {
    type: 'PathBlockValue' as const,
    commands: normalized,
    records: recordsFromCommands(normalized),
    startPoint: firstInkedPointOf(normalized) ?? { x: 0, y: 0 },
    endPoint: { x: last.end.x, y: last.end.y },
  };
}

/**
 * Re-base a command list to its own first point AND record the translation that
 * removed, as `anchor`.
 *
 * P2 of the placement audit: an operation that discards position exposes it on
 * its result, so `result.drawTo(result.anchor.x, result.anchor.y)` is an
 * identity. `variableOffset` has always done this; `subPath`, `segment` and
 * `reverse` re-base as well and used to throw on `anchor`, leaving the caller
 * to recover the position from the receiver — which it could only do by knowing
 * which `t` it had asked for.
 *
 * `anchor` is the result's first point in the RECEIVER's coordinate space:
 * exactly the origin `fromCommandsRebased` subtracts.
 */
function buildRebasedWithAnchor(cmds: PathBlockCommand[]): PathBlockValue {
  const block = fromCommandsRebased(cmds);
  if (cmds.length === 0) return block;
  (block as PathBlockValue & { anchor: { x: number; y: number } }).anchor = { ...cmds[0].start };
  return block;
}

/** A command list without its leading run of moves — the shape, without where the pen was put first. */
function withoutLeadingMoves(cmds: PathBlockCommand[]): PathBlockCommand[] {
  let firstInked = 0;
  while (firstInked < cmds.length && cmds[firstInked].command.toLowerCase() === 'm') firstInked++;
  return firstInked === 0 ? cmds : cmds.slice(firstInked);
}

/** The point a translate*PointTo method moves: the first drawn point, or the centre of the drawn shape. */
function translationReference(method: string, cmds: PathBlockCommand[], startPoint: { x: number; y: number }) {
  // The centre is of the INK: boundingBox() counts a leading move's origin, which
  // is where the pen was, and that is exactly what a translation gives up.
  return method === 'translateStartPointTo' ? startPoint : computeBoundingBoxCenter(withoutLeadingMoves(cmds));
}

/**
 * The third way a derived PathBlock is built: translated so a chosen point of it
 * (`from`, in the receiver's coordinates) lands on `to`. This is the named,
 * user-callable form of re-basing — `translateStartPointTo` / `translateCenterPointTo`
 * (placement audit V3: the intent "this piece starts at the pen" gets a spelling
 * instead of a warning).
 *
 * A leading run of moves is the receiver's position, not its shape, so it is
 * dropped: the result's position lives in its commands' coordinates, as a cut
 * piece's does, and the serializer bridges from the origin to the first command.
 * `anchor` is the translation removed (`from − to`), which makes
 * `result.drawTo(anchor.x, anchor.y)` the same ink as `receiver.drawTo(0, 0)`.
 */
function fromCommandsTranslated(
  cmds: PathBlockCommand[],
  from: { x: number; y: number },
  to: { x: number; y: number },
): PathBlockValue {
  const inked = withoutLeadingMoves(cmds);
  const anchor = { x: from.x - to.x, y: from.y - to.y };
  const block = buildDerivedPathBlock(inked, anchor.x, anchor.y);
  if (inked.length === 0) return block;
  (block as PathBlockValue & { anchor: { x: number; y: number } }).anchor = anchor;
  return block;
}

/**
 * Build a ProjectedPathValue from transform result commands.
 */
function buildProjectedPathFromCommands(cmds: PathBlockCommand[], original: ProjectedPathValue): ProjectedPathValue {
  if (cmds.length === 0) {
    return {
      type: 'ProjectedPathValue' as const,
      commands: [],
      startPoint: { ...original.startPoint },
      // B2 audit ride-along: was a copy-paste of startPoint.
      endPoint: { ...original.endPoint },
    };
  }
  // B2: leading-m chains carry stale starts; the inked point is truthful.
  const start = firstInkedPointOf(cmds) ?? cmds[0].start;
  const end = cmds[cmds.length - 1].end;
  return {
    type: 'ProjectedPathValue' as const,
    commands: cmds,
    startPoint: { x: start.x, y: start.y },
    endPoint: { x: end.x, y: end.y },
  };
}

/**
 * Project a PathBlockValue's commands to absolute coordinates from a given origin
 */
function projectCommands(commands: PathBlockCommand[], originX: number, originY: number): PathBlockCommand[] {
  return commands.map((cmd) => ({
    command: cmd.command,
    args: [...cmd.args],
    start: { x: cmd.start.x + originX, y: cmd.start.y + originY },
    end: { x: cmd.end.x + originX, y: cmd.end.y + originY },
    ...(cmd.meta !== undefined ? { meta: cmd.meta } : {}),
  }));
}

/**
 * Shared front half of `variableOffset()` / `compoundVariableOffset()`, for both
 * the PathBlock and the ProjectedPath receiver: resolve the builder block (or
 * `<<` worker), run it, and validate the stop run and the spine. The caller keeps
 * the geometry call and the result shape, so the PathBlock path stays
 * byte-identical.
 *
 * `spine` is the receiver, bound to the block's second parameter (`pb`): a
 * PathBlock on a PathBlock receiver, a ProjectedPath on a projected one. Both
 * expose length / get / tangent / normal / vertices, so `pb` behaves the same.
 *
 * Check order is deliberate: the stop-count check comes before the spine-length
 * check, so input that errors today keeps its current message and only input
 * that is currently *accepted* — and currently produces nonsense — reaches the
 * new one.
 */
function collectVariableOffsetStops(
  expr: MethodCallExpression,
  scope: Scope,
  workerExpr: Expression | undefined,
  spine: PathBlockValue | ProjectedPathValue,
  compound: boolean,
  mError: (message: string) => Error,
): VariableOffsetBuilderValue {
  const name = compound ? 'compoundVariableOffset' : 'variableOffset';
  const cb = resolveCallbackBlock(expr, scope, workerExpr);
  if (!cb)
    throw mError(
      compound
        ? 'compoundVariableOffset() requires a block or a << worker, e.g. compoundVariableOffset() {|go, pb| go.stop(50%, 10, CurveContinuity.G1, -10, CurveContinuity.G1); } or compoundVariableOffset() << f'
        : 'variableOffset() requires a block or a << worker, e.g. variableOffset() {|go, pb| go.stop(50%, 10, CurveContinuity.G1); } or variableOffset() << f',
    );
  if (cb.extraArgs !== 0)
    throw mError(`${name}() takes no arguments besides the callback — use ${name}() {|go, pb| ... } or ${name}() << f`);
  const builder: VariableOffsetBuilderValue = {
    type: 'VariableOffsetBuilderValue',
    compound,
    stops: [],
  };
  const blockScope = callScope(cb.closure, scope, cb.calledFn);
  const params = cb.params;
  if (params.length > 0) setVariable(blockScope, params[0], builder);
  // pb = the spine itself (exposes get/tangent/normal/length/vertices).
  if (params.length > 1) setVariable(blockScope, params[1], spine);
  evaluateStatementsToAccum(cb.body, blockScope, createPathStore());
  if (builder.stops.length < 2)
    throw mError(
      compound
        ? 'compoundVariableOffset() needs at least 2 stops to trace a ribbon (each go.stop() places one cross-section)'
        : 'variableOffset() needs at least 2 stops to trace a path (each go.stop() places one point)',
    );
  if (calculatePathLength(spine.commands) === 0)
    throw mError(
      `${name}() needs a spine with arc length — this path has none (it is empty, or only moves the pen), so every stop would sample the same point. A 'command' query or subscription matches the leading move too; select drawing commands with 'command[length>0]'`,
    );
  return builder;
}

/** Builder stops → the geometry module's simple-offset stop run. */
function toSimpleStops(stops: VariableOffsetBuilderValue['stops']): SimpleStop[] {
  return stops.map((s) => ({
    time: s.time,
    offset: s.offset1,
    continuity: continuityFromValue(s.continuity1),
  }));
}

/** Builder stops → the geometry module's compound (two-profile) stop run. */
function toCompoundStops(stops: VariableOffsetBuilderValue['stops']): CompoundStop[] {
  return stops.map((s) => ({
    time: s.time,
    offset1: s.offset1,
    continuity1: continuityFromValue(s.continuity1),
    offset2: s.offset2 ?? 0,
    continuity2: continuityFromValue(s.continuity2 ?? s.continuity1),
  }));
}

/** Builder cap → the geometry module's CapSpec. */
function toCapSpec(c: CapValue | undefined): CapSpec | undefined {
  return c
    ? {
        cap: c.cap,
        projection: c.projection,
        length: c.length,
        continuity: c.continuity ? continuityFromValue(c.continuity) : undefined,
      }
    : undefined;
}

/**
 * A variable-offset result on a ProjectedPath receiver: registered on its spine.
 *
 * The shared builders origin-normalize their output and report the translation
 * they subtracted as `anchor`. Translating by `anchor` undoes exactly that, so
 * the result sits where the spine sampled it and `.draw()` lands it there —
 * which is the point for annotating query and subscription results. The builders
 * are deliberately NOT given a skip-normalization option: the PathBlock path is
 * pinned by byte-snapshot fixtures, and only `start`/`end` move here. Every
 * command left after `stripLeadingMove` is a relative `c`, whose args are
 * translation-invariant, so this is a re-registration and not a recomputation.
 *
 * `anchor` rides along, equal to `startPoint`, so a worker written with the
 * documented registration idiom (`M calc(x + ribbon.anchor.x) …; ribbon.draw()`)
 * keeps working on a projected spine — the `M` becomes redundant, not wrong. It
 * is stored rather than aliased to `startPoint` because the member-access
 * guidance error distinguishes "a variable-offset result" from "any projected
 * path" by the field's presence.
 */
function buildProjectedVariableOffset(
  cmds: PathBlockCommand[],
  anchor: { x: number; y: number },
  original: ProjectedPathValue,
): ProjectedPathValue {
  const registered = buildProjectedPathFromCommands(projectCommands(cmds, anchor.x, anchor.y), original);
  // Unreachable in practice: collectVariableOffsetStops requires >= 2 stops, so the
  // spline always emits at least one cubic. Kept as a guard because the PathBlock
  // cases carry the same one, and an empty result must not claim an anchor.
  if (cmds.length === 0) return registered;
  (registered as ProjectedPathValue & { anchor: { x: number; y: number } }).anchor = { ...anchor };
  return registered;
}

/**
 * Generate the GroupLayer, PathLayer (bg), and TextLayer (code) for toCodeSnippetBlock().
 */
function generateCodeSnippetLayers(
  name: string,
  codeLines: string[],
  fontSize: number,
  padding: number,
  evalState: EvaluationState,
): { groupLayer: GroupLayerState; bgLayer: PathLayerState; codeLayer: TextLayerState } {
  const lineHeight = fontSize * 1.6;
  const charWidth = fontSize * 0.6; // monospace approximation

  // Measure max line width
  let maxLineLen = 0;
  for (const line of codeLines) {
    if (line.length > maxLineLen) maxLineLen = line.length;
  }

  const bgWidth = maxLineLen * charWidth + 2 * padding;
  const bgHeight = codeLines.length * lineHeight + 2 * padding;

  // Create background PathLayer with roundRect
  const bgName = `${name}-bg`;
  const bgLayerState: PathLayerState = {
    name: bgName,
    layerType: 'PathLayer',
    isDefault: false,
    styles: { fill: '#1e293b', stroke: '#334155', 'stroke-width': '1' },
    pathContext: createPathContext(),
    accum: createPathStore(),
    transformState: createTransformState(),
  };
  // Generate roundRect path
  const r = 6;
  const rr = Math.min(r, bgWidth / 2, bgHeight / 2);
  const rrPath = `M ${rr} 0 L ${bgWidth - rr} 0 Q ${bgWidth} 0 ${bgWidth} ${rr} L ${bgWidth} ${bgHeight - rr} Q ${bgWidth} ${bgHeight} ${bgWidth - rr} ${bgHeight} L ${rr} ${bgHeight} Q 0 ${bgHeight} 0 ${bgHeight - rr} L 0 ${rr} Q 0 0 ${rr} 0 Z`;
  recordPath(bgLayerState.accum, rrPath, parsePathStringAt(rrPath, { x: 0, y: 0 }));

  // Create code TextLayer with tokenized text
  const codeName = `${name}-code`;
  const codeLayerState: TextLayerState = {
    name: codeName,
    layerType: 'TextLayer',
    isDefault: false,
    styles: { 'font-family': 'monospace', 'font-size': String(fontSize) },
    textElements: [],
  };

  for (let i = 0; i < codeLines.length; i++) {
    const line = codeLines[i];
    // Measure leading whitespace and convert to x-offset (SVG collapses whitespace in text)
    const leadingMatch = line.match(/^( *)/);
    const leadingSpaces = leadingMatch ? leadingMatch[1].length : 0;
    const trimmedLine = line.slice(leadingSpaces);
    const indentOffset = leadingSpaces * charWidth;

    const tokens = tokenizeLine(trimmedLine);
    const children: TextChild[] = [];
    for (const token of tokens) {
      const color = getTokenColor(token.type);
      children.push({
        type: 'tspan',
        text: token.value,
        styles: { fill: color },
      });
    }
    // If line is empty, add a single space run so the element exists
    if (children.length === 0) {
      children.push({ type: 'run', text: ' ' });
    }
    codeLayerState.textElements.push({
      x: padding + indentOffset,
      y: padding + i * lineHeight + fontSize,
      children,
    });
  }

  // Create GroupLayer containing both
  const groupLayer: GroupLayerState = {
    name,
    layerType: 'GroupLayer',
    isDefault: false as const,
    styles: {},
    transformState: createTransformState(),
    children: [bgName, codeName],
  };

  // Register all layers
  evalState.layers.set(bgName, bgLayerState);
  evalState.layers.set(codeName, codeLayerState);
  evalState.layers.set(name, groupLayer);
  evalState.layerOrder.push(name);
  // Don't push children to layerOrder — they're children of the group

  return { groupLayer, bgLayer: bgLayerState, codeLayer: codeLayerState };
}

/**
 * Serialize PathBlockCommands to an absolute SVG d-attribute string.
 *
 * Delegates to the shared serializer in path-data.ts. A local copy used to live
 * here and had drifted: it dispatched on the exact lowercase letter, so every
 * case-preserved uppercase command from a layer record fell through to a
 * two-number `<LETTER> endX endY` fallback. That silently dropped control
 * points, arc radii and flags, and emitted arguments for H/V/Z that take none
 * or fewer — so `d`, `.length` and every sampling method answered from
 * malformed data on any query block whose source was authored with absolute
 * commands. The shared version branches on `isRelative`, which is correct for
 * both cases. `formatNum` is passed explicitly because path-data defaults to
 * `String`, which would lose the --to-fixed rounding.
 */
function commandsToAbsoluteD(commands: PathBlockCommand[]): string {
  return commandsToAbsoluteDFormatted(commands, { format: formatNum });
}

/**
 * Path data for a `<defs>` child (mask, clipPath, pattern, marker, contour).
 *
 * Defs content has no cursor, so unlike a layer there is no pen for a relative
 * move to resolve against — prepend the ABSOLUTE first point instead. Without
 * this, a command list that does not begin with a move serializes to
 * `d="H 40 V 40 H 0 Z"`, which the browser discards, silently emptying the mask.
 *
 * SERIALIZATION ONLY: `commands` is untouched, exactly as in the ProjectedPath
 * `d` getter, which has always done this.
 */
function defsPathData(commands: PathBlockCommand[]): string {
  const d = commandsToAbsoluteD(commands);
  const first = commands[0];
  if (!first || first.command === 'M' || first.command === 'm') return d;
  const at = firstInkedPointOf(commands) ?? first.start;
  return `M ${formatNum(at.x)} ${formatNum(at.y)}${d ? ` ${d}` : ''}`;
}

/**
 * A lexical-scope miss inside a style value, positioned at the value. The
 * value is parsed on its own, so the inner "Line 1, col N:" counts from the
 * start of the value and is dropped.
 */
function styleScopeMissError(
  prop: StyleBlockLiteral['properties'][number],
  expr: StyleBlockLiteral,
  miss: Error,
): Error {
  const line = prop.valueLoc?.line ?? prop.loc?.line ?? getLine(expr);
  const col = prop.valueLoc?.column ?? prop.loc?.column ?? getCol(expr);
  const message = miss.message.replace(/^Line \d+(?:, col \d+)?: /, '');
  return new Error(formatError(`Style value for "${prop.name}": ${message}`, line, col));
}

function evaluateStyleBlockLiteral(expr: StyleBlockLiteral, scope: Scope): StyleBlockValue {
  // A malformed declaration (e.g. a missing trailing `;`) is recorded leniently
  // during AST-building so the language service stays resilient; enforce it
  // strictly here so it fails compilation with a positioned error.
  if (expr.incomplete) {
    throw new Error(formatError(expr.incomplete.message, expr.incomplete.line, expr.incomplete.column));
  }
  const properties: Record<string, string> = {};
  for (const prop of expr.properties) {
    // Trust tracking: compiler-emitted strings (Color → hex, CSSVar → var(...),
    // gradient/pattern/marker → url(#id)) are validated at construction time;
    // they cannot be distinguished from user input by string shape, so we mark
    // them trusted here and skip the CSS-value validator. Plain string and
    // multi-token-fallback paths remain untrusted and get strict validation.
    let resolvedValue = prop.value;
    let trusted = false;
    let wasWholeValueTemplate = false;
    let structValueName: string | null = null;
    // var() strings this compiler emitted from validated CSSVarValue/Color
    // objects for THIS value — the validator allows exactly these tokens.
    const emittedVars: string[] = [];
    let angleUnitError: string | null = null;
    let scopeMiss: Error | null = null;
    try {
      const parseResult = expressionParser.parse(prop.value);
      if (parseResult.status && parseResult.value) {
        // Bare color literal (#hex) — trusted (parser shape is restrictive).
        if (parseResult.value.type === 'ColorLiteral') {
          resolvedValue = parseResult.value.raw;
          storeStyleProperty(properties, prop.name, resolvedValue);
          continue;
        }
        // For the dash-pattern properties ONLY, a bare percent literal
        // (50%, -50%) keeps its authored text: dash() resolves % against
        // path length itself, so baking it to a fraction (50% → 0.5) would
        // erase the unit. Every other property keeps the historical
        // behavior (percent evaluates to a plain fraction).
        if (prop.name === 'stroke-dasharray' || prop.name === 'stroke-dashoffset') {
          const pctNode =
            parseResult.value.type === 'UnaryExpression' && parseResult.value.operator === '-'
              ? parseResult.value.argument
              : parseResult.value;
          if (pctNode.type === 'NumberLiteral' && pctNode.unit === '%') {
            properties[prop.name] = prop.value.trim();
            continue;
          }
        }
        if (prop.name === 'rotate') {
          // The shorthand that motivated this rule: `#{ rotate: 45; }` renders
          // as rotate(2578.31) and nothing used to complain. Collected rather
          // than thrown here: the catch below swallows everything to keep raw
          // CSS strings working, so the throw has to happen outside the try.
          angleUnitError = describeMissingAngleUnit('style.rotate', 'rotate:', parseResult.value);
        }
        const evaluated = evaluateExpression(parseResult.value, scope);
        if (typeof evaluated === 'number') {
          resolvedValue = formatNum(evaluated);
          trusted = true;
        } else if (isAngleValue(evaluated)) {
          // Angles emit their radians number (rotate: 45deg → rotate="0.785…"
          // downstream converts to degrees at the transform boundary)
          resolvedValue = formatNum(evaluated.radians);
          trusted = true;
        } else if (typeof evaluated === 'string') {
          resolvedValue = evaluated;
          // Untrusted: user-supplied string. Validation runs below.
          // A whole-value template is inline-authored CSS text: run the
          // function-arg resolver on its result (below) so `blur(${s}px)
          // brightness(level)` behaves identically to the fragment form.
          wasWholeValueTemplate = parseResult.value.type === 'TemplateLiteral';
        } else if (isColorValue(evaluated)) {
          resolvedValue = colorValueToCSS(evaluated);
          trusted = true;
        } else if (isCSSVarValue(evaluated)) {
          resolvedValue = cssVarValueToCSS(evaluated);
          trusted = true;
        } else if (isGradientValue(evaluated)) {
          resolvedValue = `url(#${evaluated.id})`;
          trusted = true;
        } else if (isPatternValue(evaluated)) {
          resolvedValue = `url(#${evaluated.id})`;
          trusted = true;
        } else if (isMarkerValue(evaluated)) {
          resolvedValue = `url(#${evaluated.id})`;
          trusted = true;
        } else if (isFilterValue(evaluated)) {
          resolvedValue = `url(#${evaluated.id})`;
          trusted = true;
        } else {
          // Struct values (ViewBox, Point, Grid, ctx, …) have no CSS form;
          // silently keeping the raw source text would emit a broken
          // attribute (e.g. stroke-width="viewbox"). Record the type name
          // and reject AFTER the try/catch — throwing here would be
          // swallowed by the catch below, whose keep-raw behavior is
          // load-bearing for eval failures (rgb(...), context-stroke, …).
          structValueName = getStructDescriptor(evaluated)?.name ?? null;
        }
        // For other non-struct types, keep raw string (untrusted)
      }
    } catch (e) {
      // Parse or eval failed — keep raw string (handles rgb(...), #hex, multi-value strings, etc.)
      // Except a name a named fn's caller has (phase 0a step 3): it resolved
      // under the old rule, so keeping the raw name would change the output
      // silently. Thrown below, outside the try.
      if (isLexicalScopeMiss(e)) scopeMiss = e;
    }
    if (scopeMiss) throw styleScopeMissError(prop, expr, scopeMiss);
    if (angleUnitError) {
      throw new Error(
        formatError(angleUnitError, prop.valueLoc?.line ?? prop.loc?.line ?? getLine(expr), prop.valueLoc?.column),
      );
    }
    if (structValueName) {
      const eLine = prop.valueLoc?.line ?? prop.loc?.line ?? getLine(expr);
      const eCol = prop.valueLoc?.column ?? prop.loc?.column ?? getCol(expr);
      throw new Error(
        formatError(
          `Style value for "${prop.name}": a ${structValueName} value has no CSS form — use one of its members instead`,
          eLine,
          eCol,
        ),
      );
    }
    // Backtick template fragments inside the value (e.g. blur(`${v}`px)):
    // evaluate each span and splice its text into the surrounding value.
    // The whole-value template case never reaches here (it parses above);
    // the spliced result stays untrusted and is validated below.
    let didSplice = false;
    if (resolvedValue === prop.value && (prop.value.includes('`') || prop.value.includes('${'))) {
      try {
        const spliced = spliceTemplateFragments(prop.value, (templateSource) => {
          const parsed = expressionParser.parse(templateSource);
          if (!parsed.status || !parsed.value) {
            throw new Error(`could not parse template fragment ${templateSource}`);
          }
          const v = evaluateExpression(parsed.value, scope);
          if (typeof v === 'number') return formatNum(v);
          if (isAngleValue(v)) return formatNum(v.radians);
          if (typeof v === 'string') return v;
          // Typed values with a CSS form: var() strings from these are
          // compiler-emitted (validated at construction) — record them so
          // the validator can allow exactly these tokens. A var()-shaped
          // plain string result above is deliberately NOT recorded.
          if (isColorValue(v) || isCSSVarValue(v)) {
            const css = isColorValue(v) ? colorValueToCSS(v) : cssVarValueToCSS(v);
            if (css.startsWith('var(')) emittedVars.push(css);
            return css;
          }
          throw new Error(`template fragment ${templateSource} must produce a string or number`);
        });
        if (spliced !== null) {
          resolvedValue = spliced;
          didSplice = true;
        }
      } catch (e) {
        if (isLexicalScopeMiss(e)) throw styleScopeMissError(prop, expr, e);
        const eLine = prop.valueLoc?.line ?? prop.loc?.line ?? getLine(expr);
        const eCol = prop.valueLoc?.column ?? prop.loc?.column ?? getCol(expr);
        throw new Error(formatError(`Style value for "${prop.name}": ${(e as Error).message}`, eLine, eCol));
      }
    }
    // If the whole-value expression parse didn't resolve, try resolving
    // expressions embedded inside CSS function arguments (e.g., color args in
    // drop-shadow, numeric variables in brightness). Also runs on spliced
    // values and whole-value template results so remaining idents substitute
    // identically in every interpolation form.
    if (resolvedValue === prop.value || didSplice || wasWholeValueTemplate) {
      let cssResolved: string | null;
      try {
        cssResolved = tryResolveCSSFunctionArgs(resolvedValue, scope, emittedVars);
      } catch (e) {
        // Only a lexical-scope miss gets out of the resolver (css-function-resolve.ts).
        if (isLexicalScopeMiss(e)) throw styleScopeMissError(prop, expr, e);
        throw e;
      }
      if (cssResolved !== null) {
        resolvedValue = cssResolved;
      }
    }
    // Auto-wrap URL-reference properties with url(#...) — skip CSS function values (contain parentheses)
    if (URL_REF_PROPERTIES.has(prop.name) && typeof resolvedValue === 'string' && !/^url\(/i.test(resolvedValue) && !resolvedValue.includes('(')) {
      // The unwrapped value becomes a fragment ref — must be a valid ident.
      try {
        validateCSSIdent(resolvedValue, 'fragment-ref');
      } catch (e) {
        // Prefer the per-declaration position (prop.loc); the enclosing
        // StyleBlockLiteral carries no source location.
        const eLine = prop.loc?.line ?? getLine(expr);
        const eCol = prop.loc?.column ?? getCol(expr);
        throw new Error(formatError(`Style "${prop.name}" url() reference: ${(e as Error).message}`, eLine, eCol));
      }
      resolvedValue = `url(#${resolvedValue})`;
      trusted = true; // wrapped a validated ident
    }
    // Validate the final value against the strict CSS allow-list before
    // storing. Trusted (compiler-emitted) values bypass the validator —
    // they cannot escape the value string by shape (CSSVar/Color/Gradient
    // construction validates inputs and produces a fixed grammar).
    if (!trusted) {
      try {
        // Only var() tokens the compiler itself emitted (recorded in
        // emittedVars) may pass — never a var() that rode along in the text.
        validateCSSValue(resolvedValue, prop.name, {
          allowVar: emittedVars.length > 0 ? emittedVars : false,
        });
      } catch (e) {
        // Point at the value when we know its extent (valueLoc), else the name.
        const eLine = prop.valueLoc?.line ?? prop.loc?.line ?? getLine(expr);
        const eCol = prop.valueLoc?.column ?? prop.loc?.column ?? getCol(expr);
        throw new Error(formatError((e as Error).message, eLine, eCol));
      }
    }
    storeStyleProperty(properties, prop.name, resolvedValue);
  }
  return { type: 'StyleBlockValue', properties };
}

/**
 * Convert a ColorValue to its CSS string representation.
 */
function colorValueToCSS(color: ColorValue): string {
  if (color.lightDark) {
    return `light-dark(${color.lightDark.lightCSS}, ${color.lightDark.darkCSS})`;
  } else if (color.cssExpr) {
    return color.cssExpr;
  } else if (color.cssVar) {
    return `var(${color.cssVar.varName}, ${oklchToCSS(color.oklch)})`;
  }
  return oklchToCSS(color.oklch);
}

/**
 * Convert a CSSVarValue to its CSS string representation.
 */
function cssVarValueToCSS(v: CSSVarValue): string {
  return v.fallback ? `var(${v.varName}, ${v.fallback})` : `var(${v.varName})`;
}

/**
 * Resolve Pathogen expressions embedded within CSS function arguments (e.g.
 * color args in drop-shadow) via the shared resolver, using this evaluator's
 * expression parse/eval.
 */
function tryResolveCSSFunctionArgs(raw: string, scope: Scope, emittedVars?: string[]): string | null {
  return sharedTryResolveCSSFunctionArgs(raw, {
    parseExpression: (token) => {
      const parseResult = expressionParser.parse(token);
      return parseResult.status && parseResult.value ? parseResult.value : null;
    },
    resolveToCSS: (expr) => {
      // Literal tokens (2px, 1.4) must stay verbatim — substituting would
      // strip CSS units the Pathogen number parser consumed. (The resolver
      // also guards on the raw token text, which covers -90deg/-50% shapes
      // that parse as UnaryExpression rather than NumberLiteral.)
      if (expr.type === 'NumberLiteral') return null;
      const evaluated = evaluateExpression(expr, scope);
      if (isColorValue(evaluated)) return colorValueToCSS(evaluated);
      if (isCSSVarValue(evaluated)) return cssVarValueToCSS(evaluated);
      if (typeof evaluated === 'number') return formatNum(evaluated);
      // CSS angle slots (hue-rotate) need a unit — emit degrees
      if (isAngleValue(evaluated)) return `${formatNum(radiansToDegreesSnapped(evaluated.radians))}deg`;
      return null;
    },
  }, emittedVars);
}

function evaluateExpression(expr: Expression, scope: Scope): Value {
  switch (expr.type) {
    case 'NumberLiteral':
      if (expr.unit === 'deg' || expr.unit === 'rad' || expr.unit === 'pi') {
        return angle(convertUnitSuffix(expr.value, expr.unit), expr.unit);
      }
      return convertUnitSuffix(expr.value, expr.unit);

    case 'ColorLiteral': {
      const raw = expr.raw;
      if (raw.startsWith('#')) {
        const digits = raw.slice(1);
        if (digits.length !== 3 && digits.length !== 4 && digits.length !== 6 && digits.length !== 8) {
          throw new Error(
            formatError(
              `Invalid hex color: '${raw}' (must be 3, 4, 6, or 8 hex digits)`,
              getLine(expr),
              getCol(expr),
            ),
          );
        }
      }
      try {
        return { type: 'ColorValue' as const, oklch: parseColor(raw) };
      } catch (e) {
        throw new Error(formatError((e as Error).message, getLine(expr), getCol(expr)));
      }
    }

    case 'StringLiteral':
      return expr.value;

    case 'NullLiteral':
      return null;

    case 'BooleanLiteral':
      return boolVal(expr.value);

    case 'Identifier':
      // Reserved suffix names error here (references), NOT in
      // lookupVariable — which also resolves call targets (deg(x)).
      if (RESERVED_UNIT_NAMES.has(expr.name)) {
        throw new Error(formatError(reservedNameReferenceError(expr.name), getLine(expr), getCol(expr)));
      }
      return lookupVariable(scope, expr.name, getLine(expr), getCol(expr));

    case 'LambdaExpression':
      // Capture the live definition scope by reference: the lambda sees later
      // reassignments of captured variables, and loop bodies (fresh scope per
      // iteration) give each lambda its own iteration's bindings.
      return {
        type: 'UserFunction' as const,
        params: expr.params,
        body: expr.body,
        closure: scope,
        isLambda: true as const,
      };

    case 'ArrayLiteral': {
      const elements: Value[] = [];
      for (const el of expr.elements) {
        if (el.type === 'SpreadElement') {
          const val = evaluateExpression(el.argument, scope);
          if (!isArrayValue(val)) throw new Error(formatError('Spread argument must be an array', getLine(el.argument)));
          elements.push(...val.elements);
        } else {
          elements.push(evaluateExpression(el, scope));
        }
      }
      return { type: 'ArrayValue' as const, elements };
    }

    case 'ObjectLiteral': {
      const props = new Map<string, Value>();
      for (const prop of expr.properties) {
        if (prop.type === 'SpreadElement') {
          const val = evaluateExpression(prop.argument, scope);
          if (!isObjectValue(val)) throw new Error(formatError('Spread argument must be an object', getLine(prop.argument)));
          for (const [k, v] of val.properties) props.set(k, v);
        } else {
          props.set(prop.key, evaluateExpression(prop.value, scope));
        }
      }
      return { type: 'ObjectValue', properties: props } as ObjectValue;
    }

    case 'IndexExpression':
      return evaluateIndexExpression(expr, scope);

    case 'MethodCallExpression':
      return evaluateMethodCall(expr, scope);

    // `(a..b)` — the array of numbers `for (i in a..b)` visits. A FRESH array
    // per evaluation: it is an ordinary ArrayValue from here on (push, the
    // iteration lock, reference semantics), so it must never be shared.
    case 'RangeExpression': {
      const { start, plan } = resolveRange(expr.start, expr.end, expr.inclusive, scope, 'value', getLine(expr));
      return { type: 'ArrayValue' as const, elements: rangeValues(start, plan) };
    }

    case 'TernaryExpression': {
      const condVal = evaluateExpression(expr.condition, scope);
      return isTruthy(condVal) ? evaluateExpression(expr.consequent, scope) : evaluateExpression(expr.alternate, scope);
    }

    case 'SwitchExpression': {
      // Same matching as the statement; only the chosen arm's expression runs.
      const scrutinee = evaluateExpression(expr.discriminant, scope);
      const selected = selectSwitchArm(expr, scrutinee, () => createScope(scope), (armScope) => switchHost(armScope, expr));
      return evaluateExpression(selected.value, selected.scope);
    }

    case 'BinaryExpression': {
      // Check for angle unit misuse before evaluation (+/- mixing, angle × angle)
      if (expr.operator === '+' || expr.operator === '-' || expr.operator === '*') {
        checkAngleUnitMismatch(expr.left, expr.right, expr.operator);
      }

      // << worker application: a callback builtin written WITHOUT a trailing
      // block takes its callback from the right operand. Must run before the
      // eager operand evaluation below — the bare builtin call is not a
      // complete expression on its own. A block-bearing call falls through to
      // the merge path (vo() {|go,pb| ...} << edge is still concatenation).
      if (
        expr.operator === '<<' &&
        expr.left.type === 'MethodCallExpression' &&
        !expr.left.block &&
        CALLBACK_METHODS.has(expr.left.method)
      ) {
        // '<<' applies a worker defined ELSEWHERE (a lambda variable or a
        // named fn). An inline lambda literal here duplicates the trailing
        // block form, so it is rejected by design.
        if (expr.right.type === 'LambdaExpression') {
          throw new Error(
            `An inline lambda after '<<' is not allowed — '<<' applies a worker defined elsewhere. ` +
              `Write the callback as a trailing block (.${expr.left.method} {|...| ... }) or bind the lambda to a name first.`,
          );
        }
        return evaluateMethodCall(expr.left, scope, expr.right);
      }

      const left = evaluateExpression(expr.left, scope);
      const right = evaluateExpression(expr.right, scope);

      // << operator: merge (objects, style blocks, path blocks, text blocks)
      if (expr.operator === '<<') {
        if (isPathBlockValue(left) && isPathBlockValue(right)) {
          const concatCmds = concatenateCommands(left.commands, left.endPoint, right.commands);
          if (concatCmds.length === 0) {
            return {
              type: 'PathBlockValue' as const,
              commands: [],
              records: [],
              startPoint: { x: 0, y: 0 },
              endPoint: { x: 0, y: 0 },
            };
          }
          const lastCmd = concatCmds[concatCmds.length - 1];
          return {
            type: 'PathBlockValue' as const,
            commands: concatCmds,
            records: recordsFromCommands(concatCmds),
            startPoint: firstInkedPointOf(concatCmds) ?? { x: 0, y: 0 },
            endPoint: { x: lastCmd.end.x, y: lastCmd.end.y },
          };
        }
        if (isTextBlockValue(left) && isStyleBlock(right)) {
          return { type: 'TextBlockValue' as const, elements: left.elements, styles: { ...left.styles, ...right.properties } };
        }
        if (isProjectedTextValue(left) && isStyleBlock(right)) {
          return { type: 'ProjectedTextValue' as const, elements: left.elements, styles: { ...left.styles, ...right.properties }, origin: left.origin };
        }
        if (isStyleBlock(left) && isStyleBlock(right)) {
          return { type: 'StyleBlockValue', properties: { ...left.properties, ...right.properties } };
        }
        if (isLayerReference(left) && isStyleBlock(right)) {
          // Merge styles into layer in place, return same ref for chaining
          Object.assign(left.layer.styles, right.properties);
          return left;
        }
        if (isObjectValue(left) && isObjectValue(right)) {
          const merged = new Map(left.properties);
          for (const [key, value] of right.properties) {
            merged.set(key, value);
          }
          return { type: 'ObjectValue', properties: merged };
        }
        if (isCallableValue(right)) {
          throw new Error(
            formatError(
              'Operator << can apply a function or lambda only to a callback builtin call written without a trailing block (e.g. arr.map() << f, spine.variableOffset() << f) — the left side here is already a value',
              getLine(expr),
            ),
          );
        }
        throw new Error(
          formatError(
            'Operator << requires matching operand types (both objects, both style blocks, both path blocks, or text block << style block)',
            getLine(expr),
          ),
        );
      }

      // == / != share their semantics with `case` matching (value-semantics.ts).
      // Non-comparable operands fall through to the numeric check below,
      // which throws the "requires numeric operands" error.
      if (expr.operator === '==' || expr.operator === '!=') {
        const eq = valuesEqual(left, right);
        if (eq !== undefined) return boolVal(expr.operator === '==' ? eq : !eq);
      }

      // Null in arithmetic
      if (left === null || right === null) {
        throw new Error(formatError('Cannot use null in arithmetic expression', getLineDeep(expr)));
      }

      // Extract numeric values (number or BooleanValue)
      const leftNum = toNumber(left);
      const rightNum = toNumber(right);

      if (leftNum === undefined || rightNum === undefined) {
        // A function named without its parentheses (`PI / 2`) says so — function-values.ts.
        const functionOperand =
          describeFunctionAsNumber(left, expr.left) ?? describeFunctionAsNumber(right, expr.right);
        throw new Error(
          formatError(functionOperand ?? `Binary operator ${expr.operator} requires numeric operands`, getLineDeep(expr)),
        );
      }

      // Angle propagation: an angle stays an angle through +/-, scaling, and
      // division by a plain number. Angle×angle and angle/angle cancel to a
      // plain number (the literal-visible cases are rejected statically above).
      const leftAngle = isAngleValue(left);
      const rightAngle = isAngleValue(right);
      if (leftAngle || rightAngle) {
        const unit = leftAngle ? (left as AngleValue).unit : (right as AngleValue).unit;
        switch (expr.operator) {
          case '+':
            return angle(leftNum + rightNum, unit);
          case '-':
            return angle(leftNum - rightNum, unit);
          case '*':
            if (leftAngle !== rightAngle) return angle(leftNum * rightNum, unit);
            break;
          case '/':
            if (leftAngle && !rightAngle) return angle(leftNum / rightNum, unit);
            break;
        }
      }

      switch (expr.operator) {
        case '+':
          return leftNum + rightNum;
        case '-':
          return leftNum - rightNum;
        case '*':
          return leftNum * rightNum;
        case '/':
          return leftNum / rightNum;
        case '%':
          return leftNum % rightNum;
        case '<':
          return boolVal(leftNum < rightNum);
        case '>':
          return boolVal(leftNum > rightNum);
        case '<=':
          return boolVal(leftNum <= rightNum);
        case '>=':
          return boolVal(leftNum >= rightNum);
        case '==':
          return boolVal(leftNum === rightNum);
        case '!=':
          return boolVal(leftNum !== rightNum);
        case '&&':
          return boolVal(leftNum && rightNum);
        case '||':
          return boolVal(leftNum || rightNum);
      }
    }
    // falls through

    case 'UnaryExpression': {
      const arg = evaluateExpression(expr.argument, scope);
      if (arg === null) {
        throw new Error(formatError('Cannot use null in arithmetic expression', getLine(expr)));
      }
      const argNum = toNumber(arg);
      if (argNum === undefined) {
        const functionOperand = describeFunctionAsNumber(arg, expr.argument);
        throw new Error(
          formatError(functionOperand ?? `Unary operator ${expr.operator} requires numeric operand`, getLine(expr)),
        );
      }
      switch (expr.operator) {
        case '-':
          if (isAngleValue(arg)) return angle(-arg.radians, arg.unit);
          return -argNum;
        case '!':
          return boolVal(!argNum);
      }
    }
    // falls through

    case 'CalcExpression':
      return evaluateExpression(expr.expression, scope);

    case 'FunctionCall':
      return evaluateFunctionCall(expr, scope);

    case 'MemberExpression':
      return evaluateMemberExpression(expr, scope);

    case 'TemplateLiteral':
      return evaluateTemplateLiteral(expr, scope);

    case 'StyleBlockLiteral':
      return evaluateStyleBlockLiteral(expr, scope);

    case 'PathBlockExpression':
      return evaluatePathBlockExpression(expr, scope);

    case 'TextBlockExpression':
      return evaluateTextBlockExpression(expr, scope);

    case 'LayerConstructorExpression':
      return evaluateLayerConstructor(expr, scope);

    default:
      throw new Error(`Unknown expression type: ${(expr as Expression).type}`);
  }
}

/**
 * Evaluate a LayerConstructorExpression — creates a new layer and returns a LayerReference
 */
/** Style keys that are shorthand for an SVG transform. */
const TRANSFORM_STYLE_KEYS = ['translate', 'translate-x', 'translate-y', 'scale', 'scale-x', 'scale-y', 'rotate'];

/**
 * Move a layer's transform shorthand out of its style block and into its
 * `TransformState`, so there is ONE transform store rather than two.
 *
 * Before this, style shorthand was resolved to an attribute string at emit time
 * and never reached `transformState`, with two consequences measured in
 * project-docs/placement-audit/D2-layer-transform-queries.md:
 *   - `#{ translate-x: 100; }` rendered with the transform but read back as
 *     `ctx.transform.translate = 0,0`, so a program could not inspect or
 *     compensate for its own layer's transform;
 *   - mixing the spellings silently dropped one — a style `translate-x` plus a
 *     later `ctx.transform.scale.set(2,2)` emitted only the translate, because
 *     the shorthand string won the emit-time precedence ladder outright.
 *
 * Absorbing here fixes both: `transformStateToSvg` produces the same attribute
 * for numeric input (`translate(x, y)` / `rotate(deg)` / `scale(x, y)`), so
 * output is unchanged, and the two spellings now compose.
 *
 * Values that are not finite numbers are LEFT IN PLACE for the legacy
 * emit-time path — a CSS var or an interpolated string still reaches the
 * attribute verbatim, exactly as before.
 */
function absorbStyleTransform(styles: LayerStyle, transformState: TransformState): void {
  const present = TRANSFORM_STYLE_KEYS.filter((k) => styles[k] !== undefined);
  if (present.length === 0) return;

  const nums = (raw: string): number[] | null => {
    const parts = raw.split(/\s*,\s*/).map((v) => Number(v.trim()));
    return parts.every((n) => Number.isFinite(n)) ? parts : null;
  };
  // All-or-nothing: if any value is non-numeric, leave every key for the
  // legacy path rather than absorbing half a transform.
  const parsed = new Map<string, number[]>();
  for (const k of present) {
    const v = nums(styles[k] as string);
    if (v === null) return;
    parsed.set(k, v);
  }

  let tx: number | undefined;
  let ty: number | undefined;
  const t = parsed.get('translate');
  if (t) {
    tx = t[0];
    ty = t.length > 1 ? t[1] : 0;
  }
  if (parsed.has('translate-x')) tx = parsed.get('translate-x')![0];
  if (parsed.has('translate-y')) ty = parsed.get('translate-y')![0];
  if (tx !== undefined || ty !== undefined) transformState.translate = { x: tx ?? 0, y: ty ?? 0 };

  // The style value is already radians — an AngleValue is converted on the way
  // into the style block — and TransformState.rotate.angle is radians too.
  if (parsed.has('rotate')) transformState.rotate = { angle: parsed.get('rotate')![0] };

  let sx: number | undefined;
  let sy: number | undefined;
  const sc = parsed.get('scale');
  if (sc) {
    sx = sc[0];
    sy = sc.length > 1 ? sc[1] : sc[0];
  }
  if (parsed.has('scale-x')) sx = parsed.get('scale-x')![0];
  if (parsed.has('scale-y')) sy = parsed.get('scale-y')![0];
  if (sx !== undefined || sy !== undefined) transformState.scale = { x: sx ?? 1, y: sy ?? 1 };

  for (const k of present) delete styles[k];
}

function evaluateLayerConstructor(expr: LayerConstructorExpression, scope: Scope): LayerReference {
  if (!scope.evalState) {
    throw new Error(formatError('Layer constructors require evaluation context', getLine(expr)));
  }
  const nameValue = evaluateExpression(expr.name, scope);
  if (typeof nameValue !== 'string') {
    throw new Error(formatError('Layer name must be a string', getLine(expr)));
  }
  try {
    validateCSSIdent(nameValue, 'layer-name');
  } catch (e) {
    throw new Error(formatError((e as Error).message, getLine(expr)));
  }
  if (scope.evalState.layers.has(nameValue)) {
    throw new Error(formatError(`Duplicate layer name: '${nameValue}'`, getLine(expr)));
  }

  let styles: LayerStyle = {};
  if (expr.styleExpr) {
    const styleValue = evaluateExpression(expr.styleExpr, scope);
    if (!isStyleBlock(styleValue)) {
      throw new Error(formatError('Layer style must be a style block', getLine(expr)));
    }
    styles = { ...styleValue.properties };
  }

  let layerState: LayerState;
  if (expr.layerType === 'TextLayer') {
    layerState = {
      name: nameValue,
      layerType: 'TextLayer',
      isDefault: false,
      styles,
      textElements: [],
    };
  } else if (expr.layerType === 'GroupLayer') {
    layerState = {
      name: nameValue,
      layerType: 'GroupLayer',
      isDefault: false as const,
      styles,
      transformState: createTransformState(),
      children: [],
    };
  } else {
    layerState = {
      name: nameValue,
      layerType: 'PathLayer',
      isDefault: false,
      styles,
      pathContext: createPathContext({ trackHistory: !!scope.evalState.trace }),
      accum: createPathStore(),
      transformState: createTransformState(),
    };
  }

  // One transform store: move any shorthand out of the styles and into the
  // layer's TransformState so it is readable through ctx.transform.
  if ('transformState' in layerState && layerState.transformState) {
    absorbStyleTransform(layerState.styles, layerState.transformState);
  }

  scope.evalState.layers.set(nameValue, layerState);
  scope.evalState.layerOrder.push(nameValue);

  return { type: 'LayerReference', layer: layerState };
}

/**
 * EvaluationState fields each block state (`@{ }`, `&{ }`) keeps for itself:
 *  - pathContext, layers, layerOrder, activeLayerName, defaultLayerName,
 *    transformState: a block's own pen and layer bookkeeping;
 *  - viewBox: the `viewbox` lookup reads it from the ROOT scope (lookupVariable);
 *  - rootAccum: a block accumulates into its own store;
 *  - subscriptions: `.subscribe()` is refused inside blocks (see the subscribe
 *    method), because a callback made there would run against the block's
 *    empty layer table after the block is gone;
 *  - argTracked: a per-statement sink, saved and restored on one state;
 *    sharing it would feed an inner block's commands to the outer statement.
 */
type BlockLocalField =
  | 'pathContext'
  | 'layers'
  | 'layerOrder'
  | 'activeLayerName'
  | 'defaultLayerName'
  | 'transformState'
  | 'viewBox'
  | 'rootAccum'
  | 'subscriptions'
  | 'argTracked';

/**
 * The program-wide part of an evaluation state, shared by reference with every
 * synthetic block state. Blocks get their own pen, layer bookkeeping and
 * accumulator (BlockLocalField), but diagnostics, defs, loaded fonts,
 * glyph/coverage reports, strict mode and the running subscription are the
 * program's own.
 *
 * Every EvaluationState field must be either listed here or in
 * BlockLocalField: the `satisfies` below fails to compile, naming the field,
 * when a new one is neither. (fontRegistry, strictWarnings and missingGlyphs
 * were once dropped silently from blocks.)
 */
function sharedProgramState(parent: EvaluationState | undefined) {
  return {
    logs: parent?.logs ?? [],
    warnings: parent?.warnings ?? [],
    strictWarnings: parent?.strictWarnings,
    trace: parent?.trace,
    calledStdlibFunctions: parent?.calledStdlibFunctions ?? new Set<string>(),
    masks: parent?.masks ?? new Map(),
    clipPaths: parent?.clipPaths ?? new Map(),
    gradients: parent?.gradients ?? new Map(),
    patterns: parent?.patterns ?? new Map(),
    markers: parent?.markers ?? new Map(),
    filters: parent?.filters ?? new Map(),
    cssProperties: parent?.cssProperties ?? new Map(),
    fontRegistry: parent?.fontRegistry,
    missingGlyphs: parent?.missingGlyphs,
    fontCoverageRequests: parent?.fontCoverageRequests,
    // `sub.unsubscribe()` inside a block in a subscription callback must see
    // the callback's subscription, as it does outside the block.
    dispatching: parent?.dispatching,
  } satisfies Partial<EvaluationState> & Record<Exclude<keyof EvaluationState, BlockLocalField>, unknown>;
}

/**
 * Evaluate a PathBlockExpression — captures relative path commands into a PathBlockValue
 */
function evaluatePathBlockExpression(expr: PathBlockExpression, scope: Scope): PathBlockValue {
  // Runtime restriction: no nesting path blocks
  if (scope.evalState && (scope.evalState as EvaluationState & { _insidePathBlock?: boolean })._insidePathBlock) {
    throw new Error(formatError('Cannot nest path blocks', getLine(expr)));
  }

  // Create an isolated PathContext at origin (0, 0) with history tracking
  const blockContext = createPathContext({ trackHistory: true });

  // Create a child scope for the block body
  const blockScope = createScope(scope);

  // Create an isolated evaluation state for the block
  const blockEvalState: EvaluationState & { _insidePathBlock: boolean } = {
    ...sharedProgramState(scope.evalState),
    pathContext: blockContext,
    layers: new Map(), // Empty — layer definitions not allowed
    layerOrder: [],
    activeLayerName: null,
    defaultLayerName: null,
    transformState: createTransformState(),
    _insidePathBlock: true,
  };
  blockScope.evalState = blockEvalState;
  // `ctx` in here is the block's cursor: lookupVariable computes it from
  // blockEvalState (ctxValueFor), so it moves as the block draws.

  // Evaluate body, accumulating path command strings
  const accum = createPathStore();
  for (const stmt of expr.body) {
    // Runtime restrictions
    if (stmt.type === 'LayerDefinition') {
      throw new Error(formatError('Layer definitions are not allowed inside path blocks', getLine(stmt)));
    }
    if (stmt.type === 'LayerApplyBlock') {
      throw new Error(formatError('Layer apply blocks are not allowed inside path blocks', getLine(stmt)));
    }
    if (stmt.type === 'TextStatement') {
      throw new Error(formatError('Text statements are not allowed inside path blocks', getLine(stmt)));
    }
    if (stmt.type === 'ViewBoxDefinition') {
      throw new Error(formatError('ViewBox definitions are not allowed inside path blocks', getLine(stmt)));
    }

    // Evaluate with a wrapper that enforces relative-only commands
    evaluatePathBlockStatement(stmt, blockScope, accum);
  }

  // Build the PathBlockValue from accumulated commands
  const commands: PathBlockCommand[] = blockContext.commands.map((entry) => ({
    command: entry.command.toLowerCase(),
    args: normalizeToRelativeArgs(entry.command, entry.args, entry.start),
    start: { x: entry.start.x, y: entry.start.y },
    end: { x: entry.end.x, y: entry.end.y },
  }));

  // Transfer per-command metadata (labels, recorded corner ops) from the
  // authored records onto the normalized commands. The two lists track the
  // same evaluation 1:1; skip transfer defensively if counts ever diverge.
  const recordCommands = accum.records.flatMap((r) => r.commands);
  if (recordCommands.length === commands.length) {
    for (let i = 0; i < commands.length; i++) {
      if (recordCommands[i].meta !== undefined) commands[i].meta = recordCommands[i].meta;
    }
  } else if (recordCommands.some((c) => c.meta !== undefined)) {
    warn(
      scope.evalState,
      'annotation-transfer',
      `path block annotation transfer skipped: ${recordCommands.length} recorded vs ${commands.length} tracked commands — labels/corner ops in this block were dropped`,
      expr.loc,
    );
  }

  // Apply recorded corner ops at finalization (identity: authored records keep
  // their annotations; the finalized commands carry propagated labels).
  const finalized = applyRecordedCornerOps(commands);
  for (const w of finalized.warnings) {
    warn(scope.evalState, 'corner-op', w.message, w.loc ?? expr.loc);
  }

  return {
    type: 'PathBlockValue',
    commands: finalized.commands,
    records: accum.records.filter((r) => r.raw.length > 0),
    // B2: a literal with a leading m run starts where the ink lands.
    startPoint: firstInkedPointOf(finalized.commands) ?? { x: 0, y: 0 },
    endPoint: { x: blockContext.position.x, y: blockContext.position.y },
  };
}

/**
 * Evaluate a statement inside a path block, enforcing relative-only constraint
 */
function evaluatePathBlockStatement(stmt: Statement, scope: Scope, accum: PathStore): void {
  // Relative-only commands are enforced in evaluateStatementToAccum's
  // PathCommand case, so they hold for code called from the block too.
  // Path-block top level is a break/continue boundary (builder-enforced; defensive)
  const flow = evaluateStatementToAccum(stmt, scope, accum);
  if (flow) throw loopFlowBoundaryError(flow);
}

/**
 * Evaluate a TextBlockExpression — captures text elements into a TextBlockValue
 */
function evaluateTextBlockExpression(expr: TextBlockExpression, scope: Scope): TextBlockValue {
  // Runtime restriction: no nesting text blocks
  if (scope.evalState && (scope.evalState as EvaluationState & { _insideTextBlock?: boolean })._insideTextBlock) {
    throw new Error(formatError('Cannot nest text blocks', getLine(expr)));
  }

  // Create a child scope for the block body
  const blockScope = createScope(scope);

  // Create an isolated evaluation state for the block
  const blockEvalState: EvaluationState & { _insideTextBlock: boolean; _ctxSource?: EvaluationState } = {
    ...sharedProgramState(scope.evalState),
    pathContext: scope.evalState?.pathContext ?? createPathContext({}),
    layers: new Map(), // Empty — layer definitions not allowed
    layerOrder: [],
    activeLayerName: null,
    defaultLayerName: null,
    transformState: createTransformState(),
    _insideTextBlock: true,
    // `ctx` here is the creating state's: the same pen, and ITS transform
    // (this state's transformState is a fresh, empty one).
    _ctxSource: scope.evalState,
  };
  blockScope.evalState = blockEvalState;

  // Accumulate TextBlockElements from text statements
  const elements: TextBlockElement[] = [];
  const blockStyles: Record<string, string> = {};

  // Text-block-expression top level is a break/continue boundary
  // (builder-enforced; defensive)
  const bodyFlow = evaluateTextBlockBody(expr.body, blockScope, elements);
  if (bodyFlow) throw loopFlowBoundaryError(bodyFlow);

  return {
    type: 'TextBlockValue',
    elements,
    styles: blockStyles,
  };
}

/**
 * Recursively evaluate statements inside a text block, accumulating TextBlockElements.
 * Handles text statements directly and recurses into for/if control flow.
 * Returns a LoopFlow signal when a break/continue executes so nested loops
 * inside &{ } bodies consume it — same contract as evaluateTextBody.
 */
function evaluateTextBlockBody(stmts: Statement[], scope: Scope, elements: TextBlockElement[]): LoopFlow {
  for (const stmt of stmts) {
    if (stmt.type === 'BreakStatement') {
      return { flow: 'break', line: getLine(stmt) ?? null };
    }
    if (stmt.type === 'ContinueStatement') {
      return { flow: 'continue', line: getLine(stmt) ?? null };
    }
    if (stmt.type === 'LayerDefinition') {
      throw new Error(formatError('Layer definitions are not allowed inside text blocks', getLine(stmt)));
    }
    if (stmt.type === 'LayerApplyBlock') {
      throw new Error(formatError('Layer apply blocks are not allowed inside text blocks', getLine(stmt)));
    }
    if (stmt.type === 'PathCommand') {
      const builtinCall = asStatementBuiltinCall(stmt);
      if (builtinCall) {
        evaluateStatementBuiltin(builtinCall, scope);
        continue;
      }
      throw new Error(formatError('Path commands are not allowed inside text blocks', getLine(stmt)));
    }
    if (stmt.type === 'ViewBoxDefinition') {
      throw new Error(formatError('ViewBox definitions are not allowed inside text blocks', getLine(stmt)));
    }

    if (stmt.type === 'TextStatement') {
      const x = requireNumber(evaluateExpression(stmt.x, scope), 'text() x');
      const y = requireNumber(evaluateExpression(stmt.y, scope), 'text() y');
      if (stmt.rotation) {
        const rotMessage = describeMissingAngleUnit('text', 'text() rotation', stmt.rotation);
        if (rotMessage) throw new Error(formatError(rotMessage, getLine(stmt), getCol(stmt)));
      }
      const rotation = stmt.rotation
        ? requireNumber(evaluateExpression(stmt.rotation, scope), 'text() rotation')
        : undefined;
      let textStyles: Record<string, string> | undefined;
      if (stmt.styles) {
        const sv = evaluateExpression(stmt.styles, scope);
        if (!isStyleBlock(sv)) throw new Error(formatError('text() styles must be a style block', getLine(stmt)));
        textStyles = sv.properties;
      }
      if (stmt.content) {
        const text = evaluateTemplateLiteral(stmt.content, scope);
        elements.push({ x, y, rotation, styles: textStyles, children: [{ type: 'run', text }] });
      } else if (stmt.body) {
        const children: TextChild[] = [];
        // Nested text-statement body top level is a boundary (defensive)
        const nestedFlow = evaluateTextBody(stmt.body, scope, children);
        if (nestedFlow) throw loopFlowBoundaryError(nestedFlow);
        elements.push({ x, y, rotation, styles: textStyles, children });
      }
      continue;
    }

    if (stmt.type === 'ForLoop') {
      // Same bounds rules, limit and messages as the statement loop — this
      // site once kept a hardcoded 10,000 after MAX_ITERATIONS went to 32,000.
      const { start, plan: range } = resolveRange(
        stmt.start,
        stmt.end,
        stmt.inclusive !== false,
        scope,
        'loop',
        getLine(stmt),
      );
      for (let i = start; range.continues(i); i += range.step) {
        const loopScope = createScope(scope);
        loopScope.evalState = scope.evalState;
        setVariable(loopScope, stmt.variable, i);
        const flow = evaluateTextBlockBody(stmt.body, loopScope, elements);
        if (flow?.flow === 'break') break;
      }
      continue;
    }

    if (stmt.type === 'ForEachLoop') {
      const iterVal = evaluateExpression(stmt.iterable, scope);
      if (!isArrayValue(iterVal)) throw new Error('for-each requires an array');
      lockArray(iterVal);
      try {
        for (let idx = 0; idx < iterVal.elements.length; idx++) {
          const loopScope = createScope(scope);
          loopScope.evalState = scope.evalState;
          setVariable(loopScope, stmt.variable, iterVal.elements[idx]);
          if (stmt.indexVariable) setVariable(loopScope, stmt.indexVariable, idx);
          const flow = evaluateTextBlockBody(stmt.body, loopScope, elements);
          if (flow?.flow === 'break') break;
        }
      } finally {
        unlockArray(iterVal);
      }
      continue;
    }

    if (stmt.type === 'IfStatement') {
      const cond = evaluateExpression(stmt.condition, scope);
      // Propagate loop flow out of the taken branch to the enclosing loop
      if (isTruthy(cond)) {
        const flow = evaluateTextBlockBody(stmt.consequent, scope, elements);
        if (flow) return flow;
      } else if (stmt.alternate) {
        const flow = evaluateTextBlockBody(stmt.alternate, scope, elements);
        if (flow) return flow;
      }
      continue;
    }

    if (stmt.type === 'SwitchStatement') {
      const selected = selectSwitchBody(stmt, scope);
      if (selected) {
        const flow = evaluateTextBlockBody(selected.body, selected.scope, elements);
        if (flow) return flow;
      }
      continue;
    }

    // Every other statement — let, assignments, enums, functions, expressions,
    // return — runs exactly as it does at the top level: `x = …` updates the
    // existing binding (or errors when undeclared) instead of creating a
    // block-local copy, `let r = blk.draw()` binds the drawn path's result,
    // and nothing is skipped silently. Text blocks hold no path data, so any
    // path a right-hand side draws goes to a throwaway store.
    const flow = evaluateStatementToAccum(stmt, scope, createPathStore());
    if (flow) return flow;
  }
  return undefined;
}

function evaluateIndexExpression(expr: IndexExpression, scope: Scope): Value {
  const obj = evaluateExpression(expr.object, scope);
  const index = evaluateExpression(expr.index, scope);
  const line = getLine(expr);
  const col = getCol(expr);
  const iError = (message: string): Error => new Error(formatError(message, line, col));

  if (isObjectValue(obj)) {
    if (typeof index !== 'string') {
      throw iError('Object key must be a string');
    }
    return obj.properties.get(index) ?? null;
  }

  if (typeof obj === 'string') {
    if (typeof index !== 'number') {
      throw iError('String index must be a number');
    }
    if (!Number.isInteger(index) || index < 0 || index >= obj.length) {
      throw iError(`String index ${index} out of bounds (length ${obj.length})`);
    }
    return obj[index];
  }

  if (!isArrayValue(obj)) {
    throw iError('Index access requires an array, object, or string');
  }
  if (typeof index !== 'number') {
    throw iError('Array index must be a number');
  }
  if (!Number.isInteger(index) || index < 0 || index >= obj.elements.length) {
    throw iError(`Array index ${index} out of bounds (length ${obj.elements.length})`);
  }
  return obj.elements[index];
}

function evaluateMethodCall(expr: MethodCallExpression, scope: Scope, workerExpr?: Expression): Value {
  const obj = evaluateExpression(expr.object, scope);
  const mLine = getLine(expr);
  const mCol = getCol(expr);
  function mError(message: string): Error {
    return new Error(formatError(message, mLine, mCol));
  }

  // One angle-unit check for every receiver method: the per-case bodies each
  // read their own arguments, so this is the only place that sees them all.
  const receiver = receiverTypeName(obj);
  if (receiver) {
    const key = `${receiver}.${expr.method}`;
    for (const message of describeMissingAngleUnits(key, `${key}()`, expr.args)) {
      throw mError(message);
    }
  }

  // TransformReference methods (ctx.transform.reset())
  if (typeof obj === 'object' && obj !== null && 'type' in obj && obj.type === 'TransformReference') {
    const transformRef = obj;
    if (expr.method === 'reset') {
      if (expr.args.length !== 0) throw mError('transform.reset() expects 0 arguments');
      transformRef.state.translate = null;
      transformRef.state.rotate = null;
      transformRef.state.scale = null;
      return 0;
    }
    throw mError(`Unknown transform method: ${expr.method}`);
  }

  // TransformPropertyReference methods (ctx.transform.translate.set(), .reset())
  if (typeof obj === 'object' && obj !== null && 'type' in obj && obj.type === 'TransformPropertyReference') {
    const propRef = obj;

    if (expr.method === 'reset') {
      if (expr.args.length !== 0) throw mError(`transform.${propRef.property}.reset() expects 0 arguments`);
      propRef.state[propRef.property] = null;
      return 0;
    }

    if (expr.method === 'set') {
      const args = expr.args.map((a) => {
        const v = toNumber(evaluateExpression(a, scope));
        if (v === undefined) throw mError(`transform.${propRef.property}.set() arguments must be numbers`);
        return v;
      });

      switch (propRef.property) {
        case 'translate':
          if (args.length !== 2) throw mError('translate.set() expects 2 arguments (x, y)');
          propRef.state.translate = { x: args[0], y: args[1] };
          return 0;
        case 'rotate':
          if (args.length !== 1 && args.length !== 3) {
            throw mError('rotate.set() expects 1 or 3 arguments (angle) or (angle, cx, cy)');
          }
          // Checked after the arity test — a wrong argument count is the more
          // fundamental error. A TransformPropertyReference is not tagged
          // `<Name>Value`, so the generic receiver check cannot key it.
          for (const message of describeMissingAngleUnits(
            'transform.rotate.set',
            'transform.rotate.set()',
            expr.args,
          )) {
            throw mError(message);
          }
          propRef.state.rotate =
            args.length === 1 ? { angle: args[0] } : { angle: args[0], cx: args[1], cy: args[2] };
          return 0;
        case 'scale':
          if (args.length === 2) {
            propRef.state.scale = { x: args[0], y: args[1] };
          } else if (args.length === 4) {
            propRef.state.scale = { x: args[0], y: args[1], cx: args[2], cy: args[3] };
          } else {
            throw mError('scale.set() expects 2 or 4 arguments (sx, sy) or (sx, sy, cx, cy)');
          }
          return 0;
      }
    }

    throw mError(`Unknown transform.${propRef.property} method: ${expr.method}`);
  }

  // LayerReference methods: .append() for GroupLayer
  if (isLayerReference(obj)) {
    if (
      expr.method === 'segment' ||
      expr.method === 'segmentAll' ||
      expr.method === 'point' ||
      expr.method === 'pointAll' ||
      expr.method === 'vertex' ||
      expr.method === 'vertexAll' ||
      expr.method === 'query' ||
      expr.method === 'queryAll'
    ) {
      if (obj.layer.layerType !== 'PathLayer') {
        throw mError(`.${expr.method}() is only available on PathLayer references`);
      }
      const isQuery = expr.method === 'query' || expr.method === 'queryAll';
      if (expr.args.length !== 1) {
        throw mError(`${expr.method}() expects 1 argument (${isQuery ? 'selector' : 'name'})`);
      }
      const qName = evaluateExpression(expr.args[0], scope);
      if (typeof qName !== 'string') {
        throw mError(`${expr.method}() ${isQuery ? 'selector' : 'name'} must be a string`);
      }
      if (
        expr.method === 'point' ||
        expr.method === 'pointAll' ||
        expr.method === 'vertex' ||
        expr.method === 'vertexAll'
      ) {
        try { rejectPseudoOnNonSegmentQuery(expr.method, qName); } catch (e) { throw mError((e as Error).message); }
      }
      const layerState = obj.layer as PathLayerState;
      const authoredFlat = layerState.accum.records.flatMap((r) => r.commands);
      // Snapshot the finalized geometry on demand (non-destructive; the layer's
      // authored records keep their annotations for emit-time finalization).
      const fin = applyRecordedCornerOps(authoredFlat);
      const cmds = fin.commands;
      const layerSource: QuerySource = { kind: 'layer', value: obj, commands: cmds };

      if (expr.method === 'query' || expr.method === 'queryAll') {
        return runQueryMethod(layerSource, expr.method, qName, mError);
      }
      if (expr.method === 'segment' || expr.method === 'segmentAll') {
        const buildLayerSegment = (run: PathBlockCommand[]): ProjectedPathValue =>
          wrapCommands(run, 'layer') as ProjectedPathValue;
        const q = runSegmentQuery(cmds, qName, mError);
        if (expr.method === 'segmentAll') {
          return { type: 'ArrayValue' as const, elements: q.runs.map(buildLayerSegment) };
        }
        if (q.runs.length === 0) {
          if (q.labelMatched) throw mError(pseudoRangeError(qName, q.matchedCount, q.parsed.pseudo));
          throw mError(queryLabelError('segment', q.parsed.label, cmds));
        }
        return buildLayerSegment(q.runs[0]);
      }
      if (expr.method === 'point' || expr.method === 'pointAll') {
        const matches = findEndpointCommands(authoredFlat, qName);
        if (expr.method === 'pointAll') {
          return {
            type: 'ArrayValue' as const,
            elements: matches.map((c) => ({ type: 'PointValue' as const, x: c.end.x, y: c.end.y })),
          };
        }
        if (matches.length === 0) throw mError(queryLabelError('endpoint', qName, authoredFlat));
        return { type: 'PointValue' as const, x: matches[0].end.x, y: matches[0].end.y };
      }
      const targets = findEndpointCommands(cmds, qName);
      // Same pairing guard as the PathBlock site: never zip diverging lists.
      const authoredMatches = findEndpointCommands(authoredFlat, qName);
      const paired = authoredMatches.length === targets.length ? authoredMatches : null;
      const buildLayerHandle = (target: PathBlockCommand, i: number): EndpointValue =>
        endpointValue(layerSource, cmds.indexOf(target), {
          label: qName,
          point: { x: (paired ? paired[i] : target).end.x, y: (paired ? paired[i] : target).end.y },
        });
      if (expr.method === 'vertexAll') {
        return { type: 'ArrayValue' as const, elements: targets.map(buildLayerHandle) };
      }
      if (targets.length === 0) throw mError(queryLabelError('endpoint', qName, cmds));
      return buildLayerHandle(targets[0], 0);
    }
    if (expr.method === 'subscribe') {
      if (obj.layer.layerType !== 'PathLayer') {
        throw mError(`.subscribe() is only available on PathLayer references`);
      }
      // A subscription made inside a block would never be dispatched, and its
      // callback would see only the block's own (empty) layer table.
      if (scope.evalState) {
        const flags = scope.evalState as EvaluationState & { _insidePathBlock?: boolean; _insideTextBlock?: boolean };
        if (flags._insidePathBlock || flags._insideTextBlock) {
          const kind = flags._insidePathBlock ? 'path' : 'text';
          throw mError(`Cannot call .subscribe() inside a ${kind} block. Subscribe outside the block.`);
        }
      }
      const cb = resolveCallbackBlock(expr, scope, workerExpr);
      if (!cb) {
        throw mError(
          "subscribe() requires a trailing block or a << worker: layer.subscribe('endpoint') {|match, i, sub| ... }",
        );
      }
      if (cb.leadingArgs.length !== 1 || typeof cb.leadingArgs[0] !== 'string') {
        throw mError('subscribe() expects 1 argument (a selector string) before the block');
      }
      const selector = cb.leadingArgs[0];
      // Validate the selector now so a typo is reported at the subscribe line.
      try {
        parsePathQuery(selector);
      } catch (e) {
        throw mError(e instanceof Error ? e.message : String(e));
      }
      const sub: SubscriptionValue = {
        type: 'SubscriptionValue',
        layerName: obj.layer.name,
        selector,
        // A trailing block closes over the scope where subscribe() was called (a loop
        // iteration's own scope included); a << worker brings its own closure.
        callback: {
          params: cb.params,
          body: cb.body,
          closure: cb.closure ?? scope,
          ...(cb.calledFn ? { calledFn: cb.calledFn, subscribedIn: scope } : {}),
        },
        windowStart: peekRecordSeq(),
        windowEnd: null,
        cancelled: false,
        count: 0,
        fired: new Set<string>(),
        ...(expr.loc ? { loc: expr.loc } : {}),
      };
      if (scope.evalState) (scope.evalState.subscriptions ??= []).push(sub);
      return sub;
    }
    if (expr.method === 'append') {
      if (obj.layer.layerType !== 'GroupLayer') {
        throw mError(`.append() is only available on GroupLayer references`);
      }
      if (expr.args.length === 0) {
        throw mError('.append() requires at least 1 argument');
      }
      const groupLayer = obj.layer;
      if (!scope.evalState) throw mError('.append() requires evaluation context');

      for (const arg of expr.args) {
        const childValue = evaluateExpression(arg, scope);
        if (!isLayerReference(childValue)) {
          throw mError('.append() arguments must be layer references');
        }
        const childName = childValue.layer.name;

        // Self-reference check
        if (childName === groupLayer.name) {
          throw mError(`Cannot append group '${childName}' to itself`);
        }

        // Already a child of this group — no-op
        if (groupLayer.children.includes(childName)) {
          continue;
        }

        // Circular reference check for nested groups
        if (childValue.layer.layerType === 'GroupLayer') {
          const childGroup = childValue.layer;
          const checkCircular = (g: GroupLayerState, depth: number): void => {
            if (depth > 10) throw mError('GroupLayer nesting exceeds maximum depth of 10');
            for (const cn of g.children) {
              if (cn === groupLayer.name) {
                throw mError(
                  `Circular reference: appending '${childName}' to '${groupLayer.name}' would create a cycle`,
                );
              }
              const childLayer = scope.evalState!.layers.get(cn);
              if (childLayer?.layerType === 'GroupLayer') {
                checkCircular(childLayer, depth + 1);
              }
            }
          };
          checkCircular(childGroup, 1);
        }

        // Check nesting depth after append
        const getDepth = (name: string): number => {
          const l = scope.evalState!.layers.get(name);
          if (l?.layerType !== 'GroupLayer') return 0;
          const g = l;
          if (g.children.length === 0) return 0;
          return 1 + Math.max(...g.children.map((c) => getDepth(c)));
        };
        const parentDepth = ((): number => {
          // Walk up from groupLayer to find how deep it is nested
          const findParentDepth = (targetName: string): number => {
            for (const [, layer] of scope.evalState!.layers) {
              if (layer.layerType === 'GroupLayer') {
                const g = layer;
                if (g.children.includes(targetName)) {
                  return 1 + findParentDepth(g.name);
                }
              }
            }
            return 0;
          };
          return findParentDepth(groupLayer.name);
        })();
        const childDepth = getDepth(childName);
        if (parentDepth + 1 + childDepth + 1 > 10) {
          throw mError('GroupLayer nesting exceeds maximum depth of 10');
        }

        // If child is in another group, move it (remove from old group, log warning)
        for (const [, layer] of scope.evalState.layers) {
          if (layer.layerType === 'GroupLayer' && layer !== groupLayer) {
            const otherGroup = layer;
            const idx = otherGroup.children.indexOf(childName);
            if (idx !== -1) {
              otherGroup.children.splice(idx, 1);
              scope.evalState.logs.push({
                line: null,
                parts: [
                  {
                    type: 'string',
                    value: `Layer '${childName}' was moved from group '${otherGroup.name}' to group '${groupLayer.name}'`,
                  },
                ],
              });
            }
          }
        }

        groupLayer.children.push(childName);
        // Remove from top-level layer order (children aren't rendered at top level)
        const orderIdx = scope.evalState.layerOrder.indexOf(childName);
        if (orderIdx !== -1) {
          scope.evalState.layerOrder.splice(orderIdx, 1);
        }
      }
      return 0;
    }
    throw mError(`Unknown method '${expr.method}' on layer reference`);
  }

  // PathBlockValue methods: draw(), project()
  if (isPathBlockValue(obj)) {
    // Check if we're inside a path block — draw/project not allowed there
    if (scope.evalState && (scope.evalState as EvaluationState & { _insidePathBlock?: boolean })._insidePathBlock) {
      throw mError(`Cannot call .${expr.method}() inside a path block`);
    }

    switch (expr.method) {
      case 'draw': {
        if (expr.args.length !== 0) throw mError('draw() expects 0 arguments');
        if (!scope.evalState) throw mError('draw() requires evaluation context');

        // Get the current cursor position as the draw origin
        const activeCtx = scope.evalState.pathContext;
        const originX = activeCtx.position.x;
        const originY = activeCtx.position.y;

        // Emit relative commands from structured command data (not raw pathStrings).
        // Relative commands naturally work from the cursor position, so no projection
        // is needed for the emitted path. We reconstruct from structured commands
        // because stdlib functions like circle() store absolute coordinates in their
        // PathSegment strings, but the structured commands are already relative.
        // Serialize AND track in one walk — no serialize→reparse round-trip.
        const { d: emittedPath, tracked: emittedCommands } = serializeRelativeAndTrack(
          obj.commands,
          scope.evalState.pathContext,
          { bridgeOriginGap: true },
        );

        // Build the ProjectedPathValue with absolute coordinates for programmatic use
        const projectedCommands = projectCommands(obj.commands, originX, originY);
        const projected: ProjectedPathValue = {
          type: 'ProjectedPathValue',
          commands: projectedCommands,
          startPoint: { x: obj.startPoint.x + originX, y: obj.startPoint.y + originY },
          endPoint: { x: obj.endPoint.x + originX, y: obj.endPoint.y + originY },
        };

        // Return as PathWithResult — emits relative path AND returns ProjectedPath
        return {
          type: 'PathWithResult' as const,
          path: emittedPath,
          result: projected,
          commands: emittedCommands,
        };
      }

      case 'drawTo': {
        if (expr.args.length !== 2) throw mError('drawTo() expects 2 arguments (x, y)');
        if (!scope.evalState) throw mError('drawTo() requires evaluation context');
        const dtX = evaluateExpression(expr.args[0], scope);
        const dtY = evaluateExpression(expr.args[1], scope);
        if (typeof dtX !== 'number') throw mError('drawTo() x must be a number');
        if (typeof dtY !== 'number') throw mError('drawTo() y must be a number');

        // Emit M x y followed by relative commands, tracking in the same walk.
        const { d: emittedPath, tracked: emittedCommands } = serializeRelativeAndTrack(
          obj.commands,
          scope.evalState.pathContext,
          { bridgeOriginGap: true, moveTo: { x: dtX, y: dtY } },
        );

        // Build ProjectedPathValue with absolute coordinates
        const projectedCommands = projectCommands(obj.commands, dtX, dtY);
        const projected: ProjectedPathValue = {
          type: 'ProjectedPathValue',
          commands: projectedCommands,
          startPoint: { x: obj.startPoint.x + dtX, y: obj.startPoint.y + dtY },
          endPoint: { x: obj.endPoint.x + dtX, y: obj.endPoint.y + dtY },
        };

        return {
          type: 'PathWithResult' as const,
          path: emittedPath,
          result: projected,
          commands: emittedCommands,
        };
      }

      case 'project': {
        if (expr.args.length !== 2) throw mError('project() expects 2 arguments (x, y)');
        const x = evaluateExpression(expr.args[0], scope);
        const y = evaluateExpression(expr.args[1], scope);
        if (typeof x !== 'number') throw mError('project() x must be a number');
        if (typeof y !== 'number') throw mError('project() y must be a number');

        return {
          type: 'ProjectedPathValue' as const,
          commands: projectCommands(obj.commands, x, y),
          startPoint: { x: obj.startPoint.x + x, y: obj.startPoint.y + y },
          endPoint: { x: obj.endPoint.x + x, y: obj.endPoint.y + y },
        };
      }

      case 'get': {
        if (expr.args.length !== 1) throw mError('get() expects 1 argument (t)');
        const t = evaluateExpression(expr.args[0], scope);
        if (typeof t !== 'number') throw mError('get() argument must be a number');
        if (t < 0 || t > 1) throw mError(`get() argument must be between 0 and 1, got ${t}`);
        const result = samplePathAtFraction(obj.commands, t);
        return { type: 'PointValue' as const, x: result.point.x, y: result.point.y };
      }

      case 'tangent': {
        if (expr.args.length !== 1) throw mError('tangent() expects 1 argument (t)');
        const t = evaluateExpression(expr.args[0], scope);
        if (typeof t !== 'number') throw mError('tangent() argument must be a number');
        if (t < 0 || t > 1) throw mError(`tangent() argument must be between 0 and 1, got ${t}`);
        const result = samplePathAtFraction(obj.commands, t);
        return {
          type: 'ObjectValue' as const,
          properties: new Map<string, Value>([
            ['point', { type: 'PointValue' as const, x: result.point.x, y: result.point.y }],
            ['angle', result.tangent],
          ]),
        };
      }

      case 'normal': {
        if (expr.args.length !== 1) throw mError('normal() expects 1 argument (t)');
        const t = evaluateExpression(expr.args[0], scope);
        if (typeof t !== 'number') throw mError('normal() argument must be a number');
        if (t < 0 || t > 1) throw mError(`normal() argument must be between 0 and 1, got ${t}`);
        const result = samplePathAtFraction(obj.commands, t);
        return {
          type: 'ObjectValue' as const,
          properties: new Map<string, Value>([
            ['point', { type: 'PointValue' as const, x: result.point.x, y: result.point.y }],
            ['angle', wrapToPi(result.tangent - Math.PI / 2)],
          ]),
        };
      }

      case 'partition': {
        if (expr.args.length !== 1) throw mError('partition() expects 1 argument (n)');
        const n = evaluateExpression(expr.args[0], scope);
        if (typeof n !== 'number') throw mError('partition() argument must be a number');
        if (!Number.isInteger(n) || n < 1) throw mError('partition() argument must be a positive integer');
        const points = partitionPath(obj.commands, n);
        return {
          type: 'ArrayValue' as const,
          elements: points.map((p, i) => ({
            type: 'ObjectValue' as const,
            properties: new Map<string, Value>([
              ['point', { type: 'PointValue' as const, x: p.point.x, y: p.point.y }],
              ['angle', p.tangent],
              ['t', i / n],
            ]),
          })),
        };
      }

      case 'query':
      case 'queryAll': {
        if (expr.args.length !== 1) throw mError(`${expr.method}() expects 1 argument (selector)`);
        const selector = evaluateExpression(expr.args[0], scope);
        if (typeof selector !== 'string') throw mError(`${expr.method}() selector must be a string`);
        return runQueryMethod({ kind: 'pathblock', value: obj, commands: obj.commands }, expr.method, selector, mError);
      }

      case 'segment':
      case 'segmentAll': {
        if (expr.args.length !== 1) throw mError(`${expr.method}() expects 1 argument (name)`);
        const segName = evaluateExpression(expr.args[0], scope);
        if (typeof segName !== 'string') throw mError(`${expr.method}() name must be a string`);
        const buildSubBlock = (run: PathBlockCommand[]): PathBlockValue =>
          wrapCommands(run, 'pathblock') as PathBlockValue;
        const q = runSegmentQuery(obj.commands, segName, mError);
        if (expr.method === 'segmentAll') {
          return { type: 'ArrayValue' as const, elements: q.runs.map(buildSubBlock) };
        }
        if (q.runs.length === 0) {
          if (q.labelMatched) throw mError(pseudoRangeError(segName, q.matchedCount, q.parsed.pseudo));
          throw mError(queryLabelError('segment', q.parsed.label, obj.commands));
        }
        return buildSubBlock(q.runs[0]);
      }

      case 'point':
      case 'pointAll': {
        if (expr.args.length !== 1) throw mError(`${expr.method}() expects 1 argument (name)`);
        const ptName = evaluateExpression(expr.args[0], scope);
        if (typeof ptName !== 'string') throw mError(`${expr.method}() name must be a string`);
        try { rejectPseudoOnNonSegmentQuery(expr.method, ptName); } catch (e) { throw mError((e as Error).message); }
        // Prefer the authored store: endpoint labels name the sharp corner the
        // user wrote, even if a corner op later trimmed it.
        const authoredFlat = obj.records.flatMap((r) => r.commands);
        const matches = findEndpointCommands(authoredFlat, ptName);
        const source = matches.length > 0 ? matches : findEndpointCommands(obj.commands, ptName);
        if (expr.method === 'pointAll') {
          return {
            type: 'ArrayValue' as const,
            elements: source.map((c) => ({ type: 'PointValue' as const, x: c.end.x, y: c.end.y })),
          };
        }
        if (source.length === 0) throw mError(queryLabelError('endpoint', ptName, obj.commands));
        return { type: 'PointValue' as const, x: source[0].end.x, y: source[0].end.y };
      }

      case 'vertex':
      case 'vertexAll': {
        if (expr.args.length !== 1) throw mError(`${expr.method}() expects 1 argument (name)`);
        const vName = evaluateExpression(expr.args[0], scope);
        if (typeof vName !== 'string') throw mError(`${expr.method}() name must be a string`);
        try { rejectPseudoOnNonSegmentQuery(expr.method, vName); } catch (e) { throw mError((e as Error).message); }
        const targets = findEndpointCommands(obj.commands, vName);
        // Pair authored positions with finalized targets only when the counts
        // agree — a positional zip across diverging lists would silently hand
        // back the wrong vertex. On mismatch, fall back to finalized geometry
        // for every handle (correct commands, post-trim positions).
        const authoredMatches = findEndpointCommands(obj.records.flatMap((r) => r.commands), vName);
        const paired = authoredMatches.length === targets.length ? authoredMatches : null;
        const blockSource: QuerySource = { kind: 'pathblock', value: obj, commands: obj.commands };
        const buildHandle = (target: PathBlockCommand, i: number): EndpointValue => {
          const authoredCmd = paired ? paired[i] : target;
          return endpointValue(blockSource, obj.commands.indexOf(target), {
            label: vName,
            point: { x: authoredCmd.end.x, y: authoredCmd.end.y },
          });
        };
        if (expr.method === 'vertexAll') {
          return { type: 'ArrayValue' as const, elements: targets.map(buildHandle) };
        }
        if (targets.length === 0) throw mError(queryLabelError('endpoint', vName, obj.commands));
        return buildHandle(targets[0], 0);
      }

      case 'reverse': {
        if (expr.args.length !== 0) throw mError('reverse() expects 0 arguments');
        const reversed = reverseCommands(obj.commands);
        return buildRebasedWithAnchor(reversed);
      }

      case 'boundingBox': {
        if (expr.args.length !== 0) throw mError('boundingBox() expects 0 arguments');
        const bb = computeBoundingBox(obj.commands);
        return {
          type: 'ObjectValue' as const,
          properties: new Map<string, Value>([
            ['x', bb.x],
            ['y', bb.y],
            ['width', bb.width],
            ['height', bb.height],
          ]),
        };
      }

      case 'centerPoint': {
        if (expr.args.length !== 0) throw mError('centerPoint() expects 0 arguments');
        const center = computeBoundingBoxCenter(obj.commands);
        return { type: 'PointValue' as const, x: center.x, y: center.y };
      }

      case 'translateStartPointTo':
      case 'translateCenterPointTo': {
        if (expr.args.length !== 2) throw mError(`${expr.method}() expects 2 arguments (x, y)`);
        const toX = evaluateExpression(expr.args[0], scope);
        const toY = evaluateExpression(expr.args[1], scope);
        if (typeof toX !== 'number') throw mError(`${expr.method}() x must be a number`);
        if (typeof toY !== 'number') throw mError(`${expr.method}() y must be a number`);
        // The point being moved, in the block's own coordinates.
        const from = translationReference(expr.method, obj.commands, obj.startPoint);
        return fromCommandsTranslated(obj.commands, from, { x: toX, y: toY });
      }

      case 'offset': {
        if (expr.args.length < 1 || expr.args.length > 2) throw mError('offset() expects 1-2 arguments (distance, options?)');
        const dist = evaluateExpression(expr.args[0], scope);
        if (typeof dist !== 'number') throw mError('offset() argument must be a number');
        const offsetOpts = expr.args.length === 2 ? parseOffsetJoinOptions(evaluateExpression(expr.args[1], scope), mError) : {};
        const offsetResult = offsetCommands(obj.commands, dist, offsetOpts);
        return fromCommandsRebased(offsetResult);
      }

      case 'variableOffset': {
        const builder = collectVariableOffsetStops(expr, scope, workerExpr, obj, false, mError);
        // PolarVectorValue ({angle, distance}) is structurally a PolarOverride.
        const { commands: voCmds, anchor: voAnchor } = buildSimpleVariableOffset(
          obj.commands,
          toSimpleStops(builder.stops),
          builder.startTangent,
          builder.endTangent,
        );
        if (voCmds.length === 0) {
          return {
            type: 'PathBlockValue' as const,
            commands: [],
            records: [],
            startPoint: { x: 0, y: 0 },
            endPoint: { x: 0, y: 0 },
          };
        }
        const voLast = voCmds[voCmds.length - 1];
        const voBlock = {
          type: 'PathBlockValue' as const,
          commands: voCmds,
          records: recordsFromCommands(voCmds),
          startPoint: firstInkedPointOf(voCmds) ?? { x: 0, y: 0 },
          endPoint: { x: voLast.end.x, y: voLast.end.y },
        };
        // The translation normalizeToOrigin subtracted, in spine coordinates.
        (voBlock as PathBlockValue & { anchor: { x: number; y: number } }).anchor = voAnchor;
        return voBlock;
      }

      case 'compoundVariableOffset': {
        const builder = collectVariableOffsetStops(expr, scope, workerExpr, obj, true, mError);
        // No tangent overrides here: go.startTangent/endTangent throw in compound
        // mode (ribbon ends are shaped by caps), so the builder fields are never set.
        const { commands: cvCmds, anchor: cvAnchor } = buildCompoundVariableOffset(
          obj.commands,
          toCompoundStops(builder.stops),
          { startCap: toCapSpec(builder.startCap), endCap: toCapSpec(builder.endCap) },
        );
        if (cvCmds.length === 0) {
          return {
            type: 'PathBlockValue' as const,
            commands: [],
            records: [],
            startPoint: { x: 0, y: 0 },
            endPoint: { x: 0, y: 0 },
          };
        }
        const cvLast = cvCmds[cvCmds.length - 1];
        const cvBlock = {
          type: 'PathBlockValue' as const,
          commands: cvCmds,
          records: recordsFromCommands(cvCmds),
          startPoint: firstInkedPointOf(cvCmds) ?? { x: 0, y: 0 },
          endPoint: { x: cvLast.end.x, y: cvLast.end.y },
        };
        // The translation normalizeToOrigin subtracted, in spine coordinates.
        (cvBlock as PathBlockValue & { anchor: { x: number; y: number } }).anchor = cvAnchor;
        return cvBlock;
      }

      case 'mirror': {
        if (expr.args.length !== 1) throw mError('mirror() expects 1 argument (angle)');
        const mAngle = toNumber(evaluateExpression(expr.args[0], scope));
        if (mAngle === undefined) throw mError('mirror() argument must be a number');
        const mirrored = mirrorCommands(obj.commands, mAngle, { x: 0, y: 0 });
        return fromCommandsRebased(mirrored);
      }

      case 'rotate': {
        if (expr.args.length < 1 || expr.args.length > 2) throw mError('rotate() expects 1-2 arguments (angle) or (angle, origin)');
        const rotAngle = toNumber(evaluateExpression(expr.args[0], scope));
        if (rotAngle === undefined) throw mError('rotate() angle must be a number');
        let rotPivot = { x: 0, y: 0 };
        if (expr.args.length === 2) {
          const rotOrigin = evaluateExpression(expr.args[1], scope);
          if (!isPointValue(rotOrigin)) throw mError('rotate() origin must be a Point');
          rotPivot = { x: rotOrigin.x, y: rotOrigin.y };
        }
        // Frame-preserving: rotate about the pivot in block-local coordinates
        // and keep the geometry where it lands (no re-base) — a cut piece
        // rotated in place keeps its placement inside the subject.
        const rotCmds = rotateAboutPointCommands(obj.commands, rotAngle, rotPivot);
        return fromCommandsKeepingFrame(rotCmds);
      }

      case 'rotateAtVertexIndex': {
        if (expr.args.length !== 2) throw mError('rotateAtVertexIndex() expects 2 arguments (index, angle)');
        const rIdx = evaluateExpression(expr.args[0], scope);
        const rAngle = toNumber(evaluateExpression(expr.args[1], scope));
        if (typeof rIdx !== 'number') throw mError('rotateAtVertexIndex() index must be a number');
        if (rAngle === undefined) throw mError('rotateAtVertexIndex() angle must be a number');
        if (!Number.isInteger(rIdx)) throw mError('rotateAtVertexIndex() index must be an integer');
        const rotated = rotateAtVertexCommands(obj.commands, rIdx, rAngle);
        // Deliberately re-based (origin omitted): the result is normalized to its
        // own first point. Consequence, audited 2026-09-24: because rotations of a
        // rigid shape about different pivots are congruent and differ only by
        // translation, re-basing makes the returned value IDENTICAL for every
        // index — the pivot is observable only through where the caller places the
        // result. website/blog/samples/post40/shattered-glyph.pathogen depends on
        // this and adds the pivot back by hand. Do not "fix" it to keep the frame
        // without migrating that sample. See project-docs/placement-audit/ D6.
        return fromCommandsRebased(rotated);
      }

      case 'scale': {
        if (expr.args.length !== 2) throw mError('scale() expects 2 arguments (sx, sy)');
        const sSx = evaluateExpression(expr.args[0], scope);
        const sSy = evaluateExpression(expr.args[1], scope);
        if (typeof sSx !== 'number') throw mError('scale() sx must be a number');
        if (typeof sSy !== 'number') throw mError('scale() sy must be a number');
        const scaled = scaleCommands(obj.commands, sSx, sSy, { x: 0, y: 0 });
        if (scaled.length === 0) {
          return {
            type: 'PathBlockValue' as const,
            commands: [],
            records: [],
            startPoint: { x: 0, y: 0 },
            endPoint: { x: 0, y: 0 },
          };
        }
        const sLast = scaled[scaled.length - 1];
        return {
          type: 'PathBlockValue' as const,
          commands: scaled,
          records: recordsFromCommands(scaled),
          startPoint: firstInkedPointOf(scaled) ?? { x: 0, y: 0 },
          endPoint: { x: sLast.end.x, y: sLast.end.y },
        };
      }

      case 'subPath': {
        if (expr.args.length !== 2) throw mError('subPath() expects 2 arguments (startT, endT)');
        const spStart = evaluateExpression(expr.args[0], scope);
        const spEnd = evaluateExpression(expr.args[1], scope);
        if (typeof spStart !== 'number') throw mError('subPath() startT must be a number');
        if (typeof spEnd !== 'number') throw mError('subPath() endT must be a number');
        if (spStart < 0 || spStart > 1) throw mError('subPath() startT must be between 0 and 1');
        if (spEnd < 0 || spEnd > 1) throw mError('subPath() endT must be between 0 and 1');
        const subResult = subPathCommands(obj.commands, spStart, spEnd);
        return buildRebasedWithAnchor(subResult);
      }

      case 'dash': {
        if (expr.args.length !== 1) throw mError('dash() expects 1 argument (styles)');
        const dashStylesVal = evaluateExpression(expr.args[0], scope);
        if (!isStyleBlock(dashStylesVal)) throw mError('dash() argument must be a style block');
        let dashPieces;
        try {
          // `%` resolves against exactly the number `.length` reports (ISSUE-029): the
          // combined length of all subpaths, measured by the same function.
          const parsed = parseDashStyles(dashStylesVal.properties, calculatePathLength(obj.commands));
          dashPieces = dashCommands(obj.commands, parsed.dashes, parsed.offset, parsed.mergeSeam);
        } catch (e) {
          throw mError((e as Error).message);
        }
        return {
          type: 'ArrayValue' as const,
          elements: dashPieces.map((p) => ({
            type: 'ObjectValue' as const,
            properties: new Map<string, Value>([
              // Origin (0,0) keeps each piece's subject-local placement, so
              // drawing every piece at one position reassembles the source.
              ['path', fromCommandsKeepingFrame(p.commands)],
              ['kind', p.kind],
              ['t0', p.t0],
              ['t1', p.t1],
            ]),
          })),
        };
      }

      case 'outline': {
        if (expr.args.length !== 1) throw mError('outline() expects 1 argument (styles)');
        const outlineStylesVal = evaluateExpression(expr.args[0], scope);
        if (!isStyleBlock(outlineStylesVal)) throw mError('outline() argument must be a style block');
        let outlined;
        try {
          outlined = outlineCommands(obj.commands, parseOutlineStyles(outlineStylesVal.properties));
        } catch (e) {
          throw mError((e as Error).message);
        }
        return fromCommandsKeepingFrame(outlined);
      }

      case 'startAt': {
        if (expr.args.length !== 1) throw mError('startAt() expects 1 argument (t)');
        const startAtT = evaluateExpression(expr.args[0], scope);
        if (typeof startAtT !== 'number') throw mError('startAt() argument must be a number (arc-length fraction)');
        let rotatedCmds;
        try {
          rotatedCmds = rotateStartCommands(obj.commands, startAtT);
        } catch (e) {
          throw mError((e as Error).message);
        }
        return fromCommandsKeepingFrame(rotatedCmds);
      }

      case 'chamfer': {
        if (expr.args.length < 1 || expr.args.length > 2) throw mError('chamfer() expects 1-2 arguments (distance) or (d1, d2)');
        const cd1 = evaluateExpression(expr.args[0], scope);
        if (typeof cd1 !== 'number') throw mError('chamfer() distance must be a number');
        let cd2 = cd1;
        if (expr.args.length === 2) {
          cd2 = evaluateExpression(expr.args[1], scope) as number;
          if (typeof cd2 !== 'number') throw mError('chamfer() d2 must be a number');
        }
        const chamResult = chamferCommands(obj.commands, cd1, cd2, null);
        for (const w of chamResult.warnings) {
          warn(scope.evalState, 'corner-op', w, { line: mLine, column: mCol });
        }
        return fromCommandsKeepingFrame(chamResult.commands);
      }

      case 'chamferAtVertex': {
        if (expr.args.length < 2 || expr.args.length > 3) throw mError('chamferAtVertex() expects 2-3 arguments (index, distance) or (index, d1, d2)');
        const cvIdx = evaluateExpression(expr.args[0], scope);
        if (typeof cvIdx !== 'number' || !Number.isInteger(cvIdx)) throw mError('chamferAtVertex() index must be an integer');
        const cvD1 = evaluateExpression(expr.args[1], scope);
        if (typeof cvD1 !== 'number') throw mError('chamferAtVertex() distance must be a number');
        let cvD2 = cvD1;
        if (expr.args.length === 3) {
          cvD2 = evaluateExpression(expr.args[2], scope) as number;
          if (typeof cvD2 !== 'number') throw mError('chamferAtVertex() d2 must be a number');
        }
        const cvResult = chamferCommands(obj.commands, cvD1, cvD2, [cvIdx]);
        for (const w of cvResult.warnings) {
          warn(scope.evalState, 'corner-op', w, { line: mLine, column: mCol });
        }
        return fromCommandsKeepingFrame(cvResult.commands);
      }

      case 'fillet': {
        if (expr.args.length !== 1) throw mError('fillet() expects 1 argument (radius)');
        const fRadius = evaluateExpression(expr.args[0], scope);
        if (typeof fRadius !== 'number') throw mError('fillet() radius must be a number');
        const fResult = filletCommands(obj.commands, fRadius, null);
        for (const w of fResult.warnings) {
          warn(scope.evalState, 'corner-op', w, { line: mLine, column: mCol });
        }
        return fromCommandsKeepingFrame(fResult.commands);
      }

      case 'filletAtVertex': {
        if (expr.args.length !== 2) throw mError('filletAtVertex() expects 2 arguments (index, radius)');
        const fvIdx = evaluateExpression(expr.args[0], scope);
        if (typeof fvIdx !== 'number' || !Number.isInteger(fvIdx)) throw mError('filletAtVertex() index must be an integer');
        const fvRadius = evaluateExpression(expr.args[1], scope);
        if (typeof fvRadius !== 'number') throw mError('filletAtVertex() radius must be a number');
        const fvResult = filletCommands(obj.commands, fvRadius, [fvIdx]);
        for (const w of fvResult.warnings) {
          warn(scope.evalState, 'corner-op', w, { line: mLine, column: mCol });
        }
        return fromCommandsKeepingFrame(fvResult.commands);
      }

      case 'ellipticalFillet': {
        if (expr.args.length < 2 || expr.args.length > 3) throw mError('ellipticalFillet() expects 2-3 arguments (rx, ry) or (rx, ry, rotation)');
        const efRx = evaluateExpression(expr.args[0], scope);
        const efRy = evaluateExpression(expr.args[1], scope);
        if (typeof efRx !== 'number') throw mError('ellipticalFillet() rx must be a number');
        if (typeof efRy !== 'number') throw mError('ellipticalFillet() ry must be a number');
        let efRot = 0;
        if (expr.args.length === 3) {
          efRot = toNumber(evaluateExpression(expr.args[2], scope)) as number;
          if (typeof efRot !== 'number') throw mError('ellipticalFillet() rotation must be a number');
        }
        const efResult = ellipticalFilletCommands(obj.commands, efRx, efRy, efRot, null);
        for (const w of efResult.warnings) {
          warn(scope.evalState, 'corner-op', w, { line: mLine, column: mCol });
        }
        return fromCommandsKeepingFrame(efResult.commands);
      }

      case 'ellipticalFilletAtVertex': {
        if (expr.args.length < 3 || expr.args.length > 4) throw mError('ellipticalFilletAtVertex() expects 3-4 arguments (index, rx, ry) or (index, rx, ry, rotation)');
        const efvIdx = evaluateExpression(expr.args[0], scope);
        if (typeof efvIdx !== 'number' || !Number.isInteger(efvIdx)) throw mError('ellipticalFilletAtVertex() index must be an integer');
        const efvRx = evaluateExpression(expr.args[1], scope);
        const efvRy = evaluateExpression(expr.args[2], scope);
        if (typeof efvRx !== 'number') throw mError('ellipticalFilletAtVertex() rx must be a number');
        if (typeof efvRy !== 'number') throw mError('ellipticalFilletAtVertex() ry must be a number');
        let efvRot = 0;
        if (expr.args.length === 4) {
          efvRot = toNumber(evaluateExpression(expr.args[3], scope)) as number;
          if (typeof efvRot !== 'number') throw mError('ellipticalFilletAtVertex() rotation must be a number');
        }
        const efvResult = ellipticalFilletCommands(obj.commands, efvRx, efvRy, efvRot, [efvIdx]);
        for (const w of efvResult.warnings) {
          warn(scope.evalState, 'corner-op', w, { line: mLine, column: mCol });
        }
        return fromCommandsKeepingFrame(efvResult.commands);
      }

      case 'union':
      case 'difference':
      case 'intersection':
      case 'xor': {
        if (expr.args.length !== 1) throw mError(`${expr.method}() expects 1 argument (other path)`);
        const otherVal = evaluateExpression(expr.args[0], scope);
        let otherCmds: PathBlockCommand[];
        if (isPathBlockValue(otherVal)) {
          otherCmds = otherVal.commands;
        } else if (isProjectedPathValue(otherVal)) {
          otherCmds = otherVal.commands;
        } else {
          throw mError(`${expr.method}() argument must be a PathBlock or ProjectedPath`);
        }
        // Project PathBlockValue commands to absolute (origin 0,0)
        const aCmds = obj.commands;
        const bCmds = otherCmds;
        let resultCmds: PathBlockCommand[];
        switch (expr.method) {
          case 'union': resultCmds = pathUnion(aCmds, bCmds); break;
          case 'difference': resultCmds = pathDifference(aCmds, bCmds); break;
          case 'intersection': resultCmds = pathIntersection(aCmds, bCmds); break;
          case 'xor': resultCmds = pathXor(aCmds, bCmds); break;
          default: resultCmds = [];
        }
        return fromCommandsKeepingFrame(resultCmds);
      }

      case 'cut': {
        if (expr.args.length !== 1) throw mError('cut() expects 1 argument (cutter path or array of cutters)');
        const cutterVal = evaluateExpression(expr.args[0], scope);
        const cutterCmds: PathBlockCommand[] = resolveCutterCommands(cutterVal, mError);
        const cutWarnings: string[] = [];
        const pieceCmds = pathCut(obj.commands, cutterCmds, cutWarnings);
        for (const w of cutWarnings) {
          warn(scope.evalState, 'cut', w, { line: mLine, column: mCol });
        }
        return {
          type: 'ArrayValue' as const,
          // Origin (0,0) keeps each piece's subject-local placement, so
          // drawing every piece at one position reassembles the shape.
          elements: pieceCmds.map(p => fromCommandsKeepingFrame(p)),
        };
      }

      case 'intersects': {
        if (expr.args.length !== 1) throw mError('intersects() expects 1 argument');
        const otherVal = evaluateExpression(expr.args[0], scope);
        const myBB = computeBoundingBox(obj.commands);
        if (isPathBlockValue(otherVal) || isProjectedPathValue(otherVal)) {
          const otherBB = computeBoundingBox(otherVal.commands);
          return boolVal(bboxOverlaps(myBB, otherBB));
        }
        if (isProjectedTextValue(otherVal)) {
          const otherBB = estimateTextBoundingBox(otherVal.elements, otherVal.styles, scope.evalState?.fontRegistry);
          return boolVal(bboxOverlaps(myBB, otherBB));
        }
        if (isObjectValue(otherVal)) {
          const ox = otherVal.properties.get('x');
          const oy = otherVal.properties.get('y');
          const ow = otherVal.properties.get('width');
          const oh = otherVal.properties.get('height');
          if (typeof ox === 'number' && typeof oy === 'number' && typeof ow === 'number' && typeof oh === 'number') {
            return boolVal(bboxOverlaps(myBB, { x: ox, y: oy, width: ow, height: oh }));
          }
        }
        throw mError('intersects() argument must be a PathBlock, ProjectedPath, ProjectedText, or {x, y, width, height} object');
      }

      case 'intersectionPoints': {
        if (expr.args.length !== 1) throw mError('intersectionPoints() expects 1 argument');
        const otherVal = evaluateExpression(expr.args[0], scope);
        const myBB = computeBoundingBox(obj.commands);
        if (isPathBlockValue(otherVal) || isProjectedPathValue(otherVal)) {
          const pathSegs = otherVal.commands.map((c) => ({ start: c.start, end: c.end }));
          const pts = bboxPathIntersectionPoints(myBB, pathSegs);
          return {
            type: 'ArrayValue' as const,
            elements: pts.map((p) => ({ type: 'PointValue' as const, x: p.x, y: p.y })),
          };
        }
        if (isProjectedTextValue(otherVal)) {
          const otherBB = estimateTextBoundingBox(otherVal.elements, otherVal.styles, scope.evalState?.fontRegistry);
          if (!bboxOverlaps(myBB, otherBB)) {
            return { type: 'ArrayValue' as const, elements: [] };
          }
          const overlapX = Math.max(myBB.x, otherBB.x);
          const overlapY = Math.max(myBB.y, otherBB.y);
          const overlapX2 = Math.min(myBB.x + myBB.width, otherBB.x + otherBB.width);
          const overlapY2 = Math.min(myBB.y + myBB.height, otherBB.y + otherBB.height);
          return {
            type: 'ArrayValue' as const,
            elements: [
              { type: 'PointValue' as const, x: overlapX, y: overlapY },
              { type: 'PointValue' as const, x: overlapX2, y: overlapY },
              { type: 'PointValue' as const, x: overlapX2, y: overlapY2 },
              { type: 'PointValue' as const, x: overlapX, y: overlapY2 },
            ],
          };
        }
        throw mError('intersectionPoints() argument must be a PathBlock, ProjectedPath, or ProjectedText');
      }

      default:
        throw mError(`Unknown PathBlock method: ${expr.method}`);
    }
  }

  // ProjectedPathValue methods: draw(), drawTo(), get(), tangent(), normal(), partition()
  if (isProjectedPathValue(obj)) {
    // Check if we're inside a path block — draw/drawTo not allowed there
    if (scope.evalState && (scope.evalState as EvaluationState & { _insidePathBlock?: boolean })._insidePathBlock) {
      if (expr.method === 'drawTo' || expr.method === 'draw') {
        throw mError(`Cannot call .${expr.method}() inside a path block`);
      }
    }

    switch (expr.method) {
      case 'draw': {
        // A projected path knows where it lives: draw it exactly there,
        // anchored on its FIRST COMMAND (immune to the frame-origin vs
        // first-command distinction that makes drawTo(startPoint) a
        // footgun on cut pieces).
        if (expr.args.length !== 0) throw mError('draw() expects 0 arguments');
        if (!scope.evalState) throw mError('draw() requires evaluation context');
        const anchor = obj.commands.length > 0 ? obj.commands[0].start : obj.startPoint;
        const { d: emittedPath, tracked: emittedCommands } = serializeRelativeAndTrack(
          obj.commands,
          scope.evalState.pathContext,
          // startCursor: commands are world-space — seat the walk's cursor
          // at the anchor so mid-list `m` subpaths (boolean results, holed
          // pieces) compute correct deltas.
          { moveTo: { x: anchor.x, y: anchor.y }, startCursor: { x: anchor.x, y: anchor.y } },
        );
        return {
          type: 'PathWithResult' as const,
          path: emittedPath,
          result: obj,
          commands: emittedCommands,
        };
      }

      case 'drawTo': {
        if (expr.args.length !== 2) throw mError('drawTo() expects 2 arguments (x, y)');
        if (!scope.evalState) throw mError('drawTo() requires evaluation context');
        const dtX = evaluateExpression(expr.args[0], scope);
        const dtY = evaluateExpression(expr.args[1], scope);
        if (typeof dtX !== 'number') throw mError('drawTo() x must be a number');
        if (typeof dtY !== 'number') throw mError('drawTo() y must be a number');

        // Re-project commands from PPV origin to new drawTo origin.
        // projectCommands rather than an inline map: the hand-rolled copy here
        // omitted the `meta` spread, so this — the only way to translate a
        // ProjectedPath, since there is no `translate` member — silently dropped
        // every segment and endpoint label.
        const offsetX = dtX - obj.startPoint.x;
        const offsetY = dtY - obj.startPoint.y;
        const reProjectedCommands = projectCommands(obj.commands, offsetX, offsetY);

        // Emit M x y followed by relative commands, tracking in the same walk.
        // reProjectedCommands are world-space; the walk's deltas are unaffected.
        const { d: emittedPath, tracked: emittedCommands } = serializeRelativeAndTrack(
          reProjectedCommands,
          scope.evalState.pathContext,
          // startCursor: reprojected commands are world-space — see draw().
          { moveTo: { x: dtX, y: dtY }, startCursor: { x: dtX, y: dtY } },
        );

        // Build ProjectedPathValue with absolute coordinates
        const projected: ProjectedPathValue = {
          type: 'ProjectedPathValue',
          commands: reProjectedCommands,
          startPoint: { x: dtX, y: dtY },
          endPoint: { x: obj.endPoint.x + offsetX, y: obj.endPoint.y + offsetY },
        };

        return {
          type: 'PathWithResult' as const,
          path: emittedPath,
          result: projected,
          commands: emittedCommands,
        };
      }

      case 'get': {
        if (expr.args.length !== 1) throw mError('get() expects 1 argument (t)');
        const t = evaluateExpression(expr.args[0], scope);
        if (typeof t !== 'number') throw mError('get() argument must be a number');
        if (t < 0 || t > 1) throw mError(`get() argument must be between 0 and 1, got ${t}`);
        const result = samplePathAtFraction(obj.commands, t);
        return { type: 'PointValue' as const, x: result.point.x, y: result.point.y };
      }

      case 'tangent': {
        if (expr.args.length !== 1) throw mError('tangent() expects 1 argument (t)');
        const t = evaluateExpression(expr.args[0], scope);
        if (typeof t !== 'number') throw mError('tangent() argument must be a number');
        if (t < 0 || t > 1) throw mError(`tangent() argument must be between 0 and 1, got ${t}`);
        const result = samplePathAtFraction(obj.commands, t);
        return {
          type: 'ObjectValue' as const,
          properties: new Map<string, Value>([
            ['point', { type: 'PointValue' as const, x: result.point.x, y: result.point.y }],
            ['angle', result.tangent],
          ]),
        };
      }

      case 'normal': {
        if (expr.args.length !== 1) throw mError('normal() expects 1 argument (t)');
        const t = evaluateExpression(expr.args[0], scope);
        if (typeof t !== 'number') throw mError('normal() argument must be a number');
        if (t < 0 || t > 1) throw mError(`normal() argument must be between 0 and 1, got ${t}`);
        const result = samplePathAtFraction(obj.commands, t);
        return {
          type: 'ObjectValue' as const,
          properties: new Map<string, Value>([
            ['point', { type: 'PointValue' as const, x: result.point.x, y: result.point.y }],
            ['angle', wrapToPi(result.tangent - Math.PI / 2)],
          ]),
        };
      }

      case 'partition': {
        if (expr.args.length !== 1) throw mError('partition() expects 1 argument (n)');
        const n = evaluateExpression(expr.args[0], scope);
        if (typeof n !== 'number') throw mError('partition() argument must be a number');
        if (!Number.isInteger(n) || n < 1) throw mError('partition() argument must be a positive integer');
        const points = partitionPath(obj.commands, n);
        return {
          type: 'ArrayValue' as const,
          elements: points.map((p, i) => ({
            type: 'ObjectValue' as const,
            properties: new Map<string, Value>([
              ['point', { type: 'PointValue' as const, x: p.point.x, y: p.point.y }],
              ['angle', p.tangent],
              ['t', i / n],
            ]),
          })),
        };
      }

      case 'query':
      case 'queryAll': {
        if (expr.args.length !== 1) throw mError(`${expr.method}() expects 1 argument (selector)`);
        const selector = evaluateExpression(expr.args[0], scope);
        if (typeof selector !== 'string') throw mError(`${expr.method}() selector must be a string`);
        return runQueryMethod({ kind: 'projected', value: obj, commands: obj.commands }, expr.method, selector, mError);
      }

      case 'segment':
      case 'segmentAll': {
        if (expr.args.length !== 1) throw mError(`${expr.method}() expects 1 argument (name)`);
        const segName = evaluateExpression(expr.args[0], scope);
        if (typeof segName !== 'string') throw mError(`${expr.method}() name must be a string`);
        const buildSubProjected = (run: PathBlockCommand[]): ProjectedPathValue =>
          wrapCommands(run, 'projected') as ProjectedPathValue;
        const q = runSegmentQuery(obj.commands, segName, mError);
        if (expr.method === 'segmentAll') {
          return { type: 'ArrayValue' as const, elements: q.runs.map(buildSubProjected) };
        }
        if (q.runs.length === 0) {
          if (q.labelMatched) throw mError(pseudoRangeError(segName, q.matchedCount, q.parsed.pseudo));
          throw mError(queryLabelError('segment', q.parsed.label, obj.commands));
        }
        return buildSubProjected(q.runs[0]);
      }

      case 'point':
      case 'pointAll': {
        if (expr.args.length !== 1) throw mError(`${expr.method}() expects 1 argument (name)`);
        const ptName = evaluateExpression(expr.args[0], scope);
        if (typeof ptName !== 'string') throw mError(`${expr.method}() name must be a string`);
        try { rejectPseudoOnNonSegmentQuery(expr.method, ptName); } catch (e) { throw mError((e as Error).message); }
        const matches = findEndpointCommands(obj.commands, ptName);
        if (expr.method === 'pointAll') {
          return {
            type: 'ArrayValue' as const,
            elements: matches.map((c) => ({ type: 'PointValue' as const, x: c.end.x, y: c.end.y })),
          };
        }
        if (matches.length === 0) throw mError(queryLabelError('endpoint', ptName, obj.commands));
        return { type: 'PointValue' as const, x: matches[0].end.x, y: matches[0].end.y };
      }

      case 'vertex':
      case 'vertexAll': {
        if (expr.args.length !== 1) throw mError(`${expr.method}() expects 1 argument (name)`);
        const vName = evaluateExpression(expr.args[0], scope);
        if (typeof vName !== 'string') throw mError(`${expr.method}() name must be a string`);
        try { rejectPseudoOnNonSegmentQuery(expr.method, vName); } catch (e) { throw mError((e as Error).message); }
        const targets = findEndpointCommands(obj.commands, vName);
        const projectedSource: QuerySource = { kind: 'projected', value: obj, commands: obj.commands };
        const buildProjectedHandle = (target: PathBlockCommand): EndpointValue =>
          endpointValue(projectedSource, obj.commands.indexOf(target), { label: vName });
        if (expr.method === 'vertexAll') {
          return { type: 'ArrayValue' as const, elements: targets.map(buildProjectedHandle) };
        }
        if (targets.length === 0) throw mError(queryLabelError('endpoint', vName, obj.commands));
        return buildProjectedHandle(targets[0]);
      }

      case 'reverse': {
        if (expr.args.length !== 0) throw mError('reverse() expects 0 arguments');
        const reversed = reverseCommands(obj.commands);
        // ProjectedPathValue: starts at original endPoint, keeps absolute coords
        const projStartPoint = obj.endPoint;
        const projEndPoint = reversed.length > 0 ? reversed[reversed.length - 1].end : obj.startPoint;
        return {
          type: 'ProjectedPathValue' as const,
          commands: reversed,
          startPoint: { x: projStartPoint.x, y: projStartPoint.y },
          endPoint: { x: projEndPoint.x, y: projEndPoint.y },
        };
      }

      case 'boundingBox': {
        if (expr.args.length !== 0) throw mError('boundingBox() expects 0 arguments');
        const bb = computeBoundingBox(obj.commands);
        return {
          type: 'ObjectValue' as const,
          properties: new Map<string, Value>([
            ['x', bb.x],
            ['y', bb.y],
            ['width', bb.width],
            ['height', bb.height],
          ]),
        };
      }

      case 'centerPoint': {
        if (expr.args.length !== 0) throw mError('centerPoint() expects 0 arguments');
        const center = computeBoundingBoxCenter(obj.commands);
        return { type: 'PointValue' as const, x: center.x, y: center.y };
      }

      case 'translateStartPointTo':
      case 'translateCenterPointTo': {
        if (expr.args.length !== 2) throw mError(`${expr.method}() expects 2 arguments (x, y)`);
        const toX = evaluateExpression(expr.args[0], scope);
        const toY = evaluateExpression(expr.args[1], scope);
        if (typeof toX !== 'number') throw mError(`${expr.method}() x must be a number`);
        if (typeof toY !== 'number') throw mError(`${expr.method}() y must be a number`);
        // Page coordinates in, page coordinates out: every command shifts by the
        // same amount. `anchor` is where the receiver started, so drawTo(anchor) —
        // which seats a ProjectedPath's first drawn point — puts it back.
        const inked = withoutLeadingMoves(obj.commands);
        if (inked.length === 0) {
          // Nothing drawn: an empty path at the destination, with no shift to record —
          // the same answer the PathBlock receiver gives.
          return {
            type: 'ProjectedPathValue' as const,
            commands: [],
            startPoint: { x: toX, y: toY },
            endPoint: { x: toX, y: toY },
          };
        }
        const from = translationReference(expr.method, obj.commands, obj.startPoint);
        const moved = buildProjectedPathFromCommands(projectCommands(inked, toX - from.x, toY - from.y), obj);
        (moved as ProjectedPathValue & { anchor: { x: number; y: number } }).anchor = { ...obj.startPoint };
        return moved;
      }

      case 'offset': {
        if (expr.args.length < 1 || expr.args.length > 2) throw mError('offset() expects 1-2 arguments (distance, options?)');
        const dist = evaluateExpression(expr.args[0], scope);
        if (typeof dist !== 'number') throw mError('offset() argument must be a number');
        const offsetOpts = expr.args.length === 2 ? parseOffsetJoinOptions(evaluateExpression(expr.args[1], scope), mError) : {};
        const offsetResult = offsetCommands(obj.commands, dist, offsetOpts);
        const oStart = firstInkedPointOf(offsetResult) ?? obj.startPoint;
        const oEnd = offsetResult.length > 0 ? offsetResult[offsetResult.length - 1].end : obj.endPoint;
        return {
          type: 'ProjectedPathValue' as const,
          commands: offsetResult,
          startPoint: { x: oStart.x, y: oStart.y },
          endPoint: { x: oEnd.x, y: oEnd.y },
        };
      }

      // Unlike the PathBlock form, the result is registered on its spine: a
      // ProjectedPath already lives in page coordinates, so `.draw()` lands the
      // curve exactly where the queried or subscribed geometry is. See
      // buildProjectedVariableOffset.
      case 'variableOffset': {
        const builder = collectVariableOffsetStops(expr, scope, workerExpr, obj, false, mError);
        const { commands: voCmds, anchor: voAnchor } = buildSimpleVariableOffset(
          obj.commands,
          toSimpleStops(builder.stops),
          builder.startTangent,
          builder.endTangent,
        );
        return buildProjectedVariableOffset(voCmds, voAnchor, obj);
      }

      case 'compoundVariableOffset': {
        const builder = collectVariableOffsetStops(expr, scope, workerExpr, obj, true, mError);
        const { commands: cvCmds, anchor: cvAnchor } = buildCompoundVariableOffset(
          obj.commands,
          toCompoundStops(builder.stops),
          { startCap: toCapSpec(builder.startCap), endCap: toCapSpec(builder.endCap) },
        );
        return buildProjectedVariableOffset(cvCmds, cvAnchor, obj);
      }

      // The inverse of project()/draw(): the same geometry, re-based to its own
      // first point so it can be placed elsewhere. The position given up is still
      // readable as this value's startPoint.
      case 'toPathBlock': {
        if (expr.args.length !== 0) throw mError('toPathBlock() expects 0 arguments');
        return fromCommandsRebased(obj.commands);
      }

      case 'mirror': {
        if (expr.args.length !== 1) throw mError('mirror() expects 1 argument (angle)');
        const mAngle = toNumber(evaluateExpression(expr.args[0], scope));
        if (mAngle === undefined) throw mError('mirror() argument must be a number');
        const mirrored = mirrorCommands(obj.commands, mAngle, obj.startPoint);
        const mStart = firstInkedPointOf(mirrored) ?? obj.startPoint;
        const mEnd = mirrored.length > 0 ? mirrored[mirrored.length - 1].end : obj.endPoint;
        return {
          type: 'ProjectedPathValue' as const,
          commands: mirrored,
          startPoint: { x: mStart.x, y: mStart.y },
          endPoint: { x: mEnd.x, y: mEnd.y },
        };
      }

      case 'rotate': {
        if (expr.args.length < 1 || expr.args.length > 2) throw mError('rotate() expects 1-2 arguments (angle) or (angle, origin)');
        const rotAngle = toNumber(evaluateExpression(expr.args[0], scope));
        if (rotAngle === undefined) throw mError('rotate() angle must be a number');
        let rotPivot = { x: obj.startPoint.x, y: obj.startPoint.y };
        if (expr.args.length === 2) {
          const rotOrigin = evaluateExpression(expr.args[1], scope);
          if (!isPointValue(rotOrigin)) throw mError('rotate() origin must be a Point');
          rotPivot = { x: rotOrigin.x, y: rotOrigin.y };
        }
        const rotCmds = rotateAboutPointCommands(obj.commands, rotAngle, rotPivot);
        const rotLast = rotCmds[rotCmds.length - 1];
        return {
          type: 'ProjectedPathValue' as const,
          commands: rotCmds,
          startPoint: firstInkedPointOf(rotCmds) ?? { ...obj.startPoint },
          endPoint: rotLast ? { ...rotLast.end } : { ...obj.endPoint },
        };
      }

      case 'rotateAtVertexIndex': {
        if (expr.args.length !== 2) throw mError('rotateAtVertexIndex() expects 2 arguments (index, angle)');
        const rIdx = evaluateExpression(expr.args[0], scope);
        const rAngle = toNumber(evaluateExpression(expr.args[1], scope));
        if (typeof rIdx !== 'number') throw mError('rotateAtVertexIndex() index must be a number');
        if (rAngle === undefined) throw mError('rotateAtVertexIndex() angle must be a number');
        if (!Number.isInteger(rIdx)) throw mError('rotateAtVertexIndex() index must be an integer');
        const rotated = rotateAtVertexCommands(obj.commands, rIdx, rAngle);
        const rStart = firstInkedPointOf(rotated) ?? obj.startPoint;
        const rEnd = rotated.length > 0 ? rotated[rotated.length - 1].end : obj.endPoint;
        return {
          type: 'ProjectedPathValue' as const,
          commands: rotated,
          startPoint: { x: rStart.x, y: rStart.y },
          endPoint: { x: rEnd.x, y: rEnd.y },
        };
      }

      case 'scale': {
        if (expr.args.length !== 2) throw mError('scale() expects 2 arguments (sx, sy)');
        const sSx = evaluateExpression(expr.args[0], scope);
        const sSy = evaluateExpression(expr.args[1], scope);
        if (typeof sSx !== 'number') throw mError('scale() sx must be a number');
        if (typeof sSy !== 'number') throw mError('scale() sy must be a number');
        const scaled = scaleCommands(obj.commands, sSx, sSy, obj.startPoint);
        const sStart = firstInkedPointOf(scaled) ?? obj.startPoint;
        const sEnd = scaled.length > 0 ? scaled[scaled.length - 1].end : obj.endPoint;
        return {
          type: 'ProjectedPathValue' as const,
          commands: scaled,
          startPoint: { x: sStart.x, y: sStart.y },
          endPoint: { x: sEnd.x, y: sEnd.y },
        };
      }

      case 'subPath': {
        if (expr.args.length !== 2) throw mError('subPath() expects 2 arguments (startT, endT)');
        const spStart = evaluateExpression(expr.args[0], scope);
        const spEnd = evaluateExpression(expr.args[1], scope);
        if (typeof spStart !== 'number') throw mError('subPath() startT must be a number');
        if (typeof spEnd !== 'number') throw mError('subPath() endT must be a number');
        if (spStart < 0 || spStart > 1) throw mError('subPath() startT must be between 0 and 1');
        if (spEnd < 0 || spEnd > 1) throw mError('subPath() endT must be between 0 and 1');
        const subResult = subPathCommands(obj.commands, spStart, spEnd);
        // Return PathBlockValue (normalized to 0,0) so result is drawable
        if (subResult.length === 0) {
          return {
            type: 'PathBlockValue' as const,
            commands: [],
            records: [],
            startPoint: { x: 0, y: 0 },
            endPoint: { x: 0, y: 0 },
          };
        }
        // The slice keeps the page coordinates it was cut from. `anchor` rides
        // along equal to startPoint, as it does on a projected variableOffset
        // result, so one worker reads on either receiver.
        const spProjected = buildProjectedPathFromCommands(subResult, obj);
        (spProjected as ProjectedPathValue & { anchor: { x: number; y: number } }).anchor = {
          x: spProjected.startPoint.x,
          y: spProjected.startPoint.y,
        };
        return spProjected;
      }

      case 'dash': {
        if (expr.args.length !== 1) throw mError('dash() expects 1 argument (styles)');
        const dashStylesVal = evaluateExpression(expr.args[0], scope);
        if (!isStyleBlock(dashStylesVal)) throw mError('dash() argument must be a style block');
        let dashPieces;
        try {
          // `%` resolves against exactly the number `.length` reports (ISSUE-029): the
          // combined length of all subpaths, measured by the same function.
          const parsed = parseDashStyles(dashStylesVal.properties, calculatePathLength(obj.commands));
          dashPieces = dashCommands(obj.commands, parsed.dashes, parsed.offset, parsed.mergeSeam);
        } catch (e) {
          throw mError((e as Error).message);
        }
        // Receiver-preserving (the segmentAll pattern): pieces stay projected,
        // in absolute coordinates.
        return {
          type: 'ArrayValue' as const,
          elements: dashPieces.map((p) => ({
            type: 'ObjectValue' as const,
            properties: new Map<string, Value>([
              ['path', buildProjectedPathFromCommands(p.commands, obj)],
              ['kind', p.kind],
              ['t0', p.t0],
              ['t1', p.t1],
            ]),
          })),
        };
      }

      case 'outline': {
        if (expr.args.length !== 1) throw mError('outline() expects 1 argument (styles)');
        const outlineStylesVal = evaluateExpression(expr.args[0], scope);
        if (!isStyleBlock(outlineStylesVal)) throw mError('outline() argument must be a style block');
        let outlined;
        try {
          outlined = outlineCommands(obj.commands, parseOutlineStyles(outlineStylesVal.properties));
        } catch (e) {
          throw mError((e as Error).message);
        }
        return buildProjectedPathFromCommands(outlined, obj);
      }

      case 'startAt': {
        if (expr.args.length !== 1) throw mError('startAt() expects 1 argument (t)');
        const startAtT = evaluateExpression(expr.args[0], scope);
        if (typeof startAtT !== 'number') throw mError('startAt() argument must be a number (arc-length fraction)');
        let rotatedCmds;
        try {
          rotatedCmds = rotateStartCommands(obj.commands, startAtT);
        } catch (e) {
          throw mError((e as Error).message);
        }
        return buildProjectedPathFromCommands(rotatedCmds, obj);
      }

      case 'chamfer': {
        if (expr.args.length < 1 || expr.args.length > 2) throw mError('chamfer() expects 1-2 arguments');
        const cd1 = evaluateExpression(expr.args[0], scope);
        if (typeof cd1 !== 'number') throw mError('chamfer() distance must be a number');
        let cd2 = cd1;
        if (expr.args.length === 2) {
          cd2 = evaluateExpression(expr.args[1], scope) as number;
          if (typeof cd2 !== 'number') throw mError('chamfer() d2 must be a number');
        }
        const chamResult = chamferCommands(obj.commands, cd1, cd2, null);
        for (const w of chamResult.warnings) {
          warn(scope.evalState, 'corner-op', w, { line: mLine, column: mCol });
        }
        return buildProjectedPathFromCommands(chamResult.commands, obj);
      }

      case 'chamferAtVertex': {
        if (expr.args.length < 2 || expr.args.length > 3) throw mError('chamferAtVertex() expects 2-3 arguments');
        const cvIdx = evaluateExpression(expr.args[0], scope);
        if (typeof cvIdx !== 'number' || !Number.isInteger(cvIdx)) throw mError('chamferAtVertex() index must be an integer');
        const cvD1 = evaluateExpression(expr.args[1], scope);
        if (typeof cvD1 !== 'number') throw mError('chamferAtVertex() distance must be a number');
        let cvD2 = cvD1;
        if (expr.args.length === 3) {
          cvD2 = evaluateExpression(expr.args[2], scope) as number;
          if (typeof cvD2 !== 'number') throw mError('chamferAtVertex() d2 must be a number');
        }
        const cvResult = chamferCommands(obj.commands, cvD1, cvD2, [cvIdx]);
        for (const w of cvResult.warnings) {
          warn(scope.evalState, 'corner-op', w, { line: mLine, column: mCol });
        }
        return buildProjectedPathFromCommands(cvResult.commands, obj);
      }

      case 'fillet': {
        if (expr.args.length !== 1) throw mError('fillet() expects 1 argument (radius)');
        const fRadius = evaluateExpression(expr.args[0], scope);
        if (typeof fRadius !== 'number') throw mError('fillet() radius must be a number');
        const fResult = filletCommands(obj.commands, fRadius, null);
        for (const w of fResult.warnings) {
          warn(scope.evalState, 'corner-op', w, { line: mLine, column: mCol });
        }
        return buildProjectedPathFromCommands(fResult.commands, obj);
      }

      case 'filletAtVertex': {
        if (expr.args.length !== 2) throw mError('filletAtVertex() expects 2 arguments (index, radius)');
        const fvIdx = evaluateExpression(expr.args[0], scope);
        if (typeof fvIdx !== 'number' || !Number.isInteger(fvIdx)) throw mError('filletAtVertex() index must be an integer');
        const fvRadius = evaluateExpression(expr.args[1], scope);
        if (typeof fvRadius !== 'number') throw mError('filletAtVertex() radius must be a number');
        const fvResult = filletCommands(obj.commands, fvRadius, [fvIdx]);
        for (const w of fvResult.warnings) {
          warn(scope.evalState, 'corner-op', w, { line: mLine, column: mCol });
        }
        return buildProjectedPathFromCommands(fvResult.commands, obj);
      }

      case 'ellipticalFillet': {
        if (expr.args.length < 2 || expr.args.length > 3) throw mError('ellipticalFillet() expects 2-3 arguments');
        const efRx = evaluateExpression(expr.args[0], scope);
        const efRy = evaluateExpression(expr.args[1], scope);
        if (typeof efRx !== 'number') throw mError('ellipticalFillet() rx must be a number');
        if (typeof efRy !== 'number') throw mError('ellipticalFillet() ry must be a number');
        let efRot = 0;
        if (expr.args.length === 3) {
          efRot = toNumber(evaluateExpression(expr.args[2], scope)) as number;
          if (typeof efRot !== 'number') throw mError('ellipticalFillet() rotation must be a number');
        }
        const efResult = ellipticalFilletCommands(obj.commands, efRx, efRy, efRot, null);
        for (const w of efResult.warnings) {
          warn(scope.evalState, 'corner-op', w, { line: mLine, column: mCol });
        }
        return buildProjectedPathFromCommands(efResult.commands, obj);
      }

      case 'ellipticalFilletAtVertex': {
        if (expr.args.length < 3 || expr.args.length > 4) throw mError('ellipticalFilletAtVertex() expects 3-4 arguments');
        const efvIdx = evaluateExpression(expr.args[0], scope);
        if (typeof efvIdx !== 'number' || !Number.isInteger(efvIdx)) throw mError('ellipticalFilletAtVertex() index must be an integer');
        const efvRx = evaluateExpression(expr.args[1], scope);
        const efvRy = evaluateExpression(expr.args[2], scope);
        if (typeof efvRx !== 'number') throw mError('ellipticalFilletAtVertex() rx must be a number');
        if (typeof efvRy !== 'number') throw mError('ellipticalFilletAtVertex() ry must be a number');
        let efvRot = 0;
        if (expr.args.length === 4) {
          efvRot = toNumber(evaluateExpression(expr.args[3], scope)) as number;
          if (typeof efvRot !== 'number') throw mError('ellipticalFilletAtVertex() rotation must be a number');
        }
        const efvResult = ellipticalFilletCommands(obj.commands, efvRx, efvRy, efvRot, [efvIdx]);
        for (const w of efvResult.warnings) {
          warn(scope.evalState, 'corner-op', w, { line: mLine, column: mCol });
        }
        return buildProjectedPathFromCommands(efvResult.commands, obj);
      }

      case 'union':
      case 'difference':
      case 'intersection':
      case 'xor': {
        if (expr.args.length !== 1) throw mError(`${expr.method}() expects 1 argument (other path)`);
        const otherVal = evaluateExpression(expr.args[0], scope);
        let otherCmds: PathBlockCommand[];
        if (isPathBlockValue(otherVal)) {
          otherCmds = otherVal.commands;
        } else if (isProjectedPathValue(otherVal)) {
          otherCmds = otherVal.commands;
        } else {
          throw mError(`${expr.method}() argument must be a PathBlock or ProjectedPath`);
        }
        const aCmds = obj.commands;
        const bCmds = otherCmds;
        let resultCmds: PathBlockCommand[];
        switch (expr.method) {
          case 'union': resultCmds = pathUnion(aCmds, bCmds); break;
          case 'difference': resultCmds = pathDifference(aCmds, bCmds); break;
          case 'intersection': resultCmds = pathIntersection(aCmds, bCmds); break;
          case 'xor': resultCmds = pathXor(aCmds, bCmds); break;
          default: resultCmds = [];
        }
        // Receiver decides (placement audit P3): the operands are in page
        // coordinates and so is the result, so it comes back projected rather
        // than as a PathBlock that merely holds page numbers.
        return buildProjectedPathFromCommands(resultCmds, obj);
      }

      case 'cut': {
        if (expr.args.length !== 1) throw mError('cut() expects 1 argument (cutter path or array of cutters)');
        const cutterVal = evaluateExpression(expr.args[0], scope);
        const cutterCmds: PathBlockCommand[] = resolveCutterCommands(cutterVal, mError);
        const cutWarnings: string[] = [];
        const pieceCmds = pathCut(obj.commands, cutterCmds, cutWarnings);
        for (const w of cutWarnings) {
          warn(scope.evalState, 'cut', w, { line: mLine, column: mCol });
        }
        return {
          type: 'ArrayValue' as const,
          // Pieces stay projected, each already where it sits in the subject —
          // so `piece.draw()` reassembles the shape with no placement argument.
          elements: pieceCmds.map((piece) => buildProjectedPathFromCommands(piece, obj)),
        };
      }

      case 'intersects': {
        if (expr.args.length !== 1) throw mError('intersects() expects 1 argument');
        const otherVal = evaluateExpression(expr.args[0], scope);
        const myBB = computeBoundingBox(obj.commands);
        if (isPathBlockValue(otherVal) || isProjectedPathValue(otherVal)) {
          const otherBB = computeBoundingBox(otherVal.commands);
          return boolVal(bboxOverlaps(myBB, otherBB));
        }
        if (isProjectedTextValue(otherVal)) {
          const otherBB = estimateTextBoundingBox(otherVal.elements, otherVal.styles, scope.evalState?.fontRegistry);
          return boolVal(bboxOverlaps(myBB, otherBB));
        }
        if (isObjectValue(otherVal)) {
          const ox = otherVal.properties.get('x');
          const oy = otherVal.properties.get('y');
          const ow = otherVal.properties.get('width');
          const oh = otherVal.properties.get('height');
          if (typeof ox === 'number' && typeof oy === 'number' && typeof ow === 'number' && typeof oh === 'number') {
            return boolVal(bboxOverlaps(myBB, { x: ox, y: oy, width: ow, height: oh }));
          }
        }
        throw mError('intersects() argument must be a PathBlock, ProjectedPath, ProjectedText, or {x, y, width, height} object');
      }

      case 'intersectionPoints': {
        if (expr.args.length !== 1) throw mError('intersectionPoints() expects 1 argument');
        const otherVal = evaluateExpression(expr.args[0], scope);
        const myBB = computeBoundingBox(obj.commands);
        if (isPathBlockValue(otherVal) || isProjectedPathValue(otherVal)) {
          const pathSegs = otherVal.commands.map((c) => ({ start: c.start, end: c.end }));
          const pts = bboxPathIntersectionPoints(myBB, pathSegs);
          return {
            type: 'ArrayValue' as const,
            elements: pts.map((p) => ({ type: 'PointValue' as const, x: p.x, y: p.y })),
          };
        }
        if (isProjectedTextValue(otherVal)) {
          const otherBB = estimateTextBoundingBox(otherVal.elements, otherVal.styles, scope.evalState?.fontRegistry);
          if (!bboxOverlaps(myBB, otherBB)) {
            return { type: 'ArrayValue' as const, elements: [] };
          }
          const overlapX = Math.max(myBB.x, otherBB.x);
          const overlapY = Math.max(myBB.y, otherBB.y);
          const overlapX2 = Math.min(myBB.x + myBB.width, otherBB.x + otherBB.width);
          const overlapY2 = Math.min(myBB.y + myBB.height, otherBB.y + otherBB.height);
          return {
            type: 'ArrayValue' as const,
            elements: [
              { type: 'PointValue' as const, x: overlapX, y: overlapY },
              { type: 'PointValue' as const, x: overlapX2, y: overlapY },
              { type: 'PointValue' as const, x: overlapX2, y: overlapY2 },
              { type: 'PointValue' as const, x: overlapX, y: overlapY2 },
            ],
          };
        }
        throw mError('intersectionPoints() argument must be a PathBlock, ProjectedPath, or ProjectedText');
      }

      default:
        throw mError(`Unknown ProjectedPath method: ${expr.method}`);
    }
  }

  // SubscriptionValue methods
  if (isSubscriptionValue(obj)) {
    if (expr.method === 'unsubscribe') {
      if (expr.args.length !== 0) throw mError('unsubscribe() expects 0 arguments');
      if (scope.evalState?.dispatching) {
        // During delivery the window is already fixed: cancel what has not fired.
        obj.cancelled = true;
      } else if (obj.windowEnd === null) {
        obj.windowEnd = peekRecordSeq();
      }
      return obj;
    }
    throw mError(`Unknown method '${expr.method}' on Subscription`);
  }

  // EndpointValue methods — corner ops on a joint (query('endpoint(...)') results and legacy vertex() handles)
  if (isEndpointValue(obj)) {
    const handle = obj;
    switch (expr.method) {
      case 'fillet':
      case 'chamfer':
      case 'ellipticalFillet': {
        if (handle.source.kind !== 'pathblock') {
          const what = handle.source.kind === 'layer' ? 'layer' : 'projected path';
          throw mError(
            `${expr.method}() on a ${what} endpoint is not supported yet — corner ops via endpoints work on PathBlock values`,
          );
        }
        const who =
          handle.label !== null
            ? `endpoint('${handle.label}')`
            : `endpoint at ${formatNum(handle.point.x)}, ${formatNum(handle.point.y)}`;
        if (handle.cornerIndex === -1) {
          throw mError(`${who} is not at a corner (collinear edges) — nothing to ${expr.method}`);
        }
        const arity: Record<string, [number, number]> = { fillet: [1, 1], chamfer: [1, 2], ellipticalFillet: [2, 3] };
        const [min, max] = arity[expr.method];
        if (expr.args.length < min || expr.args.length > max) {
          throw mError(`${expr.method}() expects ${min === max ? min : `${min}-${max}`} argument${max > 1 ? 's' : ''}`);
        }
        const nums = expr.args.map((a, i) => {
          const v = cornerOpArg(evaluateExpression(a, scope), expr.method, i);
          if (v === undefined) throw mError(`${expr.method}() argument ${i + 1} must be a finite number`);
          return v;
        });
        const src = handle.source.value as PathBlockValue;
        let res: { commands: PathBlockCommand[]; warnings: string[] };
        if (expr.method === 'fillet') res = filletCommands(src.commands, nums[0], [handle.cornerIndex]);
        else if (expr.method === 'chamfer') res = chamferCommands(src.commands, nums[0], nums[1] ?? nums[0], [handle.cornerIndex]);
        else res = ellipticalFilletCommands(src.commands, nums[0], nums[1], nums[2] ?? 0, [handle.cornerIndex]);
        for (const w of res.warnings) {
          warn(scope.evalState, 'corner-op', w, { line: mLine, column: mCol });
        }
        return fromCommandsKeepingFrame(res.commands);
      }
      default:
        throw mError(`Unknown method '${expr.method}' on Endpoint`);
    }
  }

  // TextBlockValue methods
  if (isTextBlockValue(obj)) {
    switch (expr.method) {
      case 'boundingBox': {
        if (expr.args.length !== 0) throw mError('boundingBox() expects 0 arguments');
        const bb = estimateTextBoundingBox(obj.elements, obj.styles, scope.evalState?.fontRegistry);
        return {
          type: 'ObjectValue' as const,
          properties: new Map<string, Value>([
            ['x', bb.x], ['y', bb.y], ['width', bb.width], ['height', bb.height],
          ]),
        };
      }
      case 'polarProject': {
        if (expr.args.length !== 5) throw mError('polarProject() expects 5 arguments (px, py, angle, distance, anchor)');
        const ppx = evaluateExpression(expr.args[0], scope);
        const ppy = evaluateExpression(expr.args[1], scope);
        const ppAngle = toNumber(evaluateExpression(expr.args[2], scope));
        const ppDist = evaluateExpression(expr.args[3], scope);
        const ppAnchor = evaluateExpression(expr.args[4], scope);
        if (typeof ppx !== 'number') throw mError('polarProject() px must be a number');
        if (typeof ppy !== 'number') throw mError('polarProject() py must be a number');
        if (ppAngle === undefined) throw mError('polarProject() angle must be a number');
        if (typeof ppDist !== 'number') throw mError('polarProject() distance must be a number');
        if (typeof ppAnchor !== 'string') throw mError('polarProject() anchor must be a BBoxAnchor enum value');
        const targetX = ppx + ppDist * Math.cos(ppAngle);
        const targetY = ppy + ppDist * Math.sin(ppAngle);
        const originBB = estimateTextBoundingBox(obj.elements, obj.styles, scope.evalState?.fontRegistry);
        const anchorOffset = resolveAnchorPoint(originBB, ppAnchor, mError);
        const projOriginX = targetX - anchorOffset.x;
        const projOriginY = targetY - anchorOffset.y;
        return {
          type: 'ProjectedTextValue' as const,
          elements: obj.elements.map((el) => ({ ...el, x: el.x + projOriginX, y: el.y + projOriginY })),
          styles: { ...obj.styles },
          origin: { x: projOriginX, y: projOriginY },
        };
      }
      case 'radialProject': {
        // radialProject(cx, cy, angle, distance, anchor?, autoFlip?)
        // Positions text at polar coordinate, rotates along radial direction,
        // auto-flips on left hemisphere for readability, sets text-anchor.
        if (expr.args.length < 4 || expr.args.length > 7) {
          throw mError('radialProject() expects 4-7 arguments (cx, cy, angle, distance, anchor?, autoFlip?, verticalAlign?)');
        }
        const rpCx = evaluateExpression(expr.args[0], scope);
        const rpCy = evaluateExpression(expr.args[1], scope);
        const rpAngleArg = evaluateExpression(expr.args[2], scope);
        const rpDist = evaluateExpression(expr.args[3], scope);
        if (typeof rpCx !== 'number') throw mError('radialProject() cx must be a number');
        if (typeof rpCy !== 'number') throw mError('radialProject() cy must be a number');
        const rpAngle = rotationRadians(rpAngleArg);
        if (rpAngle === undefined) throw mError('radialProject() angle must be a number');
        if (typeof rpDist !== 'number') throw mError('radialProject() distance must be a number');

        // Optional anchor: 'start' (default) or 'end'
        let rpAnchor = 'start';
        if (expr.args.length >= 5) {
          const anchorVal = evaluateExpression(expr.args[4], scope);
          if (typeof anchorVal === 'string') rpAnchor = anchorVal;
        }

        // Optional autoFlip: default true
        let rpAutoFlip = true;
        if (expr.args.length >= 6) {
          const flipVal = evaluateExpression(expr.args[5], scope);
          if (typeof flipVal === 'number') rpAutoFlip = flipVal !== 0;
          else if (isBooleanValue(flipVal)) rpAutoFlip = flipVal.value !== 0;
        }

        // Target position
        let rpTargetX = rpCx + rpDist * Math.cos(rpAngle);
        let rpTargetY = rpCy + rpDist * Math.sin(rpAngle);

        // Base rotation aligns text along radial direction
        let rpRotation = rpAngle;
        let effectiveAnchor = rpAnchor;

        // Left hemisphere detection and flip
        const isLeftHemi = Math.cos(rpAngle) < -1e-10;
        if (rpAutoFlip && isLeftHemi) {
          rpRotation += Math.PI; // Flip 180° for readability
          // Swap anchor so text still extends away from center
          effectiveAnchor = rpAnchor === 'start' ? 'end' : 'start';
        }

        // Optional verticalAlign: default 'baseline'
        let rpVAlign = 'baseline';
        if (expr.args.length >= 7) {
          const vAlignVal = evaluateExpression(expr.args[6], scope);
          if (typeof vAlignVal === 'string') rpVAlign = vAlignVal;
        }

        // Compute perpendicular offset for vertical font metric alignment
        if (rpVAlign !== 'baseline') {
          const rpFontSize = typeof obj.styles['font-size'] === 'number'
            ? obj.styles['font-size']
            : (typeof obj.styles['font-size'] === 'string' ? parseFloat(obj.styles['font-size'] as string) : 16);

          let vOffset = 0;
          if (rpVAlign === 'midline') vOffset = -rpFontSize * 0.35;
          else if (rpVAlign === 'cap-height') vOffset = -rpFontSize * 0.7;
          else if (rpVAlign === 'descender') vOffset = rpFontSize * 0.2;

          if (vOffset !== 0) {
            const perpAngle = rpRotation - Math.PI / 2;
            rpTargetX += vOffset * Math.cos(perpAngle);
            rpTargetY += vOffset * Math.sin(perpAngle);
          }
        }

        // Text-anchor style
        const rpTextAnchor = effectiveAnchor === 'end' ? 'end' : 'start';

        // Project elements to target position with rotation and text-anchor
        const rpStyles = { ...obj.styles, 'text-anchor': rpTextAnchor };
        return {
          type: 'ProjectedTextValue' as const,
          elements: obj.elements.map((el) => ({
            ...el,
            x: el.x + rpTargetX,
            y: el.y + rpTargetY,
            rotation: rpRotation,
            styles: el.styles ? { ...el.styles, 'text-anchor': rpTextAnchor } : { 'text-anchor': rpTextAnchor },
          })),
          styles: rpStyles,
          origin: { x: rpTargetX, y: rpTargetY },
        };
      }
      case 'project': {
        if (expr.args.length !== 2) throw mError('project() expects 2 arguments (x, y)');
        const px = evaluateExpression(expr.args[0], scope);
        const py = evaluateExpression(expr.args[1], scope);
        if (typeof px !== 'number') throw mError('project() x must be a number');
        if (typeof py !== 'number') throw mError('project() y must be a number');
        return {
          type: 'ProjectedTextValue' as const,
          elements: obj.elements.map((el) => ({ ...el, x: el.x + px, y: el.y + py })),
          styles: { ...obj.styles },
          origin: { x: px, y: py },
        };
      }
      case 'drawTo': {
        if (expr.args.length < 2 || expr.args.length > 3) throw mError('drawTo() expects 2-3 arguments (x, y, rotation?)');
        if (!scope.evalState) throw mError('drawTo() requires evaluation context');
        const dtX = evaluateExpression(expr.args[0], scope);
        const dtY = evaluateExpression(expr.args[1], scope);
        if (typeof dtX !== 'number') throw mError('drawTo() x must be a number');
        if (typeof dtY !== 'number') throw mError('drawTo() y must be a number');
        let dtRotation: number | undefined;
        if (expr.args.length === 3) {
          dtRotation = toNumber(evaluateExpression(expr.args[2], scope)) as number;
          if (typeof dtRotation !== 'number') throw mError('drawTo() rotation must be a number');
        }
        const activeTextLayer = getActiveTextLayer(scope);
        if (!activeTextLayer) {
          throw mError('drawTo() can only be used inside a TextLayer apply block');
        }
        // Emit text elements to the active text layer
        for (const el of obj.elements) {
          const emitted: TextElement = {
            x: el.x + dtX,
            y: el.y + dtY,
            rotation: dtRotation ?? el.rotation,
            styles: el.styles ? { ...obj.styles, ...el.styles } : (Object.keys(obj.styles).length > 0 ? { ...obj.styles } : undefined),
            children: [...el.children],
          };
          activeTextLayer.textElements.push(emitted);
        }
        return {
          type: 'ProjectedTextValue' as const,
          elements: obj.elements.map((el) => ({ ...el, x: el.x + dtX, y: el.y + dtY })),
          styles: { ...obj.styles },
          origin: { x: dtX, y: dtY },
        };
      }
      case 'toPathBlock': {
        if (expr.args.length !== 0) throw mError('toPathBlock() expects 0 arguments');
        const fontRegistry = scope.evalState?.fontRegistry;
        if (!fontRegistry) {
          throw mError('toPathBlock() requires fonts to be loaded — use @font directive or pass fonts in compile options');
        }

        const allCommands: PathBlockCommand[] = [];
        for (const el of obj.elements) {
          const mergedStyles = el.styles ? { ...obj.styles, ...el.styles } : { ...obj.styles };
          let cursorX = el.x;
          let cursorY = el.y;

          for (const child of el.children) {
            let childStyles = mergedStyles;
            let text: string;
            if (child.type === 'tspan') {
              if (child.dx !== undefined) cursorX += child.dx;
              if (child.dy !== undefined) cursorY += child.dy;
              childStyles = child.styles ? { ...mergedStyles, ...child.styles } : mergedStyles;
              text = child.text;
            } else {
              text = child.text;
            }

            const fontFamily = resolveFontFamily(childStyles);
            const fontWeight = resolveFontWeight(childStyles) ?? 400;
            const fontSize = resolveEffectiveFontSize(childStyles);
            const letterSpacing = parseFloat(childStyles['letter-spacing'] ?? '0') || 0;

            if (!fontFamily) {
              throw mError('toPathBlock() requires font-family to be set in styles');
            }
            if (!getFont(fontRegistry, fontFamily, fontWeight)) {
              const available = Array.from(fontRegistry.fonts.keys()).join(', ');
              throw mError(`toPathBlock() font '${fontFamily}' not loaded. Available: ${available || 'none'}`);
            }

            for (const char of text) {
              if (char === ' ' || char === '\t') {
                // Space: advance cursor without generating outline commands.
                // Still goes through lookupGlyph so the advance width comes
                // from a variant that actually maps the character, not from
                // whichever buffer happens to be registered first.
                const { advanceWidth } = lookupGlyph(fontRegistry, fontFamily, fontWeight, 'normal', char, fontSize)!;
                cursorX += advanceWidth + letterSpacing;
                continue;
              }
              const lookup = lookupGlyph(fontRegistry, fontFamily, fontWeight, 'normal', char, fontSize)!;
              const { commands: glyphCmds, advanceWidth } = lookup;
              if (lookup.missing && scope.evalState) {
                recordMissingGlyph(scope.evalState, fontFamily, fontWeight, char);
              }
              // Offset glyph commands by cursor position
              for (const cmd of glyphCmds) {
                allCommands.push({
                  command: cmd.command,
                  args: [...cmd.args],
                  start: { x: cmd.start.x + cursorX, y: cmd.start.y + cursorY },
                  end: { x: cmd.end.x + cursorX, y: cmd.end.y + cursorY },
                });
              }
              cursorX += advanceWidth + letterSpacing;
            }
          }
        }

        return fromCommandsKeepingFrame(allCommands);
      }
      case 'toCodeSnippetBlock': {
        if (expr.args.length < 1 || expr.args.length > 3) throw mError('toCodeSnippetBlock() expects 1-3 arguments (name [, fontSize, padding])');
        if (!scope.evalState) throw mError('toCodeSnippetBlock() requires evaluation context');
        const snippetName = evaluateExpression(expr.args[0], scope);
        if (typeof snippetName !== 'string') throw mError('toCodeSnippetBlock() name must be a string');

        let snippetFontSize = 10;
        if (expr.args.length >= 2) {
          const fsVal = evaluateExpression(expr.args[1], scope);
          if (typeof fsVal !== 'number') throw mError('toCodeSnippetBlock() fontSize must be a number');
          snippetFontSize = fsVal;
        }
        let snippetPadding = 12;
        if (expr.args.length >= 3) {
          const padVal = evaluateExpression(expr.args[2], scope);
          if (typeof padVal !== 'number') throw mError('toCodeSnippetBlock() padding must be a number');
          snippetPadding = padVal;
        }

        // Check for layer name collision
        if (scope.evalState.layers.has(snippetName)) {
          throw mError(`toCodeSnippetBlock() layer name '${snippetName}' already exists`);
        }
        if (scope.evalState.layers.has(`${snippetName}-bg`)) {
          throw mError(`toCodeSnippetBlock() layer name '${snippetName}-bg' already exists`);
        }
        if (scope.evalState.layers.has(`${snippetName}-code`)) {
          throw mError(`toCodeSnippetBlock() layer name '${snippetName}-code' already exists`);
        }

        // Extract all text content from the TextBlock
        const textParts: string[] = [];
        for (const el of obj.elements) {
          for (const child of el.children) {
            textParts.push(child.text);
          }
        }
        const rawCode = textParts.join('');

        // Normalize: dedent, trim blank leading/trailing lines, tabs → 2 spaces
        const normalized = normalizeCodeText(rawCode);
        const codeLines = normalized.split('\n');

        // Tokenize and generate layers
        const { groupLayer } = generateCodeSnippetLayers(
          snippetName, codeLines, snippetFontSize, snippetPadding, scope.evalState,
        );

        return { type: 'LayerReference', layer: groupLayer };
      }
      default:
        throw mError(`Unknown TextBlock method: ${expr.method}`);
    }
  }

  // ProjectedTextValue methods
  if (isProjectedTextValue(obj)) {
    switch (expr.method) {
      case 'boundingBox': {
        if (expr.args.length !== 0) throw mError('boundingBox() expects 0 arguments');
        const bb = estimateTextBoundingBox(obj.elements, obj.styles, scope.evalState?.fontRegistry);
        return {
          type: 'ObjectValue' as const,
          properties: new Map<string, Value>([
            ['x', bb.x], ['y', bb.y], ['width', bb.width], ['height', bb.height],
          ]),
        };
      }
      case 'anchor': {
        if (expr.args.length !== 1) throw mError('anchor() expects 1 argument (BBoxAnchor)');
        const anchorVal = evaluateExpression(expr.args[0], scope);
        if (typeof anchorVal !== 'string') throw mError('anchor() argument must be a BBoxAnchor enum value');
        const bb = estimateTextBoundingBox(obj.elements, obj.styles, scope.evalState?.fontRegistry);
        const pt = resolveAnchorPoint(bb, anchorVal, mError);
        return { type: 'PointValue' as const, x: pt.x, y: pt.y };
      }
      case 'polarProject': {
        if (expr.args.length !== 5) throw mError('polarProject() expects 5 arguments (px, py, angle, distance, anchor)');
        const ppx = evaluateExpression(expr.args[0], scope);
        const ppy = evaluateExpression(expr.args[1], scope);
        const ppAngle = toNumber(evaluateExpression(expr.args[2], scope));
        const ppDist = evaluateExpression(expr.args[3], scope);
        const ppAnchor = evaluateExpression(expr.args[4], scope);
        if (typeof ppx !== 'number') throw mError('polarProject() px must be a number');
        if (typeof ppy !== 'number') throw mError('polarProject() py must be a number');
        if (ppAngle === undefined) throw mError('polarProject() angle must be a number');
        if (typeof ppDist !== 'number') throw mError('polarProject() distance must be a number');
        if (typeof ppAnchor !== 'string') throw mError('polarProject() anchor must be a BBoxAnchor enum value');
        // Compute target point
        const targetX = ppx + ppDist * Math.cos(ppAngle);
        const targetY = ppy + ppDist * Math.sin(ppAngle);
        // The elements are already absolute, so the anchor resolves to an absolute
        // point and (target − anchor) is the DELTA to move by — not a new origin.
        // `origin` is the cumulative translation from block-local (what drawTo
        // subtracts), so the delta is added to the prior origin, as translate does.
        // Storing the delta itself was ISSUE-028 (placement audit D8).
        const absBB = estimateTextBoundingBox(obj.elements, obj.styles, scope.evalState?.fontRegistry);
        const anchorAbs = resolveAnchorPoint(absBB, ppAnchor, mError);
        const shiftX = targetX - anchorAbs.x;
        const shiftY = targetY - anchorAbs.y;
        return {
          type: 'ProjectedTextValue' as const,
          elements: obj.elements.map((el) => ({ ...el, x: el.x + shiftX, y: el.y + shiftY })),
          styles: { ...obj.styles },
          origin: { x: obj.origin.x + shiftX, y: obj.origin.y + shiftY },
        };
      }
      case 'paddedBoundingBox': {
        if (expr.args.length !== 2) throw mError('paddedBoundingBox() expects 2 arguments (blockPad, inlinePad)');
        const blockPad = evaluateExpression(expr.args[0], scope);
        const inlinePad = evaluateExpression(expr.args[1], scope);
        if (typeof blockPad !== 'number') throw mError('paddedBoundingBox() blockPad must be a number');
        if (typeof inlinePad !== 'number') throw mError('paddedBoundingBox() inlinePad must be a number');
        const bb = estimateTextBoundingBox(obj.elements, obj.styles, scope.evalState?.fontRegistry);
        return {
          type: 'ObjectValue' as const,
          properties: new Map<string, Value>([
            ['x', bb.x - inlinePad],
            ['y', bb.y - blockPad],
            ['width', bb.width + 2 * inlinePad],
            ['height', bb.height + 2 * blockPad],
          ]),
        };
      }
      case 'intersects': {
        if (expr.args.length !== 1) throw mError('intersects() expects 1 argument');
        const otherVal = evaluateExpression(expr.args[0], scope);
        const myBB = estimateTextBoundingBox(obj.elements, obj.styles, scope.evalState?.fontRegistry);
        if (isProjectedTextValue(otherVal)) {
          const otherBB = estimateTextBoundingBox(otherVal.elements, otherVal.styles, scope.evalState?.fontRegistry);
          return boolVal(bboxOverlaps(myBB, otherBB));
        }
        if (isObjectValue(otherVal)) {
          const ox = otherVal.properties.get('x');
          const oy = otherVal.properties.get('y');
          const ow = otherVal.properties.get('width');
          const oh = otherVal.properties.get('height');
          if (typeof ox === 'number' && typeof oy === 'number' && typeof ow === 'number' && typeof oh === 'number') {
            return boolVal(bboxOverlaps(myBB, { x: ox, y: oy, width: ow, height: oh }));
          }
        }
        if (isProjectedPathValue(otherVal)) {
          const pathSegs = otherVal.commands.map((c) => ({ start: c.start, end: c.end }));
          return boolVal(bboxPathIntersects(myBB, pathSegs));
        }
        throw mError('intersects() argument must be a ProjectedText, ProjectedPath, or {x, y, width, height} object');
      }
      case 'intersectionPoints': {
        if (expr.args.length !== 1) throw mError('intersectionPoints() expects 1 argument');
        const otherVal = evaluateExpression(expr.args[0], scope);
        const myBB = estimateTextBoundingBox(obj.elements, obj.styles, scope.evalState?.fontRegistry);
        if (isProjectedPathValue(otherVal)) {
          const pathSegs = otherVal.commands.map((c) => ({ start: c.start, end: c.end }));
          const pts = bboxPathIntersectionPoints(myBB, pathSegs);
          return {
            type: 'ArrayValue' as const,
            elements: pts.map((p) => ({ type: 'PointValue' as const, x: p.x, y: p.y })),
          };
        }
        if (isProjectedTextValue(otherVal)) {
          // Two AABBs: intersection perimeter points are the overlap rectangle corners
          const otherBB = estimateTextBoundingBox(otherVal.elements, otherVal.styles, scope.evalState?.fontRegistry);
          if (!bboxOverlaps(myBB, otherBB)) {
            return { type: 'ArrayValue' as const, elements: [] };
          }
          // Return the 4 corners of the overlap rectangle
          const overlapX = Math.max(myBB.x, otherBB.x);
          const overlapY = Math.max(myBB.y, otherBB.y);
          const overlapX2 = Math.min(myBB.x + myBB.width, otherBB.x + otherBB.width);
          const overlapY2 = Math.min(myBB.y + myBB.height, otherBB.y + otherBB.height);
          return {
            type: 'ArrayValue' as const,
            elements: [
              { type: 'PointValue' as const, x: overlapX, y: overlapY },
              { type: 'PointValue' as const, x: overlapX2, y: overlapY },
              { type: 'PointValue' as const, x: overlapX2, y: overlapY2 },
              { type: 'PointValue' as const, x: overlapX, y: overlapY2 },
            ],
          };
        }
        throw mError('intersectionPoints() argument must be a ProjectedText or ProjectedPath');
      }
      case 'draw': {
        if (expr.args.length !== 0) throw mError('draw() expects 0 arguments');
        if (!scope.evalState) throw mError('draw() requires evaluation context');
        const activeTextLayer = getActiveTextLayer(scope);
        if (!activeTextLayer) {
          throw mError('draw() can only be used inside a TextLayer apply block');
        }
        for (const el of obj.elements) {
          const emitted: TextElement = {
            x: el.x,
            y: el.y,
            rotation: el.rotation,
            styles: el.styles ? { ...obj.styles, ...el.styles } : (Object.keys(obj.styles).length > 0 ? { ...obj.styles } : undefined),
            children: [...el.children],
          };
          activeTextLayer.textElements.push(emitted);
        }
        return obj;
      }
      case 'drawTo': {
        if (expr.args.length < 2 || expr.args.length > 3) throw mError('drawTo() expects 2-3 arguments (x, y, rotation?)');
        if (!scope.evalState) throw mError('drawTo() requires evaluation context');
        const dtX = evaluateExpression(expr.args[0], scope);
        const dtY = evaluateExpression(expr.args[1], scope);
        if (typeof dtX !== 'number') throw mError('drawTo() x must be a number');
        if (typeof dtY !== 'number') throw mError('drawTo() y must be a number');
        let dtRotation: number | undefined;
        if (expr.args.length === 3) {
          dtRotation = toNumber(evaluateExpression(expr.args[2], scope)) as number;
          if (typeof dtRotation !== 'number') throw mError('drawTo() rotation must be a number');
        }
        const activeTextLayer = getActiveTextLayer(scope);
        if (!activeTextLayer) {
          throw mError('drawTo() can only be used inside a TextLayer apply block');
        }
        // Re-project: compute offset from current origin to new position
        const offsetX = dtX - obj.origin.x;
        const offsetY = dtY - obj.origin.y;
        const reProjected = obj.elements.map((el) => ({ ...el, x: el.x + offsetX, y: el.y + offsetY }));
        for (const el of reProjected) {
          const emitted: TextElement = {
            x: el.x,
            y: el.y,
            rotation: dtRotation ?? el.rotation,
            styles: el.styles ? { ...obj.styles, ...el.styles } : (Object.keys(obj.styles).length > 0 ? { ...obj.styles } : undefined),
            children: [...el.children],
          };
          activeTextLayer.textElements.push(emitted);
        }
        return {
          type: 'ProjectedTextValue' as const,
          elements: reProjected,
          styles: { ...obj.styles },
          origin: { x: dtX, y: dtY },
        };
      }
      case 'translate': {
        if (expr.args.length !== 2) throw mError('translate() expects 2 arguments (dx, dy)');
        const dx = evaluateExpression(expr.args[0], scope);
        const dy = evaluateExpression(expr.args[1], scope);
        if (typeof dx !== 'number') throw mError('translate() dx must be a number');
        if (typeof dy !== 'number') throw mError('translate() dy must be a number');
        return {
          type: 'ProjectedTextValue' as const,
          elements: obj.elements.map((el) => ({ ...el, x: el.x + dx, y: el.y + dy })),
          styles: { ...obj.styles },
          origin: { x: obj.origin.x + dx, y: obj.origin.y + dy },
        };
      }
      default:
        throw mError(`Unknown ProjectedText method: ${expr.method}`);
    }
  }

  // Point methods
  if (isPointValue(obj)) {
    switch (expr.method) {
      case 'translate': {
        if (expr.args.length !== 2) throw mError('translate() expects 2 arguments');
        const dx = evaluateExpression(expr.args[0], scope);
        const dy = evaluateExpression(expr.args[1], scope);
        if (typeof dx !== 'number') throw mError('translate() dx must be a number');
        if (typeof dy !== 'number') throw mError('translate() dy must be a number');
        return { type: 'PointValue', x: obj.x + dx, y: obj.y + dy };
      }
      case 'polarTranslate': {
        if (expr.args.length !== 2) throw mError('polarTranslate() expects 2 arguments');
        const angle = toNumber(evaluateExpression(expr.args[0], scope));
        const distance = evaluateExpression(expr.args[1], scope);
        if (angle === undefined) throw mError('polarTranslate() angle must be a number');
        if (typeof distance !== 'number') throw mError('polarTranslate() distance must be a number');
        return { type: 'PointValue', x: obj.x + Math.cos(angle) * distance, y: obj.y + Math.sin(angle) * distance };
      }
      case 'midpoint': {
        if (expr.args.length !== 1) throw mError('midpoint() expects 1 argument');
        const other = evaluateExpression(expr.args[0], scope);
        if (!isPointValue(other)) throw mError('midpoint() argument must be a Point');
        return { type: 'PointValue', x: (obj.x + other.x) / 2, y: (obj.y + other.y) / 2 };
      }
      case 'lerp': {
        if (expr.args.length !== 2) throw mError('lerp() expects 2 arguments');
        const other = evaluateExpression(expr.args[0], scope);
        const t = evaluateExpression(expr.args[1], scope);
        if (!isPointValue(other)) throw mError('lerp() first argument must be a Point');
        if (typeof t !== 'number') throw mError('lerp() second argument (t) must be a number');
        return { type: 'PointValue', x: obj.x + (other.x - obj.x) * t, y: obj.y + (other.y - obj.y) * t };
      }
      case 'rotate': {
        if (expr.args.length !== 2) throw mError('rotate() expects 2 arguments');
        const angle = toNumber(evaluateExpression(expr.args[0], scope));
        const origin = evaluateExpression(expr.args[1], scope);
        if (angle === undefined) throw mError('rotate() angle must be a number');
        if (!isPointValue(origin)) throw mError('rotate() origin must be a Point');
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const dx = obj.x - origin.x;
        const dy = obj.y - origin.y;
        return { type: 'PointValue', x: origin.x + dx * cos - dy * sin, y: origin.y + dx * sin + dy * cos };
      }
      case 'distanceTo': {
        if (expr.args.length !== 1) throw mError('distanceTo() expects 1 argument');
        const other = evaluateExpression(expr.args[0], scope);
        if (!isPointValue(other)) throw mError('distanceTo() argument must be a Point');
        const dx = other.x - obj.x;
        const dy = other.y - obj.y;
        return Math.sqrt(dx * dx + dy * dy);
      }
      case 'angleTo': {
        if (expr.args.length !== 1) throw mError('angleTo() expects 1 argument');
        const other = evaluateExpression(expr.args[0], scope);
        if (!isPointValue(other)) throw mError('angleTo() argument must be a Point');
        return Math.atan2(other.y - obj.y, other.x - obj.x);
      }
      case 'offset': {
        if (expr.args.length !== 1) throw mError('offset() expects 1 argument');
        const other = evaluateExpression(expr.args[0], scope);
        if (!isPointValue(other)) throw mError('offset() argument must be a Point');
        return {
          type: 'ObjectValue' as const,
          properties: new Map<string, Value>([
            ['dx', other.x - obj.x],
            ['dy', other.y - obj.y],
          ]),
        };
      }
      default:
        throw mError(`Unknown Point method: ${expr.method}`);
    }
  }

  // Angle methods (.toDeg/.toRad/.toPi/.toTurns — display re-tagging)
  if (isAngleValue(obj)) {
    const retagged = angleMethod(obj, expr.method, expr.args.length, (m): never => {
      throw mError(m);
    });
    if (retagged) return retagged;
    throw mError(`Unknown Angle method: ${expr.method}`);
  }

  // PolarVector methods
  if (isPolarVectorValue(obj)) {
    switch (expr.method) {
      case 'turn': {
        if (expr.args.length !== 1) throw mError('turn() expects 1 argument');
        const delta = toNumber(evaluateExpression(expr.args[0], scope));
        if (delta === undefined) throw mError('turn() argument must be a number');
        return { type: 'PolarVectorValue' as const, angle: obj.angle + delta, distance: obj.distance };
      }
      case 'scale': {
        if (expr.args.length !== 1) throw mError('scale() expects 1 argument');
        const factor = evaluateExpression(expr.args[0], scope);
        if (typeof factor !== 'number') throw mError('scale() argument must be a number');
        if (factor < 0) throw mError('scale() factor must be non-negative — use mirror() to flip direction');
        return { type: 'PolarVectorValue' as const, angle: obj.angle, distance: obj.distance * factor };
      }
      case 'mirror': {
        if (expr.args.length !== 0) throw mError('mirror() expects no arguments');
        return { type: 'PolarVectorValue' as const, angle: obj.angle + Math.PI, distance: obj.distance };
      }
      default:
        throw mError(`Unknown PolarVector method: ${expr.method}`);
    }
  }

  // VariableOffset builder methods (go.stop / go.startTangent / go.endTangent /
  // go.startCap / go.endCap). The methods mutate the builder accumulator in place.
  if (typeof obj === 'object' && obj !== null && 'type' in obj && obj.type === 'VariableOffsetBuilderValue') {
    const b = obj as VariableOffsetBuilderValue;
    const validContinuity = (v: Value): v is string =>
      typeof v === 'string' && Object.values(BUILTIN_ENUMS.CurveContinuity).includes(v);
    switch (expr.method) {
      case 'stop': {
        const time = evaluateExpression(expr.args[0], scope);
        if (typeof time !== 'number') throw mError('go.stop() time must be a number');
        if (time < 0 || time > 1) throw mError(`go.stop() time must be between 0 and 1, got ${time}`);
        // Decreasing times silently flip the outward direction used by end caps —
        // reject them (equal times are tolerated; they add a zero-length segment).
        const prevStop = b.stops[b.stops.length - 1];
        if (prevStop && time < prevStop.time)
          throw mError(
            `go.stop() times must not decrease along the spine (got ${time} after ${prevStop.time})`,
          );
        if (!b.compound) {
          if (expr.args.length !== 3)
            throw mError('go.stop() expects 3 arguments (time, offset, continuity)');
          const offset = evaluateExpression(expr.args[1], scope);
          const cont = evaluateExpression(expr.args[2], scope);
          if (typeof offset !== 'number') throw mError('go.stop() offset must be a number');
          if (!validContinuity(cont)) throw mError('go.stop() continuity must be a CurveContinuity value');
          b.stops.push({ time, offset1: offset, continuity1: cont });
        } else {
          if (expr.args.length !== 5)
            throw mError('go.stop() expects 5 arguments (time, offset1, continuity1, offset2, continuity2)');
          const o1 = evaluateExpression(expr.args[1], scope);
          const c1 = evaluateExpression(expr.args[2], scope);
          const o2 = evaluateExpression(expr.args[3], scope);
          const c2 = evaluateExpression(expr.args[4], scope);
          if (typeof o1 !== 'number' || typeof o2 !== 'number') throw mError('go.stop() offsets must be numbers');
          if (!validContinuity(c1) || !validContinuity(c2))
            throw mError('go.stop() continuity arguments must be CurveContinuity values');
          b.stops.push({ time, offset1: o1, continuity1: c1, offset2: o2, continuity2: c2 });
        }
        return b;
      }
      case 'startTangent':
      case 'endTangent': {
        if (b.compound)
          throw mError(
            `go.${expr.method}() is only available in the simple variableOffset() — compoundVariableOffset() ends are shaped by startCap()/endCap()`,
          );
        if (expr.args.length !== 1) throw mError(`go.${expr.method}() expects 1 argument (PolarVector)`);
        const v = evaluateExpression(expr.args[0], scope);
        if (!isPolarVectorValue(v)) throw mError(`go.${expr.method}() argument must be a PolarVector`);
        if (expr.method === 'startTangent') b.startTangent = v;
        else b.endTangent = v;
        return b;
      }
      case 'startCap':
      case 'endCap': {
        if (!b.compound)
          throw mError(`go.${expr.method}() is only available inside compoundVariableOffset()`);
        if (expr.args.length !== 1) throw mError(`go.${expr.method}() expects 1 argument (Cap)`);
        const v = evaluateExpression(expr.args[0], scope);
        if (!isCapValue(v)) throw mError(`go.${expr.method}() argument must be a Cap (e.g. Cap.round())`);
        if (expr.method === 'startCap') b.startCap = v;
        else b.endCap = v;
        return b;
      }
      default:
        throw mError(`Unknown variableOffset builder method: ${expr.method}`);
    }
  }

  // Cycler methods
  if (isCyclerValue(obj)) {
    switch (expr.method) {
      case 'pick': {
        if (expr.args.length !== 0) throw mError('pick() expects 0 arguments');
        const value = obj.elements[obj.index];
        obj.index = (obj.index + 1) % obj.elements.length;
        return value;
      }
      default:
        throw mError(`Unknown Cycler method: ${expr.method}`);
    }
  }

  // SVGFragmentValue methods
  if (isSVGFragmentValue(obj)) {
    switch (expr.method) {
      case 'insert': {
        if (expr.args.length !== 0) throw mError('insert() expects 0 arguments');
        if (!scope.evalState) throw mError('insert() requires evaluation context');
        // Generate unique fragment layer name
        let counter = 1;
        let name = `__fragment_${counter}`;
        while (scope.evalState.layers.has(name)) {
          counter++;
          name = `__fragment_${counter}`;
        }
        const fragmentLayer: FragmentLayerState = {
          name,
          layerType: 'FragmentLayer',
          isDefault: false as const,
          styles: {},
          defsContent: obj.defsContent,
          visualContent: obj.visualContent,
        };
        scope.evalState.layers.set(name, fragmentLayer);
        scope.evalState.layerOrder.push(name);
        return 0;
      }
      default:
        throw mError(`Unknown SVGDocumentFragment method: ${expr.method}`);
    }
  }

  // MaskValue methods
  if (isMaskValue(obj)) {
    switch (expr.method) {
      case 'append': {
        if (expr.args.length < 1 || expr.args.length > 2)
          throw mError('Mask.append() expects 1-2 arguments (path, styles?)');
        const pathArg = evaluateExpression(expr.args[0], scope);
        let commands: PathBlockCommand[];
        if (isProjectedPathValue(pathArg)) {
          commands = pathArg.commands;
        } else if (isPathBlockValue(pathArg)) {
          commands = projectCommands(pathArg.commands, 0, 0);
        } else {
          throw mError('Mask.append() first argument must be a PathBlock or ProjectedPath');
        }
        const d = defsPathData(commands);
        let styles: Record<string, string> = {};
        if (expr.args.length === 2) {
          const styleArg = evaluateExpression(expr.args[1], scope);
          if (!isStyleBlock(styleArg)) throw mError('Mask.append() second argument must be a style block');
          styles = { ...styleArg.properties };
        }
        obj.paths.push({ d, styles });
        return 0;
      }
      default:
        throw mError(`Unknown Mask method: ${expr.method}`);
    }
  }

  // ClipPathValue methods
  if (isClipPathValue(obj)) {
    switch (expr.method) {
      case 'append': {
        if (expr.args.length !== 1) throw mError('ClipPath.append() expects 1 argument (path)');
        const pathArg = evaluateExpression(expr.args[0], scope);
        let commands: PathBlockCommand[];
        if (isProjectedPathValue(pathArg)) {
          commands = pathArg.commands;
        } else if (isPathBlockValue(pathArg)) {
          commands = projectCommands(pathArg.commands, 0, 0);
        } else {
          throw mError('ClipPath.append() argument must be a PathBlock or ProjectedPath');
        }
        const d = defsPathData(commands);
        obj.paths.push(d);
        return 0;
      }
      default:
        throw mError(`Unknown ClipPath method: ${expr.method}`);
    }
  }

  // PatternValue methods
  if (isPatternValue(obj)) {
    switch (expr.method) {
      case 'append': {
        if (expr.args.length < 1 || expr.args.length > 2)
          throw mError('Pattern.append() expects 1-2 arguments (path, styles?)');
        const pathArg = evaluateExpression(expr.args[0], scope);
        let commands: PathBlockCommand[];
        if (isProjectedPathValue(pathArg)) {
          commands = pathArg.commands;
        } else if (isPathBlockValue(pathArg)) {
          commands = projectCommands(pathArg.commands, 0, 0);
        } else {
          throw mError('Pattern.append() first argument must be a PathBlock or ProjectedPath');
        }
        const d = defsPathData(commands);
        let styles: Record<string, string> = {};
        if (expr.args.length === 2) {
          const styleArg = evaluateExpression(expr.args[1], scope);
          if (!isStyleBlock(styleArg)) throw mError('Pattern.append() second argument must be a style block');
          styles = { ...styleArg.properties };
        }
        obj.paths.push({ d, styles });
        return 0;
      }
      default:
        throw mError(`Unknown Pattern method: ${expr.method}`);
    }
  }

  // MarkerValue methods
  if (isMarkerValue(obj)) {
    switch (expr.method) {
      case 'append': {
        if (expr.args.length < 1 || expr.args.length > 2)
          throw mError('Marker.append() expects 1-2 arguments (path, styles?)');
        const pathArg = evaluateExpression(expr.args[0], scope);
        let commands: PathBlockCommand[];
        if (isProjectedPathValue(pathArg)) {
          commands = pathArg.commands;
        } else if (isPathBlockValue(pathArg)) {
          commands = projectCommands(pathArg.commands, 0, 0);
        } else {
          throw mError('Marker.append() first argument must be a PathBlock or ProjectedPath');
        }
        const d = defsPathData(commands);
        let styles: Record<string, string> = {};
        if (expr.args.length === 2) {
          const styleArg = evaluateExpression(expr.args[1], scope);
          if (!isStyleBlock(styleArg)) throw mError('Marker.append() second argument must be a style block');
          styles = { ...styleArg.properties };
        }
        obj.paths.push({
          d,
          styles,
          // For the marker-space check at the end of the program (V7).
          ...(commands.length > 0 ? { bbox: computeBoundingBox(commands) } : {}),
          projected: isProjectedPathValue(pathArg),
          loc: { line: getLine(expr), column: getCol(expr) },
        });
        return 0;
      }
      default:
        throw mError(`Unknown Marker method: ${expr.method}`);
    }
  }

  // GridValue methods
  if (isGridValue(obj)) {
    switch (expr.method) {
      case 'get': {
        if (expr.args.length !== 2) throw mError('Grid.get() expects 2 arguments (row, col)');
        const r = evaluateExpression(expr.args[0], scope);
        const c = evaluateExpression(expr.args[1], scope);
        if (typeof r !== 'number' || typeof c !== 'number') throw mError('Grid.get() arguments must be numbers');
        if (!Number.isInteger(r) || !Number.isInteger(c)) throw mError('Grid.get() arguments must be integers');
        if (r < 0 || r >= obj.rows || c < 0 || c >= obj.cols) {
          throw mError(`Grid.get(${r}, ${c}) out of bounds for ${obj.rows}×${obj.cols} grid`);
        }
        return obj.cells[r][c];
      }
      case 'set': {
        if (expr.args.length !== 3) throw mError('Grid.set() expects 3 arguments (row, col, value)');
        const r = evaluateExpression(expr.args[0], scope);
        const c = evaluateExpression(expr.args[1], scope);
        const v = evaluateExpression(expr.args[2], scope);
        if (typeof r !== 'number' || typeof c !== 'number') throw mError('Grid.set() row/col must be numbers');
        if (!Number.isInteger(r) || !Number.isInteger(c)) throw mError('Grid.set() row/col must be integers');
        if (r < 0 || r >= obj.rows || c < 0 || c >= obj.cols) {
          throw mError(`Grid.set(${r}, ${c}) out of bounds for ${obj.rows}×${obj.cols} grid`);
        }
        obj.cells[r][c] = v;
        return obj;
      }
      case 'getPoint': {
        if (expr.args.length !== 2) throw mError('Grid.getPoint() expects 2 arguments (row, col)');
        const r = evaluateExpression(expr.args[0], scope);
        const c = evaluateExpression(expr.args[1], scope);
        if (typeof r !== 'number' || typeof c !== 'number') throw mError('Grid.getPoint() arguments must be numbers');
        if (!Number.isInteger(r) || !Number.isInteger(c)) throw mError('Grid.getPoint() arguments must be integers');
        if (r < 0 || r >= obj.rows || c < 0 || c >= obj.cols) {
          throw mError(`Grid.getPoint(${r}, ${c}) out of bounds for ${obj.rows}×${obj.cols} grid`);
        }
        return {
          type: 'PointValue' as const,
          x: obj.origin.x + (c + 0.5) * obj.xDim,
          y: obj.origin.y + (r + 0.5) * obj.yDim,
        };
      }
      case 'getRow': {
        if (expr.args.length !== 1) throw mError('Grid.getRow() expects 1 argument (row)');
        const r = evaluateExpression(expr.args[0], scope);
        if (typeof r !== 'number' || !Number.isInteger(r)) throw mError('Grid.getRow() argument must be an integer');
        if (r < 0 || r >= obj.rows) throw mError(`Grid.getRow(${r}) out of bounds for grid with ${obj.rows} rows`);
        return { type: 'ArrayValue' as const, elements: [...obj.cells[r]] };
      }
      case 'getCol': {
        if (expr.args.length !== 1) throw mError('Grid.getCol() expects 1 argument (col)');
        const c = evaluateExpression(expr.args[0], scope);
        if (typeof c !== 'number' || !Number.isInteger(c)) throw mError('Grid.getCol() argument must be an integer');
        if (c < 0 || c >= obj.cols) throw mError(`Grid.getCol(${c}) out of bounds for grid with ${obj.cols} columns`);
        return { type: 'ArrayValue' as const, elements: obj.cells.map((row) => row[c]) };
      }
      case 'cells': {
        if (expr.args.length !== 0) throw mError('Grid.cells() does not take arguments');
        const flat: Value[] = [];
        for (let r = 0; r < obj.rows; r++) for (let c = 0; c < obj.cols; c++) flat.push(obj.cells[r][c]);
        return { type: 'ArrayValue' as const, elements: flat };
      }
      case 'fill': {
        const cb = resolveCallbackBlock(expr, scope, workerExpr);
        if (!cb) throw mError('Grid.fill() requires a trailing block or a << worker: grid.fill {|row, col, center| return ...; } or grid.fill() << f');
        if (cb.extraArgs !== 0) throw mError('Grid.fill() takes no arguments besides the callback');
        const params = cb.params;
        const body = cb.body;
        const callLine = getLine(expr);
        // Commands emitted inside a fill body are discarded (fill computes cell
        // values). Reuse one throwaway accum rather than allocating per cell.
        const fillSink = createPathStore();
        for (let r = 0; r < obj.rows; r++) {
          for (let c = 0; c < obj.cols; c++) {
            const blockScope = callScope(cb.closure, scope, cb.calledFn);
            if (params.length > 0) setVariable(blockScope, params[0], r);
            if (params.length > 1) setVariable(blockScope, params[1], c);
            if (params.length > 2) {
              const center: PointValue = {
                type: 'PointValue',
                x: obj.origin.x + (c + 0.5) * obj.xDim,
                y: obj.origin.y + (r + 0.5) * obj.yDim,
              };
              setVariable(blockScope, params[2], center);
            }
            try {
              const res = evaluateGridCellBody(body, blockScope, fillSink);
              obj.cells[r][c] = res.returned ? res.value : null;
            } catch (e) {
              if (e instanceof ReturnSignal) {
                obj.cells[r][c] = e.value;
              } else {
                const msg = e instanceof Error ? e.message : String(e);
                throw new Error(formatError(`Error in Grid.fill() callback at (${r}, ${c}): ${msg}`, callLine));
              }
            }
          }
        }
        return obj;
      }
      case 'forEach': {
        const cb = resolveCallbackBlock(expr, scope, workerExpr);
        if (!cb) throw mError('Grid.forEach() requires a trailing block or a << worker: grid.forEach {|cell, row, col| ... } or grid.forEach() << f');
        if (cb.extraArgs !== 0) throw mError('Grid.forEach() takes no arguments besides the callback');
        const params = cb.params;
        const callLine = getLine(expr);
        // Thread the active layer's accum so drawTo/path commands inside the block
        // emit to the surrounding layer.apply { ... }, matching for-loop semantics.
        let blockAccum = createPathStore();
        if (scope.evalState?.activeLayerName) {
          const activeLayer = scope.evalState.layers.get(scope.evalState.activeLayerName);
          if (activeLayer && activeLayer.layerType === 'PathLayer') {
            blockAccum = (activeLayer as PathLayerState).accum;
          }
        }
        for (let r = 0; r < obj.rows; r++) {
          for (let c = 0; c < obj.cols; c++) {
            const blockScope = callScope(cb.closure, scope, cb.calledFn);
            if (params.length > 0) setVariable(blockScope, params[0], obj.cells[r][c]);
            if (params.length > 1) setVariable(blockScope, params[1], r);
            if (params.length > 2) setVariable(blockScope, params[2], c);
            if (params.length > 3) {
              const center: PointValue = {
                type: 'PointValue',
                x: obj.origin.x + (c + 0.5) * obj.xDim,
                y: obj.origin.y + (r + 0.5) * obj.yDim,
              };
              setVariable(blockScope, params[3], center);
            }
            try {
              evaluateGridCellBody(cb.body, blockScope, blockAccum);
            } catch (e) {
              if (e instanceof ReturnSignal) {
                // forEach ignores returns
              } else {
                const msg = e instanceof Error ? e.message : String(e);
                throw new Error(formatError(`Error in Grid.forEach() callback at (${r}, ${c}): ${msg}`, callLine));
              }
            }
          }
        }
        return null;
      }
      case 'map': {
        const cb = resolveCallbackBlock(expr, scope, workerExpr);
        if (!cb) throw mError('Grid.map() requires a trailing block or a << worker: grid.map {|cell, row, col| return ...; } or grid.map() << f');
        if (cb.extraArgs !== 0) throw mError('Grid.map() takes no arguments besides the callback');
        const params = cb.params;
        const callLine = getLine(expr);
        const newCells: Value[][] = [];
        // Commands emitted inside a map body are discarded (map computes cell
        // values). Reuse one throwaway accum rather than allocating per cell.
        const mapSink = createPathStore();
        for (let r = 0; r < obj.rows; r++) {
          const row: Value[] = [];
          for (let c = 0; c < obj.cols; c++) {
            const blockScope = callScope(cb.closure, scope, cb.calledFn);
            if (params.length > 0) setVariable(blockScope, params[0], obj.cells[r][c]);
            if (params.length > 1) setVariable(blockScope, params[1], r);
            if (params.length > 2) setVariable(blockScope, params[2], c);
            if (params.length > 3) {
              const center: PointValue = {
                type: 'PointValue',
                x: obj.origin.x + (c + 0.5) * obj.xDim,
                y: obj.origin.y + (r + 0.5) * obj.yDim,
              };
              setVariable(blockScope, params[3], center);
            }
            let cellResult: Value = null;
            try {
              const res = evaluateGridCellBody(cb.body, blockScope, mapSink);
              if (res.returned) cellResult = res.value;
            } catch (e) {
              if (e instanceof ReturnSignal) {
                cellResult = e.value;
              } else {
                const msg = e instanceof Error ? e.message : String(e);
                throw new Error(formatError(`Error in Grid.map() callback at (${r}, ${c}): ${msg}`, callLine));
              }
            }
            row.push(cellResult);
          }
          newCells.push(row);
        }
        return {
          type: 'GridValue' as const,
          rows: obj.rows,
          cols: obj.cols,
          xDim: obj.xDim,
          yDim: obj.yDim,
          origin: obj.origin,
          outOfBounds: obj.outOfBounds,
          interpolation: obj.interpolation,
          cells: newCells,
        };
      }
      case 'sampleNearest': {
        if (expr.args.length !== 2) throw mError('Grid.sampleNearest() expects 2 arguments (x, y)');
        const x = evaluateExpression(expr.args[0], scope);
        const y = evaluateExpression(expr.args[1], scope);
        if (typeof x !== 'number' || typeof y !== 'number') throw mError('Grid.sampleNearest() arguments must be numbers');
        return gridSampleNearest(obj, x, y);
      }
      case 'sampleBilinear': {
        if (expr.args.length !== 2) throw mError('Grid.sampleBilinear() expects 2 arguments (x, y)');
        const x = evaluateExpression(expr.args[0], scope);
        const y = evaluateExpression(expr.args[1], scope);
        if (typeof x !== 'number' || typeof y !== 'number') throw mError('Grid.sampleBilinear() arguments must be numbers');
        return gridSampleBilinear(obj, x, y, mError);
      }
      case 'sample': {
        if (expr.args.length !== 2) throw mError('Grid.sample() expects 2 arguments (x, y)');
        const x = evaluateExpression(expr.args[0], scope);
        const y = evaluateExpression(expr.args[1], scope);
        if (typeof x !== 'number' || typeof y !== 'number') throw mError('Grid.sample() arguments must be numbers');
        if (obj.interpolation === 'bilinear') return gridSampleBilinear(obj, x, y, mError);
        return gridSampleNearest(obj, x, y);
      }
      default:
        throw mError(`Unknown Grid method: ${expr.method}`);
    }
  }

  // GradientValue methods
  if (isGradientValue(obj)) {
    switch (expr.method) {
      case 'stop': {
        if (expr.args.length !== 2) throw mError('Gradient.stop() expects 2 arguments (offset, color)');
        const offset = evaluateExpression(expr.args[0], scope);
        const color = evaluateExpression(expr.args[1], scope);
        if (typeof offset !== 'number') throw mError('stop() offset must be a number');
        if (!isColorValue(color)) throw mError('stop() color must be a Color value');
        if (color.cssVar) {
          const fallbackCSS = oklchToCSS(color.oklch);
          obj.stops.push({ offset, color: `var(${color.cssVar.varName}, ${fallbackCSS})` });
        } else {
          obj.stops.push({ offset, color: oklchToCSS(color.oklch), oklch: { ...color.oklch } });
        }
        return 0;
      }
      case 'inherit': {
        if (expr.args.length !== 1) throw mError('Gradient.inherit() expects 1 argument (newId)');
        const newId = evaluateExpression(expr.args[0], scope);
        if (typeof newId !== 'string') throw mError('Gradient.inherit() argument must be a string');
        if (!scope.evalState) throw mError('Gradient.inherit() requires evaluation context');
        if (
          scope.evalState.masks.has(newId) ||
          scope.evalState.clipPaths.has(newId) ||
          scope.evalState.gradients.has(newId) ||
          scope.evalState.patterns.has(newId) ||
          scope.evalState.markers.has(newId) ||
          scope.evalState.filters.has(newId)
        ) {
          throw new Error(
            `Duplicate defs ID '${newId}': a Mask, ClipPath, Gradient, Pattern, Marker, or Filter with this ID already exists`,
          );
        }
        const child: GradientValue = {
          type: 'GradientValue',
          gradientType: obj.gradientType,
          id: newId,
          attrs: { ...obj.attrs },
          stops: [],
          href: obj.id,
          interpolation: obj.interpolation,
          steps: obj.steps,
          // Propagate conic fields
          from: obj.from,
          to: obj.to,
          direction: obj.direction,
          spread: obj.spread,
          innerRadius: obj.innerRadius,
          innerFill: obj.innerFill,
        };
        scope.evalState.gradients.set(newId, child);
        return child;
      }
      // Mesh-specific methods
      case 'getPoint': {
        if (obj.gradientType !== 'mesh') throw mError('getPoint() is only available on MeshGradient');
        if (expr.args.length !== 2) throw mError('getPoint() expects 2 arguments (row, col)');
        const row = evaluateExpression(expr.args[0], scope);
        const col = evaluateExpression(expr.args[1], scope);
        if (typeof row !== 'number' || typeof col !== 'number') throw mError('getPoint() arguments must be numbers');
        const grid = obj.meshGrid!;
        if (row < 0 || row >= grid.length || col < 0 || col >= grid[0].length) {
          throw mError(`getPoint(${row}, ${col}) out of bounds for ${grid.length}×${grid[0].length} grid`);
        }
        return grid[row][col];
      }
      case 'getRow': {
        if (obj.gradientType !== 'mesh') throw mError('getRow() is only available on MeshGradient');
        if (expr.args.length !== 1) throw mError('getRow() expects 1 argument (row)');
        const row = evaluateExpression(expr.args[0], scope);
        if (typeof row !== 'number') throw mError('getRow() argument must be a number');
        const grid = obj.meshGrid!;
        if (row < 0 || row >= grid.length) {
          throw mError(`getRow(${row}) out of bounds for grid with ${grid.length} rows`);
        }
        return { type: 'ArrayValue' as const, elements: [...grid[row]] };
      }
      case 'getCol': {
        if (obj.gradientType !== 'mesh') throw mError('getCol() is only available on MeshGradient');
        if (expr.args.length !== 1) throw mError('getCol() expects 1 argument (col)');
        const col = evaluateExpression(expr.args[0], scope);
        if (typeof col !== 'number') throw mError('getCol() argument must be a number');
        const grid = obj.meshGrid!;
        if (col < 0 || col >= grid[0].length) {
          throw mError(`getCol(${col}) out of bounds for grid with ${grid[0].length} columns`);
        }
        return { type: 'ArrayValue' as const, elements: grid.map((r) => r[col]) };
      }
      case 'colorAll': {
        if (obj.gradientType !== 'mesh') throw mError('colorAll() is only available on MeshGradient');
        if (expr.args.length !== 1) throw mError('colorAll() expects 1 argument (color)');
        const color = evaluateExpression(expr.args[0], scope);
        if (!isColorValue(color)) throw mError('colorAll() argument must be a Color value');
        const css = oklchToCSS(color.oklch);
        for (const row of obj.meshGrid!) {
          for (const pt of row) {
            pt.color = { ...color.oklch };
            pt.colorCSS = css;
          }
        }
        return 0;
      }
      // Freeform-specific methods
      case 'point': {
        if (obj.gradientType !== 'freeform') throw mError('point() is only available on FreeformGradient');
        if (expr.args.length !== 3) throw mError('point() expects 3 arguments (x, y, color)');
        const x = evaluateExpression(expr.args[0], scope);
        const y = evaluateExpression(expr.args[1], scope);
        const color = evaluateExpression(expr.args[2], scope);
        if (typeof x !== 'number' || typeof y !== 'number') throw mError('point() x and y must be numbers');
        if (!isColorValue(color)) throw mError('point() third argument must be a Color value');
        obj.freeformPoints!.push({
          x,
          y,
          color: { ...color.oklch },
          colorCSS: oklchToCSS(color.oklch),
        });
        return 0;
      }
      // Topo-specific methods
      case 'contour': {
        if (obj.gradientType !== 'topo') throw mError('.contour() is only available on TopoGradient');
        if (expr.args.length !== 3) throw mError('.contour() expects 3 arguments (path, elevation, color)');
        const pathVal = evaluateExpression(expr.args[0], scope);
        const elevation = evaluateExpression(expr.args[1], scope);
        const color = evaluateExpression(expr.args[2], scope);
        if (!isProjectedPathValue(pathVal))
          throw mError('.contour() first argument must be a ProjectedPathValue (use .project(x, y) on a path block)');
        if (typeof elevation !== 'number') throw mError('.contour() elevation must be a number');
        if (elevation < 0 || elevation > 1) throw mError('.contour() elevation must be between 0 and 1');
        if (!isColorValue(color)) throw mError('.contour() third argument must be a Color value');
        // Validate path is closed
        const cmds = pathVal.commands;
        if (cmds.length === 0 || cmds[cmds.length - 1].command !== 'z')
          throw mError('.contour() path must be closed (end with closePath())');
        const dString = defsPathData(cmds);
        obj.topoContours!.push({
          elevation,
          commands: cmds.map((c) => ({
            command: c.command,
            args: [...c.args],
            start: { ...c.start },
            end: { ...c.end },
          })),
          dString,
          color: { ...color.oklch },
          colorCSS: oklchToCSS(color.oklch),
        });
        return 0;
      }
      default:
        throw mError(`Unknown Gradient method: ${expr.method}`);
    }
  }

  // MeshPointValue methods
  if (isMeshPointValue(obj)) {
    switch (expr.method) {
      case 'translate': {
        if (expr.args.length !== 2) throw mError('translate() expects 2 arguments (dx, dy)');
        const dx = evaluateExpression(expr.args[0], scope);
        const dy = evaluateExpression(expr.args[1], scope);
        if (typeof dx !== 'number' || typeof dy !== 'number') throw mError('translate() arguments must be numbers');
        obj.x += dx;
        obj.y += dy;
        return 0;
      }
      default:
        throw mError(`Unknown MeshPoint method: ${expr.method}`);
    }
  }

  // Color methods
  if (isColorValue(obj)) {
    switch (expr.method) {
      case 'lighten': {
        if (expr.args.length !== 1) throw mError('lighten() expects 1 argument');
        const amount = evaluateExpression(expr.args[0], scope);
        if (typeof amount !== 'number') throw mError('lighten() amount must be a number');
        const src = cssSourceExpr(obj.cssVar, obj.cssExpr);
        return {
          type: 'ColorValue',
          oklch: lighten(obj.oklch, amount),
          cssExpr: src ? lightenCSS(src, amount) : undefined,
        };
      }
      case 'darken': {
        if (expr.args.length !== 1) throw mError('darken() expects 1 argument');
        const amount = evaluateExpression(expr.args[0], scope);
        if (typeof amount !== 'number') throw mError('darken() amount must be a number');
        const src = cssSourceExpr(obj.cssVar, obj.cssExpr);
        return {
          type: 'ColorValue',
          oklch: darken(obj.oklch, amount),
          cssExpr: src ? darkenCSS(src, amount) : undefined,
        };
      }
      case 'saturate': {
        if (expr.args.length !== 1) throw mError('saturate() expects 1 argument');
        const factor = evaluateExpression(expr.args[0], scope);
        if (typeof factor !== 'number') throw mError('saturate() factor must be a number');
        const src = cssSourceExpr(obj.cssVar, obj.cssExpr);
        return {
          type: 'ColorValue',
          oklch: saturate(obj.oklch, factor),
          cssExpr: src ? saturateCSS(src, factor) : undefined,
        };
      }
      case 'desaturate': {
        if (expr.args.length !== 1) throw mError('desaturate() expects 1 argument');
        const factor = evaluateExpression(expr.args[0], scope);
        if (typeof factor !== 'number') throw mError('desaturate() factor must be a number');
        const src = cssSourceExpr(obj.cssVar, obj.cssExpr);
        return {
          type: 'ColorValue',
          oklch: desaturate(obj.oklch, factor),
          cssExpr: src ? desaturateCSS(src, factor) : undefined,
        };
      }
      case 'alpha': {
        if (expr.args.length !== 1) throw mError('alpha() expects 1 argument');
        const a = evaluateExpression(expr.args[0], scope);
        if (typeof a !== 'number') throw mError('alpha() value must be a number');
        const src = cssSourceExpr(obj.cssVar, obj.cssExpr);
        return { type: 'ColorValue', oklch: setAlpha(obj.oklch, a), cssExpr: src ? setAlphaCSS(src, a) : undefined };
      }
      case 'hueShift': {
        if (expr.args.length !== 1) throw mError('hueShift() expects 1 argument');
        const raw = evaluateExpression(expr.args[0], scope);
        const degrees = colorAngleDegrees(raw);
        if (degrees === undefined) throw mError('hueShift() degrees must be a number');
        const src = cssSourceExpr(obj.cssVar, obj.cssExpr);
        return {
          type: 'ColorValue',
          oklch: hueShift(obj.oklch, degrees),
          cssExpr: src ? hueShiftCSS(src, degrees) : undefined,
        };
      }
      case 'complement': {
        if (expr.args.length !== 0) throw mError('complement() expects 0 arguments');
        const src = cssSourceExpr(obj.cssVar, obj.cssExpr);
        return {
          type: 'ColorValue',
          oklch: hueShift(obj.oklch, 180),
          cssExpr: src ? hueShiftCSS(src, 180) : undefined,
        };
      }
      case 'mix': {
        if (expr.args.length !== 2) throw mError('mix() expects 2 arguments');
        const other = evaluateExpression(expr.args[0], scope);
        const t = evaluateExpression(expr.args[1], scope);
        if (!isColorValue(other)) throw mError('mix() first argument must be a Color');
        if (typeof t !== 'number') throw mError('mix() second argument (ratio) must be a number');
        const src = cssSourceExpr(obj.cssVar, obj.cssExpr);
        const otherSrc = cssSourceExpr(other.cssVar, other.cssExpr);
        const cssExpr = src ? mixCSS(src, otherSrc || oklchToCSS(other.oklch), t) : undefined;
        return { type: 'ColorValue', oklch: mixColors(obj.oklch, other.oklch, t), cssExpr };
      }
      case 'flatten': {
        if (expr.args.length > 1) throw mError('flatten() expects 0 or 1 arguments');
        let bg: ColorValue = { type: 'ColorValue', oklch: { L: 1, C: 0, H: 0, alpha: 1 } };
        if (expr.args.length === 1) {
          const arg = evaluateExpression(expr.args[0], scope);
          if (!isColorValue(arg)) throw mError('flatten() background must be a Color');
          bg = arg;
        }
        if (obj.cssVar || obj.cssExpr || obj.lightDark || bg.cssVar || bg.cssExpr || bg.lightDark) {
          throw mError(
            'flatten() cannot be used with theme-dynamic colors (CSSVar or Color.lightDark) — CSS has no alpha-compositing expression, so the result could not follow the theme. Flatten the underlying static color instead.'
          );
        }
        return { type: 'ColorValue', oklch: flattenColor(obj.oklch, bg.oklch) };
      }
      case 'analogous': {
        if (expr.args.length > 1) throw mError('analogous() expects 0 or 1 arguments');
        let angle = 30;
        if (expr.args.length === 1) {
          const a = evaluateExpression(expr.args[0], scope);
          const d = colorAngleDegrees(a);
          if (d === undefined) throw mError('analogous() angle must be a number');
          angle = d;
        }
        const src = cssSourceExpr(obj.cssVar, obj.cssExpr);
        const colors: ColorValue[] = [
          {
            type: 'ColorValue',
            oklch: hueShift(obj.oklch, -angle),
            cssExpr: src ? hueShiftCSS(src, -angle) : undefined,
          },
          { type: 'ColorValue', oklch: obj.oklch, cssVar: obj.cssVar, cssExpr: obj.cssExpr },
          { type: 'ColorValue', oklch: hueShift(obj.oklch, angle), cssExpr: src ? hueShiftCSS(src, angle) : undefined },
        ];
        return { type: 'ArrayValue', elements: colors };
      }
      case 'triadic': {
        if (expr.args.length !== 0) throw mError('triadic() expects 0 arguments');
        const src = cssSourceExpr(obj.cssVar, obj.cssExpr);
        const colors: ColorValue[] = [
          { type: 'ColorValue', oklch: obj.oklch, cssVar: obj.cssVar, cssExpr: obj.cssExpr },
          { type: 'ColorValue', oklch: hueShift(obj.oklch, 120), cssExpr: src ? hueShiftCSS(src, 120) : undefined },
          { type: 'ColorValue', oklch: hueShift(obj.oklch, 240), cssExpr: src ? hueShiftCSS(src, 240) : undefined },
        ];
        return { type: 'ArrayValue', elements: colors };
      }
      case 'tetradic': {
        if (expr.args.length !== 0) throw mError('tetradic() expects 0 arguments');
        const src = cssSourceExpr(obj.cssVar, obj.cssExpr);
        const colors: ColorValue[] = [
          { type: 'ColorValue', oklch: obj.oklch, cssVar: obj.cssVar, cssExpr: obj.cssExpr },
          { type: 'ColorValue', oklch: hueShift(obj.oklch, 90), cssExpr: src ? hueShiftCSS(src, 90) : undefined },
          { type: 'ColorValue', oklch: hueShift(obj.oklch, 180), cssExpr: src ? hueShiftCSS(src, 180) : undefined },
          { type: 'ColorValue', oklch: hueShift(obj.oklch, 270), cssExpr: src ? hueShiftCSS(src, 270) : undefined },
        ];
        return { type: 'ArrayValue', elements: colors };
      }
      case 'splitComplementary': {
        if (expr.args.length > 1) throw mError('splitComplementary() expects 0 or 1 arguments');
        let angle = 30;
        if (expr.args.length === 1) {
          const a = evaluateExpression(expr.args[0], scope);
          const d = colorAngleDegrees(a);
          if (d === undefined) throw mError('splitComplementary() angle must be a number');
          angle = d;
        }
        const src = cssSourceExpr(obj.cssVar, obj.cssExpr);
        const colors: ColorValue[] = [
          { type: 'ColorValue', oklch: obj.oklch, cssVar: obj.cssVar, cssExpr: obj.cssExpr },
          {
            type: 'ColorValue',
            oklch: hueShift(obj.oklch, 180 - angle),
            cssExpr: src ? hueShiftCSS(src, 180 - angle) : undefined,
          },
          {
            type: 'ColorValue',
            oklch: hueShift(obj.oklch, 180 + angle),
            cssExpr: src ? hueShiftCSS(src, 180 + angle) : undefined,
          },
        ];
        return { type: 'ArrayValue', elements: colors };
      }
      default:
        throw mError(`Unknown Color method: ${expr.method}`);
    }
  }

  // ColorNamespace methods (Color.mix, Color.palette)
  if (typeof obj === 'object' && obj !== null && 'type' in obj && obj.type === 'ColorNamespace') {
    switch (expr.method) {
      case 'mix': {
        if (expr.args.length !== 3) throw mError('Color.mix() expects 3 arguments');
        const c1 = evaluateExpression(expr.args[0], scope);
        const c2 = evaluateExpression(expr.args[1], scope);
        const t = evaluateExpression(expr.args[2], scope);
        if (!isColorValue(c1)) throw mError('Color.mix() first argument must be a Color');
        if (!isColorValue(c2)) throw mError('Color.mix() second argument must be a Color');
        if (typeof t !== 'number') throw mError('Color.mix() third argument (ratio) must be a number');
        const src1 = cssSourceExpr(c1.cssVar, c1.cssExpr);
        const src2 = cssSourceExpr(c2.cssVar, c2.cssExpr);
        const cssExpr =
          src1 || src2 ? mixCSS(src1 || oklchToCSS(c1.oklch), src2 || oklchToCSS(c2.oklch), t) : undefined;
        return { type: 'ColorValue', oklch: mixColors(c1.oklch, c2.oklch, t), cssExpr };
      }
      case 'palette': {
        if (expr.args.length === 2) {
          // Lightness ramp: Color.palette(color, n)
          const c = evaluateExpression(expr.args[0], scope);
          const n = evaluateExpression(expr.args[1], scope);
          if (!isColorValue(c)) throw mError('Color.palette() first argument must be a Color');
          if (typeof n !== 'number' || !Number.isInteger(n) || n < 2)
            throw mError('Color.palette() count must be an integer >= 2');
          const src = cssSourceExpr(c.cssVar, c.cssExpr);
          const colors: ColorValue[] = [];
          for (let i = 0; i < n; i++) {
            const L = Math.round((0.15 + 0.8 * (i / (n - 1))) * 1000) / 1000;
            colors.push({
              type: 'ColorValue',
              oklch: { ...c.oklch, L },
              cssExpr: src ? setLightnessCSS(src, L) : undefined,
            });
          }
          return { type: 'ArrayValue', elements: colors };
        }
        if (expr.args.length === 3) {
          // Interpolation: Color.palette(c1, c2, n)
          const c1 = evaluateExpression(expr.args[0], scope);
          const c2 = evaluateExpression(expr.args[1], scope);
          const n = evaluateExpression(expr.args[2], scope);
          if (!isColorValue(c1)) throw mError('Color.palette() first argument must be a Color');
          if (!isColorValue(c2)) throw mError('Color.palette() second argument must be a Color');
          if (typeof n !== 'number' || !Number.isInteger(n) || n < 2)
            throw mError('Color.palette() count must be an integer >= 2');
          const src1 = cssSourceExpr(c1.cssVar, c1.cssExpr);
          const src2 = cssSourceExpr(c2.cssVar, c2.cssExpr);
          const colors: ColorValue[] = [];
          for (let i = 0; i < n; i++) {
            const t = i / (n - 1);
            const cssExpr =
              src1 || src2 ? mixCSS(src1 || oklchToCSS(c1.oklch), src2 || oklchToCSS(c2.oklch), t) : undefined;
            colors.push({
              type: 'ColorValue',
              oklch: mixColors(c1.oklch, c2.oklch, t),
              cssExpr,
            });
          }
          return { type: 'ArrayValue', elements: colors };
        }
        throw mError('Color.palette() expects 2 or 3 arguments');
      }
      case 'lightDark': {
        if (expr.args.length !== 2) throw mError('Color.lightDark() expects 2 arguments');
        const light = evaluateExpression(expr.args[0], scope);
        const dark = evaluateExpression(expr.args[1], scope);
        if (!isColorValue(light)) throw mError('Color.lightDark() first argument must be a Color');
        if (!isColorValue(dark)) throw mError('Color.lightDark() second argument must be a Color');
        const lightCSS = cssSourceExpr(light.cssVar, light.cssExpr) || oklchToCSS(light.oklch);
        const darkCSS = cssSourceExpr(dark.cssVar, dark.cssExpr) || oklchToCSS(dark.oklch);
        return { type: 'ColorValue', oklch: light.oklch, lightDark: { lightCSS, darkCSS } };
      }
      default:
        throw mError(`Unknown Color method: ${expr.method}`);
    }
  }

  // ObjectNamespace methods (Object.keys, Object.values, Object.entries, Object.delete)
  if (typeof obj === 'object' && obj !== null && 'type' in obj && obj.type === 'ObjectNamespace') {
    const args = expr.args.map((a) => evaluateExpression(a, scope));
    switch (expr.method) {
      case 'keys': {
        // Built-in structs (Point, Command, Endpoint, …) answer through the shared registry.
        const struct = args.length === 1 ? getStructDescriptor(args[0]) : null;
        if (struct) return { type: 'ArrayValue', elements: struct.keys(args[0]) };
        if (args.length !== 1 || !isObjectValue(args[0])) throw mError('Object.keys() expects 1 object argument');
        return { type: 'ArrayValue', elements: Array.from(args[0].properties.keys()) };
      }
      case 'values': {
        const struct = args.length === 1 ? getStructDescriptor(args[0]) : null;
        if (struct) return { type: 'ArrayValue', elements: struct.keys(args[0]).map((k) => struct.get(args[0], k)) };
        if (args.length !== 1 || !isObjectValue(args[0])) throw mError('Object.values() expects 1 object argument');
        return { type: 'ArrayValue', elements: Array.from(args[0].properties.values()) };
      }
      case 'entries': {
        const struct = args.length === 1 ? getStructDescriptor(args[0]) : null;
        if (struct) {
          return {
            type: 'ArrayValue',
            elements: struct
              .keys(args[0])
              .map((k) => ({ type: 'ArrayValue' as const, elements: [k, struct.get(args[0], k)] as Value[] })),
          };
        }
        if (args.length !== 1 || !isObjectValue(args[0])) throw mError('Object.entries() expects 1 object argument');
        const entries = Array.from(args[0].properties.entries()).map(([k, v]) => ({
          type: 'ArrayValue' as const,
          elements: [k, v] as Value[],
        }));
        return { type: 'ArrayValue', elements: entries };
      }
      case 'delete': {
        if (args.length !== 2 || !isObjectValue(args[0]))
          throw mError('Object.delete() expects 2 arguments (object, key)');
        const key = args[1];
        if (typeof key !== 'string') throw mError('Object.delete() key must be a string');
        const val = args[0].properties.get(key) ?? null;
        args[0].properties.delete(key);
        return val;
      }
      default:
        throw mError(`Unknown Object method: ${expr.method}`);
    }
  }

  // CapNamespace methods (Cap.butt, Cap.round, Cap.elliptical, Cap.tapered)
  if (typeof obj === 'object' && obj !== null && 'type' in obj && obj.type === 'CapNamespace') {
    switch (expr.method) {
      case 'butt': {
        if (expr.args.length !== 0) throw mError('Cap.butt() expects 0 arguments');
        return { type: 'CapValue' as const, cap: 'butt' };
      }
      case 'round': {
        if (expr.args.length !== 0) throw mError('Cap.round() expects 0 arguments');
        return { type: 'CapValue' as const, cap: 'round' };
      }
      case 'elliptical': {
        if (expr.args.length !== 1) throw mError('Cap.elliptical() expects 1 argument (projection)');
        const projection = evaluateExpression(expr.args[0], scope);
        if (typeof projection !== 'number') throw mError('Cap.elliptical() projection must be a number');
        if (projection <= 0)
          throw mError('Cap.elliptical() projection must be positive (use Cap.butt() for a flat end)');
        return { type: 'CapValue' as const, cap: 'elliptical', projection };
      }
      case 'tapered': {
        if (expr.args.length < 1 || expr.args.length > 2)
          throw mError('Cap.tapered() expects 1 or 2 arguments (length, continuity?)');
        const length = evaluateExpression(expr.args[0], scope);
        if (typeof length !== 'number') throw mError('Cap.tapered() length must be a number');
        let continuity: string | undefined;
        if (expr.args.length === 2) {
          const c = evaluateExpression(expr.args[1], scope);
          if (typeof c !== 'string' || !Object.values(BUILTIN_ENUMS.CurveContinuity).includes(c))
            throw mError('Cap.tapered() second argument must be a CurveContinuity value');
          continuity = c;
        }
        return { type: 'CapValue' as const, cap: 'tapered', length, continuity };
      }
      default:
        throw mError(`Unknown Cap method: ${expr.method}`);
    }
  }

  // MarkerNamespace methods (Marker.fromPathBlock) — placement audit V7
  if (typeof obj === 'object' && obj !== null && 'type' in obj && obj.type === 'MarkerNamespace') {
    switch (expr.method) {
      case 'fromPathBlock': {
        if (expr.args.length < 2 || expr.args.length > 4)
          throw mError('Marker.fromPathBlock() expects 2-4 arguments (id, shape, styles?, anchor?)');
        if (!scope.evalState) throw mError('Marker.fromPathBlock() requires evaluation context');
        const id = evaluateExpression(expr.args[0], scope);
        if (typeof id !== 'string') throw mError('Marker.fromPathBlock() first argument must be a string');
        try {
          validateCSSIdent(id, 'marker-id');
        } catch (e) {
          throw mError((e as Error).message);
        }
        const shapeArg = evaluateExpression(expr.args[1], scope);
        let commands: PathBlockCommand[];
        let projected = false;
        if (isProjectedPathValue(shapeArg)) {
          commands = shapeArg.commands;
          projected = true;
        } else if (isPathBlockValue(shapeArg)) {
          commands = projectCommands(shapeArg.commands, 0, 0);
        } else {
          throw mError('Marker.fromPathBlock() second argument must be a PathBlock or ProjectedPath');
        }
        if (commands.length === 0) throw mError('Marker.fromPathBlock() cannot fit an empty shape');
        let styles: Record<string, string> = {};
        if (expr.args.length >= 3) {
          const styleArg = evaluateExpression(expr.args[2], scope);
          if (!isStyleBlock(styleArg)) throw mError('Marker.fromPathBlock() third argument must be a style block');
          styles = { ...styleArg.properties };
        }
        let anchor = 'center';
        if (expr.args.length === 4) {
          const anchorArg = evaluateExpression(expr.args[3], scope);
          if (typeof anchorArg !== 'string') throw mError('Marker.fromPathBlock() anchor must be a BBoxAnchor value');
          anchor = anchorArg;
        }
        if (
          scope.evalState.masks.has(id) ||
          scope.evalState.clipPaths.has(id) ||
          scope.evalState.gradients.has(id) ||
          scope.evalState.patterns.has(id) ||
          scope.evalState.markers.has(id) ||
          scope.evalState.filters.has(id)
        ) {
          throw mError(`Duplicate defs ID '${id}': a Mask, ClipPath, Gradient, Pattern, Marker, or Filter with this ID already exists`);
        }
        // The viewBox IS the shape's bounding box (what .boundingBox() reports), padded by
        // half a numeric stroke-width so a stroke is not clipped at the edges; the
        // reference point is the anchor of the UNPADDED box.
        const bb = computeBoundingBox(commands);
        const ref = resolveAnchorPoint(bb, anchor, mError);
        const strokeWidth = Number(styles['stroke-width']);
        const pad = Number.isFinite(strokeWidth) && strokeWidth > 0 ? strokeWidth / 2 : 0;
        const vx = bb.x - pad;
        const vy = bb.y - pad;
        const vw = bb.width + 2 * pad;
        const vh = bb.height + 2 * pad;
        const marker: MarkerValue = {
          type: 'MarkerValue',
          id,
          viewBox: `${formatNum(vx)} ${formatNum(vy)} ${formatNum(vw)} ${formatNum(vh)}`,
          markerWidth: vw,
          markerHeight: vh,
          refX: ref.x,
          refY: ref.y,
          markerUnits: 'strokeWidth',
          orient: 'auto',
          preserveAspectRatio: 'xMidYMid meet',
          paths: [{ d: defsPathData(commands), styles, bbox: bb, projected, loc: { line: getLine(expr), column: getCol(expr) } }],
        };
        scope.evalState.markers.set(id, marker);
        return marker;
      }
      default:
        throw mError(`Unknown Marker method: ${expr.method}`);
    }
  }

  // PathBlockNamespace methods (PathBlock.fromGlyph)
  if (typeof obj === 'object' && obj !== null && 'type' in obj && obj.type === 'PathBlockNamespace') {
    switch (expr.method) {
      case 'fromGlyph': {
        if (expr.args.length !== 2) throw mError('PathBlock.fromGlyph() expects 2 arguments (text, styles)');
        const textArg = evaluateExpression(expr.args[0], scope);
        const stylesArg = evaluateExpression(expr.args[1], scope);
        if (typeof textArg !== 'string') throw mError('PathBlock.fromGlyph() first argument must be a string');
        if (typeof stylesArg !== 'object' || stylesArg === null || !('type' in stylesArg) || stylesArg.type !== 'StyleBlockValue') {
          throw mError('PathBlock.fromGlyph() second argument must be a style block');
        }
        const styles = (stylesArg as StyleBlockValue).properties;
        const fontFamily = styles['font-family']?.split(',')[0]?.trim()?.replace(/^['"]|['"]$/g, '');
        const fontSize = parseFloat(styles['font-size'] ?? '16') || 16;
        const fontWeight = parseInt(styles['font-weight'] ?? '400', 10) || 400;

        if (!fontFamily) throw mError('PathBlock.fromGlyph() requires font-family in style block');

        const registry = scope.evalState?.fontRegistry;
        if (!registry) {
          throw mError(
            'PathBlock.fromGlyph() requires font data, but no fonts were loaded. ' +
              'If you wrote an @font directive, font loading may have failed earlier — ' +
              'look for a preceding font-loading message.',
          );
        }

        if (!getFont(registry, fontFamily, fontWeight)) {
          const available = Array.from(registry.fonts.keys()).join(', ');
          throw mError(`Font '${fontFamily}' not found in font registry. Available fonts: ${available || 'none'}`);
        }

        const glyphs = makeGlyphBlocks(Array.from(textArg), registry, fontFamily, fontWeight, fontSize, scope.evalState);
        return { type: 'ArrayValue' as const, elements: glyphs };
      }
      default:
        throw mError(`Unknown PathBlock method: ${expr.method}`);
    }
  }

  // FontNamespace methods (Font.info / Font.characters / Font.glyphs — docs/fonts.md)
  if (typeof obj === 'object' && obj !== null && 'type' in obj && obj.type === 'FontNamespace') {
    const args = expr.args.map((arg) => evaluateExpression(arg, scope));
    const deps: FontNamespaceDeps = {
      evalState: scope.evalState,
      makeGlyphs: (chars, family, weight, fontSize) =>
        makeGlyphBlocks(chars, scope.evalState!.fontRegistry!, family, weight, fontSize, scope.evalState),
      warnLargeSelection: (message) =>
        warn(scope.evalState, 'font-glyph', message, { line: getLine(expr), column: getCol(expr) }),
    };
    try {
      switch (expr.method) {
        case 'info':
          return fontInfo(deps, args);
        case 'characters':
          return fontCharacters(deps, args);
        case 'glyphs':
          return fontGlyphs(deps, args);
        default:
          throw new Error(`Unknown Font method: ${expr.method}. Font has info(), characters() and glyphs()`);
      }
    } catch (err) {
      throw mError((err as Error).message);
    }
  }

  // ObjectValue methods
  if (isObjectValue(obj)) {
    if (expr.method === 'has') {
      if (expr.args.length !== 1) throw mError('has() expects 1 argument');
      const key = evaluateExpression(expr.args[0], scope);
      if (typeof key !== 'string') throw mError('has() argument must be a string');
      return boolVal(obj.properties.has(key));
    }
    throw mError(`Unknown object method: ${expr.method}`);
  }

  // String methods
  if (typeof obj === 'string') {
    switch (expr.method) {
      case 'empty': {
        if (expr.args.length !== 0) throw mError('empty() expects 0 arguments');
        return boolVal(obj.length === 0);
      }
      case 'split': {
        if (expr.args.length !== 0) throw mError('split() expects 0 arguments');
        const chars = Array.from(obj);
        return { type: 'ArrayValue' as const, elements: chars };
      }
      case 'append': {
        if (expr.args.length !== 1) throw mError('append() expects 1 argument');
        const val = evaluateExpression(expr.args[0], scope);
        if (typeof val !== 'string') throw mError('append() argument must be a string');
        return obj + val;
      }
      case 'prepend': {
        if (expr.args.length !== 1) throw mError('prepend() expects 1 argument');
        const val = evaluateExpression(expr.args[0], scope);
        if (typeof val !== 'string') throw mError('prepend() argument must be a string');
        return val + obj;
      }
      case 'includes': {
        if (expr.args.length !== 1) throw mError('includes() expects 1 argument');
        const val = evaluateExpression(expr.args[0], scope);
        if (typeof val !== 'string') throw mError('includes() argument must be a string');
        return boolVal(obj.includes(val));
      }
      case 'slice': {
        if (expr.args.length !== 2) throw mError('slice() expects 2 arguments');
        const start = evaluateExpression(expr.args[0], scope);
        const end = evaluateExpression(expr.args[1], scope);
        if (typeof start !== 'number' || typeof end !== 'number') {
          throw mError('slice() arguments must be numbers');
        }
        return obj.slice(start, end);
      }
      default:
        throw mError(`Unknown string method: ${expr.method}`);
    }
  }

  // Array methods
  if (!isArrayValue(obj)) {
    throw mError(`Cannot call method '${expr.method}' on non-array value`);
  }

  switch (expr.method) {
    case 'push': {
      if (expr.args.length !== 1) throw mError('push() expects 1 argument');
      if (isArrayLocked(obj)) throw mError(arrayMutationError('call push() on'));
      const val = evaluateExpression(expr.args[0], scope);
      obj.elements.push(val);
      return obj.elements.length;
    }
    case 'pop': {
      if (expr.args.length !== 0) throw mError('pop() expects 0 arguments');
      if (isArrayLocked(obj)) throw mError(arrayMutationError('call pop() on'));
      if (obj.elements.length === 0) return null;
      return obj.elements.pop()!;
    }
    case 'shift': {
      if (expr.args.length !== 0) throw mError('shift() expects 0 arguments');
      if (isArrayLocked(obj)) throw mError(arrayMutationError('call shift() on'));
      if (obj.elements.length === 0) return null;
      return obj.elements.shift()!;
    }
    case 'unshift': {
      if (expr.args.length !== 1) throw mError('unshift() expects 1 argument');
      if (isArrayLocked(obj)) throw mError(arrayMutationError('call unshift() on'));
      const val = evaluateExpression(expr.args[0], scope);
      obj.elements.unshift(val);
      return obj.elements.length;
    }
    case 'empty': {
      if (expr.args.length !== 0) throw mError('empty() expects 0 arguments');
      return boolVal(obj.elements.length === 0);
    }
    case 'map': {
      const cb = resolveCallbackBlock(expr, scope, workerExpr);
      if (!cb) throw mError('map() requires a trailing block or a << worker: array.map {|item| return ...; } or array.map() << f');
      if (cb.extraArgs !== 0) throw mError('map() takes no arguments besides the callback');
      const result: Value[] = [];
      const mapParams = cb.params;
      const mapLine = getLine(expr);
      lockArray(obj);
      try {
        for (let i = 0; i < obj.elements.length; i++) {
          const blockScope = callScope(cb.closure, scope, cb.calledFn);
          setVariable(blockScope, mapParams[0], obj.elements[i]);
          if (mapParams.length > 1) setVariable(blockScope, mapParams[1], i);
          if (mapParams.length > 2) setVariable(blockScope, mapParams[2], obj);
          // Path output inside the callback is discarded (one sink per
          // element, shared by its statements — as in sort and Grid.map).
          // no return → null
          try {
            result.push(runCallbackBody(cb.body, blockScope, createPathStore()));
          } catch (e) {
            throw callbackError('map', i, e, mapLine);
          }
        }
      } finally {
        unlockArray(obj);
      }
      return { type: 'ArrayValue' as const, elements: result };
    }
    case 'filter': {
      const cb = resolveCallbackBlock(expr, scope, workerExpr);
      if (!cb) throw mError('filter() requires a trailing block or a << worker: array.filter {|item| return ...; } or array.filter() << f');
      if (cb.extraArgs !== 0) throw mError('filter() takes no arguments besides the callback');
      const kept: Value[] = [];
      const filterParams = cb.params;
      const filterLine = getLine(expr);
      lockArray(obj);
      try {
        for (let i = 0; i < obj.elements.length; i++) {
          const blockScope = callScope(cb.closure, scope, cb.calledFn);
          setVariable(blockScope, filterParams[0], obj.elements[i]);
          if (filterParams.length > 1) setVariable(blockScope, filterParams[1], i);
          if (filterParams.length > 2) setVariable(blockScope, filterParams[2], obj);
          // no return → null → falsy → dropped
          let verdict: Value;
          try {
            verdict = runCallbackBody(cb.body, blockScope, createPathStore());
          } catch (e) {
            throw callbackError('filter', i, e, filterLine);
          }
          const verdictNum = toNumber(verdict);
          if (verdict !== null && (verdictNum !== undefined ? verdictNum !== 0 : Boolean(verdict))) {
            kept.push(obj.elements[i]);
          }
        }
      } finally {
        unlockArray(obj);
      }
      return { type: 'ArrayValue' as const, elements: kept };
    }
    case 'reduce': {
      const cb = resolveCallbackBlock(expr, scope, workerExpr);
      if (!cb) throw mError('reduce() requires a trailing block or a << worker: array.reduce(init) {|acc, item| return acc; } or array.reduce(init) << f');
      if (cb.extraArgs !== 1) throw mError('reduce() expects 1 argument (initial value) plus the callback');
      let accumulator: Value = cb.leadingArgs[0];
      const reduceParams = cb.params;
      const reduceLine = getLine(expr);
      lockArray(obj);
      try {
        for (let i = 0; i < obj.elements.length; i++) {
          const blockScope = callScope(cb.closure, scope, cb.calledFn);
          setVariable(blockScope, reduceParams[0], accumulator);
          if (reduceParams.length > 1) setVariable(blockScope, reduceParams[1], obj.elements[i]);
          if (reduceParams.length > 2) setVariable(blockScope, reduceParams[2], i);
          if (reduceParams.length > 3) setVariable(blockScope, reduceParams[3], obj);
          // no return → null
          try {
            accumulator = runCallbackBody(cb.body, blockScope, createPathStore());
          } catch (e) {
            throw callbackError('reduce', i, e, reduceLine);
          }
        }
      } finally {
        unlockArray(obj);
      }
      return accumulator;
    }
    case 'mapSlice': {
      if (expr.args.length < 1 || expr.args.length > 2)
        throw mError('mapSlice() expects 1-2 arguments (length, options?)');
      if (expr.block) throw mError('mapSlice() does not take a trailing block');
      const lengthVal = evaluateExpression(expr.args[0], scope);
      if (typeof lengthVal !== 'number') throw mError('mapSlice() length must be a number');
      // No rounding: mapSlice(2.6) used to make windows of 3 without a word, and
      // mapSlice(0.4) rounded to 0 and then complained about "at least 1".
      if (!Number.isInteger(lengthVal) || lengthVal < 1) {
        // The round() hint only where rounding is the fix — not for 0, -2 or NaN.
        const fractional = Number.isFinite(lengthVal) && !Number.isInteger(lengthVal);
        const hint = fractional ? ' — wrap a computed length in round()' : '';
        throw mError(`mapSlice() length must be a positive integer, got ${lengthVal}${hint}`);
      }
      const len = lengthVal;
      const { partial } =
        expr.args.length === 2
          ? parseMapSliceOptions(evaluateExpression(expr.args[1], scope), mError)
          : { partial: false };
      // Full windows only by default: n elements hold n - len + 1 of them (none
      // when len > n). { partial: true } also keeps the windows that run off the
      // end — one per element, the last len - 1 of them short.
      const windowCount = partial ? obj.elements.length : obj.elements.length - len + 1;
      const sliceResult: Value[] = [];
      for (let i = 0; i < windowCount; i++) {
        sliceResult.push({ type: 'ArrayValue' as const, elements: obj.elements.slice(i, i + len) });
      }
      return { type: 'ArrayValue' as const, elements: sliceResult };
    }
    case 'slice': {
      if (expr.args.length < 1 || expr.args.length > 2) throw mError('slice() expects 1-2 arguments');
      const startVal = evaluateExpression(expr.args[0], scope);
      if (typeof startVal !== 'number') throw mError('slice() start must be a number');
      let s = Math.round(startVal);
      if (s < 0) s = Math.max(0, obj.elements.length + s);
      if (expr.args.length === 2) {
        const endVal = evaluateExpression(expr.args[1], scope);
        if (typeof endVal !== 'number') throw mError('slice() end must be a number');
        let e = Math.round(endVal);
        if (e < 0) e = obj.elements.length + e;
        if (e < 0) e = -1; // clamp: nothing before index 0 is valid
        return { type: 'ArrayValue' as const, elements: obj.elements.slice(s, e + 1) };
      }
      return { type: 'ArrayValue' as const, elements: obj.elements.slice(s) };
    }
    case 'reverse': {
      if (expr.args.length !== 0) throw mError('reverse() expects 0 arguments');
      if (expr.block) throw mError('reverse() does not take a trailing block');
      return { type: 'ArrayValue' as const, elements: [...obj.elements].reverse() };
    }
    case 'seams': {
      if (expr.args.length !== 0) throw mError('seams() expects 0 arguments');
      if (expr.block) throw mError('seams() does not take a trailing block');
      // Collect seamId-carrying stretches per (id, element): within one
      // element a given id is one contiguous stretch; across elements the
      // same id is either the seam's TWIN (reversed-identical geometry —
      // deduped) or, after chained cuts, a genuinely distinct fragment of
      // an inherited seam (kept as its own entry).
      const seamStretches = new Map<number, { owner: Value; cmds: PathBlockCommand[] }[]>();
      for (const el of obj.elements) {
        if (typeof el !== 'object' || el === null || !('type' in el) || (el.type !== 'PathBlockValue' && el.type !== 'ProjectedPathValue')) {
          throw mError('seams() elements must each be a PathBlock or ProjectedPath (call it on the array cut() returns)');
        }
        for (const cmd of (el as PathBlockValue | ProjectedPathValue).commands) {
          const seamId = cmd.meta?.seamId;
          if (seamId === undefined) continue;
          let stretches = seamStretches.get(seamId);
          if (!stretches) {
            stretches = [];
            seamStretches.set(seamId, stretches);
          }
          const last = stretches[stretches.length - 1];
          if (last && last.owner === el) last.cmds.push(cmd);
          else stretches.push({ owner: el, cmds: [cmd] });
        }
      }
      const seamClose = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.y - b.y) < 1e-6;
      const chordSum = (cmds: PathBlockCommand[]) => cmds.reduce((sum, c) => sum + Math.hypot(c.end.x - c.start.x, c.end.y - c.start.y), 0);
      const isReversedTwin = (a: PathBlockCommand[], b: PathBlockCommand[]) =>
        a.length === b.length &&
        seamClose(a[0].start, b[b.length - 1].end) &&
        seamClose(a[a.length - 1].end, b[0].start) &&
        Math.abs(chordSum(a) - chordSum(b)) < 1e-6;
      const seamResults: PathBlockCommand[][] = [];
      for (const seamId of [...seamStretches.keys()].sort((idA, idB) => idA - idB)) {
        const accepted: PathBlockCommand[][] = [];
        for (const stretch of seamStretches.get(seamId)!) {
          if (accepted.some((have) => isReversedTwin(have, stretch.cmds))) continue;
          accepted.push(stretch.cmds);
        }
        seamResults.push(...accepted);
      }
      return {
        type: 'ArrayValue' as const,
        elements: seamResults.map((cmds) => {
          // A closed ring seam (cookie) closes properly so dashes wrap
          // and joins render at the seam's wrap point.
          const first = cmds[0];
          const tail = cmds[cmds.length - 1];
          const ringClosed = cmds.length > 1 && seamClose(first.start, tail.end) && tail.command.toLowerCase() !== 'z';
          const withClose = ringClosed
            ? [...cmds, { command: 'z', args: [], start: { ...tail.end }, end: { ...first.start } }]
            : cmds;
          return fromCommandsKeepingFrame(withClose);
        }),
      };
    }
    case 'sort': {
      const cb = resolveCallbackBlock(expr, scope, workerExpr);
      if (!cb && expr.args.length !== 0) {
        throw mError('sort() does not take arguments — use sort {|a, b| return ...; } or sort() << cmp for a custom order');
      }
      if (cb && cb.extraArgs !== 0) {
        throw mError('sort() takes no arguments besides the comparator');
      }
      const sorted = [...obj.elements];
      if (!cb) {
        const allNumbers = sorted.every((e) => toNumber(e) !== undefined);
        const allStrings = !allNumbers && sorted.every((e) => typeof e === 'string');
        if (!allNumbers && !allStrings) {
          throw mError(
            'sort() without a comparator requires all-number or all-string elements — use sort {|a, b| return ...; } to define the order',
          );
        }
        if (allNumbers) {
          // NaN would be silently coerced to "equal" by the sort algorithm,
          // producing an arbitrary order instead of the documented ascending one.
          if (sorted.some((e) => Number.isNaN(toNumber(e)))) {
            throw mError('sort() without a comparator cannot order NaN elements — remove them before sorting');
          }
          sorted.sort((a, b) => (toNumber(a) as number) - (toNumber(b) as number));
        } else {
          sorted.sort((a, b) => ((a as string) < (b as string) ? -1 : (a as string) > (b as string) ? 1 : 0));
        }
        return { type: 'ArrayValue' as const, elements: sorted };
      }
      const sortParams = cb.params;
      const sortBody = cb.body;
      const sortLine = getLine(expr);
      // One shared sink for all comparator invocations — path output inside a
      // comparator is discarded (same semantics as Grid.fill). Hoisting the
      // store and short-circuiting top-level returns via evaluateGridCellBody
      // avoids the per-invocation throw/catch deopt; the comparator runs
      // O(n log n) times.
      const sortSink = createPathStore();
      // sort iterates a copy, but the receiver stays locked while comparators
      // run — mutating the array being sorted is still an iteration hazard.
      lockArray(obj);
      try {
        sorted.sort((a, b) => {
          const blockScope = callScope(cb.closure, scope, cb.calledFn);
          if (sortParams.length > 0) setVariable(blockScope, sortParams[0], a);
          if (sortParams.length > 1) setVariable(blockScope, sortParams[1], b);
          let cmp: Value = null;
          try {
            const res = evaluateGridCellBody(sortBody, blockScope, sortSink);
            cmp = res.returned ? res.value : null;
          } catch (e) {
            if (e instanceof ReturnSignal) {
              cmp = e.value;
            } else {
              const msg = e instanceof Error ? e.message : String(e);
              throw new Error(formatError(`Error in .sort() comparator: ${msg}`, sortLine));
            }
          }
          if (typeof cmp !== 'number' || Number.isNaN(cmp)) {
            throw mError(
              'sort() comparator must return a number (negative = a first, positive = b first, zero = keep order) — e.g. return calc(a - b);',
            );
          }
          return cmp;
        });
      } finally {
        unlockArray(obj);
      }
      return { type: 'ArrayValue' as const, elements: sorted };
    }
    default:
      throw mError(`Unknown array method: ${expr.method}`);
  }
}

function formatValueForDisplay(val: Value): string {
  if (val === null) return 'null';
  if (isBooleanValue(val)) return val.value ? 'true' : 'false';
  if (isAngleValue(val)) return formatAngleForDisplay(val);
  if (typeof val === 'number') return formatNum(val);
  if (typeof val === 'string') return val;
  if (isPointValue(val)) {
    return `Point(${formatNum(val.x)}, ${formatNum(val.y)})`;
  }
  if (isPolarVectorValue(val)) {
    return `PolarVector(${formatNum(val.angle)}, ${formatNum(val.distance)})`;
  }
  if (typeof val === 'object' && 'type' in val && val.type === 'ViewBoxStructValue') {
    return `ViewBox(${formatNum(val.originX)}, ${formatNum(val.originY)}, ${formatNum(val.width)}, ${formatNum(val.height)})`;
  }
  if (isPathBlockValue(val)) {
    // Show the first few commands so a logged block is recognizable.
    const preview = commandsToRelativeD(val.commands.slice(0, 6), { bridgeOriginGap: true });
    const more = val.commands.length > 6 ? ' …' : '';
    return `PathBlock(${val.commands.length} command${val.commands.length === 1 ? '' : 's'}${preview ? `: ${preview}${more}` : ''})`;
  }
  if (isEndpointValue(val)) {
    const at = `at ${formatNum(val.point.x)}, ${formatNum(val.point.y)}`;
    return val.label !== null ? `Endpoint('${val.label}' ${at})` : `Endpoint(${at})`;
  }
  if (isCommandValue(val)) {
    const cmd = val.source.commands[val.index];
    const args = cmd.args.map((a) => formatNum(a)).join(' ');
    const from = `Point(${formatNum(cmd.start.x)}, ${formatNum(cmd.start.y)})`;
    const to = `Point(${formatNum(cmd.end.x)}, ${formatNum(cmd.end.y)})`;
    return `Command(${cmd.command}${args ? ` ${args}` : ''}: ${from} → ${to})`;
  }
  if (isCallValue(val)) {
    const n = val.to - val.from;
    return `Call(${val.fn}: ${n} command${n === 1 ? '' : 's'})`;
  }
  if (isSegmentValue(val)) {
    const n = val.to - val.from;
    return `Segment('${val.label}': ${n} command${n === 1 ? '' : 's'})`;
  }
  if (isSubpathValue(val)) {
    const n = val.to - val.from;
    return `Subpath(${val.index}: ${n} command${n === 1 ? '' : 's'}, ${val.closed ? 'closed' : 'open'})`;
  }
  if (isSubscriptionValue(val)) {
    return `Subscription(${val.layerName}: '${val.selector}', ${val.count} delivered)`;
  }
  if (isProjectedPathValue(val)) {
    return `ProjectedPath(${formatNum(val.startPoint.x)}, ${formatNum(val.startPoint.y)} → ${formatNum(val.endPoint.x)}, ${formatNum(val.endPoint.y)})`;
  }
  if (isTextBlockValue(val)) {
    return `TextBlock(${val.elements.length} elements)`;
  }
  if (isProjectedTextValue(val)) {
    return `ProjectedText(${formatNum(val.origin.x)}, ${formatNum(val.origin.y)}, ${val.elements.length} elements)`;
  }
  if (isObjectValue(val)) {
    const entries = Array.from(val.properties.entries()).map(([k, v]) => `${k}: ${formatValueForDisplay(v)}`);
    return `{${entries.join(', ')}}`;
  }
  if (isCyclerValue(val)) {
    return `Cycler(${val.elements.length} items, index ${val.index})`;
  }
  if (isSVGFragmentValue(val)) {
    const preview = val.rawContent.length > 60 ? `${val.rawContent.slice(0, 60)}...` : val.rawContent;
    return `SVGDocumentFragment(${preview})`;
  }
  if (isMaskValue(val)) {
    return `Mask(${val.id}, ${val.paths.length} paths)`;
  }
  if (isClipPathValue(val)) {
    return `ClipPath(${val.id}, ${val.paths.length} paths)`;
  }
  if (isPatternValue(val)) {
    return `Pattern(${val.id}, ${val.paths.length} elements)`;
  }
  if (isMarkerValue(val)) {
    return `Marker(${val.id}, ${val.paths.length} elements)`;
  }
  if (isGradientValue(val)) {
    if (val.gradientType === 'mesh') {
      return `MeshGradient(${val.id}, ${val.meshCols}×${val.meshRows})`;
    }
    if (val.gradientType === 'freeform') {
      return `FreeformGradient(${val.id}, ${val.freeformPoints?.length ?? 0} points)`;
    }
    if (val.gradientType === 'topo') {
      return `TopoGradient(${val.id}, ${val.topoContours?.length ?? 0} contours)`;
    }
    return `${val.gradientType.charAt(0).toUpperCase() + val.gradientType.slice(1)}Gradient(${val.id}, ${val.stops.length} stops)`;
  }
  if (isMeshPointValue(val)) {
    return `MeshPoint(${val.gridRow},${val.gridCol} @ ${val.x.toFixed(1)},${val.y.toFixed(1)})`;
  }
  if (isColorValue(val)) {
    if (val.lightDark) {
      return `Color.lightDark(${val.lightDark.lightCSS}, ${val.lightDark.darkCSS})`;
    }
    return `Color(${oklchToCSS(val.oklch)})`;
  }
  if (isCSSVarValue(val)) {
    return val.fallback ? `CSSVar(${val.varName}, ${val.fallback})` : `CSSVar(${val.varName})`;
  }
  if (isArrayValue(val)) {
    return `[${val.elements.map(formatValueForDisplay).join(', ')}]`;
  }
  if (typeof val === 'object' && val !== null && 'type' in val && val.type === 'UserFunction') {
    return `${val.isLambda ? 'Lambda' : 'Function'}(${val.params.join(', ')})`;
  }
  // Never the function's JavaScript source (`()=>Math.PI`): its stdlib name.
  if (isStdlibFunctionValue(val)) return formatStdlibFunction(val);
  return String(val);
}

function evaluateTemplateLiteral(tl: TemplateLiteral, scope: Scope): string {
  return tl.parts
    .map((part) => {
      if (typeof part === 'string') return part;
      const val = evaluateExpression(part, scope);
      return formatValueForDisplay(val);
    })
    .join('');
}

/** `.commands` / `.subPathCommands`: Command structs — the same values `queryAll('command')` returns. */
function commandRecordsValue(source: QuerySource): Value {
  return commandValues(source);
}

function evaluateMemberExpression(expr: MemberExpression, scope: Scope): Value {
  const obj = evaluateExpression(expr.object, scope);

  // Context .transform is synthesized from _transformState, not a data property
  if (
    typeof obj === 'object' && obj !== null && 'type' in obj && obj.type === 'ContextObject' &&
    expr.property === 'transform'
  ) {
    const transformState = obj.value._transformState as TransformState | undefined;
    if (transformState) {
      return { type: 'TransformReference' as const, state: transformState };
    }
    throw new Error(`Property 'transform' does not exist on context object`);
  }

  // Data properties of built-in structs (Point, PolarVector, Grid, MeshPoint,
  // Color, context objects) resolve through the shared registry, which is also
  // what object destructuring reads.
  const struct = getStructDescriptor(obj);
  if (struct) {
    if (struct.has(obj, expr.property)) return struct.get(obj, expr.property);
    throw new Error(`Property '${expr.property}' does not exist on ${struct.name}`);
  }

  // Handle PathBlockValue property access
  if (isPathBlockValue(obj)) {
    switch (expr.property) {
      case 'length':
        return calculatePathLength(obj.commands);
      case 'vertices':
        return { type: 'ArrayValue' as const, elements: extractVertices(obj.commands) };
      case 'subPathCount':
        return countSubPaths(obj.commands);
      case 'subPathCommands':
      case 'commands':
        return commandRecordsValue({ kind: 'pathblock', value: obj, commands: obj.commands });
      case 'd':
        // The relative path data the block emits when drawn at the origin —
        // exactly what .draw() writes before placement.
        return commandsToRelativeD(obj.commands, { bridgeOriginGap: true });
      case 'startPoint':
        return { type: 'PointValue' as const, x: obj.startPoint.x, y: obj.startPoint.y };
      case 'endPoint':
        return { type: 'PointValue' as const, x: obj.endPoint.x, y: obj.endPoint.y };
      case 'advanceWidth': {
        const aw = (obj as PathBlockValue & { advanceWidth?: number }).advanceWidth;
        return aw !== undefined ? aw : 0;
      }
      case 'anchor': {
        const anchor = (obj as PathBlockValue & { anchor?: { x: number; y: number } }).anchor;
        if (anchor === undefined)
          throw new Error(
            "'anchor' is only available on a result that was re-based to its own origin — variableOffset/compoundVariableOffset, subPath, segment, reverse, and translateStartPointTo/translateCenterPointTo — where it recovers the position that re-basing removed. Operations that keep their placement (offset, outline, dash, fillet, the boolean ops, cut) have nothing to recover, and composing or transforming a result produces a new value without it; read anchor before composing",
          );
        return { type: 'PointValue' as const, x: anchor.x, y: anchor.y };
      }
      case 'contours': {
        // Decompose a multi-contour glyph into individual PathBlockValues
        const contourGroups = splitContours(obj.commands);
        const contourBlocks: Value[] = contourGroups.map((cmds) =>
          fromCommandsKeepingFrame(cmds),
        );
        return { type: 'ArrayValue' as const, elements: contourBlocks };
      }
      case 'isEmpty':
        return boolVal(obj.commands.length === 0);
      case 'char': {
        const char = (obj as PathBlockValue & { char?: string }).char;
        if (char === undefined)
          throw new Error(
            "'char' is only available on glyphs produced by PathBlock.fromGlyph() — it records the source character. Composing or transforming a glyph produces a new block without it",
          );
        return char;
      }
      case 'isWhitespace': {
        const char = (obj as PathBlockValue & { char?: string }).char;
        if (char === undefined)
          throw new Error(
            "'isWhitespace' is only available on glyphs produced by PathBlock.fromGlyph() — it classifies the source character. Use 'isEmpty' to test whether any PathBlock has no commands",
          );
        return boolVal(isWhitespaceChar(char));
      }
      case 'codePoint': {
        const char = (obj as PathBlockValue & { char?: string }).char;
        if (char === undefined)
          throw new Error(
            "'codePoint' is only available on glyphs produced by PathBlock.fromGlyph() — it reports the source character's Unicode code point. Composing or transforming a glyph produces a new block without it",
          );
        return char.codePointAt(0)!;
      }
      case 'isSpace':
      case 'isTab':
      case 'isNewline':
      case 'isMark': {
        const char = (obj as PathBlockValue & { char?: string }).char;
        if (char === undefined)
          throw new Error(
            `'${expr.property}' is only available on glyphs produced by PathBlock.fromGlyph() — it classifies the source character. Use 'isEmpty' to test whether any PathBlock has no commands`,
          );
        return boolVal(CHAR_CLASS_PREDICATES[expr.property](char));
      }
      case 'script':
      case 'block':
      case 'category':
      case 'isLetter':
      case 'isDigit':
      case 'isPunctuation':
      case 'isSymbol': {
        // Unicode classification of the source character (docs/fonts.md).
        const char = (obj as PathBlockValue & { char?: string }).char;
        if (char === undefined)
          throw new Error(
            `'${expr.property}' is only available on glyphs produced by PathBlock.fromGlyph() or Font.glyphs() — it classifies the source character. Composing or transforming a glyph produces a new block without it`,
          );
        const cp = char.codePointAt(0)!;
        switch (expr.property) {
          case 'script':
            return scriptOf(cp);
          case 'block':
            return blockOf(cp);
          case 'category':
            return categoryOf(char);
          case 'isLetter':
            return boolVal(categoryOf(char) === 'letter');
          case 'isDigit':
            return boolVal(categoryOf(char) === 'digit');
          case 'isPunctuation':
            return boolVal(categoryOf(char) === 'punctuation');
          default:
            return boolVal(categoryOf(char) === 'symbol');
        }
      }
      case 'glyphName': {
        const glyph = obj as PathBlockValue & { char?: string; glyphName?: string | null };
        if (glyph.char === undefined)
          throw new Error(
            "'glyphName' is only available on glyphs produced by PathBlock.fromGlyph() or Font.glyphs() — it is the font's name for the glyph",
          );
        return glyph.glyphName ?? null;
      }
      default:
        throw new Error(`Property '${expr.property}' does not exist on PathBlock`);
    }
  }

  // Handle TextBlockValue property access
  if (isTextBlockValue(obj)) {
    switch (expr.property) {
      case 'elementCount':
        return obj.elements.length;
      case 'styles':
        return { type: 'StyleBlockValue' as const, properties: { ...obj.styles } };
      default:
        throw new Error(`Property '${expr.property}' does not exist on TextBlock`);
    }
  }

  // Handle ProjectedTextValue property access
  if (isProjectedTextValue(obj)) {
    switch (expr.property) {
      case 'elementCount':
        return obj.elements.length;
      case 'styles':
        return { type: 'StyleBlockValue' as const, properties: { ...obj.styles } };
      case 'origin':
        return { type: 'PointValue' as const, x: obj.origin.x, y: obj.origin.y };
      default:
        throw new Error(`Property '${expr.property}' does not exist on ProjectedText`);
    }
  }

  // Handle ProjectedPathValue property access
  if (isProjectedPathValue(obj)) {
    switch (expr.property) {
      case 'length':
        return calculatePathLength(obj.commands);
      case 'vertices':
        return { type: 'ArrayValue' as const, elements: extractVertices(obj.commands) };
      case 'subPathCount':
        return countSubPaths(obj.commands);
      case 'subPathCommands':
      case 'commands':
        return commandRecordsValue({ kind: 'projected', value: obj, commands: obj.commands });
      case 'd': {
        // Absolute path data: a ProjectedPath already lives in world space,
        // so it starts with an explicit move to its first point.
        const abs = commandsToAbsoluteD(obj.commands);
        const first = obj.commands[0];
        if (!first || first.command === 'M' || first.command === 'm') return abs;
        return `M ${formatNum(obj.startPoint.x)} ${formatNum(obj.startPoint.y)}${abs ? ` ${abs}` : ''}`;
      }
      case 'startPoint':
        return { type: 'PointValue' as const, x: obj.startPoint.x, y: obj.startPoint.y };
      case 'endPoint':
        return { type: 'PointValue' as const, x: obj.endPoint.x, y: obj.endPoint.y };
      case 'isEmpty':
        return boolVal(obj.commands.length === 0);
      case 'anchor': {
        const anchor = (obj as ProjectedPathValue & { anchor?: { x: number; y: number } }).anchor;
        if (anchor === undefined)
          throw new Error(
            "'anchor' is only available on variableOffset/compoundVariableOffset results, where it equals 'startPoint' (a ProjectedPath result is already registered on its spine), and on translateStartPointTo/translateCenterPointTo results, where it is the point the path started at before the move. Composing or transforming a result produces a new path without it; read anchor before composing",
          );
        return { type: 'PointValue' as const, x: anchor.x, y: anchor.y };
      }
      default:
        throw new Error(`Property '${expr.property}' does not exist on ProjectedPath`);
    }
  }

  // Handle TransformReference property access
  if (typeof obj === 'object' && obj !== null && 'type' in obj && obj.type === 'TransformReference') {
    const transformRef = obj;
    if (expr.property === 'translate' || expr.property === 'rotate' || expr.property === 'scale') {
      return { type: 'TransformPropertyReference' as const, state: transformRef.state, property: expr.property };
    }
    throw new Error(`Property '${expr.property}' does not exist on transform`);
  }

  // Handle TransformPropertyReference property access (read)
  if (typeof obj === 'object' && obj !== null && 'type' in obj && obj.type === 'TransformPropertyReference') {
    const propRef = obj;
    switch (propRef.property) {
      case 'translate': {
        if (expr.property === 'x') return propRef.state.translate?.x ?? 0;
        if (expr.property === 'y') return propRef.state.translate?.y ?? 0;
        throw new Error(`Property '${expr.property}' does not exist on transform.translate`);
      }
      case 'rotate': {
        if (expr.property === 'angle') return propRef.state.rotate?.angle ?? 0;
        if (expr.property === 'cx') return propRef.state.rotate?.cx ?? 0;
        if (expr.property === 'cy') return propRef.state.rotate?.cy ?? 0;
        throw new Error(`Property '${expr.property}' does not exist on transform.rotate`);
      }
      case 'scale': {
        if (expr.property === 'x') return propRef.state.scale?.x ?? 1;
        if (expr.property === 'y') return propRef.state.scale?.y ?? 1;
        if (expr.property === 'cx') return propRef.state.scale?.cx ?? 0;
        if (expr.property === 'cy') return propRef.state.scale?.cy ?? 0;
        throw new Error(`Property '${expr.property}' does not exist on transform.scale`);
      }
    }
  }

  // Handle MaskValue property access
  if (isMaskValue(obj)) {
    if (expr.property === 'id') return obj.id;
    throw new Error(`Property '${expr.property}' does not exist on Mask`);
  }

  // Handle ClipPathValue property access
  if (isClipPathValue(obj)) {
    if (expr.property === 'id') return obj.id;
    throw new Error(`Property '${expr.property}' does not exist on ClipPath`);
  }

  // Handle PatternValue property access
  if (isPatternValue(obj)) {
    switch (expr.property) {
      case 'id':
        return obj.id;
      case 'patternUnits':
        return obj.patternUnits ?? null;
      case 'patternTransform':
        return obj.patternTransform ?? null;
      case 'patternContentUnits':
        return obj.patternContentUnits ?? null;
      default:
        throw new Error(`Property '${expr.property}' does not exist on Pattern`);
    }
  }

  // Handle MarkerValue property access
  if (isMarkerValue(obj)) {
    switch (expr.property) {
      case 'id':
        return obj.id;
      case 'viewBox':
        return obj.viewBox;
      case 'markerWidth':
        return obj.markerWidth;
      case 'markerHeight':
        return obj.markerHeight;
      case 'refX':
        return typeof obj.refX === 'number' ? obj.refX : obj.refX;
      case 'refY':
        return typeof obj.refY === 'number' ? obj.refY : obj.refY;
      case 'markerUnits':
        return obj.markerUnits;
      case 'orient':
        return typeof obj.orient === 'number' ? obj.orient : obj.orient;
      case 'preserveAspectRatio':
        return obj.preserveAspectRatio;
      default:
        throw new Error(`Property '${expr.property}' does not exist on Marker`);
    }
  }

  // Handle NoiseFilterValue property access
  if (isNoiseFilterValue(obj)) {
    switch (expr.property) {
      case 'id':
        return obj.id;
      case 'style':
        return obj.style;
      case 'scale':
        return obj.scale;
      case 'octaves':
        return obj.octaves;
      case 'amount':
        return obj.amount;
      case 'monochrome':
        return boolVal(obj.monochrome);
      case 'seed':
        return obj.seed;
      case 'blend':
        return obj.blend;
      case 'contrast':
        return obj.contrast;
      case 'stitch':
        return boolVal(obj.stitch);
      default:
        throw new Error(`Property '${expr.property}' does not exist on NoiseFilter`);
    }
  }

  if (isGlowFilterValue(obj)) {
    switch (expr.property) {
      case 'id':
        return obj.id;
      case 'mode':
        return obj.mode;
      case 'color':
        return obj.color;
      case 'radius':
        return obj.radius;
      case 'spread':
        return obj.spread;
      case 'opacity':
        return obj.opacity;
      default:
        throw new Error(`Property '${expr.property}' does not exist on GlowFilter`);
    }
  }

  if (isEmbossFilterValue(obj)) {
    switch (expr.property) {
      case 'id':
        return obj.id;
      case 'angle':
        return obj.angle;
      case 'elevation':
        return obj.elevation;
      case 'depth':
        return obj.depth;
      case 'strength':
        return obj.strength;
      case 'shininess':
        return obj.shininess;
      case 'lightColor':
        return obj.lightColor;
      case 'smooth':
        return obj.smooth;
      default:
        throw new Error(`Property '${expr.property}' does not exist on EmbossFilter`);
    }
  }

  if (isElevationShadowFilterValue(obj)) {
    switch (expr.property) {
      case 'id':
        return obj.id;
      case 'elevation':
        return obj.elevation;
      case 'color':
        return obj.color;
      case 'direction':
        return obj.direction;
      case 'tightness':
        return obj.tightness;
      default:
        throw new Error(`Property '${expr.property}' does not exist on ElevationShadowFilter`);
    }
  }

  if (isInnerShadowFilterValue(obj)) {
    switch (expr.property) {
      case 'id':
        return obj.id;
      case 'offsetX':
        return obj.offsetX;
      case 'offsetY':
        return obj.offsetY;
      case 'blur':
        return obj.blur;
      case 'color':
        return obj.color;
      case 'opacity':
        return obj.opacity;
      default:
        throw new Error(`Property '${expr.property}' does not exist on InnerShadowFilter`);
    }
  }

  if (isPixelateFilterValue(obj)) {
    switch (expr.property) {
      case 'id':
        return obj.id;
      case 'width':
        return obj.width;
      case 'height':
        return obj.height;
      case 'radius':
        return obj.radius;
      default:
        throw new Error(`Property '${expr.property}' does not exist on PixelateFilter`);
    }
  }

  if (isMotionBlurFilterValue(obj)) {
    switch (expr.property) {
      case 'id':
        return obj.id;
      case 'type':
        return obj.motionType;
      case 'distance':
        return obj.distance;
      case 'angle':
        return obj.angle;
      case 'samples':
        return obj.samples;
      default:
        throw new Error(`Property '${expr.property}' does not exist on MotionBlurFilter`);
    }
  }

  // Handle GradientValue property access
  if (isGradientValue(obj)) {
    switch (expr.property) {
      case 'id':
        return obj.id;
      case 'spreadMethod':
        return obj.spreadMethod ?? null;
      case 'gradientUnits':
        return obj.gradientUnits ?? null;
      case 'gradientTransform':
        return obj.gradientTransform ?? null;
      case 'interpolation':
        return obj.interpolation ?? null;
      case 'steps':
        return obj.steps ?? null;
      // Conic-specific properties
      case 'from':
        return obj.from ?? 0;
      case 'to':
        return obj.to ?? 2 * Math.PI;
      case 'direction':
        return obj.direction ?? 'cw';
      case 'spread':
        return obj.spread ?? 'clamp';
      case 'innerRadius':
        return obj.innerRadius ?? 0;
      case 'innerFill':
        return obj.innerFill ?? 'transparent';
      // Mesh/Freeform-specific properties
      case 'falloff':
        return obj.falloff ?? 2.0;
      case 'cols':
        return obj.meshCols ?? 0;
      case 'rows':
        return obj.meshRows ?? 0;
      case 'width': {
        if (obj.gradientType === 'mesh') return obj.meshWidth!;
        if (obj.gradientType === 'freeform') return obj.freeformWidth!;
        if (obj.gradientType === 'topo') return obj.topoWidth!;
        throw new Error(`Property 'width' does not exist on ${obj.gradientType} gradient`);
      }
      case 'height': {
        if (obj.gradientType === 'mesh') return obj.meshHeight!;
        if (obj.gradientType === 'freeform') return obj.freeformHeight!;
        if (obj.gradientType === 'topo') return obj.topoHeight!;
        throw new Error(`Property 'height' does not exist on ${obj.gradientType} gradient`);
      }
      // Topo-specific properties
      case 'easing':
        return obj.topoEasing ?? 'linear';
      case 'method':
        return obj.topoMethod ?? 'distance';
      case 'iterations':
        return obj.topoIterations ?? 200;
      case 'blend':
        return obj.topoBlend ?? 1.0;
      case 'baseColor': {
        if (obj.topoBaseColor) {
          return { type: 'ColorValue', oklch: { ...obj.topoBaseColor } } as ColorValue;
        }
        return null;
      }
      default:
        throw new Error(`Property '${expr.property}' does not exist on Gradient`);
    }
  }

  // Handle CSSVarValue property access
  if (isCSSVarValue(obj)) {
    switch (expr.property) {
      case 'var':
        return obj.varName;
      case 'fallback':
        return obj.fallback;
      case 'css':
        return obj.fallback ? `var(${obj.varName}, ${obj.fallback})` : `var(${obj.varName})`;
      default:
        throw new Error(`Property '${expr.property}' does not exist on CSSVar`);
    }
  }

  // Handle StyleBlockValue property access (camelCase → kebab-case)
  if (isStyleBlock(obj)) {
    const kebabName = camelToKebab(expr.property);
    const value = obj.properties[kebabName] ?? obj.properties[expr.property];
    if (value === undefined) {
      // The `marker` shorthand is stored as its three longhands (ISSUE-031);
      // read it back the way CSS serializes a shorthand — only when they agree.
      if (kebabName === 'marker') {
        const start = obj.properties['marker-start'];
        if (start !== undefined && obj.properties['marker-mid'] === start && obj.properties['marker-end'] === start) {
          return start;
        }
      }
      throw new Error(`Property '${expr.property}' does not exist on style block`);
    }
    return value;
  }

  // Handle LayerReference property access
  if (isLayerReference(obj)) {
    const layerRef = obj;
    if (expr.property === 'ctx') {
      if (layerRef.layer.layerType === 'GroupLayer') {
        const groupLayer = layerRef.layer;
        return { type: 'ContextObject' as const, value: { _transformState: groupLayer.transformState } };
      }
      if (layerRef.layer.layerType !== 'PathLayer') {
        throw new Error(`Property 'ctx' is only available on PathLayer and GroupLayer references`);
      }
      const pathLayer = layerRef.layer;
      return {
        type: 'ContextObject' as const,
        value: contextToObject(pathLayer.pathContext, pathLayer.transformState),
      };
    }
    if (expr.property === 'name') {
      return layerRef.layer.name;
    }
    if (expr.property === 'styles') {
      return { type: 'StyleBlockValue' as const, properties: { ...layerRef.layer.styles } };
    }
    throw new Error(`Property '${expr.property}' does not exist on layer reference`);
  }

  // Handle ObjectValue property access (dot notation)
  if (isObjectValue(obj)) {
    if (expr.property === 'length') return obj.properties.size;
    return obj.properties.get(expr.property) ?? null;
  }

  // Handle CyclerValue property access
  if (isCyclerValue(obj)) {
    if (expr.property === 'length') return obj.elements.length;
    throw new Error(`Property '${expr.property}' does not exist on Cycler`);
  }

  // Handle ArrayValue property access
  if (isArrayValue(obj)) {
    if (expr.property === 'length') {
      return obj.elements.length;
    }
    if (expr.property === 'first') {
      return obj.elements.length === 0 ? null : obj.elements[0];
    }
    if (expr.property === 'last') {
      return obj.elements.length === 0 ? null : obj.elements[obj.elements.length - 1];
    }
    throw new Error(`Property '${expr.property}' does not exist on array. Use methods like .push(), .pop(), etc.`);
  }

  // Handle string property access
  if (typeof obj === 'string') {
    if (expr.property === 'length') {
      return obj.length;
    }
    throw new Error(`Property '${expr.property}' does not exist on string`);
  }

  throw new Error(`Cannot access property '${expr.property}' on non-object value`);
}

const STATEMENT_BUILTIN_SET: ReadonlySet<string> = new Set(STATEMENT_BUILTINS);

function isStatementBuiltinName(name: string): name is (typeof STATEMENT_BUILTINS)[number] {
  return STATEMENT_BUILTIN_SET.has(name);
}

const STATEMENT_BUILTIN_VALUE_ERROR: Record<(typeof STATEMENT_BUILTINS)[number], string> = {
  log: 'log() records a message and has no value — use ln(x) for the natural logarithm',
  assert: 'assert() is a statement and has no value',
};

/** A `PathCommand` statement that is really a bare `log(...)` / `assert(...)` call. */
function asStatementBuiltinCall(stmt: Statement): FunctionCall | null {
  if (stmt.type !== 'PathCommand' || stmt.command !== '' || stmt.args.length !== 1) return null;
  const arg = stmt.args[0];
  return arg.type === 'FunctionCall' && isStatementBuiltinName(arg.name) ? arg : null;
}

/**
 * Run a statement builtin. `log(...)` records its arguments as a log entry;
 * `assert(cond, message?)` stops compilation with a positioned error when
 * the condition is falsy. Neither produces a value or path output.
 */
function evaluateStatementBuiltin(call: FunctionCall, scope: Scope): void {
  if (!scope.evalState) return;
  if (call.name === 'assert') {
    if (call.args.length < 1 || call.args.length > 2) {
      throw new Error(
        formatError(`assert() expects 1 or 2 arguments, got ${call.args.length}`, getLine(call), getCol(call)),
      );
    }
    const condition = evaluateExpression(call.args[0], scope);
    if (isTruthy(condition)) return;
    let message: string;
    if (call.args.length === 2) {
      const raw = evaluateExpression(call.args[1], scope);
      message = typeof raw === 'string' ? raw : formatValueForDisplay(raw);
    } else {
      // The condition's own source text, minus one pair of outer parentheses
      // that expressionToSource adds around a binary expression.
      message = expressionToSource(call.args[0]).replace(/^\((.*)\)$/s, '$1');
    }
    throw new Error(formatError(`assertion failed: ${message}`, getLine(call), getCol(call)));
  }
  // Debug log handling
  const lineNumber = call.loc?.line ?? null;
  const parts: LogPart[] = [];

  for (const arg of call.args) {
    const value = evaluateExpression(arg, scope);

    // String literals are displayed directly without a label
    if (arg.type === 'StringLiteral') {
      parts.push({
        type: 'string',
        value: arg.value,
      });
    } else {
      // Non-string expressions get a label showing what was logged
      const label = expressionToSource(arg);
      let stringValue: string;

      if (value === null) {
        stringValue = 'null';
      } else if (isBooleanValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isAngleValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isPointValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isPolarVectorValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (typeof value === 'object' && 'type' in value && value.type === 'ViewBoxStructValue') {
        stringValue = formatValueForDisplay(value);
      } else if (isObjectValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isArrayValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isPathBlockValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isProjectedPathValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (
        isEndpointValue(value) ||
        isCommandValue(value) ||
        isCallValue(value) ||
        isSegmentValue(value) ||
        isSubpathValue(value) ||
        isSubscriptionValue(value)
      ) {
        stringValue = formatValueForDisplay(value);
      } else if (isCyclerValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isMaskValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isClipPathValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isPatternValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isMarkerValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isGradientValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isColorValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isCSSVarValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isTextBlockValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (isProjectedTextValue(value)) {
        stringValue = formatValueForDisplay(value);
      } else if (typeof value === 'object' && value !== null && 'type' in value) {
        const typed = value as { type: string; value?: unknown };
        if (typed.type === 'ContextObject' && typed.value) {
          stringValue = JSON.stringify(typed.value, null, 2);
        } else if (typed.type === 'PathSegment') {
          stringValue = (typed as PathSegment).value;
        } else if (typed.type === 'UserFunction') {
          stringValue = formatValueForDisplay(value);
        } else {
          stringValue = String(value);
        }
      } else if (isStdlibFunctionValue(value)) {
        // `log(PI)`: its stdlib name, never the function's JavaScript source.
        stringValue = formatStdlibFunction(value);
      } else if (typeof value === 'number') {
        stringValue = String(value);
      } else if (typeof value === 'string') {
        stringValue = value;
      } else {
        stringValue = String(value);
      }

      parts.push({
        type: 'value',
        label,
        value: stringValue,
      });
    }
  }

  scope.evalState.logs.push({ line: lineNumber, parts });
}

function evaluateFunctionCall(call: FunctionCall, scope: Scope): Value {
  // log() / assert() are statements: they only run from statement position
  // (see evaluateStatementBuiltin). Reaching one here means it was used as a
  // value — `let y = log(3);`, `calc(log(x))` — which used to silently pick
  // the natural logarithm for number-shaped arguments and leak the result.
  if (isStatementBuiltinName(call.name) && scope.evalState) {
    throw new Error(formatError(STATEMENT_BUILTIN_VALUE_ERROR[call.name], getLine(call), getCol(call)));
  }

  // Handle layer() function — returns a LayerReference
  if (call.name === 'layer' && scope.evalState) {
    if (call.args.length !== 1) {
      throw new Error(`layer() expects 1 argument, got ${call.args.length}`);
    }
    const nameValue = evaluateExpression(call.args[0], scope);
    if (typeof nameValue !== 'string') {
      throw new Error('Layer name must be a string');
    }
    const layerState = scope.evalState.layers.get(nameValue);
    if (!layerState) {
      throw new Error(`Undefined layer: '${nameValue}'`);
    }
    return { type: 'LayerReference' as const, layer: layerState };
  }

  // Handle Point() constructor
  if (call.name === 'Point') {
    if (call.args.length !== 2) {
      throw new Error(`Point() expects 2 arguments, got ${call.args.length}`);
    }
    const x = evaluateExpression(call.args[0], scope);
    const y = evaluateExpression(call.args[1], scope);
    if (typeof x !== 'number') throw new Error('Point() x must be a number');
    if (typeof y !== 'number') throw new Error('Point() y must be a number');
    return { type: 'PointValue' as const, x, y };
  }

  // Handle PolarVector() constructor
  if (call.name === 'PolarVector') {
    if (call.args.length !== 2) {
      throw new Error(`PolarVector() expects 2 arguments, got ${call.args.length}`);
    }
    // After the arity guard, not before: a wrong argument count is the more
    // fundamental mistake, the same ordering the stdlib and context-aware
    // branches use. (Color needs no check here — its hue is a degrees slot,
    // which the rule exempts.)
    for (const message of describeMissingAngleUnits('PolarVector', 'PolarVector()', call.args)) {
      throw new Error(formatError(message, getLine(call), getCol(call)));
    }
    const angle = toNumber(evaluateExpression(call.args[0], scope));
    const distance = evaluateExpression(call.args[1], scope);
    if (angle === undefined) throw new Error('PolarVector() angle must be a number');
    if (typeof distance !== 'number') throw new Error('PolarVector() distance must be a number');
    return { type: 'PolarVectorValue' as const, angle, distance };
  }

  // Handle Cycler() constructor
  if (call.name === 'Cycler') {
    if (call.args.length < 1 || call.args.length > 2) {
      throw new Error(`Cycler() expects 1-2 arguments, got ${call.args.length}`);
    }
    const list = evaluateExpression(call.args[0], scope);
    if (!isArrayValue(list)) throw new Error('Cycler() first argument must be an array');
    if (list.elements.length === 0) throw new Error('Cycler() array must not be empty');

    const elements = [...list.elements]; // shallow copy

    if (call.args.length === 2) {
      const shuffle = evaluateExpression(call.args[1], scope);
      if (shuffle) {
        // Fisher-Yates shuffle
        for (let i = elements.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [elements[i], elements[j]] = [elements[j], elements[i]];
        }
      }
    }

    return { type: 'CyclerValue' as const, elements, index: 0 };
  }

  // Handle SVGDocumentFragment() constructor
  if (call.name === 'SVGDocumentFragment') {
    if (call.args.length !== 1) {
      throw new Error(`SVGDocumentFragment() expects 1 argument, got ${call.args.length}`);
    }
    const arg = evaluateExpression(call.args[0], scope);
    if (typeof arg !== 'string') {
      throw new Error('SVGDocumentFragment() argument must be a string');
    }
    const result = sanitizeSVGFragment(arg);
    return {
      type: 'SVGFragmentValue' as const,
      defsContent: result.defsContent,
      visualContent: result.visualContent,
      rawContent: result.rawContent,
    };
  }

  // Handle Mask() constructor
  if (call.name === 'Mask') {
    if (call.args.length !== 1) {
      throw new Error(`Mask() expects 1 argument (id), got ${call.args.length}`);
    }
    if (!scope.evalState) throw new Error('Mask() requires evaluation context');
    const id = evaluateExpression(call.args[0], scope);
    if (typeof id !== 'string') throw new Error('Mask() argument must be a string');
    try { validateCSSIdent(id, 'mask-id'); } catch (e) { throw new Error(formatError((e as Error).message, getLine(call), getCol(call))); }
    if (
      scope.evalState.masks.has(id) ||
      scope.evalState.clipPaths.has(id) ||
      scope.evalState.gradients.has(id) ||
      scope.evalState.patterns.has(id) ||
      scope.evalState.markers.has(id) ||
      scope.evalState.filters.has(id)
    ) {
      throw new Error(`Duplicate defs ID '${id}': a Mask, ClipPath, Gradient, Pattern, Marker, or Filter with this ID already exists`);
    }
    const mask: MaskValue = { type: 'MaskValue', id, paths: [] };
    scope.evalState.masks.set(id, mask);
    return mask;
  }

  // Handle ClipPath() constructor
  if (call.name === 'ClipPath') {
    if (call.args.length !== 1) {
      throw new Error(`ClipPath() expects 1 argument (id), got ${call.args.length}`);
    }
    if (!scope.evalState) throw new Error('ClipPath() requires evaluation context');
    const id = evaluateExpression(call.args[0], scope);
    if (typeof id !== 'string') throw new Error('ClipPath() argument must be a string');
    try { validateCSSIdent(id, 'clippath-id'); } catch (e) { throw new Error(formatError((e as Error).message, getLine(call), getCol(call))); }
    if (
      scope.evalState.masks.has(id) ||
      scope.evalState.clipPaths.has(id) ||
      scope.evalState.gradients.has(id) ||
      scope.evalState.patterns.has(id) ||
      scope.evalState.markers.has(id) ||
      scope.evalState.filters.has(id)
    ) {
      throw new Error(`Duplicate defs ID '${id}': a Mask, ClipPath, Gradient, Pattern, Marker, or Filter with this ID already exists`);
    }
    const clipPath: ClipPathValue = { type: 'ClipPathValue', id, paths: [] };
    scope.evalState.clipPaths.set(id, clipPath);
    return clipPath;
  }

  // Handle LinearGradient() constructor
  if (call.name === 'LinearGradient') {
    if (call.args.length !== 5) {
      throw new Error(
        formatError(
          `LinearGradient() expects 5 arguments (id, x1, y1, x2, y2), got ${call.args.length}`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('LinearGradient() requires evaluation context');
    const id = evaluateExpression(call.args[0], scope);
    if (typeof id !== 'string')
      throw new Error(formatError('LinearGradient() first argument must be a string', getLine(call), getCol(call)));
    try { validateCSSIdent(id, 'gradient-id'); } catch (e) { throw new Error(formatError((e as Error).message, getLine(call), getCol(call))); }
    const x1 = evaluateExpression(call.args[1], scope);
    const y1 = evaluateExpression(call.args[2], scope);
    const x2 = evaluateExpression(call.args[3], scope);
    const y2 = evaluateExpression(call.args[4], scope);
    if (typeof x1 !== 'number' || typeof y1 !== 'number' || typeof x2 !== 'number' || typeof y2 !== 'number') {
      throw new Error(
        formatError('LinearGradient() coordinate arguments must be numbers', getLine(call), getCol(call)),
      );
    }
    if (
      scope.evalState.masks.has(id) ||
      scope.evalState.clipPaths.has(id) ||
      scope.evalState.gradients.has(id) ||
      scope.evalState.patterns.has(id) ||
      scope.evalState.markers.has(id) ||
      scope.evalState.filters.has(id)
    ) {
      throw new Error(`Duplicate defs ID '${id}': a Mask, ClipPath, Gradient, Pattern, Marker, or Filter with this ID already exists`);
    }
    const gradient: GradientValue = {
      type: 'GradientValue',
      gradientType: 'linear',
      id,
      attrs: { x1: String(x1), y1: String(y1), x2: String(x2), y2: String(y2) },
      stops: [],
    };
    scope.evalState.gradients.set(id, gradient);
    // Execute trailing block if present
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], gradient);
      runBuilderBlock(call.block.body, blockScope);
    }
    return gradient;
  }

  // Handle RadialGradient() constructor
  if (call.name === 'RadialGradient') {
    if (call.args.length < 4 || call.args.length > 6) {
      throw new Error(
        formatError(
          `RadialGradient() expects 4-6 arguments (id, cx, cy, r [, fx, fy]), got ${call.args.length}`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('RadialGradient() requires evaluation context');
    const id = evaluateExpression(call.args[0], scope);
    if (typeof id !== 'string')
      throw new Error(formatError('RadialGradient() first argument must be a string', getLine(call), getCol(call)));
    try { validateCSSIdent(id, 'gradient-id'); } catch (e) { throw new Error(formatError((e as Error).message, getLine(call), getCol(call))); }
    const cx = evaluateExpression(call.args[1], scope);
    const cy = evaluateExpression(call.args[2], scope);
    const r = evaluateExpression(call.args[3], scope);
    if (typeof cx !== 'number' || typeof cy !== 'number' || typeof r !== 'number') {
      throw new Error(
        formatError('RadialGradient() coordinate arguments must be numbers', getLine(call), getCol(call)),
      );
    }
    const attrs: Record<string, string> = { cx: String(cx), cy: String(cy), r: String(r) };
    if (call.args.length >= 5) {
      const fx = evaluateExpression(call.args[4], scope);
      if (typeof fx !== 'number')
        throw new Error(formatError('RadialGradient() fx must be a number', getLine(call), getCol(call)));
      attrs.fx = String(fx);
    }
    if (call.args.length === 6) {
      const fy = evaluateExpression(call.args[5], scope);
      if (typeof fy !== 'number')
        throw new Error(formatError('RadialGradient() fy must be a number', getLine(call), getCol(call)));
      attrs.fy = String(fy);
    }
    if (
      scope.evalState.masks.has(id) ||
      scope.evalState.clipPaths.has(id) ||
      scope.evalState.gradients.has(id) ||
      scope.evalState.patterns.has(id) ||
      scope.evalState.markers.has(id) ||
      scope.evalState.filters.has(id)
    ) {
      throw new Error(`Duplicate defs ID '${id}': a Mask, ClipPath, Gradient, Pattern, Marker, or Filter with this ID already exists`);
    }
    const gradient: GradientValue = {
      type: 'GradientValue',
      gradientType: 'radial',
      id,
      attrs,
      stops: [],
    };
    scope.evalState.gradients.set(id, gradient);
    // Execute trailing block if present
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], gradient);
      runBuilderBlock(call.block.body, blockScope);
    }
    return gradient;
  }

  // Handle Pattern() constructor
  if (call.name === 'Pattern') {
    if (call.args.length !== 5) {
      throw new Error(
        formatError(
          `Pattern() expects 5 arguments (id, x, y, width, height), got ${call.args.length}`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('Pattern() requires evaluation context');
    const id = evaluateExpression(call.args[0], scope);
    if (typeof id !== 'string')
      throw new Error(formatError('Pattern() first argument must be a string', getLine(call), getCol(call)));
    try { validateCSSIdent(id, 'pattern-id'); } catch (e) { throw new Error(formatError((e as Error).message, getLine(call), getCol(call))); }
    const x = evaluateExpression(call.args[1], scope);
    const y = evaluateExpression(call.args[2], scope);
    const w = evaluateExpression(call.args[3], scope);
    const h = evaluateExpression(call.args[4], scope);
    if (typeof x !== 'number' || typeof y !== 'number' || typeof w !== 'number' || typeof h !== 'number') {
      throw new Error(formatError('Pattern() coordinate arguments must be numbers', getLine(call), getCol(call)));
    }
    if (
      scope.evalState.masks.has(id) ||
      scope.evalState.clipPaths.has(id) ||
      scope.evalState.gradients.has(id) ||
      scope.evalState.patterns.has(id) ||
      scope.evalState.markers.has(id) ||
      scope.evalState.filters.has(id)
    ) {
      throw new Error(`Duplicate defs ID '${id}': a Mask, ClipPath, Gradient, Pattern, Marker, or Filter with this ID already exists`);
    }
    const pattern: PatternValue = {
      type: 'PatternValue',
      id,
      x,
      y,
      width: w,
      height: h,
      paths: [],
    };
    scope.evalState.patterns.set(id, pattern);
    // Execute trailing block if present
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], pattern);
      runBuilderBlock(call.block.body, blockScope);
    }
    return pattern;
  }

  // Handle Marker() constructor
  if (call.name === 'Marker') {
    if (call.args.length !== 3) {
      throw new Error(
        formatError(
          `Marker() expects 3 arguments (id, markerWidth, markerHeight), got ${call.args.length}`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('Marker() requires evaluation context');
    const id = evaluateExpression(call.args[0], scope);
    if (typeof id !== 'string')
      throw new Error(formatError('Marker() first argument must be a string', getLine(call), getCol(call)));
    try { validateCSSIdent(id, 'marker-id'); } catch (e) { throw new Error(formatError((e as Error).message, getLine(call), getCol(call))); }
    const markerWidth = evaluateExpression(call.args[1], scope);
    const markerHeight = evaluateExpression(call.args[2], scope);
    if (typeof markerWidth !== 'number' || typeof markerHeight !== 'number') {
      throw new Error(
        formatError('Marker() markerWidth and markerHeight arguments must be numbers', getLine(call), getCol(call)),
      );
    }
    if (
      scope.evalState.masks.has(id) ||
      scope.evalState.clipPaths.has(id) ||
      scope.evalState.gradients.has(id) ||
      scope.evalState.patterns.has(id) ||
      scope.evalState.markers.has(id) ||
      scope.evalState.filters.has(id)
    ) {
      throw new Error(`Duplicate defs ID '${id}': a Mask, ClipPath, Gradient, Pattern, Marker, or Filter with this ID already exists`);
    }
    const marker: MarkerValue = {
      type: 'MarkerValue',
      id,
      viewBox: `0 0 ${markerWidth} ${markerHeight}`,
      markerWidth,
      markerHeight,
      refX: markerWidth / 2,
      refY: markerHeight / 2,
      markerUnits: 'strokeWidth',
      orient: 'auto',
      preserveAspectRatio: 'xMidYMid meet',
      paths: [],
    };
    scope.evalState.markers.set(id, marker);
    // Execute trailing block if present
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], marker);
      runBuilderBlock(call.block.body, blockScope);
    }
    return marker;
  }

  // Handle Grid() constructor: Grid(rows, cols, options) {|g| ... }
  if (call.name === 'Grid') {
    if (call.args.length < 2 || call.args.length > 3) {
      throw new Error(
        formatError(
          `Grid() expects 2 or 3 arguments (rows, cols, options?), got ${call.args.length}`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    const rowsVal = evaluateExpression(call.args[0], scope);
    const colsVal = evaluateExpression(call.args[1], scope);
    if (typeof rowsVal !== 'number' || !Number.isInteger(rowsVal) || rowsVal <= 0) {
      throw new Error(formatError('Grid() rows must be a positive integer', getLine(call), getCol(call)));
    }
    if (typeof colsVal !== 'number' || !Number.isInteger(colsVal) || colsVal <= 0) {
      throw new Error(formatError('Grid() cols must be a positive integer', getLine(call), getCol(call)));
    }

    let xDim = 1;
    let yDim = 1;
    let origin: PointValue = { type: 'PointValue', x: 0, y: 0 };
    let defaultValue: Value = null;
    let outOfBounds: GridOutOfBoundsMode = 'clamp';
    let interpolation: GridInterpolationMode = 'nearest';

    if (call.args.length === 3) {
      const optsVal = evaluateExpression(call.args[2], scope);
      if (!isObjectValue(optsVal)) {
        throw new Error(formatError('Grid() options (3rd arg) must be an object literal', getLine(call), getCol(call)));
      }
      const opts = optsVal.properties;
      if (opts.has('xDim')) {
        const v = opts.get('xDim');
        if (typeof v !== 'number' || v <= 0) throw new Error(formatError('Grid() options.xDim must be a positive number', getLine(call), getCol(call)));
        xDim = v;
      }
      if (opts.has('yDim')) {
        const v = opts.get('yDim');
        if (typeof v !== 'number' || v <= 0) throw new Error(formatError('Grid() options.yDim must be a positive number', getLine(call), getCol(call)));
        yDim = v;
      }
      if (opts.has('origin')) {
        const v = opts.get('origin');
        if (!isPointValue(v as Value)) throw new Error(formatError('Grid() options.origin must be a Point', getLine(call), getCol(call)));
        origin = v as PointValue;
      }
      if (opts.has('defaultValue')) {
        defaultValue = opts.get('defaultValue') as Value;
      }
      if (opts.has('outOfBounds')) {
        const v = opts.get('outOfBounds');
        if (v !== 'clamp' && v !== 'wrap' && v !== 'null') {
          throw new Error(formatError(`Grid() options.outOfBounds must be 'clamp', 'wrap', or 'null'`, getLine(call), getCol(call)));
        }
        outOfBounds = v as GridOutOfBoundsMode;
      }
      if (opts.has('interpolation')) {
        const v = opts.get('interpolation');
        if (v !== 'nearest' && v !== 'bilinear') {
          throw new Error(formatError(`Grid() options.interpolation must be 'nearest' or 'bilinear'`, getLine(call), getCol(call)));
        }
        interpolation = v as GridInterpolationMode;
      }
    }

    const cells: Value[][] = [];
    for (let r = 0; r < rowsVal; r++) {
      const row: Value[] = [];
      for (let c = 0; c < colsVal; c++) row.push(defaultValue);
      cells.push(row);
    }

    const grid: GridValue = {
      type: 'GridValue',
      rows: rowsVal,
      cols: colsVal,
      xDim,
      yDim,
      origin,
      outOfBounds,
      interpolation,
      cells,
    };

    if (call.block) {
      if (call.block.params.length < 1) {
        throw new Error(formatError('Grid() trailing block requires a parameter binding, e.g. {|g| ... }', getLine(call), getCol(call)));
      }
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], grid);
      runBuilderBlock(call.block.body, blockScope);
    }
    return grid;
  }

  // Handle NoiseFilter() constructor
  if (call.name === 'NoiseFilter') {
    if (call.args.length !== 0) {
      throw new Error(
        formatError(
          `NoiseFilter() takes no positional arguments — configure via the trailing block`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('NoiseFilter() requires evaluation context');
    const id = nextAutoFilterId(scope.evalState, 'noise');
    const filter: NoiseFilterValue = makeDefaultNoiseFilter(id, 'grain');
    scope.evalState.filters.set(id, filter);
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], filter);
      runBuilderBlock(call.block.body, blockScope);
    }
    return filter;
  }

  // Handle GlowFilter() constructor
  if (call.name === 'GlowFilter') {
    if (call.args.length !== 0) {
      throw new Error(
        formatError(
          `GlowFilter() takes no positional arguments — configure via the trailing block`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('GlowFilter() requires evaluation context');
    const id = nextAutoFilterId(scope.evalState, 'glow');
    const filter: GlowFilterValue = makeDefaultGlowFilter(id);
    scope.evalState.filters.set(id, filter);
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], filter);
      runBuilderBlock(call.block.body, blockScope);
    }
    return filter;
  }

  // Handle EmbossFilter() constructor
  if (call.name === 'EmbossFilter') {
    if (call.args.length !== 0) {
      throw new Error(
        formatError(
          `EmbossFilter() takes no positional arguments — configure via the trailing block`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('EmbossFilter() requires evaluation context');
    const id = nextAutoFilterId(scope.evalState, 'emboss');
    const filter: EmbossFilterValue = makeDefaultEmbossFilter(id);
    scope.evalState.filters.set(id, filter);
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], filter);
      runBuilderBlock(call.block.body, blockScope);
    }
    return filter;
  }

  // Handle ElevationShadowFilter() constructor
  if (call.name === 'ElevationShadowFilter') {
    if (call.args.length !== 0) {
      throw new Error(
        formatError(
          `ElevationShadowFilter() takes no positional arguments — configure via the trailing block`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('ElevationShadowFilter() requires evaluation context');
    const id = nextAutoFilterId(scope.evalState, 'elevation-shadow');
    const filter: ElevationShadowFilterValue = makeDefaultElevationShadowFilter(id);
    scope.evalState.filters.set(id, filter);
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], filter);
      runBuilderBlock(call.block.body, blockScope);
    }
    return filter;
  }

  // Handle InnerShadowFilter() constructor
  if (call.name === 'InnerShadowFilter') {
    if (call.args.length !== 0) {
      throw new Error(
        formatError(
          `InnerShadowFilter() takes no positional arguments — configure via the trailing block`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('InnerShadowFilter() requires evaluation context');
    const id = nextAutoFilterId(scope.evalState, 'inner-shadow');
    const filter: InnerShadowFilterValue = makeDefaultInnerShadowFilter(id);
    scope.evalState.filters.set(id, filter);
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], filter);
      runBuilderBlock(call.block.body, blockScope);
    }
    return filter;
  }

  // Handle PixelateFilter() constructor — accepts 0 args (block-style) or 3 args (positional)
  if (call.name === 'PixelateFilter') {
    if (call.args.length !== 0 && call.args.length !== 3) {
      throw new Error(
        formatError(
          `PixelateFilter() expects 0 or 3 arguments (width, height, radius), got ${call.args.length}`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (call.args.length === 3 && call.block) {
      throw new Error(
        formatError(
          `PixelateFilter() cannot combine positional arguments with a trailing block`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('PixelateFilter() requires evaluation context');
    const id = nextAutoFilterId(scope.evalState, 'pixelate');
    const filter: PixelateFilterValue = makeDefaultPixelateFilter(id);
    if (call.args.length === 3) {
      const w = evaluateExpression(call.args[0], scope);
      const h = evaluateExpression(call.args[1], scope);
      const r = evaluateExpression(call.args[2], scope);
      for (const [name, v] of [['width', w], ['height', h], ['radius', r]] as const) {
        if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) {
          throw new Error(
            formatError(
              `PixelateFilter() arguments must be finite positive numbers; ${name} is ${String(v)}`,
              getLine(call),
              getCol(call),
            ),
          );
        }
      }
      filter.width = w as number;
      filter.height = h as number;
      filter.radius = r as number;
    }
    scope.evalState.filters.set(id, filter);
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], filter);
      runBuilderBlock(call.block.body, blockScope);
    }
    return filter;
  }

  // Handle MotionBlurFilter() constructor — directional/progressive blur.
  // No positional arguments; configure via the trailing block.
  if (call.name === 'MotionBlurFilter') {
    if (call.args.length !== 0) {
      throw new Error(
        formatError(
          `MotionBlurFilter() takes no positional arguments — configure via the trailing block`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('MotionBlurFilter() requires evaluation context');
    const id = nextAutoFilterId(scope.evalState, 'motion-blur');
    const filter: MotionBlurFilterValue = makeDefaultMotionBlurFilter(id);
    scope.evalState.filters.set(id, filter);
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], filter);
      runBuilderBlock(call.block.body, blockScope);
    }
    return filter;
  }

  // Handle ConicGradient() constructor
  if (call.name === 'ConicGradient') {
    if (call.args.length !== 3) {
      throw new Error(
        formatError(
          `ConicGradient() expects 3 arguments (id, cx, cy), got ${call.args.length}`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('ConicGradient() requires evaluation context');
    const id = evaluateExpression(call.args[0], scope);
    if (typeof id !== 'string')
      throw new Error(formatError('ConicGradient() first argument must be a string', getLine(call), getCol(call)));
    try { validateCSSIdent(id, 'gradient-id'); } catch (e) { throw new Error(formatError((e as Error).message, getLine(call), getCol(call))); }
    const cx = evaluateExpression(call.args[1], scope);
    const cy = evaluateExpression(call.args[2], scope);
    if (typeof cx !== 'number' || typeof cy !== 'number') {
      throw new Error(formatError('ConicGradient() coordinate arguments must be numbers', getLine(call), getCol(call)));
    }
    if (
      scope.evalState.masks.has(id) ||
      scope.evalState.clipPaths.has(id) ||
      scope.evalState.gradients.has(id) ||
      scope.evalState.patterns.has(id) ||
      scope.evalState.markers.has(id) ||
      scope.evalState.filters.has(id)
    ) {
      throw new Error(`Duplicate defs ID '${id}': a Mask, ClipPath, Gradient, Pattern, Marker, or Filter with this ID already exists`);
    }
    const gradient: GradientValue = {
      type: 'GradientValue',
      gradientType: 'conic',
      id,
      attrs: { cx: String(cx), cy: String(cy) },
      stops: [],
      from: 0,
      to: 2 * Math.PI,
      direction: 'cw',
      spread: 'clamp',
      innerRadius: 0,
      innerFill: 'transparent',
    };
    scope.evalState.gradients.set(id, gradient);
    // Execute trailing block if present
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], gradient);
      runBuilderBlock(call.block.body, blockScope);
    }
    return gradient;
  }

  // Handle MeshGradient() constructor
  if (call.name === 'MeshGradient') {
    if (call.args.length !== 5) {
      throw new Error(
        formatError(
          `MeshGradient() expects 5 arguments (id, width, height, cols, rows), got ${call.args.length}`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('MeshGradient() requires evaluation context');
    const id = evaluateExpression(call.args[0], scope);
    if (typeof id !== 'string')
      throw new Error(formatError('MeshGradient() first argument must be a string', getLine(call), getCol(call)));
    try { validateCSSIdent(id, 'gradient-id'); } catch (e) { throw new Error(formatError((e as Error).message, getLine(call), getCol(call))); }
    const width = evaluateExpression(call.args[1], scope);
    const height = evaluateExpression(call.args[2], scope);
    const cols = evaluateExpression(call.args[3], scope);
    const rows = evaluateExpression(call.args[4], scope);
    if (
      typeof width !== 'number' ||
      typeof height !== 'number' ||
      typeof cols !== 'number' ||
      typeof rows !== 'number'
    ) {
      throw new Error(
        formatError('MeshGradient() width, height, cols, rows must be numbers', getLine(call), getCol(call)),
      );
    }
    if (cols < 2 || rows < 2) {
      throw new Error(
        formatError('MeshGradient() cols and rows must be >= 2 (need at least one patch)', getLine(call), getCol(call)),
      );
    }
    if (
      scope.evalState.masks.has(id) ||
      scope.evalState.clipPaths.has(id) ||
      scope.evalState.gradients.has(id) ||
      scope.evalState.patterns.has(id) ||
      scope.evalState.markers.has(id) ||
      scope.evalState.filters.has(id)
    ) {
      throw new Error(`Duplicate defs ID '${id}': a Mask, ClipPath, Gradient, Pattern, Marker, or Filter with this ID already exists`);
    }
    // Build grid: rows × cols MeshPointValue objects, evenly spaced
    const meshGrid: MeshPointValue[][] = [];
    for (let r = 0; r < rows; r++) {
      const row: MeshPointValue[] = [];
      for (let c = 0; c < cols; c++) {
        row.push({
          type: 'MeshPointValue',
          x: (c / (cols - 1)) * width,
          y: (r / (rows - 1)) * height,
          color: { L: 0, C: 0, H: 0, alpha: 0 },
          colorCSS: 'oklch(0 0 0 / 0)',
          gridRow: r,
          gridCol: c,
        });
      }
      meshGrid.push(row);
    }
    const gradient: GradientValue = {
      type: 'GradientValue',
      gradientType: 'mesh',
      id,
      attrs: {},
      stops: [],
      meshGrid,
      meshWidth: width,
      meshHeight: height,
      meshCols: cols,
      meshRows: rows,
    };
    scope.evalState.gradients.set(id, gradient);
    // Execute trailing block if present
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], gradient);
      runBuilderBlock(call.block.body, blockScope);
    }
    return gradient;
  }

  // Handle FreeformGradient() constructor
  if (call.name === 'FreeformGradient') {
    if (call.args.length !== 3) {
      throw new Error(
        formatError(
          `FreeformGradient() expects 3 arguments (id, width, height), got ${call.args.length}`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('FreeformGradient() requires evaluation context');
    const id = evaluateExpression(call.args[0], scope);
    if (typeof id !== 'string')
      throw new Error(formatError('FreeformGradient() first argument must be a string', getLine(call), getCol(call)));
    try { validateCSSIdent(id, 'gradient-id'); } catch (e) { throw new Error(formatError((e as Error).message, getLine(call), getCol(call))); }
    const width = evaluateExpression(call.args[1], scope);
    const height = evaluateExpression(call.args[2], scope);
    if (typeof width !== 'number' || typeof height !== 'number') {
      throw new Error(formatError('FreeformGradient() width and height must be numbers', getLine(call), getCol(call)));
    }
    if (
      scope.evalState.masks.has(id) ||
      scope.evalState.clipPaths.has(id) ||
      scope.evalState.gradients.has(id) ||
      scope.evalState.patterns.has(id) ||
      scope.evalState.markers.has(id) ||
      scope.evalState.filters.has(id)
    ) {
      throw new Error(`Duplicate defs ID '${id}': a Mask, ClipPath, Gradient, Pattern, Marker, or Filter with this ID already exists`);
    }
    const gradient: GradientValue = {
      type: 'GradientValue',
      gradientType: 'freeform',
      id,
      attrs: {},
      stops: [],
      freeformPoints: [],
      freeformWidth: width,
      freeformHeight: height,
      falloff: 2.0,
    };
    scope.evalState.gradients.set(id, gradient);
    // Execute trailing block if present
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], gradient);
      runBuilderBlock(call.block.body, blockScope);
    }
    return gradient;
  }

  // Handle TopoGradient() constructor
  if (call.name === 'TopoGradient') {
    if (call.args.length !== 3) {
      throw new Error(
        formatError(
          `TopoGradient() expects 3 arguments (id, width, height), got ${call.args.length}`,
          getLine(call),
          getCol(call),
        ),
      );
    }
    if (!scope.evalState) throw new Error('TopoGradient() requires evaluation context');
    const id = evaluateExpression(call.args[0], scope);
    if (typeof id !== 'string')
      throw new Error(formatError('TopoGradient() first argument must be a string', getLine(call), getCol(call)));
    try { validateCSSIdent(id, 'gradient-id'); } catch (e) { throw new Error(formatError((e as Error).message, getLine(call), getCol(call))); }
    const width = evaluateExpression(call.args[1], scope);
    const height = evaluateExpression(call.args[2], scope);
    if (typeof width !== 'number' || typeof height !== 'number') {
      throw new Error(formatError('TopoGradient() width and height must be numbers', getLine(call), getCol(call)));
    }
    if (
      scope.evalState.masks.has(id) ||
      scope.evalState.clipPaths.has(id) ||
      scope.evalState.gradients.has(id) ||
      scope.evalState.patterns.has(id) ||
      scope.evalState.markers.has(id) ||
      scope.evalState.filters.has(id)
    ) {
      throw new Error(`Duplicate defs ID '${id}': a Mask, ClipPath, Gradient, Pattern, Marker, or Filter with this ID already exists`);
    }
    const gradient: GradientValue = {
      type: 'GradientValue',
      gradientType: 'topo',
      id,
      attrs: {},
      stops: [],
      topoContours: [],
      topoWidth: width,
      topoHeight: height,
      topoEasing: 'linear',
      topoMethod: 'distance',
      topoIterations: 200,
      topoBlend: 1.0,
    };
    scope.evalState.gradients.set(id, gradient);
    // Execute trailing block if present
    if (call.block) {
      const blockScope = createScope(scope);
      setVariable(blockScope, call.block.params[0], gradient);
      runBuilderBlock(call.block.body, blockScope);
    }
    return gradient;
  }

  // Handle Color() constructor
  if (call.name === 'Color') {
    const cLine = getLine(call);
    const cCol = getCol(call);
    if (call.args.length === 1) {
      // String-based: Color('#ff0000'), Color('red'), Color('rgb(...)'), etc.
      const arg = evaluateExpression(call.args[0], scope);
      if (isCSSVarValue(arg)) {
        // Color(CSSVar('--name', 'fallback')) — parse fallback for Color methods, preserve var ref for style output
        if (!arg.fallback)
          throw new Error(formatError('Color(CSSVar(...)) requires a CSSVar with a fallback color', cLine, cCol));
        const oklch = parseColor(arg.fallback);
        // Collect @property declaration for CSS custom property registration (dedup: first wins)
        if (scope.evalState && !scope.evalState.cssProperties.has(arg.varName)) {
          scope.evalState.cssProperties.set(arg.varName, {
            name: arg.varName,
            syntax: '<color>',
            inherits: true,
            initialValue: oklchToCSS(oklch),
          });
        }
        return { type: 'ColorValue' as const, oklch, cssVar: { varName: arg.varName, fallback: arg.fallback } };
      }
      if (isColorValue(arg)) return arg; // Color(#cc0000) → pass-through
      if (typeof arg !== 'string')
        throw new Error(formatError('Color() with 1 argument expects a color string, CSSVar, or Color', cLine, cCol));
      return { type: 'ColorValue' as const, oklch: parseColor(arg) };
    }
    if (call.args.length === 3 || call.args.length === 4) {
      // Numeric OKLCH: Color(L, C, H) or Color(L, C, H, alpha)
      const L = evaluateExpression(call.args[0], scope);
      const C = evaluateExpression(call.args[1], scope);
      // Hue is degrees for bare numbers; an Angle value converts exactly
      const H = colorAngleDegrees(evaluateExpression(call.args[2], scope));
      if (typeof L !== 'number') throw new Error(formatError('Color() L must be a number', cLine, cCol));
      if (typeof C !== 'number') throw new Error(formatError('Color() C must be a number', cLine, cCol));
      if (H === undefined) throw new Error(formatError('Color() H must be a number', cLine, cCol));
      let alpha = 1;
      if (call.args.length === 4) {
        const a = evaluateExpression(call.args[3], scope);
        if (typeof a !== 'number') throw new Error(formatError('Color() alpha must be a number', cLine, cCol));
        alpha = a;
      }
      return { type: 'ColorValue' as const, oklch: { L, C, H, alpha } };
    }
    throw new Error(formatError(`Color() expects 1, 3, or 4 arguments, got ${call.args.length}`, cLine, cCol));
  }

  // Handle CSSVar() constructor
  if (call.name === 'CSSVar') {
    const cvLine = getLine(call);
    const cvCol = getCol(call);
    if (call.args.length < 1 || call.args.length > 2) {
      throw new Error(formatError(`CSSVar() expects 1 or 2 arguments, got ${call.args.length}`, cvLine, cvCol));
    }
    const name = evaluateExpression(call.args[0], scope);
    if (typeof name !== 'string')
      throw new Error(formatError('CSSVar() first argument must be a string', cvLine, cvCol));
    // Preserve the existing user-facing message for the common "no --" case
    // before invoking the strict ident check (which gives a more pedantic
    // error message).
    if (!name.startsWith('--'))
      throw new Error(formatError("CSSVar() variable name must start with '--'", cvLine, cvCol));
    try {
      validateCSSIdent(name, 'css-var');
    } catch (e) {
      throw new Error(formatError((e as Error).message, cvLine, cvCol));
    }
    let fallback: string | null = null;
    if (call.args.length === 2) {
      const fb = evaluateExpression(call.args[1], scope);
      if (typeof fb === 'number') {
        fallback = String(fb);
      } else if (typeof fb === 'string') {
        // Validate user-supplied string fallback against the CSS value
        // allow-list. Use a synthetic property name so URL_VALUED_PROPERTIES
        // and STRING_VALUED_PROPERTIES don't match — fallbacks are bound at
        // use-site, not at any specific property.
        try {
          validateCSSValue(fb, '__cssvar_fallback__');
        } catch (e) {
          throw new Error(formatError(`CSSVar() fallback rejected: ${(e as Error).message}`, cvLine, cvCol));
        }
        fallback = fb;
      } else if (isColorValue(fb)) {
        fallback = oklchToCSS(fb.oklch);
      } else {
        throw new Error(formatError('CSSVar() fallback must be a string, number, or Color', cvLine, cvCol));
      }
    }
    return { type: 'CSSVarValue' as const, varName: name, fallback };
  }

  // Check if it's a context-aware function
  if (contextAwareFunctions.has(call.name)) {
    if (!scope.evalState) {
      throw new Error(`Function '${call.name}' requires evaluation context`);
    }
    // Intentionally unwrap-only: context-aware functions consume angles into
    // geometry (heading() is documented plain radians) — no angle re-wrap.
    const args = call.args.map((arg) => {
      const v = evaluateExpression(arg, scope);
      return isAngleValue(v) ? v.radians : v;
    });
    // `heading(PI)`: a function named without its parentheses (function-values.ts).
    const functionArg = describeFunctionArgument(call.name, call.args, args);
    if (functionArg) throw new Error(formatError(functionArg, getLine(call), getCol(call)));
    // Every context-aware function is numeric geometry — null is never a
    // meaningful argument (polarLine used to coerce it to 0, tangentArc wrote
    // `A null null`). Checked before dispatch: these mutate the path context.
    const nullCtxArg = describeNullPathArg(call.name, call.args, args);
    if (nullCtxArg) throw new Error(formatError(nullCtxArg, getLine(call), getCol(call)));
    // NaN / Infinity is a warning, not an error (path-emit-guard.ts, severity note).
    const nonFiniteCtxArgs = describeNonFinitePathArgs(call.name, call.args, args);
    for (const message of nonFiniteCtxArgs) warn(scope.evalState, 'non-finite', message, call.loc);
    for (const message of describeMissingAngleUnits(call.name, `${call.name}()`, call.args)) {
      throw new Error(formatError(message, getLine(call), getCol(call)));
    }
    const ctxResult = evaluateContextAwareFunction(call.name, args, scope, call.loc);

    // A MISSING argument is not in `args`, so the checks above cannot see it; it
    // is `undefined` inside the case and comes out as NaN (`polarLine(0.5)` →
    // `L NaN NaN`). These are switch cases, not function objects — no arity to
    // read — so inspect what was produced instead. See describeNonFiniteEmit.
    // Only when every argument was finite: a NaN argument has already warned,
    // and the NaN it produces is expected rather than evidence of a missing one.
    // (Finite-but-degenerate inputs — zero radius, zero sweep — were measured to
    // produce finite output, so NaN here does mean a missing argument.)
    if (nonFiniteCtxArgs.length === 0) {
      const badCtxEmit = describeNonFiniteContextResult(
        call.name,
        ctxResult,
        args.length,
        scope.evalState.pathContext.lastTangent,
      );
      if (badCtxEmit) throw new Error(formatError(badCtxEmit, getLine(call), getCol(call)));
    }
    return ctxResult;
  }

  const fn = lookupVariable(scope, call.name, getLine(call), getCol(call));

  // Check if it's a stdlib function
  if (typeof fn === 'function') {
    // Track stdlib function usage
    if (scope.evalState) {
      scope.evalState.calledStdlibFunctions.add(call.name);
    }

    const stdlibArgs = call.args.map((arg) => evaluateExpression(arg, scope));

    // `sin(PI)`: a function named without its parentheses would become NaN (function-values.ts).
    const functionArg = describeFunctionArgument(call.name, call.args, stdlibArgs);
    if (functionArg) throw new Error(formatError(functionArg, getLine(call), getCol(call)));

    // A path-emitting function must never write a non-number into `d`
    // (docs/syntax.md → Null). See path-emit-guard.ts.
    const emitsPath = isPathEmittingStdlib(fn);
    let warnedNonFiniteArg = false;
    if (emitsPath) {
      // Errors: too few arguments, or a null one — always mistakes.
      const badArg =
        describeMissingPathArgs(call.name, fn, stdlibArgs.length) ??
        describeNullPathArg(call.name, call.args, stdlibArgs);
      if (badArg) throw new Error(formatError(badArg, getLine(call), getCol(call)));
      // Warnings: NaN / Infinity — the path is still emitted.
      for (const message of describeNonFinitePathArgs(call.name, call.args, stdlibArgs)) {
        warn(scope.evalState, 'non-finite', message, call.loc);
        warnedNonFiniteArg = true;
      }
    }
    for (const message of describeMissingAngleUnits(call.name, `${call.name}()`, call.args)) {
      throw new Error(formatError(message, getLine(call), getCol(call)));
    }
    let result: Value;
    try {
      result = callStdlibPreservingAngles(call.name, fn as (...ns: number[]) => unknown, stdlibArgs) as Value;
    } catch (e) {
      // Stdlib functions throw bare messages (e.g. cubicBezier handle
      // validation); attach the call site so the user can find it.
      throw new Error(formatError(e instanceof Error ? e.message : String(e), getLine(call), getCol(call)));
    }

    // Backstop on what was emitted: a NaN the argument check could not see (one
    // buried in a structured argument — a point handed to cubicSpline). Skipped
    // when an argument already warned, so one bad value is one warning. Outside
    // the try: under strict mode warn() throws an already-formatted error.
    if (
      emitsPath &&
      !warnedNonFiniteArg &&
      typeof result === 'object' &&
      result !== null &&
      (result as PathSegment).type === 'PathSegment'
    ) {
      const badEmit = describeNonFiniteEmit(call.name, (result as PathSegment).value);
      if (badEmit) warn(scope.evalState, 'non-finite', badEmit, call.loc);
    }

    // If stdlib function returns a PathSegment, track its commands
    if (typeof result === 'object' && result !== null && 'type' in result) {
      const typed = result as PathSegment;
      if (typed.type === 'PathSegment' && typed.value && scope.evalState) {
        typed.commands = parseAndTrackPathString(typed.value, scope);
      }
    }

    return result;
  }

  // Check if it's a user-defined function
  if (isCallableValue(fn)) {
    const userFn = fn;
    const args = call.args.map((arg) => evaluateExpression(arg, scope));

    if (args.length !== userFn.params.length) {
      throw new Error(
        formatError(
          `${userFn.isLambda ? 'Lambda' : 'Function'} ${call.name} expects ${userFn.params.length} arguments, got ${args.length}`,
          getLine(call),
          getCol(call),
        ),
      );
    }

    // Free names resolve where the fn or lambda was written (lexical); the
    // body draws with the caller's pen (callScope).
    const fnScope = callScope(userFn.closure, scope, calledFnFor(userFn, false));
    userFn.params.forEach((param, i) => {
      setVariable(fnScope, param, args[i]);
    });

    try {
      const result = evaluateStatements(userFn.body, fnScope);
      // Return as PathSegment if it looks like a path (contains path-like content)
      if (result) {
        return { type: 'PathSegment' as const, value: result };
      }
      return result;
    } catch (e) {
      // Catch ReturnSignal and return its value
      if (e instanceof ReturnSignal) {
        return e.value;
      }
      throw e;
    }
  }

  throw new Error(formatError(`${call.name} is not a function`, getLine(call), getCol(call)));
}

/**
 * Evaluate and validate a range's bounds — shared by EVERY for-loop site (the
 * statement loop, the `&{ }` text-block loop, the `text() { }` body loop) and
 * the range value `(a..b)`, so all of them read bounds the same way (start,
 * then end; Angles and booleans through toNumber), reject the same inputs and
 * enforce the same limit. The wording differs per `kind` (RANGE_MESSAGES); the
 * rules do not. A new loop site must call this rather than planRange directly.
 * `a..b` includes both ends; `a..<b` stops before b (range-loop.ts).
 */
function resolveRange(
  startExpr: Expression,
  endExpr: Expression,
  inclusive: boolean,
  scope: Scope,
  kind: RangeKind,
  line: number | undefined,
): { start: number; plan: RangePlan } {
  const messages = RANGE_MESSAGES[kind];
  const start = toNumber(evaluateExpression(startExpr, scope));
  const end = toNumber(evaluateExpression(endExpr, scope));
  if (start === undefined || end === undefined) {
    throw new Error(formatError(messages.numeric, line));
  }
  // Guard against infinite loops
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    throw new Error(formatError(messages.finite, line));
  }
  const plan = planRange(start, end, inclusive);
  if (plan.iterations > MAX_ITERATIONS) {
    throw new Error(formatError(messages.cap(plan.iterations, MAX_ITERATIONS), line));
  }
  return { start, plan };
}

function isCallableValue(v: Value): v is UserFunction {
  return typeof v === 'object' && v !== null && 'type' in v && v.type === 'UserFunction';
}

// The callback for a block-consuming builtin: the literal trailing block, or a
// UserFunction value (lambda or named fn) applied with `<<` (the workerExpr,
// passed unevaluated from the BinaryExpression pre-dispatch). A worker's
// `closure` makes the callback body resolve free names where it was written;
// a literal block has none and resolves in the caller's scope, which is where
// it is written. `calledFn` marks a named-fn worker for the miss hint.
// Parenthesized args are evaluated here ONCE, left-to-right, BEFORE the worker
// expression (reduce's init runs before the worker); `leadingArgs` holds the
// evaluated values for the builtin, which must consume them from here rather
// than re-evaluating expr.args. The old argument form (map(f)) is gone: a
// callable in the parentheses is just an arg, and the builtin's own arity
// error points the user at `<<`.
function resolveCallbackBlock(
  expr: { block?: { params: string[]; body: Statement[] }; args: Expression[]; method: string },
  scope: Scope,
  workerExpr?: Expression,
): {
  params: string[];
  body: Statement[];
  closure?: Scope;
  calledFn?: CalledFn;
  leadingArgs: Value[];
  extraArgs: number;
} | null {
  if (expr.block) {
    // Pre-dispatch never routes a worker to a block-bearing call.
    const leadingArgs = expr.args.map((a) => evaluateExpression(a, scope));
    return { params: expr.block.params, body: expr.block.body, leadingArgs, extraArgs: leadingArgs.length };
  }
  if (workerExpr) {
    const leadingArgs = expr.args.map((a) => evaluateExpression(a, scope));
    const worker = evaluateExpression(workerExpr, scope);
    if (!isCallableValue(worker)) {
      throw new Error(
        formatError(
          `${expr.method}() << expects a function or lambda on the right side`,
          getLine(workerExpr),
        ),
      );
    }
    return {
      params: worker.params,
      body: worker.body,
      closure: worker.closure,
      calledFn: calledFnFor(worker, true),
      leadingArgs,
      extraArgs: leadingArgs.length,
    };
  }
  return null;
}

/**
 * Format a COMPUTED path argument. NaN / Infinity is still written — five tests
 * observe documented math contracts through `M calc(…) 0`, and degenerate math
 * should cost the rest of that layer's path, not the drawing — but no longer silently: it warns
 * (`non-finite`), and strict mode makes that an error. Literals skip this; a
 * number literal is finite by construction.
 */
/**
 * Flatten a path argument to a number. In the SVG arc rotation slot an Angle
 * becomes DEGREES, because that slot is degrees by spec; everywhere else
 * radians are the internal standard, which is what toNumber already yields.
 */
function pathArgToNumber(value: Value, degreesSlot: boolean): number | undefined {
  if (degreesSlot && isAngleValue(value)) {
    return radiansToDegreesSnapped(value.radians);
  }
  return toNumber(value);
}

/**
 * A rotation argument: a finite number of radians, or an Angle.
 *
 * Deliberately NOT toNumber, which also unwraps a BooleanValue — `true` is not
 * a rotation. Returns undefined for anything else so each caller can raise its
 * own worded error.
 */
function rotationRadians(v: Value): number | undefined {
  const n = isAngleValue(v) ? v.radians : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : undefined;
}

/**
 * The rotation slot of a corner operation. Only `ellipticalFillet`'s third
 * argument is an angle; a fillet radius and the rx/ry pair are lengths, and an
 * Angle there stays an error.
 */
function isCornerOpRotation(kind: string, index: number): boolean {
  return kind === 'ellipticalFillet' && index === 2;
}

/** A corner-operation argument: the rotation slot takes an Angle, lengths do not. */
function cornerOpArg(v: Value, kind: string, index: number): number | undefined {
  if (isCornerOpRotation(kind, index)) return rotationRadians(v);
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/**
 * The `A`/`a` rotation slot, allowing for repeated argument sets in one command.
 *
 * Indexing args naively is safe because the only way one `cmd.args` entry can
 * expand to several numeric tokens is a call returning a multi-command
 * PathSegment — and that always carries a command letter, so `hasCommandLetter`
 * routes it through the reparse branch before this index is ever consulted.
 */
function isArcRotationSlot(command: string, index: number): boolean {
  return (command === 'A' || command === 'a') && index % 7 === 2;
}

/**
 * One evaluated path argument: the text it emits and, when it is a number, the
 * unrounded value the pen moves by. An argument that emits path data (a
 * PathSegment, a drawn block) or nothing (a fn that only has side effects)
 * has no `num`.
 */
interface PathArgValue {
  text: string;
  num?: number;
}

function pathArgNumber(n: number, arg: PathArg, scope: Scope): PathArgValue {
  const message = describeNonFiniteRawArg(n, arg.type === 'Identifier' ? arg.name : undefined);
  if (message) warn(scope.evalState, 'non-finite', message, { line: getLine(arg), column: getCol(arg) });
  return { text: formatNum(n), num: n };
}

/**
 * Evaluate a path argument ONCE. The text goes to the output and `num` moves
 * the pen, so both come from the same evaluation: a Cycler, random() or a fn
 * with side effects in an argument runs once per command.
 */
function evaluatePathArg(arg: PathArg, scope: Scope, degreesSlot = false): PathArgValue {
  switch (arg.type) {
    case 'NumberLiteral': {
      const n = degreesSlot ? convertUnitSuffixToDegrees(arg.value, arg.unit) : convertUnitSuffix(arg.value, arg.unit);
      return { text: formatNum(n), num: n };
    }

    case 'BooleanLiteral':
      return arg.value ? { text: '1', num: 1 } : { text: '0', num: 0 };

    case 'Identifier': {
      if (RESERVED_UNIT_NAMES.has(arg.name)) {
        throw new Error(formatError(reservedNameReferenceError(arg.name), getLine(arg), getCol(arg)));
      }
      const value = lookupVariable(scope, arg.name, getLine(arg), getCol(arg));
      if (value === null) {
        throw new Error('Cannot use null as a path argument');
      }
      const n = pathArgToNumber(value, degreesSlot);
      if (n !== undefined) {
        return pathArgNumber(n, arg, scope);
      }
      if (typeof value === 'object' && value !== null && 'type' in value && value.type === 'PathSegment') {
        return { text: value.value };
      }
      throw new Error(`Variable ${arg.name} cannot be used as path argument`);
    }

    case 'CalcExpression': {
      const value = evaluateExpression(arg.expression, scope);
      if (value === null) {
        throw new Error('Cannot use null as a path argument');
      }
      const n = pathArgToNumber(value, degreesSlot);
      if (n === undefined) {
        throw new Error('calc() must evaluate to a number');
      }
      return pathArgNumber(n, arg, scope);
    }

    case 'FunctionCall': {
      const value = evaluateFunctionCall(arg, scope);
      // Void functions (side-effect only) return undefined/null/'' — treat as empty path
      if (value === undefined || value === null || value === '') {
        return { text: '' };
      }
      const n = pathArgToNumber(value, degreesSlot);
      if (n !== undefined) {
        return pathArgNumber(n, arg, scope);
      }
      if (typeof value === 'object' && value !== null && 'type' in value) {
        if (value.type === 'PathSegment') {
          return { text: value.value };
        }
        if (value.type === 'PathWithResult') {
          // Extract path from compound result (result is stored but path is emitted)
          noteTrackedArg(scope, value);
          return { text: value.path };
        }
      }
      throw new Error(`Function ${arg.name} did not return a valid path value`);
    }

    case 'MemberExpression': {
      const value = evaluateMemberExpression(arg, scope);
      if (value === null) {
        throw new Error('Cannot use null as a path argument');
      }
      const n = pathArgToNumber(value, degreesSlot);
      if (n !== undefined) {
        return pathArgNumber(n, arg, scope);
      }
      throw new Error(`Member expression did not evaluate to a number`);
    }

    case 'IndexExpression': {
      const value = evaluateIndexExpression(arg, scope);
      if (value === null) {
        throw new Error('Cannot use null as a path argument');
      }
      const n = pathArgToNumber(value, degreesSlot);
      if (n !== undefined) {
        return pathArgNumber(n, arg, scope);
      }
      throw new Error('Index expression did not evaluate to a number');
    }

    case 'MethodCallExpression': {
      const value = evaluateMethodCall(arg, scope);
      // Void method calls (side-effect only) return undefined/null/'' — treat as empty path
      if (value === undefined || value === null || value === '') {
        return { text: '' };
      }
      const n = pathArgToNumber(value, degreesSlot);
      if (n !== undefined) {
        return pathArgNumber(n, arg, scope);
      }
      if (typeof value === 'object' && value !== null && 'type' in value) {
        if (value.type === 'PathSegment') {
          return { text: value.value };
        }
        if (value.type === 'PathWithResult') {
          noteTrackedArg(scope, value);
          return { text: value.path };
        }
      }
      throw new Error(`Method call did not return a valid path value`);
    }

    default:
      throw new Error(`Unknown path argument type: ${(arg as PathArg).type}`);
  }
}


/**
 * Resolve a BBoxAnchor string to a point within the given bounding box.
 */
function resolveAnchorPoint(
  bb: { x: number; y: number; width: number; height: number },
  anchor: string,
  mError: (msg: string) => Error,
): { x: number; y: number } {
  switch (anchor) {
    case 'top-left': return { x: bb.x, y: bb.y };
    case 'top': return { x: bb.x + bb.width / 2, y: bb.y };
    case 'top-right': return { x: bb.x + bb.width, y: bb.y };
    case 'right': return { x: bb.x + bb.width, y: bb.y + bb.height / 2 };
    case 'bottom-right': return { x: bb.x + bb.width, y: bb.y + bb.height };
    case 'bottom': return { x: bb.x + bb.width / 2, y: bb.y + bb.height };
    case 'bottom-left': return { x: bb.x, y: bb.y + bb.height };
    case 'left': return { x: bb.x, y: bb.y + bb.height / 2 };
    case 'center': return { x: bb.x + bb.width / 2, y: bb.y + bb.height / 2 };
    default: throw mError(`Invalid BBoxAnchor value: '${anchor}'. Expected one of: top-left, top, top-right, right, bottom-right, bottom, bottom-left, left, center`);
  }
}

function getActiveTextLayer(scope: Scope): TextLayerState | null {
  if (!scope.evalState?.activeLayerName) return null;
  const layer = scope.evalState.layers.get(scope.evalState.activeLayerName);
  if (layer?.layerType !== 'TextLayer') return null;
  return layer;
}

function requireNumber(value: Value, label: string): number {
  const n = toNumber(value);
  if (n === undefined) {
    throw new Error(`${label} must be a number`);
  }
  return n;
}

/**
 * Evaluate a context-aware function that needs access to path context
 */
function evaluateContextAwareFunction(
  name: string,
  args: Value[],
  scope: Scope,
  loc?: { line?: number; column?: number },
): Value {
  const ctx = scope.evalState!.pathContext;
  const inPathBlock = !!(scope.evalState as EvaluationState & { _insidePathBlock?: boolean })._insidePathBlock;

  switch (name) {
    case 'polarPoint': {
      // polarPoint(angle, distance) → ContextObject with absolute {x, y}
      const [angle, distance] = args as [number, number];
      return {
        type: 'ContextObject' as const,
        value: {
          x: ctx.position.x + Math.cos(angle) * distance,
          y: ctx.position.y + Math.sin(angle) * distance,
        },
      };
    }

    case 'polarOffset': {
      // polarOffset(angle, distance) → ContextObject with relative {dx, dy}
      const [angle, distance] = args as [number, number];
      return {
        type: 'ContextObject' as const,
        value: {
          dx: Math.cos(angle) * distance,
          dy: Math.sin(angle) * distance,
        },
      };
    }

    case 'polarMove': {
      // polarMove(angle, distance, isMoveTo?) → PathSegment
      const [angle, distance, isMoveTo = 0] = args as number[];
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance;
      const x = ctx.position.x + dx;
      const y = ctx.position.y + dy;
      const command = isMoveTo ? 'M' : 'L';

      updateContextForCommand(ctx, command, [x, y]);
      setLastTangent(ctx, angle); // Set tangent to movement direction

      if (inPathBlock) {
        return { type: 'PathSegment' as const, value: `${command.toLowerCase()} ${formatNum(dx)} ${formatNum(dy)}` };
      }
      return { type: 'PathSegment' as const, value: `${command} ${formatNum(x)} ${formatNum(y)}` };
    }

    case 'polarLine': {
      // polarLine(angle, distance) → PathSegment (always L command)
      const [angle, distance] = args as [number, number];
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance;
      const x = ctx.position.x + dx;
      const y = ctx.position.y + dy;

      updateContextForCommand(ctx, 'L', [x, y]);
      setLastTangent(ctx, angle);

      if (inPathBlock) {
        return { type: 'PathSegment' as const, value: `l ${formatNum(dx)} ${formatNum(dy)}` };
      }
      return { type: 'PathSegment' as const, value: `L ${formatNum(x)} ${formatNum(y)}` };
    }

    case 'arcFromCenter': {
      // arcFromCenter(dcx, dcy, radius, startAngle, endAngle, clockwise) → PathWithResult
      // dcx, dcy are relative offsets from current position to the arc center
      //
      // WARNING: If current position doesn't match the calculated arc start point,
      // a visible line segment (L command) will be drawn to the arc start.
      // For guaranteed continuous arcs without extra line segments, use arcFromPolarOffset.
      const [dcx, dcy, radius, startAngle, endAngle, clockwise] = args as number[];

      // Calculate absolute center from current position + offset
      const centerX = ctx.position.x + dcx;
      const centerY = ctx.position.y + dcy;

      // Calculate start/end points from center
      const startX = centerX + radius * Math.cos(startAngle);
      const startY = centerY + radius * Math.sin(startAngle);
      const endX = centerX + radius * Math.cos(endAngle);
      const endY = centerY + radius * Math.sin(endAngle);

      // Calculate arc flags
      const sweep = clockwise ? 1 : 0;
      const angleDiff = clockwise ? endAngle - startAngle : startAngle - endAngle;
      // Normalize angle difference to handle wrap-around
      const normalizedDiff = ((angleDiff % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
      const largeArc = normalizedDiff > Math.PI ? 1 : 0;

      // Tangent angle at endpoint (perpendicular to radius)
      const tangentAngle = clockwise ? endAngle + Math.PI / 2 : endAngle - Math.PI / 2;

      // Check if current position matches arc start (within tolerance)
      const tolerance = 1e-10;
      const positionMatches =
        Math.abs(ctx.position.x - startX) < tolerance && Math.abs(ctx.position.y - startY) < tolerance;

      // Generate path string - use L instead of M to keep path continuous
      let pathStr: string;
      if (inPathBlock) {
        if (positionMatches) {
          const adx = endX - ctx.position.x;
          const ady = endY - ctx.position.y;
          pathStr = `a ${formatNum(radius)} ${formatNum(radius)} 0 ${largeArc} ${sweep} ${formatNum(adx)} ${formatNum(ady)}`;
        } else {
          const ldx = startX - ctx.position.x;
          const ldy = startY - ctx.position.y;
          const adx = endX - startX;
          const ady = endY - startY;
          pathStr = `l ${formatNum(ldx)} ${formatNum(ldy)} a ${formatNum(radius)} ${formatNum(radius)} 0 ${largeArc} ${sweep} ${formatNum(adx)} ${formatNum(ady)}`;
        }
      } else if (positionMatches) {
        // Current position is at arc start - just emit arc
        pathStr = `A ${formatNum(radius)} ${formatNum(radius)} 0 ${largeArc} ${sweep} ${formatNum(endX)} ${formatNum(endY)}`;
      } else {
        // Position mismatch - draw line to arc start, then arc (keeps path continuous)
        pathStr = `L ${formatNum(startX)} ${formatNum(startY)} A ${formatNum(radius)} ${formatNum(radius)} 0 ${largeArc} ${sweep} ${formatNum(endX)} ${formatNum(endY)}`;
      }

      // Update context tracking
      const emittedCommands = parseAndTrackPathString(pathStr, scope);

      // Store tangent for tangentLine/tangentArc
      setLastTangent(ctx, tangentAngle);

      // Return both path and result info
      return {
        type: 'PathWithResult' as const,
        path: pathStr,
        commands: emittedCommands,
        result: {
          type: 'ContextObject' as const,
          value: {
            point: { x: endX, y: endY },
            angle: tangentAngle,
          },
        },
      };
    }

    case 'arcFromPolarOffset': {
      // arcFromPolarOffset(angle, radius, angleOfArc) → PathWithResult
      // Creates an arc where the center is at a polar offset from current position.
      // Current position is guaranteed to be on the circle, so only an A command is emitted.
      //
      // Parameters:
      // - angle: Direction from current position to arc center (radians)
      // - radius: Arc radius
      // - angleOfArc: Sweep angle (positive = clockwise, negative = counterclockwise)
      const [angle, radius, angleOfArc] = args as number[];

      // Center is at polar offset from current position
      const centerX = ctx.position.x + radius * Math.cos(angle);
      const centerY = ctx.position.y + radius * Math.sin(angle);

      // Current position is on circle at angle + π from center
      const startAngle = angle + Math.PI;
      const endAngle = startAngle + angleOfArc;

      // Calculate endpoint
      const endX = centerX + radius * Math.cos(endAngle);
      const endY = centerY + radius * Math.sin(endAngle);

      // Determine arc flags
      const largeArc = Math.abs(angleOfArc) > Math.PI ? 1 : 0;
      const sweep = angleOfArc > 0 ? 1 : 0; // positive = CW (sweep=1), negative = CCW (sweep=0)

      // Tangent angle at endpoint (perpendicular to radius)
      // For CW (positive angleOfArc), tangent is endAngle + π/2
      // For CCW (negative angleOfArc), tangent is endAngle - π/2
      const tangentAngle = angleOfArc > 0 ? endAngle + Math.PI / 2 : endAngle - Math.PI / 2;

      // No M or L command - current position is guaranteed on circle
      let pathStr: string;
      if (inPathBlock) {
        const adx = endX - ctx.position.x;
        const ady = endY - ctx.position.y;
        pathStr = `a ${formatNum(radius)} ${formatNum(radius)} 0 ${largeArc} ${sweep} ${formatNum(adx)} ${formatNum(ady)}`;
      } else {
        pathStr = `A ${formatNum(radius)} ${formatNum(radius)} 0 ${largeArc} ${sweep} ${formatNum(endX)} ${formatNum(endY)}`;
      }

      // Update context tracking
      const emittedCommands = parseAndTrackPathString(pathStr, scope);

      // Store tangent for tangentLine/tangentArc
      setLastTangent(ctx, tangentAngle);

      // Return both path and result info
      return {
        type: 'PathWithResult' as const,
        path: pathStr,
        commands: emittedCommands,
        result: {
          type: 'ContextObject' as const,
          value: {
            point: { x: endX, y: endY },
            angle: tangentAngle,
          },
        },
      };
    }

    case 'tangentLine': {
      // tangentLine(length) → PathSegment
      const [length] = args as [number];

      if (ctx.lastTangent === undefined) {
        throw new Error(
          formatError(
            'tangentLine requires a previous path command that establishes direction',
            loc?.line,
            loc?.column,
          ),
        );
      }

      const savedTangent = ctx.lastTangent;
      const dx = Math.cos(ctx.lastTangent) * length;
      const dy = Math.sin(ctx.lastTangent) * length;
      const x = ctx.position.x + dx;
      const y = ctx.position.y + dy;

      updateContextForCommand(ctx, 'L', [x, y]);
      setLastTangent(ctx, savedTangent);

      if (inPathBlock) {
        return { type: 'PathSegment' as const, value: `l ${formatNum(dx)} ${formatNum(dy)}` };
      }
      return { type: 'PathSegment' as const, value: `L ${formatNum(x)} ${formatNum(y)}` };
    }

    case 'tangentArc': {
      // tangentArc(radius, sweepAngle) → PathWithResult
      const [radius, sweepAngle] = args as [number, number];

      if (ctx.lastTangent === undefined) {
        throw new Error(
          formatError('tangentArc requires a previous path command that establishes direction', loc?.line, loc?.column),
        );
      }

      // Center is perpendicular to tangent direction
      // For positive sweep (turning right on screen): center is to the right (+π/2 from heading)
      // For negative sweep (turning left on screen): center is to the left (-π/2 from heading)
      const toCenter = ctx.lastTangent + (sweepAngle >= 0 ? Math.PI / 2 : -Math.PI / 2);
      const cx = ctx.position.x + Math.cos(toCenter) * radius;
      const cy = ctx.position.y + Math.sin(toCenter) * radius;

      // Start angle is from center to current position
      const startAngle = Math.atan2(ctx.position.y - cy, ctx.position.x - cx);
      const endAngle = startAngle + sweepAngle;

      // End point
      const endX = cx + radius * Math.cos(endAngle);
      const endY = cy + radius * Math.sin(endAngle);

      // Arc flags
      const sweep = sweepAngle >= 0 ? 1 : 0;
      const largeArc = Math.abs(sweepAngle) > Math.PI ? 1 : 0;

      // New tangent at endpoint
      const newTangent = sweepAngle >= 0 ? endAngle + Math.PI / 2 : endAngle - Math.PI / 2;

      // Generate path string
      let pathStr: string;
      if (inPathBlock) {
        const adx = endX - ctx.position.x;
        const ady = endY - ctx.position.y;
        pathStr = `a ${formatNum(radius)} ${formatNum(radius)} 0 ${largeArc} ${sweep} ${formatNum(adx)} ${formatNum(ady)}`;
      } else {
        pathStr = `A ${formatNum(radius)} ${formatNum(radius)} 0 ${largeArc} ${sweep} ${formatNum(endX)} ${formatNum(endY)}`;
      }

      // Update context tracking
      const emittedCommands = parseAndTrackPathString(pathStr, scope);

      setLastTangent(ctx, newTangent);

      // Return both path and result info
      return {
        type: 'PathWithResult' as const,
        path: pathStr,
        commands: emittedCommands,
        result: {
          type: 'ContextObject' as const,
          value: {
            point: { x: endX, y: endY },
            angle: newTangent,
          },
        },
      };
    }

    case 'heading': {
      // heading(angle) → set tangent direction without emitting commands or moving cursor
      const [angle] = args as [number];
      setLastTangent(ctx, angle);
      return { type: 'PathSegment' as const, value: '' };
    }

    case 'turn': {
      // turn(delta) → add delta to current tangent direction
      const [delta] = args as [number];
      if (ctx.lastTangent === undefined) {
        throw new Error(
          formatError('turn requires an existing heading — use heading(angle) first', loc?.line, loc?.column),
        );
      }
      setLastTangent(ctx, ctx.lastTangent + delta);
      return { type: 'PathSegment' as const, value: '' };
    }

    default:
      throw new Error(`Unknown context-aware function: ${name}`);
  }
}

function evaluatePathCommand(cmd: PathCommand, scope: Scope): { text: string; commands: PathBlockCommand[] } {
  // Empty command means it's a statement-level function call
  if (cmd.command === '') {
    // Context tracking for any emitted PathSegments happens during arg
    // evaluation, so derive structured commands from a scratch context seeded
    // at the pre-statement cursor rather than re-applying to the live one.
    const ctx = scope.evalState?.pathContext;
    const startPos = ctx ? { x: ctx.position.x, y: ctx.position.y } : { x: 0, y: 0 };
    const subpathStart = ctx ? { x: ctx.start.x, y: ctx.start.y } : undefined;
    // cmd.command is '' here (statement form), so no argument can be an arc slot.
    const { value: args, tracked } = withTrackedArgs(scope, () => cmd.args.map((arg) => evaluatePathArg(arg, scope)));
    const text = args.map((a) => a.text).join(' ');
    const commands = text ? parsePathStringAt(text, startPos, subpathStart) : [];
    mergeTrackedMeta(commands, tracked);
    return { text, commands };
  }

  // Get string args for output. A path-emitting argument (`M x y block.draw()`)
  // tracks its own commands against the live context WHILE the args evaluate —
  // i.e. from the pen position before this command moves it — so snapshot first.
  const before = scope.evalState ? snapshotContext(scope.evalState.pathContext) : null;
  // Each argument is evaluated exactly once (arc rotation slots keyed by source index).
  const { value: argValues, tracked } = withTrackedArgs(scope, () =>
    cmd.args.map((arg, i) => evaluatePathArg(arg, scope, isArcRotationSlot(cmd.command, i))),
  );
  const result = cmd.command + (argValues.length > 0 ? ` ${argValues.map((a) => a.text).join(' ')}` : '');

  // Update path context if tracking is enabled
  let commands: PathBlockCommand[] = [];
  if (scope.evalState && cmd.command !== '') {
    const ctx = scope.evalState.pathContext;
    // hasCommandLetter, not a letter regex: the `a` in NaN and the `t` in Infinity
    // are not commands (path-data.ts, "Non-finite numbers").
    if (before && hasCommandLetter(result.slice(1))) {
      // The emitted text carries more commands than the letter we started
      // with: rewind and replay the whole fragment in order, so the structured
      // record, the command history, and the pen all agree with the bytes.
      restoreContext(ctx, before);
      commands = parsePathStringToCommands(result, ctx);
      mergeTrackedMeta(commands, tracked);
    } else {
      // The pen moves by the same unrounded numbers the text was formatted
      // from. An argument with no number (path data that carried no command
      // letter, or a call that drew nothing) contributes none, as before.
      const numericArgs = argValues.flatMap((a) => (a.num === undefined ? [] : [a.num]));
      const start = { x: ctx.position.x, y: ctx.position.y };
      updateContextForCommand(ctx, cmd.command, numericArgs);
      commands = [{ command: cmd.command, args: numericArgs, start, end: { x: ctx.position.x, y: ctx.position.y } }];
    }
  }

  return { text: result, commands };
}

/** Collect the tracked commands of a statement's path-emitting arguments while they evaluate. */
function withTrackedArgs<T>(scope: Scope, evaluate: () => T): { value: T; tracked: PathBlockCommand[] } {
  const state = scope.evalState;
  if (!state) return { value: evaluate(), tracked: [] };
  const saved = state.argTracked;
  const tracked: PathBlockCommand[] = [];
  state.argTracked = tracked;
  try {
    return { value: evaluate(), tracked };
  } finally {
    state.argTracked = saved;
  }
}

/** A path-emitting argument (block.draw(), drawTo(), arc helpers) hands its tracked commands to the statement. */
function noteTrackedArg(scope: Scope, value: { commands?: PathBlockCommand[] }): void {
  const sink = scope.evalState?.argTracked;
  if (sink && value.commands) sink.push(...value.commands);
}

/**
 * The record site re-parses the emitted text, which loses the per-command
 * meta a drawn block carried (labels, seam and call identity). The tracked
 * commands of the statement's path-emitting arguments mirror that text one
 * letter at a time, so when the tail of the parsed list lines up with them
 * the meta is copied back by position; any mismatch leaves the parse alone.
 */
function mergeTrackedMeta(parsed: PathBlockCommand[], tracked: PathBlockCommand[]): void {
  // The tracked list is a suffix of the parsed one because the grammar
  // allows at most one call-shaped argument per path command, placed last;
  // the letter check below is what guards that assumption.
  if (tracked.length === 0) return;
  const offset = parsed.length - tracked.length;
  if (offset < 0) return;
  for (let i = 0; i < tracked.length; i++) {
    if (parsed[offset + i].command.toLowerCase() !== tracked[i].command.toLowerCase()) return;
  }
  for (let i = 0; i < tracked.length; i++) {
    const meta = tracked[i].meta;
    if (meta) parsed[offset + i].meta = { ...parsed[offset + i].meta, ...meta };
  }
}

/**
 * Parse a path string, update context for each command found, and return the
 * structured commands. Used for tracking context when stdlib functions and
 * draw()/drawTo() return path strings.
 */
function parseAndTrackPathString(pathStr: string, scope: Scope): PathBlockCommand[] {
  if (!scope.evalState) return [];
  const commands = parsePathStringToCommands(pathStr, scope.evalState.pathContext);
  return commands;
}

/**
 * Evaluate text body items (TemplateLiteral, TspanStatement, ForLoop, IfStatement, LetDeclaration)
 * into TextChild array. Used by TextStatement block form evaluation.
 */
function evaluateTextBody(items: TextBodyItem[], scope: Scope, children: TextChild[]): LoopFlow {
  for (const item of items) {
    if (item.type === 'BreakStatement') {
      return { flow: 'break', line: getLine(item) ?? null };
    } else if (item.type === 'ContinueStatement') {
      return { flow: 'continue', line: getLine(item) ?? null };
    } else if (item.type === 'TemplateLiteral') {
      const text = evaluateTemplateLiteral(item, scope);
      children.push({ type: 'run', text });
    } else if (item.type === 'TspanStatement') {
      const text = evaluateTemplateLiteral(item.content, scope);
      const dx = item.dx ? requireNumber(evaluateExpression(item.dx, scope), 'tspan() dx') : undefined;
      const dy = item.dy ? requireNumber(evaluateExpression(item.dy, scope), 'tspan() dy') : undefined;
      if (item.rotation) {
        const tsMessage = describeMissingAngleUnit('tspan', 'tspan() rotation', item.rotation);
        if (tsMessage) throw new Error(formatError(tsMessage, getLine(item), getCol(item)));
      }
      const rot = item.rotation
        ? requireNumber(evaluateExpression(item.rotation, scope), 'tspan() rotation')
        : undefined;
      let tspanStyles: Record<string, string> | undefined;
      if (item.styles) {
        const sv = evaluateExpression(item.styles, scope);
        if (!isStyleBlock(sv)) throw new Error('tspan() styles must be a style block');
        tspanStyles = sv.properties;
      }
      children.push({ type: 'tspan', text, dx, dy, rotation: rot, styles: tspanStyles });
    } else if (item.type === 'ForLoop') {
      // Same bounds rules, limit and messages as the statement loop.
      const { start, plan: range } = resolveRange(
        item.start,
        item.end,
        item.inclusive !== false,
        scope,
        'loop',
        getLine(item),
      );

      for (let i = start; range.continues(i); i += range.step) {
        const loopScope = createScope(scope);
        setVariable(loopScope, item.variable, i);
        const flow = evaluateTextBody(item.body as TextBodyItem[], loopScope, children);
        if (flow?.flow === 'break') break;
      }
    } else if (item.type === 'ForEachLoop') {
      const iterable = evaluateExpression(item.iterable, scope);
      if (!isArrayValue(iterable)) {
        throw new Error('for-each requires an array');
      }
      lockArray(iterable);
      try {
        for (let i = 0; i < iterable.elements.length; i++) {
          const loopScope = createScope(scope);
          setVariable(loopScope, item.variable, iterable.elements[i]);
          if (item.indexVariable) {
            setVariable(loopScope, item.indexVariable, i);
          }
          const flow = evaluateTextBody(item.body as TextBodyItem[], loopScope, children);
          if (flow?.flow === 'break') break;
        }
      } finally {
        unlockArray(iterable);
      }
    } else if (item.type === 'IfStatement') {
      const condition = evaluateExpression(item.condition, scope);
      // Propagate loop flow out of the taken branch to the enclosing loop
      if (isTruthy(condition)) {
        const flow = evaluateTextBody(item.consequent as TextBodyItem[], scope, children);
        if (flow) return flow;
      } else if (item.alternate) {
        const flow = evaluateTextBody(item.alternate as TextBodyItem[], scope, children);
        if (flow) return flow;
      }
    } else if (item.type === 'SwitchStatement') {
      const selected = selectSwitchBody(item, scope);
      if (selected) {
        const flow = evaluateTextBody(selected.body as TextBodyItem[], selected.scope, children);
        if (flow) return flow;
      }
    } else if (item.type === 'LetDeclaration') {
      const value = evaluateExpression(item.value, scope);
      if (item.pattern) {
        bindDestructuringPattern(item.pattern, value, scope, getLine(item), item.kind);
      } else {
        declareBinding(scope, item.name, value, item.kind, getLine(item));
      }
    }
  }
  return undefined;
}

/**
 * Bind a destructuring pattern to a value, setting variables in the given scope.
 */
function bindDestructuringPattern(
  pattern: ArrayDestructuringPattern | ObjectDestructuringPattern,
  value: Value,
  scope: Scope,
  line?: number,
  kind: 'let' | 'const' = 'let',
): void {
  const bind = (name: string, bound: Value): void => declareBinding(scope, name, bound, kind, line);
  if (pattern.type === 'ArrayDestructuringPattern') {
    if (!isArrayValue(value)) {
      throw new Error(formatError('Cannot destructure non-array value with array pattern', line));
    }
    for (let i = 0; i < pattern.elements.length; i++) {
      bind(pattern.elements[i], value.elements[i] ?? null);
    }
    if (pattern.rest) {
      bind(pattern.rest, {
        type: 'ArrayValue' as const,
        elements: value.elements.slice(pattern.elements.length),
      });
    }
  } else {
    // A property taken under its own built-in name (`{ min }`) gets the
    // message that names the fix, before anything binds.
    for (const { key, alias } of pattern.properties) {
      if (!alias && !RESERVED_UNIT_NAMES.has(key) && BUILTIN_NAMES.has(key)) {
        throw new Error(formatError(builtinNameError(key, true), line));
      }
    }
    if (isObjectValue(value)) {
      const usedKeys = new Set<string>();
      for (const { key, alias } of pattern.properties) {
        usedKeys.add(key);
        bind(alias ?? key, value.properties.get(key) ?? null);
      }
      if (pattern.rest) {
        const remaining = new Map<string, Value>();
        for (const [k, v] of value.properties) {
          if (!usedKeys.has(k)) remaining.set(k, v);
        }
        bind(pattern.rest, { type: 'ObjectValue' as const, properties: remaining });
      }
      return;
    }

    // Built-in structs (Point, PolarVector, Grid, MeshPoint, Color, context
    // objects) destructure through the shared registry. Unlike plain objects,
    // their property set is fixed, so a missing key is an error — the same
    // contract as dot access.
    const struct = getStructDescriptor(value);
    if (!struct) {
      throw new Error(formatError('Cannot destructure non-object value with object pattern', line));
    }
    const usedKeys = new Set<string>();
    for (const { key, alias } of pattern.properties) {
      if (!struct.has(value, key)) {
        throw new Error(formatError(`Property '${key}' does not exist on ${struct.name}`, line));
      }
      usedKeys.add(key);
      bind(alias ?? key, struct.get(value, key));
    }
    if (pattern.rest) {
      const remaining = new Map<string, Value>();
      for (const key of struct.keys(value)) {
        if (!usedKeys.has(key)) remaining.set(key, struct.get(value, key));
      }
      bind(pattern.rest, { type: 'ObjectValue' as const, properties: remaining });
    }
  }
}

/** MatchHost for switch-match.ts: pattern expressions, bindings, and errors in the case scope. */
function switchHost(caseScope: Scope, stmt: { loc?: SourceLocation }): MatchHost {
  return {
    evaluate: (expr) => evaluateExpression(expr, caseScope),
    bind: (pattern, value) => bindDestructuringPattern(pattern, value, caseScope, getLine(stmt)),
    fail: (message) => {
      throw new Error(formatError(message, getLine(stmt)));
    },
  };
}

/** Evaluate the discriminant once and pick the clause body + scope to run (null = no match, no default). */
function selectSwitchBody(stmt: SwitchStatement, scope: Scope): { body: Statement[]; scope: Scope } | null {
  const scrutinee = evaluateExpression(stmt.discriminant, scope);
  return selectSwitchClause(stmt, scrutinee, () => createScope(scope), (caseScope) => switchHost(caseScope, stmt));
}

/**
 * Evaluate a statement, appending output to the accumulator array.
 * Using an accumulator avoids O(n^2) string concatenation from nested joins.
 */
/**
 * Fast-path evaluation of a Grid cell-callback body (fill/map/forEach).
 *
 * A top-level `return` is handled by short-circuiting WITHOUT throwing — the
 * per-cell `throw new ReturnSignal(...)` / `catch` round-trip was the dominant
 * cost for large grids (it kept V8 from optimizing the loop, so a 64k-cell
 * grid took ~14s even when the body was a bare `return 0.5;`). Nested returns
 * (inside if/for) still throw ReturnSignal and are caught by the caller's
 * try/catch, preserving full early-return semantics.
 *
 * Returns { returned: true, value } when a top-level return fired (the rest of
 * the body is skipped), otherwise { returned: false, value: null }.
 */
function evaluateGridCellBody(
  body: Statement[],
  scope: Scope,
  accum: PathStore,
): { returned: boolean; value: Value } {
  for (const stmt of body) {
    if (stmt.type === 'ReturnStatement') {
      return { returned: true, value: evaluateExpression(stmt.value, scope) };
    }
    // Grid cell bodies are break/continue boundaries (builder-enforced; defensive)
    const flow = evaluateStatementToAccum(stmt, scope, accum);
    if (flow) throw loopFlowBoundaryError(flow);
  }
  return { returned: false, value: null };
}

/**
 * Run one invocation of an array callback body (map / filter / reduce) and
 * return what it returned, or null when no `return` executed.
 *
 * Two costs are being avoided, both measured (project-docs/range-values):
 *  - a top-level `return` short-circuits in evaluateGridCellBody and never
 *    throws at all;
 *  - a NESTED return (inside if/for) still throws ReturnSignal, and it is
 *    caught HERE, in a small function, rather than in the callers' inline
 *    try/catch. The same throw caught inside the ~3,700-line
 *    evaluateMethodCall cost ~0.2-0.5 ms (V8 will not optimize a function
 *    that size around the handler) versus ~0.04 ms here: 32,000 elements
 *    went from ~14 s to well under a second.
 * Keep this function small and keep the catch out of evaluateMethodCall.
 * Any other error propagates so the caller can add its index context.
 */
function runCallbackBody(body: Statement[], scope: Scope, sink: PathStore): Value {
  try {
    const result = evaluateGridCellBody(body, scope, sink);
    return result.returned ? result.value : null;
  } catch (e) {
    if (e instanceof ReturnSignal) return e.value;
    throw e;
  }
}

/**
 * Run a constructor's builder block: `LinearGradient(...) {|g| ... }`, the
 * other gradients, Pattern, Marker, Grid and the filters. The block's value
 * isn't used, so a last line written without `;` — which the parser makes an
 * implicit return, the `{|v| v * 2}` sugar — runs as the statement it would
 * be with the `;`, and an explicit `return` ends the block, as in a
 * subscription callback. Kept small for the reason given on runCallbackBody.
 */
function runBuilderBlock(body: Statement[], scope: Scope): void {
  try {
    for (const stmt of body) {
      const run =
        stmt.type === 'ReturnStatement' && stmt.implicit
          ? statementForExpression(stmt.value, (stmt.value as { loc?: SourceLocation }).loc)
          : stmt;
      // Builder blocks are break/continue boundaries (builder-enforced; defensive)
      const flow = evaluateStatementToAccum(run, scope, createPathStore());
      if (flow) throw loopFlowBoundaryError(flow);
    }
  } catch (e) {
    if (e instanceof ReturnSignal) return;
    throw e;
  }
}

/** Wrap a callback failure with the method and element index it happened at. */
function callbackError(method: string, index: number, e: unknown, line: number | undefined): Error {
  const msg = e instanceof Error ? e.message : String(e);
  return new Error(formatError(`Error in .${method}() callback at index ${index}: ${msg}`, line));
}

/** Applies to the EVALUATED string, so computed labels are validated per
 *  iteration. Pure rule lives in segments.ts (labelNameError). */
function validateLabelName(value: string, kind: 'segment' | 'endpoint', line: number | undefined): void {
  const err = labelNameError(value, kind);
  if (err) throw new Error(formatError(err, line));
}

/**
 * Evaluate a PathCommand's `with` / `as` clause expressions into plain values.
 * Validates label types and corner-op arity; placement validation happens in
 * applyAnnotationsToStore once the record exists.
 */
function evaluatePathAnnotations(
  stmt: PathCommand,
  scope: Scope,
): import('./segments').EvaluatedAnnotations | undefined {
  const ann = stmt.annotations;
  if (!ann) return undefined;
  const line = getLine(stmt);
  const result: import('./segments').EvaluatedAnnotations = {};

  if (ann.cornerOp) {
    const { kind, args } = ann.cornerOp;
    const arity: Record<string, [number, number]> = { fillet: [1, 1], chamfer: [1, 2], ellipticalFillet: [2, 3] };
    const [min, max] = arity[kind];
    if (args.length < min || args.length > max) {
      throw new Error(
        formatError(
          `${kind}() in a with clause expects ${min === max ? min : `${min}-${max}`} argument${max > 1 ? 's' : ''}, got ${args.length}`,
          line,
        ),
      );
    }
    const values = args.map((a, i) => {
      const v = cornerOpArg(evaluateExpression(a, scope), kind, i);
      if (v === undefined) {
        throw new Error(formatError(`${kind}() argument ${i + 1} must be a finite number`, line));
      }
      return v;
    });
    result.cornerOp = { kind, args: values, loc: ann.cornerOp.loc };
  }

  for (const label of ann.labels ?? []) {
    const value = evaluateExpression(label.name, scope);
    if (typeof value !== 'string' || value.length === 0) {
      throw new Error(formatError(`${label.kind}() label name must be a non-empty string`, line));
    }
    validateLabelName(value, label.kind, line);
    if (label.kind === 'segment') {
      if (result.segmentLabel !== undefined) {
        throw new Error(formatError('At most one segment() label per as clause', line));
      }
      result.segmentLabel = value;
    } else {
      if (result.endpointLabel !== undefined) {
        throw new Error(formatError('At most one endpoint() label per as clause', line));
      }
      result.endpointLabel = value;
    }
  }
  return result;
}

/**
 * Loop-control signal propagated by RETURN VALUE, never by throw: the Grid
 * callback fast path exists because per-cell throw/catch deopted V8 (~100x on
 * 64k cells), and for loops run up to MAX_ITERATIONS statements per pass.
 * `undefined` (the implicit return of every ordinary statement) means normal
 * completion, so the hot path allocates nothing; a flow object is created
 * only when a break/continue actually executes. Only statements that can
 * carry flow propagate it (Break/Continue themselves, IfStatement branches,
 * the statement-list driver); loops consume it. The AST builder guarantees
 * lexical placement, so flow reaching any other boundary is a bug guarded by
 * the defensive throws at those boundaries.
 */
type LoopFlow = { flow: 'continue' | 'break'; line: number | null } | undefined;

function loopFlowBoundaryError(flow: NonNullable<LoopFlow>): Error {
  return new Error(
    formatError(`'${flow.flow}' is only valid inside a for loop`, flow.line ?? undefined),
  );
}

function evaluateStatementToAccum(stmt: Statement, scope: Scope, accum: PathStore): LoopFlow {
  switch (stmt.type) {
    case 'LetDeclaration': {
      const value = evaluateExpression(stmt.value, scope);
      // Handle destructuring patterns
      if (stmt.pattern) {
        const bindValue = (typeof value === 'object' && value !== null && 'type' in value && value.type === 'PathWithResult') ? value.result : value;
        bindDestructuringPattern(stmt.pattern, bindValue, scope, getLine(stmt), stmt.kind);
        if (typeof value === 'object' && value !== null && 'type' in value && value.type === 'PathWithResult' && value.path) {
          recordPath(accum, value.path, value.commands ?? [], { loc: stmt.loc, fn: calleeName(stmt.value) });
        }
        return;
      }
      // Handle PathWithResult: assign the result to variable, emit the path
      if (typeof value === 'object' && value !== null && 'type' in value && value.type === 'PathWithResult') {
        const pwr = value;
        declareBinding(scope, stmt.name, pwr.result, stmt.kind, getLine(stmt));
        if (pwr.path) recordPath(accum, pwr.path, pwr.commands ?? [], { loc: stmt.loc, fn: calleeName(stmt.value) });
        return;
      }
      declareBinding(scope, stmt.name, value, stmt.kind, getLine(stmt));
      return;
    }

    case 'AssignmentStatement': {
      const value = evaluateExpression(stmt.value, scope);
      if (typeof value === 'object' && value !== null && 'type' in value && value.type === 'PathWithResult') {
        const pwr = value;
        updateVariable(scope, stmt.name, pwr.result, getLine(stmt));
        if (pwr.path) recordPath(accum, pwr.path, pwr.commands ?? [], { loc: stmt.loc, fn: calleeName(stmt.value) });
        return;
      }
      updateVariable(scope, stmt.name, value, getLine(stmt));
      return;
    }

    case 'IndexedAssignmentStatement': {
      const obj = evaluateExpression(stmt.object, scope);
      const index = evaluateExpression(stmt.index, scope);
      const value = evaluateExpression(stmt.value, scope);

      if (isObjectValue(obj)) {
        if (typeof index !== 'string') throw new Error(formatError('Object key must be a string', getLine(stmt)));
        obj.properties.set(index, value);
        return;
      }
      if (isArrayValue(obj)) {
        if (isArrayLocked(obj)) throw new Error(formatError(arrayMutationError('assign to an element of'), getLine(stmt)));
        if (typeof index !== 'number') throw new Error(formatError('Array index must be a number', getLine(stmt)));
        if (!Number.isInteger(index) || index < 0 || index >= obj.elements.length)
          throw new Error(formatError(`Array index ${index} out of bounds (length ${obj.elements.length})`, getLine(stmt)));
        obj.elements[index] = value;
        return;
      }
      throw new Error(formatError('Indexed assignment requires an object or array', getLine(stmt)));
    }

    case 'ForLoop': {
      const { start, plan: range } = resolveRange(
        stmt.start,
        stmt.end,
        stmt.inclusive !== false,
        scope,
        'loop',
        getLine(stmt),
      );

      for (let i = start; range.continues(i); i += range.step) {
        const loopScope = createScope(scope);
        setVariable(loopScope, stmt.variable, i);
        const flow = evaluateStatementsToAccum(stmt.body, loopScope, accum);
        if (flow?.flow === 'break') break;
        // 'continue' already ended this iteration's body — fall through
      }
      return;
    }

    case 'IfStatement': {
      const condition = evaluateExpression(stmt.condition, scope);

      // Propagate loop flow out of the taken branch to the enclosing loop
      if (isTruthy(condition)) {
        return evaluateStatementsToAccum(stmt.consequent, createScope(scope), accum);
      } else if (stmt.alternate) {
        return evaluateStatementsToAccum(stmt.alternate, createScope(scope), accum);
      }
      return;
    }

    case 'SwitchStatement': {
      // First matching case (or default) runs in its own child scope; loop
      // flow propagates out of the body exactly as it does for IfStatement.
      const selected = selectSwitchBody(stmt, scope);
      return selected ? evaluateStatementsToAccum(selected.body, selected.scope, accum) : undefined;
    }

    case 'ForEachLoop': {
      const iterable = evaluateExpression(stmt.iterable, scope);

      // Object iteration
      if (isObjectValue(iterable)) {
        const keys = Array.from(iterable.properties.keys());
        for (const key of keys) {
          const loopScope = createScope(scope);
          if (stmt.indexVariable) {
            // for ([key, value] in obj) — key-value pairs
            setVariable(loopScope, stmt.variable, key);
            setVariable(loopScope, stmt.indexVariable, iterable.properties.get(key)!);
          } else {
            // for (key in obj) — keys only
            setVariable(loopScope, stmt.variable, key);
          }
          const flow = evaluateStatementsToAccum(stmt.body, loopScope, accum);
          if (flow?.flow === 'break') break;
        }
        return;
      }

      // Array iteration (with smart destructuring)
      if (!isArrayValue(iterable)) {
        throw new Error(formatError('for-each requires an array or object', getLine(stmt)));
      }

      lockArray(iterable);
      try {
        for (let i = 0; i < iterable.elements.length; i++) {
          const loopScope = createScope(scope);
          const element = iterable.elements[i];
          setVariable(loopScope, stmt.variable, element);
          if (stmt.indexVariable) setVariable(loopScope, stmt.indexVariable, i);
          const flow = evaluateStatementsToAccum(stmt.body, loopScope, accum);
          if (flow?.flow === 'break') break;
        }
      } finally {
        unlockArray(iterable);
      }
      return;
    }

    case 'FunctionDefinition': {
      // Lexical, like a lambda (phase 0a step 3): the body's free names
      // resolve in the scope the fn is written in. The scope is captured by
      // reference, so a later top-level let or fn is visible by call time.
      const fn: UserFunction = {
        type: 'UserFunction',
        params: stmt.params,
        body: stmt.body,
        closure: scope,
        name: stmt.name,
      };
      declareBinding(scope, stmt.name, fn, 'fn', getLine(stmt));
      return;
    }

    case 'EnumDefinition': {
      if (scope.variables.has(stmt.name)) {
        throw new Error(formatError(`Enum '${stmt.name}' is already defined`, getLine(stmt)));
      }
      const props = new Map<string, Value>();
      for (const member of stmt.members) {
        const value = member.value
          ? evaluateExpression(member.value, scope)
          : member.name.toLowerCase(); // auto-value: lowercase string
        props.set(member.name, value);
      }
      setVariable(scope, stmt.name, { type: 'ObjectValue', properties: props } as ObjectValue);
      return;
    }

    case 'PathCommand': {
      // Bare log(...) / assert(...) statements: run for their effect, emit nothing.
      const builtinCall = asStatementBuiltinCall(stmt);
      if (builtinCall) {
        evaluateStatementBuiltin(builtinCall, scope);
        return;
      }
      // Path-block rule: relative commands only — for the block's own lines and
      // for any fn or lambda body called from them (docs/path-blocks.md "Restrictions").
      if (stmt.command !== '' && stmt.command !== stmt.command.toLowerCase() && isInsidePathBlock(scope)) {
        throw new Error(
          formatError(
            `Absolute path command '${stmt.command}' is not allowed inside path blocks. Use lowercase '${stmt.command.toLowerCase()}' for relative commands`,
            getLine(stmt),
          ),
        );
      }
      // Method call statements: evaluate for side effects, emit path if PathWithResult
      if (stmt.command === '' && stmt.args.length === 1 && stmt.args[0].type === 'MethodCallExpression') {
        const methodResult = evaluateMethodCall(stmt.args[0], scope);
        // If the method returns a PathWithResult (e.g., draw()), emit its path
        if (
          typeof methodResult === 'object' &&
          methodResult !== null &&
          'type' in methodResult &&
          methodResult.type === 'PathWithResult'
        ) {
          const pwr = methodResult;
          if (pwr.path) {
            // `accum` is the default layer's accumulator at top level (it adopts the
            // top-level accum) and the active layer's inside an apply block, so a
            // single push routes correctly in all cases.
            recordPath(accum, pwr.path, pwr.commands ?? [], { loc: stmt.loc, fn: calleeName(stmt.args[0]) });
            const annotations = evaluatePathAnnotations(stmt, scope);
            if (annotations) {
              applyAnnotationsToStore(accum, annotations, (msg) => {
                throw new Error(formatError(msg, getLine(stmt)));
              });
            }
          }
        }
        return;
      }

      // Validate path commands aren't targeting a TextLayer
      if (scope.evalState) {
        if (scope.evalState.activeLayerName) {
          const activeLayer = scope.evalState.layers.get(scope.evalState.activeLayerName);
          if (activeLayer?.layerType === 'TextLayer') {
            throw new Error(formatError('Path commands cannot be used inside a TextLayer apply block', getLine(stmt)));
          }
        } else if (scope.evalState.defaultLayerName) {
          const defaultLayer = scope.evalState.layers.get(scope.evalState.defaultLayerName);
          if (defaultLayer?.layerType === 'TextLayer') {
            throw new Error(
              formatError(
                'Path commands cannot be routed to a TextLayer. Use a PathLayer as default or wrap in a layer().apply block',
                getLine(stmt),
              ),
            );
          }
        }
      }
      const result = evaluatePathCommand(stmt, scope);
      if (result.text) {
        // `M 85 52 roundRect(0, 0, …)`: a stdlib shape starts its own subpath, so the
        // move before it does nothing — say so (ignored-move.ts). Read before recording.
        const call = stmt.command === '' ? stmt.args[0] : undefined;
        if (call?.type === 'FunctionCall' && isPlainStdlibFunction(call.name, scope)) {
          const ignored = describeIgnoredMove(call.name, result.commands, accum, call.loc);
          if (ignored) warn(scope.evalState, 'ignored-move', ignored.message, ignored.loc);
        }
        // `accum` is the default layer's accumulator at top level (the default layer
        // adopts the top-level accum) and the active layer's accumulator inside an
        // apply block — a single push routes correctly in all cases.
        recordPath(accum, result.text, result.commands, {
          loc: stmt.loc,
          // `circle(...)` names the statement; so does the block a one-line
          // `M x y block.draw()` idiom draws — the whole fragment is one call.
          fn:
            stmt.command === ''
              ? calleeName(stmt.args[0])
              : calleeName(stmt.args.find((a) => a.type === 'FunctionCall' || a.type === 'MethodCallExpression')),
        });
        const annotations = evaluatePathAnnotations(stmt, scope);
        if (annotations) {
          applyAnnotationsToStore(accum, annotations, (msg) => {
            throw new Error(formatError(msg, getLine(stmt)));
          });
        }
      } else if (stmt.annotations) {
        throw new Error(formatError('with/as clauses require the statement to emit path data', getLine(stmt)));
      }
      return;
    }

    case 'ViewBoxDefinition': {
      if (!scope.evalState) {
        throw new Error(formatError('ViewBox definitions require evaluation context', getLine(stmt)));
      }
      // Guard at the case, not only in the block-body loops: a definition
      // nested in if/for inside a block reaches here with the block's
      // synthetic evalState (activeLayerName reset to null), so the flags
      // are the only reliable placement signal at any nesting depth.
      if ((scope.evalState as EvaluationState & { _insidePathBlock?: boolean })._insidePathBlock) {
        throw new Error(formatError('ViewBox definitions are not allowed inside path blocks', getLine(stmt)));
      }
      if ((scope.evalState as EvaluationState & { _insideTextBlock?: boolean })._insideTextBlock) {
        throw new Error(formatError('ViewBox definitions are not allowed inside text blocks', getLine(stmt)));
      }
      if (scope.evalState.activeLayerName !== null) {
        throw new Error(formatError('ViewBox must appear at top level', getLine(stmt)));
      }
      if (scope.evalState.viewBox) {
        const prev = scope.evalState.viewBox.loc?.line;
        const where = prev ? ` (first defined at line ${prev})` : '';
        throw new Error(formatError(`Duplicate ViewBox definition${where}`, getLine(stmt)));
      }
      const evalArg = (label: string, expr: typeof stmt.originX): number => {
        const v = evaluateExpression(expr, scope);
        if (typeof v !== 'number' || !Number.isFinite(v)) {
          throw new Error(formatError(`ViewBox ${label} must evaluate to a finite number`, getLine(stmt)));
        }
        return v;
      };
      const originX = evalArg('originX', stmt.originX);
      const originY = evalArg('originY', stmt.originY);
      const width = evalArg('width', stmt.width);
      const height = evalArg('height', stmt.height);
      if (width <= 0) {
        throw new Error(formatError(`ViewBox width must be greater than 0 (got ${width})`, getLine(stmt)));
      }
      if (height <= 0) {
        throw new Error(formatError(`ViewBox height must be greater than 0 (got ${height})`, getLine(stmt)));
      }
      scope.evalState.viewBox = { originX, originY, width, height, loc: stmt.loc };
      return;
    }

    case 'LayerDefinition': {
      if (!scope.evalState) {
        throw new Error(formatError('Layer definitions require evaluation context', getLine(stmt)));
      }
      // Path-block rule, also for a fn or lambda called inside a block.
      if (isInsidePathBlock(scope)) {
        throw new Error(formatError('Layer definitions are not allowed inside path blocks', getLine(stmt)));
      }
      const nameValue = evaluateExpression(stmt.name, scope);
      if (typeof nameValue !== 'string') {
        throw new Error(formatError('Layer name must be a string', getLine(stmt)));
      }
      try {
        validateCSSIdent(nameValue, 'layer-name');
      } catch (e) {
        throw new Error(formatError((e as Error).message, getLine(stmt)));
      }
      if (scope.evalState.layers.has(nameValue)) {
        throw new Error(formatError(`Duplicate layer name: '${nameValue}'`, getLine(stmt)));
      }
      if (stmt.isDefault && scope.evalState.defaultLayerName !== null) {
        throw new Error(
          formatError(
            `Cannot define multiple default layers. '${scope.evalState.defaultLayerName}' is already the default`,
            getLine(stmt),
          ),
        );
      }
      const styleValue = evaluateExpression(stmt.styleExpr, scope);
      if (!isStyleBlock(styleValue)) {
        throw new Error(formatError('Layer style must be a style block', getLine(stmt)));
      }
      const styles: LayerStyle = { ...styleValue.properties };
      if (stmt.layerType === 'GroupLayer') {
        if (stmt.isDefault) {
          throw new Error(formatError('GroupLayer cannot be the default layer', getLine(stmt)));
        }
        const layerState: GroupLayerState = {
          name: nameValue,
          layerType: 'GroupLayer',
          isDefault: false as const,
          styles,
          transformState: createTransformState(),
          children: [],
        };
        scope.evalState.layers.set(nameValue, layerState);
        scope.evalState.layerOrder.push(nameValue);
        return;
      }
      if (stmt.layerType === 'TextLayer') {
        const layerState: TextLayerState = {
          name: nameValue,
          layerType: 'TextLayer',
          isDefault: stmt.isDefault,
          styles,
          textElements: [],
        };
        scope.evalState.layers.set(nameValue, layerState);
        scope.evalState.layerOrder.push(nameValue);
        if (stmt.isDefault) {
          scope.evalState.defaultLayerName = nameValue;
        }
        return;
      }
      // The default layer IS the implicit/global layer — there is only ever one
      // default layer, not a separate "global" layer alongside it. So a default
      // PathLayer adopts the global pen context, transform state, and the top-level
      // (root) accumulator rather than forking its own. Bare top-level commands —
      // which write to those global objects and the root accum — thereby flow into
      // this layer automatically, and `define default PathLayer` simply names and
      // styles that single layer. We adopt `rootAccum` (the top-level accumulator
      // stored on evalState) rather than the `accum` parameter, because the `define`
      // may be evaluated where the threaded `accum` is a throwaway (function body,
      // map callback) or another layer's — only the root accum is the default layer's.
      // Non-default layers get their own fresh state.
      const layerState: PathLayerState = {
        name: nameValue,
        layerType: 'PathLayer',
        isDefault: stmt.isDefault,
        styles,
        pathContext: stmt.isDefault
          ? scope.evalState.pathContext
          : createPathContext({ trackHistory: !!scope.evalState.trace }),
        accum: stmt.isDefault ? (scope.evalState.rootAccum ?? accum) : createPathStore(),
        transformState: stmt.isDefault ? scope.evalState.transformState : createTransformState(),
      };
      absorbStyleTransform(layerState.styles, layerState.transformState);
      scope.evalState.layers.set(nameValue, layerState);
      scope.evalState.layerOrder.push(nameValue);
      if (stmt.isDefault) {
        scope.evalState.defaultLayerName = nameValue;
      }
      return;
    }

    case 'LayerApplyBlock': {
      if (!scope.evalState) {
        throw new Error(formatError('Layer apply blocks require evaluation context', getLine(stmt)));
      }
      // Path-block rule, also for a fn or lambda called inside a block.
      if (isInsidePathBlock(scope)) {
        throw new Error(formatError('Layer apply blocks are not allowed inside path blocks', getLine(stmt)));
      }
      const target = evaluateExpression(stmt.layerName, scope);
      let layer: LayerState;
      let nameValue: string;

      if (typeof target === 'string') {
        // Existing path: look up by name
        const found = scope.evalState.layers.get(target);
        if (!found) {
          throw new Error(formatError(`Undefined layer: '${target}'`, getLine(stmt)));
        }
        layer = found;
        nameValue = target;
      } else if (isLayerReference(target)) {
        // New path: use reference directly
        layer = target.layer;
        nameValue = layer.name;
      } else {
        throw new Error(formatError('layer apply target must be a string or layer reference', getLine(stmt)));
      }
      if (scope.evalState.activeLayerName !== null) {
        throw new Error(
          formatError(
            `Cannot nest layer apply blocks. Already inside layer '${scope.evalState.activeLayerName}'`,
            getLine(stmt),
          ),
        );
      }
      if (layer.layerType === 'GroupLayer') {
        throw new Error(
          formatError('GroupLayer does not support apply blocks. Use .append() to add children', getLine(stmt)),
        );
      }
      if (layer.layerType === 'TextLayer') {
        // TextLayer apply: set activeLayerName so TextStatements write here
        const prevActiveLayerName = scope.evalState.activeLayerName;
        scope.evalState.activeLayerName = nameValue;
        // One scope for the whole block (parity with the PathLayer branch):
        // a `let` or `fn` declared here is visible to the statements after
        // it, including text bodies and their interpolations.
        const blockScope = createScope(scope);
        for (const bodyStmt of stmt.body) {
          // Apply blocks are break/continue boundaries (builder-enforced; defensive)
          const flow = evaluateStatementToAccum(bodyStmt, blockScope, createPathStore());
          if (flow) throw loopFlowBoundaryError(flow);
        }
        scope.evalState.activeLayerName = prevActiveLayerName;
        return;
      }
      // PathLayer apply: save current state, switch to layer's context
      const prevPathContext = scope.evalState.pathContext;
      const prevActiveLayerName = scope.evalState.activeLayerName;
      scope.evalState.pathContext = (layer as PathLayerState).pathContext;
      scope.evalState.activeLayerName = nameValue;
      // Apply blocks are break/continue boundaries (builder-enforced; defensive)
      const applyFlow = evaluateStatementsToAccum(stmt.body, createScope(scope), (layer as PathLayerState).accum);
      if (applyFlow) throw loopFlowBoundaryError(applyFlow);
      scope.evalState.pathContext = prevPathContext;
      scope.evalState.activeLayerName = prevActiveLayerName;
      return;
    }

    case 'TextStatement': {
      if (!scope.evalState) throw new Error(formatError('text() requires evaluation context', getLine(stmt)));
      const activeTextLayer = getActiveTextLayer(scope);
      if (!activeTextLayer) {
        throw new Error(formatError('text() can only be used inside a TextLayer apply block', getLine(stmt)));
      }

      const x = requireNumber(evaluateExpression(stmt.x, scope), 'text() x');
      const y = requireNumber(evaluateExpression(stmt.y, scope), 'text() y');
      if (stmt.rotation) {
        const rotMessage = describeMissingAngleUnit('text', 'text() rotation', stmt.rotation);
        if (rotMessage) throw new Error(formatError(rotMessage, getLine(stmt), getCol(stmt)));
      }
      const rotation = stmt.rotation
        ? requireNumber(evaluateExpression(stmt.rotation, scope), 'text() rotation')
        : undefined;
      let textStyles: Record<string, string> | undefined;
      if (stmt.styles) {
        const sv = evaluateExpression(stmt.styles, scope);
        if (!isStyleBlock(sv)) throw new Error('text() styles must be a style block');
        textStyles = sv.properties;
      }

      if (stmt.content) {
        // Inline form: text(x, y)`content`
        const text = evaluateTemplateLiteral(stmt.content, scope);
        activeTextLayer.textElements.push({ x, y, rotation, styles: textStyles, children: [{ type: 'run', text }] });
      } else if (stmt.body) {
        // Block form: text(x, y) { `text` tspan() for/if/let... }
        const children: TextChild[] = [];
        // Text-block top level is a break/continue boundary (builder-enforced; defensive)
        const textFlow = evaluateTextBody(stmt.body, scope, children);
        if (textFlow) throw loopFlowBoundaryError(textFlow);
        activeTextLayer.textElements.push({ x, y, rotation, styles: textStyles, children });
      }
      return;
    }

    case 'MemberAssignmentStatement': {
      const obj = evaluateExpression(stmt.object, scope);
      // The one place every angle-valued property assignment passes through —
      // Marker.orient, the filter angles — before each assign* helper, which
      // sees only the runtime value and so cannot tell a literal from a variable.
      const assignReceiver = receiverTypeName(obj);
      if (assignReceiver) {
        const message = describeMissingAngleUnitForProp(assignReceiver, stmt.property, stmt.value);
        if (message) throw new Error(formatError(message, getLine(stmt), getCol(stmt)));
      }
      const value = evaluateExpression(stmt.value, scope);
      if (isLayerReference(obj) && stmt.property === 'styles') {
        if (!isStyleBlock(value)) throw new Error(formatError('Layer styles must be a style block', getLine(stmt)));
        obj.layer.styles = { ...value.properties };
        return;
      }
      if (isPatternValue(obj)) {
        assignPatternProperty(obj, stmt.property, value, (message) => {
          throw new Error(formatError(message, getLine(stmt)));
        });
        return;
      }
      if (isMarkerValue(obj)) {
        assignMarkerProperty(obj, stmt.property, value, (message) => {
          throw new Error(formatError(message, getLine(stmt)));
        });
        return;
      }
      if (isNoiseFilterValue(obj)) {
        switch (stmt.property) {
          case 'style': {
            if (typeof value !== 'string')
              throw new Error(
                formatError(
                  `NoiseFilter.style must be a NoiseFilterStyle enum value`,
                  getLine(stmt),
                ),
              );
            const valid = Object.values(BUILTIN_ENUMS.NoiseFilterStyle);
            if (!valid.includes(value))
              throw new Error(
                formatError(
                  `Invalid value '${value}' for NoiseFilter.style. Valid values: ${valid.join(', ')}`,
                  getLine(stmt),
                ),
              );
            // Re-baseline parameters from the new preset's defaults, but
            // preserve the seed (it's keyed off the id, not the style).
            const seed = obj.seed;
            const reset = makeDefaultNoiseFilter(obj.id, value as NoiseFilterStyleName);
            obj.style = reset.style;
            obj.scale = reset.scale;
            obj.octaves = reset.octaves;
            obj.amount = reset.amount;
            obj.monochrome = reset.monochrome;
            obj.blend = reset.blend;
            obj.contrast = reset.contrast;
            obj.stitch = reset.stitch;
            obj.seed = seed;
            return;
          }
          case 'scale': {
            if (typeof value === 'number') {
              if (!Number.isFinite(value) || value <= 0)
                throw new Error(formatError(`NoiseFilter.scale must be a finite positive number`, getLine(stmt)));
              obj.scale = value;
            } else if (value === 'fine') {
              obj.scale = 5.0;
            } else if (value === 'medium') {
              obj.scale = 1.0;
            } else if (value === 'coarse') {
              obj.scale = 0.3;
            } else {
              throw new Error(
                formatError(
                  `NoiseFilter.scale must be a positive number or one of 'fine' | 'medium' | 'coarse'`,
                  getLine(stmt),
                ),
              );
            }
            return;
          }
          case 'octaves': {
            if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > 10)
              throw new Error(
                formatError(`NoiseFilter.octaves must be an integer between 1 and 10`, getLine(stmt)),
              );
            obj.octaves = value;
            return;
          }
          case 'amount': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1)
              throw new Error(formatError(`NoiseFilter.amount must be a number between 0 and 1`, getLine(stmt)));
            obj.amount = value;
            return;
          }
          case 'monochrome': {
            if (isBooleanValue(value)) {
              obj.monochrome = value.value === 1;
            } else if (typeof value === 'number') {
              obj.monochrome = value !== 0;
            } else {
              throw new Error(formatError(`NoiseFilter.monochrome must be a boolean`, getLine(stmt)));
            }
            return;
          }
          case 'seed': {
            if (typeof value !== 'number' || !Number.isFinite(value))
              throw new Error(formatError(`NoiseFilter.seed must be a finite number`, getLine(stmt)));
            obj.seed = value;
            return;
          }
          case 'blend': {
            if (typeof value !== 'string')
              throw new Error(formatError(`NoiseFilter.blend must be a BlendMode enum value`, getLine(stmt)));
            const valid = Object.values(BUILTIN_ENUMS.BlendMode);
            if (!valid.includes(value))
              throw new Error(
                formatError(
                  `Invalid value '${value}' for NoiseFilter.blend. Valid values: ${valid.join(', ')}`,
                  getLine(stmt),
                ),
              );
            obj.blend = value;
            return;
          }
          case 'contrast': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0)
              throw new Error(formatError(`NoiseFilter.contrast must be a finite non-negative number`, getLine(stmt)));
            obj.contrast = value;
            return;
          }
          case 'stitch': {
            if (isBooleanValue(value)) {
              obj.stitch = value.value === 1;
            } else if (typeof value === 'number') {
              obj.stitch = value !== 0;
            } else {
              throw new Error(formatError(`NoiseFilter.stitch must be a boolean`, getLine(stmt)));
            }
            return;
          }
          default:
            throw new Error(
              formatError(`Cannot assign to NoiseFilter property '${stmt.property}'`, getLine(stmt)),
            );
        }
      }
      if (isGlowFilterValue(obj)) {
        switch (stmt.property) {
          case 'mode': {
            if (typeof value !== 'string') {
              throw new Error(formatError(`GlowFilter.mode must be a GlowMode enum value`, getLine(stmt)));
            }
            const valid = Object.values(BUILTIN_ENUMS.GlowMode);
            if (!valid.includes(value)) {
              throw new Error(
                formatError(
                  `Invalid value '${value}' for GlowFilter.mode. Valid values: ${valid.join(', ')}`,
                  getLine(stmt),
                ),
              );
            }
            obj.mode = value as GlowModeName;
            return;
          }
          case 'color': {
            if (!isColorValue(value)) {
              throw new Error(formatError(`GlowFilter.color must be a Color value`, getLine(stmt)));
            }
            obj.color = colorValueToCSS(value);
            return;
          }
          case 'radius': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
              throw new Error(formatError(`GlowFilter.radius must be a finite non-negative number`, getLine(stmt)));
            }
            obj.radius = value;
            return;
          }
          case 'spread': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
              throw new Error(formatError(`GlowFilter.spread must be a finite non-negative number`, getLine(stmt)));
            }
            obj.spread = value;
            return;
          }
          case 'opacity': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1) {
              throw new Error(formatError(`GlowFilter.opacity must be a number between 0 and 1`, getLine(stmt)));
            }
            obj.opacity = value;
            return;
          }
          default:
            throw new Error(
              formatError(`Cannot assign to GlowFilter property '${stmt.property}'`, getLine(stmt)),
            );
        }
      }
      if (isEmbossFilterValue(obj)) {
        switch (stmt.property) {
          case 'angle': {
            const n = toNumber(value);
            if (n === undefined || !Number.isFinite(n)) {
              throw new Error(formatError(`EmbossFilter.angle must be a finite number (with angle unit)`, getLine(stmt)));
            }
            obj.angle = n;
            return;
          }
          case 'elevation': {
            const n = toNumber(value);
            if (n === undefined || !Number.isFinite(n)) {
              throw new Error(formatError(`EmbossFilter.elevation must be a finite number (with angle unit)`, getLine(stmt)));
            }
            obj.elevation = n;
            return;
          }
          case 'depth': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
              throw new Error(formatError(`EmbossFilter.depth must be a finite non-negative number`, getLine(stmt)));
            }
            obj.depth = value;
            return;
          }
          case 'strength': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
              throw new Error(formatError(`EmbossFilter.strength must be a finite non-negative number`, getLine(stmt)));
            }
            obj.strength = value;
            return;
          }
          case 'shininess': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 1) {
              throw new Error(formatError(`EmbossFilter.shininess must be a finite number >= 1`, getLine(stmt)));
            }
            obj.shininess = value;
            return;
          }
          case 'lightColor': {
            if (!isColorValue(value)) {
              throw new Error(formatError(`EmbossFilter.lightColor must be a Color value`, getLine(stmt)));
            }
            obj.lightColor = colorValueToCSS(value);
            return;
          }
          case 'smooth': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
              throw new Error(formatError(`EmbossFilter.smooth must be a finite non-negative number`, getLine(stmt)));
            }
            obj.smooth = value;
            return;
          }
          default:
            throw new Error(
              formatError(`Cannot assign to EmbossFilter property '${stmt.property}'`, getLine(stmt)),
            );
        }
      }
      if (isElevationShadowFilterValue(obj)) {
        switch (stmt.property) {
          case 'elevation': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 24) {
              throw new Error(
                formatError(`ElevationShadowFilter.elevation must be a finite number between 0 and 24`, getLine(stmt)),
              );
            }
            obj.elevation = value;
            return;
          }
          case 'color': {
            if (!isColorValue(value)) {
              throw new Error(formatError(`ElevationShadowFilter.color must be a Color value`, getLine(stmt)));
            }
            obj.color = colorValueToCSS(value);
            return;
          }
          case 'direction': {
            const n = toNumber(value);
            if (n === undefined || !Number.isFinite(n)) {
              throw new Error(
                formatError(`ElevationShadowFilter.direction must be a finite number (with angle unit)`, getLine(stmt)),
              );
            }
            obj.direction = n;
            return;
          }
          case 'tightness': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
              throw new Error(
                formatError(`ElevationShadowFilter.tightness must be a finite non-negative number`, getLine(stmt)),
              );
            }
            obj.tightness = value;
            return;
          }
          default:
            throw new Error(
              formatError(`Cannot assign to ElevationShadowFilter property '${stmt.property}'`, getLine(stmt)),
            );
        }
      }
      if (isInnerShadowFilterValue(obj)) {
        switch (stmt.property) {
          case 'offsetX': {
            if (typeof value !== 'number' || !Number.isFinite(value)) {
              throw new Error(formatError(`InnerShadowFilter.offsetX must be a finite number`, getLine(stmt)));
            }
            obj.offsetX = value;
            return;
          }
          case 'offsetY': {
            if (typeof value !== 'number' || !Number.isFinite(value)) {
              throw new Error(formatError(`InnerShadowFilter.offsetY must be a finite number`, getLine(stmt)));
            }
            obj.offsetY = value;
            return;
          }
          case 'blur': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
              throw new Error(formatError(`InnerShadowFilter.blur must be a finite non-negative number`, getLine(stmt)));
            }
            obj.blur = value;
            return;
          }
          case 'color': {
            if (!isColorValue(value)) {
              throw new Error(formatError(`InnerShadowFilter.color must be a Color value`, getLine(stmt)));
            }
            obj.color = colorValueToCSS(value);
            return;
          }
          case 'opacity': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1) {
              throw new Error(formatError(`InnerShadowFilter.opacity must be a number between 0 and 1`, getLine(stmt)));
            }
            obj.opacity = value;
            return;
          }
          default:
            throw new Error(
              formatError(`Cannot assign to InnerShadowFilter property '${stmt.property}'`, getLine(stmt)),
            );
        }
      }
      if (isPixelateFilterValue(obj)) {
        const positiveFinite = (v: Value, name: string): number => {
          if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) {
            throw new Error(formatError(`PixelateFilter.${name} must be a finite positive number`, getLine(stmt)));
          }
          return v;
        };
        switch (stmt.property) {
          case 'width':
            obj.width = positiveFinite(value, 'width');
            return;
          case 'height':
            obj.height = positiveFinite(value, 'height');
            return;
          case 'radius':
            obj.radius = positiveFinite(value, 'radius');
            return;
          default:
            throw new Error(
              formatError(`Cannot assign to PixelateFilter property '${stmt.property}'`, getLine(stmt)),
            );
        }
      }
      if (isMotionBlurFilterValue(obj)) {
        switch (stmt.property) {
          case 'type': {
            if (typeof value !== 'string')
              throw new Error(
                formatError(`MotionBlurFilter.type must be a MotionBlurType enum value`, getLine(stmt)),
              );
            const valid = Object.values(BUILTIN_ENUMS.MotionBlurType);
            if (!valid.includes(value))
              throw new Error(
                formatError(
                  `Invalid value '${value}' for MotionBlurFilter.type. Valid values: ${valid.join(', ')}`,
                  getLine(stmt),
                ),
              );
            obj.motionType = value as MotionBlurTypeName;
            return;
          }
          case 'distance': {
            if (typeof value !== 'number' || !Number.isFinite(value) || value < 0)
              throw new Error(
                formatError(`MotionBlurFilter.distance must be a finite non-negative number`, getLine(stmt)),
              );
            obj.distance = value;
            return;
          }
          case 'angle': {
            const n = toNumber(value);
            if (n === undefined || !Number.isFinite(n))
              throw new Error(
                formatError(`MotionBlurFilter.angle must be a finite number (with angle unit)`, getLine(stmt)),
              );
            obj.angle = n;
            return;
          }
          case 'samples': {
            if (typeof value !== 'number' || !Number.isInteger(value) || value < 2 || value > 32)
              throw new Error(
                formatError(`MotionBlurFilter.samples must be an integer between 2 and 32`, getLine(stmt)),
              );
            obj.samples = value;
            return;
          }
          default:
            throw new Error(
              formatError(`Cannot assign to MotionBlurFilter property '${stmt.property}'`, getLine(stmt)),
            );
        }
      }
      if (isMeshPointValue(obj)) {
        assignMeshPointProperty(obj, stmt.property, value, (message) => {
          throw new Error(formatError(message, getLine(stmt)));
        });
        return;
      }
      if (isGradientValue(obj)) {
        assignGradientProperty(obj, stmt.property, value, stmt.value, (message) => {
          throw new Error(formatError(message, getLine(stmt)));
        });
        return;
      }
      throw new Error(formatError(`Cannot assign to property '${stmt.property}'`, getLine(stmt)));
    }

    case 'ExpressionStatement': {
      evaluateExpression(stmt.expression, scope);
      return;
    }

    case 'ReturnStatement': {
      const value = evaluateExpression(stmt.value, scope);
      throw new ReturnSignal(value, getLine(stmt));
    }

    case 'BreakStatement':
      return { flow: 'break', line: getLine(stmt) ?? null };

    case 'ContinueStatement':
      return { flow: 'continue', line: getLine(stmt) ?? null };

    case 'FontDirective':
      // Declarative metadata — font loading handled by host environment before compilation
      return;

    case 'Comment':
      // Comments are no-ops at runtime
      return;

    default:
      throw new Error(`Unknown statement type: ${(stmt as Statement).type}`);
  }
}

/**
 * Evaluate statements, appending output to the accumulator array.
 * Stops at (and returns) the first break/continue flow signal so the
 * enclosing loop can consume it; returns undefined on normal completion.
 */
function evaluateStatementsToAccum(stmts: Statement[], scope: Scope, accum: PathStore): LoopFlow {
  for (const stmt of stmts) {
    const flow = evaluateStatementToAccum(stmt, scope, accum);
    if (flow) return flow;
  }
  return undefined;
}

/**
 * Evaluate statements and return the joined result.
 * This is the public interface that uses the optimized accumulator internally.
 */
function evaluateStatements(stmts: Statement[], scope: Scope): string {
  const accum = createPathStore();
  const flow = evaluateStatementsToAccum(stmts, scope, accum);
  // Defensive: the AST builder rejects break/continue outside loops, so flow
  // can only surface here through a builder gap.
  if (flow) throw loopFlowBoundaryError(flow);
  // Corner ops recorded inside this body (e.g. a user function) finalize here,
  // since the joined string is this store's only output channel.
  const finalized = applyRecordedCornerOps(accum.records.flatMap((r) => r.commands));
  // Warnings first: a skipped op (no corner at that vertex) changes nothing
  // but must still be reported.
  for (const w of finalized.warnings) {
    warn(scope.evalState, 'corner-op', w.message, w.loc);
  }
  if (!finalized.changed) return storeToPathData(accum);
  return commandsToPathData(finalized.commands);
}

/**
 * Expand gradient stops using OKLCh interpolation.
 * Iterates adjacent stop pairs, generating intermediate stops via mixColors().
 * Skips pairs where either stop lacks oklch data (e.g., CSSVar stops).
 */
function expandOklchStops(stops: GradientStop[], stepsPerUnit: number): { offset: number; color: string }[] {
  if (stops.length < 2) return stops.map((s) => ({ offset: s.offset, color: s.color }));
  const result: { offset: number; color: string }[] = [];
  for (let i = 0; i < stops.length; i++) {
    // Always include the original stop
    result.push({ offset: stops[i].offset, color: stops[i].color });
    // Generate intermediates between this stop and the next
    if (i < stops.length - 1) {
      const a = stops[i];
      const b = stops[i + 1];
      // Skip if either stop lacks oklch (CSSVar stops)
      if (!a.oklch || !b.oklch) continue;
      const span = b.offset - a.offset;
      if (span <= 0) continue;
      const count = Math.ceil(stepsPerUnit * span) - 1;
      for (let j = 1; j <= count; j++) {
        const t = j / (count + 1);
        const offset = a.offset + span * t;
        const mixed = mixColors(a.oklch, b.oklch, t);
        result.push({ offset, color: oklchToCSS(mixed) });
      }
    }
  }
  // Sort by offset to ensure correct order after interleaving
  result.sort((a, b) => a.offset - b.offset);
  return result;
}

/**
 * Build the CompileResult from evaluation state
 */
/**
 * Emit a store's path data, applying any recorded corner ops at finalization.
 * Zero-op stores (all pre-existing programs) take the byte-exact raw join.
 */
/** Per-fragment provenance for `trace: true` output. */
function storeToRecordsOutput(store: PathStore): PathRecordOutput[] {
  return store.records.map((r) => ({
    ...(r.loc ? { loc: r.loc } : {}),
    ...(r.label !== undefined ? { label: r.label } : {}),
    ...(r.fn !== undefined ? { fn: r.fn } : {}),
    raw: r.raw,
    commandCount: r.commands.length,
  }));
}

/** Trace fields for a path layer: records + that context's command history. */
function traceFields(
  store: PathStore,
  context: PathContext,
  evalState: EvaluationState,
): Pick<LayerOutput, 'records' | 'commands'> {
  if (!evalState.trace) return {};
  return { records: storeToRecordsOutput(store), commands: context.commands.map((c) => ({ ...c, args: [...c.args] })) };
}

function finalizeStore(store: PathStore, evalState: EvaluationState): { data: string; commands: PathBlockCommand[] } {
  const flat = store.records.flatMap((r) => r.commands);
  const finalized = applyRecordedCornerOps(flat);
  for (const w of finalized.warnings) {
    warn(evalState, 'corner-op', w.message, w.loc);
  }
  // Every consumer of this function builds a LayerOutput, so the leading-move
  // guard belongs here rather than at each call site.
  if (!finalized.changed) return { data: ensureLeadingMove(storeToPathData(store)), commands: flat };
  return { data: ensureLeadingMove(commandsToPathData(finalized.commands)), commands: finalized.commands };
}

interface TabStop {
  offset: number;
  anchor: 'start' | 'middle' | 'end';
}

/** `tab-stops: 11 end, 24 end, 40` → offsets from the row's x, with an anchor each (start when omitted). */
function parseTabStops(value: string, layerName: string): TabStop[] {
  const stops = value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part) => {
      const m = /^(-?\d+(?:\.\d+)?)(?:\s+(start|middle|end))?$/.exec(part);
      if (!m) {
        throw new Error(
          `tab-stops on layer '${layerName}': each stop is an offset with an optional anchor (start, middle or end), got '${part}'`,
        );
      }
      return { offset: Number(m[1]), anchor: (m[2] as TabStop['anchor']) ?? 'start' };
    });
  if (stops.length === 0) throw new Error(`tab-stops on layer '${layerName}' needs at least one stop`);
  return stops;
}

/**
 * Split each run at its tabs: the first field stays where the text is, and
 * every field after a tab becomes a tspan at the next stop's x with that
 * stop's anchor. Fields past the last stop stay inline.
 */
function applyTabStops(te: TextElement, stops: TabStop[]): TextElement {
  if (!te.children.some((child) => child.type === 'run' && child.text.includes('\t'))) return te;
  const children: TextChild[] = [];
  let field = 0;
  for (const child of te.children) {
    if (child.type !== 'run' || !child.text.includes('\t')) {
      children.push(child);
      continue;
    }
    const pieces = child.text.split('\t');
    children.push({ type: 'run', text: pieces[0] });
    for (const piece of pieces.slice(1)) {
      const stop = stops[field];
      field++;
      if (!stop) {
        children.push({ type: 'run', text: piece });
        continue;
      }
      children.push({ type: 'tspan', text: piece, x: te.x + stop.offset, styles: { 'text-anchor': stop.anchor } });
    }
  }
  return { ...te, children };
}

function storeToFinalizedData(store: PathStore, evalState: EvaluationState): string {
  return finalizeStore(store, evalState).data;
}

/**
 * One d-string per subpath (the SVG rule: a new run at every move, and after
 * a `z` when drawing continues without one). A run that begins without a
 * move is given an absolute M at its own start, so each string draws where
 * the run did when it stands alone as its own <path>.
 */
function subpathData(commands: PathBlockCommand[]): string[] {
  return splitSubpaths(commands).map((span) => {
    const run = commands.slice(span.from, span.to);
    const first = run[0];
    const startsWithMove = first.command === 'm' || first.command === 'M';
    const at = startsWithMove ? first.end : first.start;
    const head: PathBlockCommand = { command: 'M', args: [at.x, at.y], start: first.start, end: at };
    return commandsToPathData([head, ...(startsWithMove ? run.slice(1) : run)]);
  });
}

function buildCompileResult(mainAccum: PathStore, evalState: EvaluationState): CompileResult {
  const layers: LayerOutput[] = [];

  if (evalState.layerOrder.length === 0) {
    // No layers defined: single implicit default layer
    const transform = transformStateToSvg(evalState.transformState) ?? undefined;
    layers.push({
      name: 'default',
      type: 'path',
      data: storeToFinalizedData(mainAccum, evalState),
      ...traceFields(mainAccum, evalState.pathContext, evalState),
      styles: {},
      isDefault: true,
      transform,
    });
  } else {
    // Check if main accum has content that wasn't routed to a default layer
    const mainContent = storeToFinalizedData(mainAccum, evalState);
    if (mainContent && !evalState.defaultLayerName) {
      // Prepend implicit default layer for bare commands
      const transform = transformStateToSvg(evalState.transformState) ?? undefined;
      layers.push({
        name: 'default',
        type: 'path',
        data: mainContent,
        ...traceFields(mainAccum, evalState.pathContext, evalState),
        styles: {},
        isDefault: true,
        transform,
      });
    }
    // Extract transform convenience properties from a styles dict, returning
    // a transform string (or undefined) and mutating the dict to remove consumed keys.
    // Convenience properties: translate-x, translate-y, translate, scale-x, scale-y, scale, rotate
    const TRANSFORM_CONVENIENCE_KEYS = new Set([
      'translate-x',
      'translate-y',
      'translate',
      'scale-x',
      'scale-y',
      'scale',
      'rotate',
    ]);

    function extractConvenienceTransform(styles: Record<string, string>): string | undefined {
      const hasConvenience = Object.keys(styles).some((k) => TRANSFORM_CONVENIENCE_KEYS.has(k));
      if (!hasConvenience) return undefined;

      const parts: string[] = [];

      // Translate
      let tx: string | undefined;
      let ty: string | undefined;
      if (styles.translate) {
        const vals = styles.translate.split(/\s*,\s*/);
        tx = vals[0];
        ty = vals.length > 1 ? vals[1] : '0';
        delete styles.translate;
      }
      if (styles['translate-x']) {
        tx = styles['translate-x'];
        delete styles['translate-x'];
      }
      if (styles['translate-y']) {
        ty = styles['translate-y'];
        delete styles['translate-y'];
      }
      if (tx != null || ty != null) {
        parts.push(`translate(${tx ?? '0'}, ${ty ?? '0'})`);
      }

      // Rotate
      if (styles.rotate) {
        const angleRad = parseFloat(styles.rotate);
        if (!isNaN(angleRad)) {
          const angleDeg = (angleRad * 180) / Math.PI;
          parts.push(`rotate(${angleDeg})`);
        }
        delete styles.rotate;
      }

      // Scale
      let sx: string | undefined;
      let sy: string | undefined;
      if (styles.scale) {
        const vals = styles.scale.split(/\s*,\s*/);
        sx = vals[0];
        sy = vals.length > 1 ? vals[1] : vals[0];
        delete styles.scale;
      }
      if (styles['scale-x']) {
        sx = styles['scale-x'];
        delete styles['scale-x'];
      }
      if (styles['scale-y']) {
        sy = styles['scale-y'];
        delete styles['scale-y'];
      }
      if (sx != null || sy != null) {
        parts.push(`scale(${sx ?? '1'}, ${sy ?? '1'})`);
      }

      return parts.length > 0 ? parts.join(' ') : undefined;
    }

    // Helper to build a single layer's output (used for top-level and group children)
    function buildLayerOutput(layer: LayerState): LayerOutput {
      if (layer.layerType === 'FragmentLayer') {
        const fragmentLayer = layer;
        return {
          name: layer.name,
          type: 'fragment',
          data: '',
          fragmentDefs: fragmentLayer.defsContent,
          fragmentVisuals: fragmentLayer.visualContent,
          styles: { ...layer.styles },
          isDefault: false,
        };
      }
      if (layer.layerType === 'TextLayer') {
        const textLayer = layer;
        const textStyles = { ...layer.styles };
        // `tab-stops` is ours, not SVG's: fields after each tab become tspans
        // with their own x and anchor, so a table is one statement per row.
        const tabStops = textStyles['tab-stops'] !== undefined ? parseTabStops(textStyles['tab-stops'], layer.name) : null;
        delete textStyles['tab-stops'];
        const textElements = tabStops ? textLayer.textElements.map((te) => applyTabStops(te, tabStops)) : textLayer.textElements;
        const convenienceTransform = extractConvenienceTransform(textStyles);
        let transform: string | undefined;
        if (textStyles.transform) {
          transform = textStyles.transform;
          delete textStyles.transform;
        } else if (convenienceTransform) {
          transform = convenienceTransform;
        }
        const allText = textElements.map((te) => te.children.map((c) => c.text).join('')).join(' ');
        return {
          name: layer.name,
          type: 'text',
          data: allText,
          textElements,
          styles: textStyles,
          isDefault: layer.isDefault,
          transform,
        };
      }
      if (layer.layerType === 'GroupLayer') {
        const groupLayer = layer;
        const groupStyles = { ...groupLayer.styles };
        // Convenience transform properties → transform string
        const convenienceTransform = extractConvenienceTransform(groupStyles);
        // Explicit transform property takes highest precedence, then convenience, then imperative
        let transform: string | undefined;
        if (groupStyles.transform) {
          transform = groupStyles.transform;
          delete groupStyles.transform;
        } else if (convenienceTransform) {
          transform = convenienceTransform;
        } else {
          transform = transformStateToSvg(groupLayer.transformState) ?? undefined;
        }
        const children = groupLayer.children.map((childName) => {
          const childLayer = evalState.layers.get(childName)!;
          return buildLayerOutput(childLayer);
        });
        return {
          name: layer.name,
          type: 'group',
          data: '',
          children,
          styles: groupStyles,
          isDefault: false,
          transform,
        };
      }
      const pathLayer = layer;
      const pathStyles = { ...layer.styles };
      const convenienceTransform = extractConvenienceTransform(pathStyles);
      let transform: string | undefined;
      if (pathStyles.transform) {
        transform = pathStyles.transform;
        delete pathStyles.transform;
      } else if (convenienceTransform) {
        transform = convenienceTransform;
      } else {
        transform = transformStateToSvg(pathLayer.transformState) ?? undefined;
      }
      // `marker-scope: subpath` is ours, not SVG's: it asks the emitters to
      // write each subpath as its own <path> so markers land on every run.
      let subpaths: string[] | undefined;
      const markerScope = pathStyles['marker-scope'];
      if (markerScope !== undefined) {
        delete pathStyles['marker-scope'];
        if (markerScope !== 'path' && markerScope !== 'subpath') {
          throw new Error(`marker-scope on layer '${layer.name}' must be 'path' or 'subpath', got '${markerScope}'`);
        }
      }
      const finalized = finalizeStore(pathLayer.accum, evalState);
      if (markerScope === 'subpath' && finalized.commands.length > 0) {
        subpaths = subpathData(finalized.commands);
      }
      return {
        name: layer.name,
        type: 'path',
        data: finalized.data,
        ...traceFields(pathLayer.accum, pathLayer.pathContext, evalState),
        styles: pathStyles,
        isDefault: layer.isDefault,
        transform,
        ...(subpaths ? { subpaths } : {}),
      };
    }

    // Add defined layers in definition order
    for (const name of evalState.layerOrder) {
      const layer = evalState.layers.get(name)!;
      layers.push(buildLayerOutput(layer));
    }
  }

  // Build masks output
  const masks: MaskOutput[] = [];
  for (const [, mask] of evalState.masks) {
    masks.push({
      id: mask.id,
      elements: mask.paths.map((p) => ({ pathData: p.d, styles: { ...p.styles } })),
    });
  }

  // Build clipPaths output
  const clipPaths: ClipPathOutput[] = [];
  for (const [, clip] of evalState.clipPaths) {
    clipPaths.push({
      id: clip.id,
      elements: clip.paths.map((d) => ({ pathData: d })),
    });
  }

  // Build gradients output
  const gradients: GradientOutput[] = [];
  for (const [, grad] of evalState.gradients) {
    // For inherited GPU-rendered gradients (conic, mesh, freeform, topo),
    // resolve stops from parent — these are rasterized and can't use SVG xlink:href inheritance
    const isGpuType =
      grad.gradientType === 'conic' ||
      grad.gradientType === 'mesh' ||
      grad.gradientType === 'freeform' ||
      grad.gradientType === 'topo';
    let resolvedStops = grad.stops;
    if (isGpuType && resolvedStops.length === 0 && grad.href) {
      const parent = evalState.gradients.get(grad.href);
      if (parent) resolvedStops = parent.stops;
    }
    const stepsPerUnit = grad.steps ?? 10;
    const stops =
      grad.interpolation === 'oklch'
        ? expandOklchStops(resolvedStops, stepsPerUnit)
        : resolvedStops.map((s) => ({ offset: s.offset, color: s.color }));
    const output: GradientOutput = {
      id: grad.id,
      type: grad.gradientType,
      attrs: { ...grad.attrs },
      stops,
    };
    if (grad.spreadMethod) output.spreadMethod = grad.spreadMethod;
    if (grad.gradientUnits) output.gradientUnits = grad.gradientUnits;
    if (grad.gradientTransform) output.gradientTransform = grad.gradientTransform;
    if (grad.href) output.href = grad.href;
    if (grad.interpolation === 'linearRGB') output.colorInterpolation = 'linearRGB';
    // Conic-specific output
    if (grad.gradientType === 'conic') {
      output.cx = parseFloat(grad.attrs.cx);
      output.cy = parseFloat(grad.attrs.cy);
      output.from = grad.from ?? 0;
      output.to = grad.to ?? 2 * Math.PI;
      output.direction = grad.direction ?? 'cw';
      output.spread = grad.spread ?? 'clamp';
      output.innerRadius = grad.innerRadius ?? 0;
      const fill = grad.innerFill;
      if (fill && typeof fill === 'object' && 'oklch' in fill) {
        output.innerFill = oklchToCSS(fill.oklch);
      } else {
        output.innerFill = fill ?? 'transparent';
      }
      // Warn if conic gradient has CSSVar stops — they are baked at compile time
      if (resolvedStops.some((s) => s.color.startsWith('var('))) {
        warn(
          evalState,
          'gradient',
          `Conic gradient '${grad.id}' has CSSVar stops — CSS variable changes won't update conic gradients (rasterized at compile time).`,
        );
      }
      // Preserve oklch on stops for rendering.
      // For CSSVar stops, extract the fallback color from var(--name, fallback)
      // so Canvas 2D renderers have a concrete color to work with.
      output.stopsWithOklch = resolvedStops.map((s) => {
        let color: string;
        if (s.oklch) {
          color = oklchToCSS(s.oklch);
        } else if (s.color.startsWith('var(')) {
          // Extract fallback: "var(--name, #hex)" → "#hex"
          const commaIdx = s.color.indexOf(',');
          color = commaIdx >= 0 ? s.color.slice(commaIdx + 1, -1).trim() : '#000000';
        } else {
          color = s.color;
        }
        return {
          offset: s.offset,
          color,
          ...(s.oklch ? { oklch: { ...s.oklch } } : {}),
        };
      });
    }
    // Mesh-specific output
    if (grad.gradientType === 'mesh') {
      output.meshWidth = grad.meshWidth;
      output.meshHeight = grad.meshHeight;
      output.meshGrid = (grad.meshGrid ?? []).map((row) => row.map((p) => ({ x: p.x, y: p.y, color: p.colorCSS })));
    }
    // Freeform-specific output
    if (grad.gradientType === 'freeform') {
      output.freeformWidth = grad.freeformWidth;
      output.freeformHeight = grad.freeformHeight;
      output.falloff = grad.falloff ?? 2.0;
      output.freeformPoints = (grad.freeformPoints ?? []).map((p) => ({
        x: p.x,
        y: p.y,
        color: p.colorCSS,
      }));
      // Warn if freeform gradient has fewer than 2 points
      if ((grad.freeformPoints ?? []).length < 2) {
        warn(
          evalState,
          'gradient',
          `FreeformGradient '${grad.id}' has fewer than 2 points — gradient will be empty or uniform.`,
        );
      }
    }
    // Topo-specific output
    if (grad.gradientType === 'topo') {
      output.topoWidth = grad.topoWidth;
      output.topoHeight = grad.topoHeight;
      output.topoEasing = grad.topoEasing ?? 'linear';
      output.topoMethod = grad.topoMethod ?? 'distance';
      output.topoIterations = grad.topoIterations ?? 200;
      output.topoBlend = grad.topoBlend ?? 1.0;
      output.topoContours = (grad.topoContours ?? []).map((c) => ({
        elevation: c.elevation,
        path: c.dString,
        color: c.colorCSS,
        oklch: { ...c.color },
      }));
      if (grad.topoBaseColor) {
        output.topoBaseColor = grad.topoBaseColorCSS;
        output.topoBaseColorOklch = { ...grad.topoBaseColor };
      }
      // Build stopsWithOklch from baseColor + contours (sorted by elevation) for rendering
      const contoursSorted = [...(grad.topoContours ?? [])].sort((a, b) => a.elevation - b.elevation);
      const rampStops: { offset: number; color: string; oklch?: OKLCH }[] = [];
      if (grad.topoBaseColor) {
        rampStops.push({ offset: 0, color: grad.topoBaseColorCSS!, oklch: { ...grad.topoBaseColor } });
      } else if (contoursSorted.length > 0) {
        // If no base color, use first contour's color at offset 0
        rampStops.push({ offset: 0, color: contoursSorted[0].colorCSS, oklch: { ...contoursSorted[0].color } });
      }
      for (const c of contoursSorted) {
        rampStops.push({ offset: c.elevation, color: c.colorCSS, oklch: { ...c.color } });
      }
      output.stopsWithOklch = rampStops;
      // Warnings
      if ((grad.topoContours ?? []).length < 1) {
        warn(evalState, 'gradient', `TopoGradient '${grad.id}' has no contours — gradient will be uniform.`);
      }
    }
    gradients.push(output);
  }

  // Build patterns output
  const patterns: PatternOutput[] = [];
  for (const [, pat] of evalState.patterns) {
    patterns.push({
      id: pat.id,
      x: pat.x,
      y: pat.y,
      width: pat.width,
      height: pat.height,
      elements: pat.paths.map((p) => ({ pathData: p.d, styles: { ...p.styles } })),
      patternUnits: pat.patternUnits,
      patternTransform: pat.patternTransform,
      patternContentUnits: pat.patternContentUnits,
    });
  }

  // Build markers output
  const markers: MarkerOutput[] = [];
  for (const [, marker] of evalState.markers) {
    // marker-space (placement audit V7): a marker draws only inside its FINAL viewBox,
    // so the check runs here, after the program — assigning viewBox after append is fine.
    const vb = marker.viewBox.trim().split(/\s+/).map(Number);
    if (vb.length === 4 && vb.every((n) => Number.isFinite(n))) {
      const [vx, vy, vw, vh] = vb;
      const EPS = 1e-6;
      const outside = marker.paths.filter(
        (p) =>
          p.bbox !== undefined &&
          (p.bbox.x < vx - EPS ||
            p.bbox.y < vy - EPS ||
            p.bbox.x + p.bbox.width > vx + vw + EPS ||
            p.bbox.y + p.bbox.height > vy + vh + EPS),
      );
      if (outside.length > 0) {
        const boxes = outside.map((p) => p.bbox!);
        const x0 = Math.min(...boxes.map((b) => b.x));
        const y0 = Math.min(...boxes.map((b) => b.y));
        const x1 = Math.max(...boxes.map((b) => b.x + b.width));
        const y1 = Math.max(...boxes.map((b) => b.y + b.height));
        const span = `${formatNum(x0)}…${formatNum(x1)} × ${formatNum(y0)}…${formatNum(y1)}`;
        const projected = outside.every((p) => p.projected === true);
        const what =
          outside.length === 1
            ? projected
              ? 'the appended ProjectedPath kept its page coordinates and spans'
              : 'the appended shape spans'
            : `${outside.length} appended shapes${projected ? ' (ProjectedPaths, which keep their page coordinates)' : ''} span`;
        const fix = projected
          ? 'Use Marker.fromPathBlock(), or toPathBlock() and append that'
          : 'Use Marker.fromPathBlock(), set viewBox, or translate the shape';
        warn(
          evalState,
          'marker-space',
          `Marker '${marker.id}': ${what} ${span} but the marker's viewBox is ${marker.viewBox} — the part outside it is clipped. ${fix}`,
          outside[0].loc,
        );
      }
    }
    // Convert refX/refY to output string: keyword stays as-is, number is stringified
    const refX = typeof marker.refX === 'number' ? formatNum(marker.refX) : marker.refX;
    const refY = typeof marker.refY === 'number' ? formatNum(marker.refY) : marker.refY;
    // orient: numeric values are radians → convert to degrees string; enum values stay as-is
    let orientOut: string;
    if (typeof marker.orient === 'number') {
      orientOut = formatNum((marker.orient * 180) / Math.PI);
    } else {
      orientOut = marker.orient;
    }
    const output: MarkerOutput = {
      id: marker.id,
      viewBox: marker.viewBox,
      markerWidth: marker.markerWidth,
      markerHeight: marker.markerHeight,
      refX,
      refY,
      elements: marker.paths.map((p) => ({ pathData: p.d, styles: { ...p.styles } })),
    };
    // Omit attrs that match SVG defaults
    if (marker.markerUnits !== 'strokeWidth') output.markerUnits = marker.markerUnits;
    // orient default in SVG is "0", but we default to "auto" (more useful); always emit
    output.orient = orientOut;
    if (marker.preserveAspectRatio !== 'xMidYMid meet') output.preserveAspectRatio = marker.preserveAspectRatio;
    markers.push(output);
  }

  // Build filters output
  const filters: FilterOutput[] = [];
  for (const [, filter] of evalState.filters) {
    if (filter.kind === 'noise') {
      filters.push({
        kind: 'noise',
        id: filter.id,
        style: filter.style,
        scale: filter.scale,
        octaves: filter.octaves,
        amount: filter.amount,
        monochrome: filter.monochrome,
        seed: filter.seed,
        blend: filter.blend,
        contrast: filter.contrast,
        stitch: filter.stitch,
      });
    } else if (filter.kind === 'glow') {
      filters.push({
        kind: 'glow',
        id: filter.id,
        mode: filter.mode,
        color: filter.color,
        radius: filter.radius,
        spread: filter.spread,
        opacity: filter.opacity,
      });
    } else if (filter.kind === 'emboss') {
      filters.push({
        kind: 'emboss',
        id: filter.id,
        angle: filter.angle,
        elevation: filter.elevation,
        depth: filter.depth,
        strength: filter.strength,
        shininess: filter.shininess,
        lightColor: filter.lightColor,
        smooth: filter.smooth,
      });
    } else if (filter.kind === 'elevation-shadow') {
      filters.push({
        kind: 'elevation-shadow',
        id: filter.id,
        elevation: filter.elevation,
        color: filter.color,
        direction: filter.direction,
        tightness: filter.tightness,
      });
    } else if (filter.kind === 'inner-shadow') {
      filters.push({
        kind: 'inner-shadow',
        id: filter.id,
        offsetX: filter.offsetX,
        offsetY: filter.offsetY,
        blur: filter.blur,
        color: filter.color,
        opacity: filter.opacity,
      });
    } else if (filter.kind === 'pixelate') {
      filters.push({
        kind: 'pixelate',
        id: filter.id,
        width: filter.width,
        height: filter.height,
        radius: filter.radius,
      });
    } else if (filter.kind === 'motion-blur') {
      filters.push({
        kind: 'motion-blur',
        id: filter.id,
        motionType: filter.motionType,
        distance: filter.distance,
        angle: filter.angle,
        samples: filter.samples,
      });
    } else {
      // Exhaustiveness guard — if a new filter kind is added to FilterValue
      // without a matching arm above, this fails at compile time and prevents
      // silent data loss between the evaluator and the renderer.
      const _exhaustive: never = filter;
      throw new Error(`Unsupported filter kind: ${String((_exhaustive as { kind: string }).kind)}`);
    }
  }

  // Build cssProperties output
  const cssProperties: CSSPropertyDeclaration[] = Array.from(evalState.cssProperties.values());

  const { reports: missingGlyphs, warnings: missingGlyphWarnings } = buildMissingGlyphReports(
    evalState.missingGlyphs,
  );
  for (const warning of missingGlyphWarnings) {
    warn(evalState, 'font-glyph', warning.replace(/^\[warn\] /, ''));
  }
  const fontCoverageRequests = buildFontCoverageRequests(evalState.fontCoverageRequests);

  return {
    layers,
    masks,
    clipPaths,
    gradients,
    patterns,
    markers,
    filters,
    cssProperties,
    logs: evalState.logs,
    warnings: evalState.warnings,
    ...(evalState.trace ? { commands: evalState.pathContext.commands.map((c) => ({ ...c, args: [...c.args] })) } : {}),
    calledStdlibFunctions: Array.from(evalState.calledStdlibFunctions),
    ...(missingGlyphs.length > 0 ? { missingGlyphs } : {}),
    ...(fontCoverageRequests.length > 0 ? { fontCoverageRequests } : {}),
    viewBox: evalState.viewBox
      ? {
          originX: evalState.viewBox.originX,
          originY: evalState.viewBox.originY,
          width: evalState.viewBox.width,
          height: evalState.viewBox.height,
        }
      : undefined,
  };
}

/**
 * Attach the Font namespace's slice requests to an error thrown mid-program,
 * so the playground can fetch them and recompile (see
 * playground/services/compiler-worker.ts).
 */
function annotateFontCoverage(err: unknown, evalState: EvaluationState | undefined): void {
  if (!(err instanceof Error) || !evalState?.fontCoverageRequests?.size) return;
  (err as Error & { fontCoverageRequests?: unknown }).fontCoverageRequests = buildFontCoverageRequests(
    evalState.fontCoverageRequests,
  );
}

export function evaluate(
  program: Program,
  options?: { toFixed?: number; fonts?: FontRegistry; trace?: boolean; strict?: StrictOption },
): CompileResult {
  setNumberFormat(options?.toFixed);
  let annotateState: EvaluationState | undefined;
  try {
    const pathContext = createPathContext({ trackHistory: !!options?.trace });
    const logs: LogEntry[] = [];
    const transformState = createTransformState();
    const evalState: EvaluationState = {
      pathContext,
      logs,
      warnings: [],
      strictWarnings: resolveStrict(options?.strict),
      trace: !!options?.trace,
      calledStdlibFunctions: new Set(),
      layers: new Map(),
      layerOrder: [],
      activeLayerName: null,
      defaultLayerName: null,
      transformState,
      masks: new Map(),
      clipPaths: new Map(),
      gradients: new Map(),
      patterns: new Map(),
      markers: new Map(),
      filters: new Map(),
      cssProperties: new Map(),
      fontRegistry: options?.fonts,
      // Created up front (not lazily) so blocks share the same maps — see
      // sharedProgramState. Reports are emitted only when non-empty.
      missingGlyphs: new Map(),
      fontCoverageRequests: new Map(),
    };
    annotateState = evalState;

    const scope = createScope();
    scope.evalState = evalState;
    // `ctx` isn't bound: lookupVariable computes it from the drawing state (ctxValueFor).

    const accum = createPathStore();
    evalState.rootAccum = accum;
    // Top level is a break/continue boundary (builder-enforced; defensive)
    const topFlow = evaluateStatementsToAccum(program.body, scope, accum);
    if (topFlow) throw loopFlowBoundaryError(topFlow);
    runSubscriptionDispatch(scope, evalState);

    return buildCompileResult(accum, evalState);
  } catch (caught) {
    const err = strayReturnError(caught);
    // A program that fails after a Font call on a sliced family (e.g. it
    // indexes a selection that's empty until the slices arrive) still tells
    // the playground host which slices to fetch before it retries.
    annotateFontCoverage(err, annotateState);
    throw err;
  } finally {
    resetNumberFormat();
  }
}

/**
 * Result of context-aware evaluation
 */
export interface EvaluateWithContextResult {
  path: string;
  context: PathContext & { heading?: number };
  logs: LogEntry[];
  warnings: CompileWarning[];
  /** Default-layer command history; `trace: true` only. */
  commands?: CommandTraceEntry[];
  calledStdlibFunctions: string[]; // Stdlib function names invoked during evaluation
  layers: LayerOutput[];
  masks: MaskOutput[];
  clipPaths: ClipPathOutput[];
  gradients: GradientOutput[];
  patterns: PatternOutput[];
  markers: MarkerOutput[];
  filters: FilterOutput[];
  cssProperties: CSSPropertyDeclaration[];
  viewBox?: import('./types').ViewBoxValue;
  missingGlyphs?: Array<{ family: string; weight: number; chars: string[] }>;
  fontCoverageRequests?: Array<{ family: string; weight: number; ranges: [number, number][] | null }>;
}

/**
 * Options for evaluateWithContext
 */
export interface EvaluateWithContextOptions {
  /** Whether to track command history (default: false for performance) */
  trackHistory?: boolean;
  /** Keep per-fragment records and per-layer command histories in the result (implies trackHistory). */
  trace?: boolean;
  /** Fixed decimal precision for number formatting */
  toFixed?: number;
  /** Font registry with loaded font data for precise metrics and glyph extraction */
  fonts?: import('./types').FontRegistry;
  /** Strict mode: warnings become errors — all (`true`) or the listed codes. */
  strict?: StrictOption;
}

/**
 * Evaluate a program with path context tracking
 * Returns compile result with layers, context, and log() outputs
 */
export function evaluateWithContext(
  program: Program,
  options: EvaluateWithContextOptions = {},
): EvaluateWithContextResult {
  setNumberFormat(options.toFixed);
  let annotateState: EvaluationState | undefined;
  try {
    const pathContext = createPathContext({ trackHistory: (options.trackHistory ?? false) || !!options.trace });
    const logs: LogEntry[] = [];
    const calledStdlibFunctions = new Set<string>();
    const transformState = createTransformState();
    const evalState: EvaluationState = {
      pathContext,
      logs,
      warnings: [],
      strictWarnings: resolveStrict(options.strict),
      trace: !!options.trace,
      calledStdlibFunctions,
      layers: new Map(),
      layerOrder: [],
      activeLayerName: null,
      defaultLayerName: null,
      transformState,
      masks: new Map(),
      clipPaths: new Map(),
      gradients: new Map(),
      patterns: new Map(),
      markers: new Map(),
      filters: new Map(),
      cssProperties: new Map(),
      fontRegistry: options.fonts,
      // Created up front (not lazily) so blocks share the same maps — see
      // sharedProgramState. Reports are emitted only when non-empty.
      missingGlyphs: new Map(),
      fontCoverageRequests: new Map(),
    };
    annotateState = evalState;

    const scope = createScope();
    scope.evalState = evalState;
    // `ctx` isn't bound: lookupVariable computes it from the drawing state (ctxValueFor).

    // Note: log() is handled specially in evaluateFunctionCall, not registered here

    const accum = createPathStore();
    evalState.rootAccum = accum;
    // Top level is a break/continue boundary (builder-enforced; defensive)
    const topFlow = evaluateStatementsToAccum(program.body, scope, accum);
    if (topFlow) throw loopFlowBoundaryError(topFlow);
    runSubscriptionDispatch(scope, evalState);

    const compileResult = buildCompileResult(accum, evalState);

    // Expose heading as user-facing alias for internal lastTangent
    const context = Object.create(pathContext, {
      heading: {
        get() {
          return pathContext.lastTangent;
        },
        enumerable: true,
      },
    });

    return {
      path: compileResult.layers[0]?.data ?? '',
      context,
      logs,
      calledStdlibFunctions: Array.from(calledStdlibFunctions),
      layers: compileResult.layers,
      warnings: compileResult.warnings,
      ...(compileResult.commands ? { commands: compileResult.commands } : {}),
      masks: compileResult.masks,
      clipPaths: compileResult.clipPaths,
      gradients: compileResult.gradients,
      patterns: compileResult.patterns,
      markers: compileResult.markers,
      filters: compileResult.filters,
      cssProperties: compileResult.cssProperties,
      viewBox: compileResult.viewBox,
      ...(compileResult.missingGlyphs ? { missingGlyphs: compileResult.missingGlyphs } : {}),
      ...(compileResult.fontCoverageRequests ? { fontCoverageRequests: compileResult.fontCoverageRequests } : {}),
    };
  } catch (caught) {
    const err = strayReturnError(caught);
    // A program that fails after a Font call on a sliced family (e.g. it
    // indexes a selection that's empty until the slices arrive) still tells
    // the playground host which slices to fetch before it retries.
    annotateFontCoverage(err, annotateState);
    throw err;
  } finally {
    resetNumberFormat();
  }
}

// Re-export types from context module
export type { CommandHistoryEntry, PathContext, Point, TransformState } from './context';

